// src/components/Badge/Badge.tsx
import { Flex, IconButton, Text, Icon } from "@chakra-ui/react";
import {
  BackgroundColoursNeutral,
  GreenGreen10,
  GreenGreen80,
  GreyGrey100,
  GreyGrey20,
  GreyGrey70,
  GreyWhite,
  Inconsolata13Bold,
  Inconsolata14Regular,
  IndigoIndigo10,
  Lato12UppercaseBold,
  Lato13Bold,
  OnBackgroundColoursNeutral,
  OrangeOrange10,
  OrangeOrange80,
  RedRed10,
  RedRed80,
  TurquoiseTurquoise10,
  TurquoiseTurquoise20,
  TurquoiseTurquoise70,
  YellowYellow10,
  YellowYellow80
} from "@sequencehq/design-tokens";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { jsx, jsxs } from "react/jsx-runtime";
var Badge = ({
  children,
  sentiment = "neutral",
  size = "md",
  onDelete,
  margin,
  wrapText = false,
  containerStyle,
  textStyle,
  icon,
  textTransform = "capitalize",
  "data-testid": dataTestId
}) => {
  const { color, backgroundColor, ...rest } = sentiments[sentiment];
  const sizeStyles = badgeSizes[size](!children);
  return /* @__PURE__ */ jsxs(
    Flex,
    {
      color,
      backgroundColor,
      ...rest.textStyle,
      ...sizeStyles,
      margin,
      alignItems: "center",
      gap: "4px",
      flexShrink: 0,
      style: { overflow: "hidden" },
      sx: containerStyle,
      borderRadius: "6px",
      height: "24px",
      textTransform,
      display: "inline-flex",
      placeItems: "center",
      "data-testid": dataTestId,
      children: [
        icon && /* @__PURE__ */ jsx(Icon, { as: icon, boxSize: "16px" }),
        wrapText ? /* @__PURE__ */ jsx(Text, { sx: textStyle, children }) : children,
        onDelete ? /* @__PURE__ */ jsx(
          IconButton,
          {
            "aria-label": `Delete ${typeof children === "string" ? children : ""}`,
            icon: /* @__PURE__ */ jsx(XMarkIcon, { width: "16px", height: "16px" }),
            onClick: onDelete,
            variant: "clear",
            minWidth: "16px",
            height: "16px"
          }
        ) : null
      ]
    }
  );
};
var sentiments = {
  success: {
    backgroundColor: GreenGreen10,
    color: GreenGreen80,
    textStyle: Lato12UppercaseBold
  },
  neutral: {
    backgroundColor: GreyGrey20,
    color: GreyGrey70,
    textStyle: Lato12UppercaseBold
  },
  error: {
    backgroundColor: RedRed10,
    color: RedRed80,
    textStyle: Lato12UppercaseBold
  },
  info: {
    backgroundColor: IndigoIndigo10,
    color: "#5F5CFF",
    textStyle: Lato12UppercaseBold
  },
  highlight: {
    backgroundColor: YellowYellow10,
    color: YellowYellow80,
    textStyle: Lato12UppercaseBold
  },
  "neutral-mono": {
    color: OnBackgroundColoursNeutral,
    backgroundColor: BackgroundColoursNeutral,
    textStyle: Inconsolata14Regular
  },
  warning: {
    color: OrangeOrange80,
    backgroundColor: OrangeOrange10,
    textStyle: Lato12UppercaseBold
  },
  metric: {
    color: GreyGrey100,
    backgroundColor: TurquoiseTurquoise20,
    textStyle: Inconsolata14Regular
  },
  monospaced: {
    color: TurquoiseTurquoise70,
    backgroundColor: TurquoiseTurquoise10,
    textStyle: Inconsolata13Bold
  },
  infoStrong: {
    color: GreyWhite,
    backgroundColor: "#3742FA",
    textStyle: Lato13Bold
  }
};
var badgeSizes = {
  md: (iconOnly = false) => ({
    py: "4px",
    px: iconOnly ? "4px" : "6px",
    width: "max-content",
    height: "min-content",
    borderRadius: "4px"
  }),
  sm: (iconOnly = false) => ({
    py: "2px",
    px: iconOnly ? "2px" : "4px",
    width: "max-content",
    height: "min-content",
    borderRadius: "4px"
  })
};

// src/components/BulkActions/BulkActionActionsList.tsx
import { Box, Flex as Flex2, Text as Text2 } from "@chakra-ui/react";
import {
  GreyGrey10,
  GreyGrey30,
  GreyGrey70 as GreyGrey702,
  Lato13Bold as Lato13Bold2
} from "@sequencehq/design-tokens";
import countBy from "lodash/countBy";
import { useState } from "react";
import sortBy from "lodash/fp/sortBy";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var MINIMUM_LIST_HEIGHT = 120;
var MAXIMUM_LIST_HEIGHT = 234;
var LIST_ITEM_ROW_HEIGHT = 33;
var LIST_PADDING = 16;
var BulkActionActionsList = ({ title, actions }) => {
  const counts = countBy(actions, "status");
  const [statusFilter, setStatusFilter] = useState("all");
  const heightOfList = Math.max(
    Math.min(
      MAXIMUM_LIST_HEIGHT,
      LIST_PADDING + actions.length * LIST_ITEM_ROW_HEIGHT
    ),
    MINIMUM_LIST_HEIGHT
  );
  const options = [
    { label: `All\xA0\xA0${actions.length}`, value: "all" },
    {
      label: `Success\xA0\xA0${counts.success ?? 0}`,
      value: "success"
    },
    ...counts.skipped ? [
      {
        label: `Skipped\xA0\xA0${counts.skipped ?? 0}`,
        value: "skipped"
      }
    ] : [],
    {
      label: `Failed\xA0\xA0${counts.failure ?? 0}`,
      value: "failure"
    }
  ];
  const filteredActions = statusFilter === "all" ? actions : actions.filter((action) => action.status === statusFilter);
  const sortedActions = sortBy([
    (a) => {
      return {
        success: 3,
        skipped: 2,
        failure: 1
      }[a.status];
    }
  ])(filteredActions);
  return /* @__PURE__ */ jsxs2(Flex2, { gap: "16px", paddingTop: "16px", flexDirection: "column", children: [
    /* @__PURE__ */ jsx2(Box, { padding: "0 16px", children: /* @__PURE__ */ jsx2(
      RadioButtonGroup,
      {
        "data-testid": "bulkActionList.tabs",
        fullWidth: true,
        options,
        onChange: setStatusFilter
      }
    ) }),
    sortedActions.length === 0 ? /* @__PURE__ */ jsx2(Box, { padding: "0 16px", children: /* @__PURE__ */ jsx2(
      Flex2,
      {
        marginBottom: "16px",
        "data-testid": `bulkActionList.${statusFilter}.empty`,
        borderRadius: "6px",
        backgroundColor: GreyGrey10,
        alignItems: "center",
        justifyContent: "center",
        height: heightOfList + 12,
        ...Lato13Bold2,
        color: GreyGrey702,
        children: "No matching invoices"
      }
    ) }) : /* @__PURE__ */ jsxs2(Box, { children: [
      /* @__PURE__ */ jsx2(Text2, { ...Lato13Bold2, color: GreyGrey702, padding: "0 16px 12px", children: title }),
      /* @__PURE__ */ jsx2(
        ScrollFade,
        {
          "data-testid": `bulkActionList.${statusFilter}.table`,
          height: heightOfList,
          children: sortedActions.map((action) => /* @__PURE__ */ jsx2(Box, { padding: "0 16px", children: /* @__PURE__ */ jsxs2(
            Flex2,
            {
              borderTop: `1px solid ${GreyGrey30}`,
              py: 1,
              alignItems: "center",
              "data-testid": `bulkActionList.action.${action.id}`,
              children: [
                /* @__PURE__ */ jsx2(Box, { flex: 1, fontSize: 13, children: action.title }),
                /* @__PURE__ */ jsx2(Badge, { sentiment: STATUS_TO_SENTIMENT[action.status], children: STATUS_TO_LABEL[action.status] })
              ]
            }
          ) }, action.id))
        }
      )
    ] })
  ] });
};
var STATUS_TO_SENTIMENT = {
  success: "success",
  skipped: "neutral",
  failure: "error"
};
var STATUS_TO_LABEL = {
  success: "Successful",
  skipped: "Skipped",
  failure: "Failed"
};

// src/components/BulkActions/BulkActions.tsx
import countBy2 from "lodash/countBy";
import { Box as Box2, Divider } from "@chakra-ui/react";
import { GreyGrey30 as GreyGrey302, GreyGrey40, ShadowL } from "@sequencehq/design-tokens";
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var BulkActions = ({
  actions,
  title,
  listTitle,
  status,
  onDismiss,
  onChangeReviewing,
  isReviewing = false
}) => {
  const counts = countBy2(actions, "status");
  return /* @__PURE__ */ jsxs3(Box2, { backgroundColor: "white", "data-testid": "bulkActions", children: [
    /* @__PURE__ */ jsx3(Box2, { px: 4, children: /* @__PURE__ */ jsx3(
      BulkActionsSummary,
      {
        title,
        status,
        counts: {
          success: counts.success ?? 0,
          failure: counts.failure ?? 0,
          skipped: counts.skipped ?? 0
        },
        isReviewing,
        onChangeReviewing,
        onDismiss
      }
    ) }),
    isReviewing && /* @__PURE__ */ jsxs3(Fragment, { children: [
      /* @__PURE__ */ jsx3(Divider, { color: GreyGrey302 }),
      /* @__PURE__ */ jsx3(Box2, { "data-testid": `bulkActionsList`, children: /* @__PURE__ */ jsx3(
        BulkActionActionsList,
        {
          title: listTitle,
          actions: onlyCompleteActions(actions)
        }
      ) })
    ] })
  ] });
};
var BulkActionsPositioner = ({ children }) => {
  return /* @__PURE__ */ jsx3(
    Box2,
    {
      position: "absolute",
      w: "420px",
      bottom: "20px",
      background: "white",
      left: "50%",
      transform: "translateX(-50%)",
      borderRadius: "8px",
      boxShadow: ShadowL,
      border: "1px solid",
      borderColor: GreyGrey40,
      overflow: "hidden",
      children
    }
  );
};
function isCompleteAction(action) {
  return action.status !== "pending";
}
function onlyCompleteActions(actions) {
  return actions.filter(isCompleteAction);
}

// src/components/BulkActions/BulkActionBar.tsx
import { Center as Center2, Divider as Divider2, Flex as Flex4, MenuItem as MenuItem2, Text as Text4 } from "@chakra-ui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { GreyGrey40 as GreyGrey403, GreyGrey60 as GreyGrey604, Lato13Bold as Lato13Bold3 } from "@sequencehq/design-tokens";

// src/components/SequenceMenu/SequenceMenu.tsx
import {
  cloneElement,
  useCallback as useCallback2,
  useRef as useRef4,
  useState as useState5
} from "react";
import { Portal } from "@chakra-ui/react";

// src/components/SequenceMenu/MenuPopover.tsx
import {
  Fragment as Fragment4,
  isValidElement,
  useEffect as useEffect2,
  useMemo as useMemo2,
  useRef as useRef3,
  useState as useState4
} from "react";
import { v4 as uuidv42 } from "uuid";
import { match } from "ts-pattern";

// src/components/SequenceMenu/MenuItem.tsx
import {
  Fragment as Fragment2,
  useLayoutEffect,
  useMemo,
  useRef as useRef2,
  useState as useState3
} from "react";
import { Box as Box4 } from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { GreyGrey20 as GreyGrey203 } from "@sequencehq/design-tokens";

// src/components/SequenceMenu/useMousePosition.tsx
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState as useState2 } from "react";
var useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState2({
    x: 0,
    y: 0
  });
  const hasInitialUpdate = useRef(false);
  const updateMousePosition = useCallback((ev) => {
    setMousePosition({ x: ev.clientX, y: ev.clientY });
    hasInitialUpdate.current = true;
  }, []);
  const debouncedUpdateMousePosition = debounce(
    updateMousePosition,
    // update with the mouse co-ordinates instantly on initial render
    hasInitialUpdate.current ? 150 : 0
  );
  useEffect(() => {
    window.addEventListener("mousemove", debouncedUpdateMousePosition);
    return () => {
      window.removeEventListener("mousemove", debouncedUpdateMousePosition);
    };
  }, [debouncedUpdateMousePosition]);
  return mousePosition;
};
var useMousePosition_default = useMousePosition;

// src/components/SequenceMenu/MenuContainerSafeArea.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var MenuContainerSafeArea = ({
  subMenu,
  showSubmenuOnLeft
}) => {
  const { x: mouseX, y: mouseY } = useMousePosition_default();
  const {
    height: subMenuHeight,
    width: subMenuWidth,
    x: subMenuX,
    y: subMenuY
  } = subMenu.getBoundingClientRect();
  const svgWidth = showSubmenuOnLeft ? mouseX - subMenuX + 4 : subMenuX - mouseX + 4;
  const svgHeight = subMenuHeight;
  const rightSideSafeAreaPath = `M 0, ${mouseY - subMenuY} 
            L ${svgWidth},${svgHeight}  
            L ${svgWidth},0 z`;
  const leftSideSafeAreaPath = `M ${svgWidth}, ${mouseY - subMenuY} 
            L 0,${svgHeight}  
            L 0,0 z`;
  return /* @__PURE__ */ jsxs4(
    "svg",
    {
      style: {
        position: "fixed",
        width: svgWidth,
        height: subMenuHeight,
        pointerEvents: "none",
        zIndex: 2,
        top: subMenuY,
        left: showSubmenuOnLeft ? subMenuX + subMenuWidth - 2 : mouseX - 2
      },
      children: [
        /* @__PURE__ */ jsx4(
          "path",
          {
            pointerEvents: "none",
            width: "100%",
            height: "100%",
            fill: "transparent",
            d: `M 0,0 L ${svgWidth},0 L ${svgWidth},${svgHeight} L 0,${svgHeight} z`
          }
        ),
        /* @__PURE__ */ jsx4(
          "path",
          {
            pointerEvents: "auto",
            fill: "transparent",
            d: showSubmenuOnLeft ? leftSideSafeAreaPath : rightSideSafeAreaPath
          }
        )
      ]
    }
  );
};

// src/components/SequenceMenu/MenuContainer.tsx
import { Flex as Flex3 } from "@chakra-ui/react";
import { GreyGrey40 as GreyGrey402, GreyWhite as GreyWhite2 } from "@sequencehq/design-tokens";
import { jsx as jsx5 } from "react/jsx-runtime";
var MenuContainer = ({
  children,
  refMenu,
  width,
  "data-testid": testId,
  ...rest
}) => /* @__PURE__ */ jsx5(
  Flex3,
  {
    ref: refMenu,
    flexDirection: "column",
    width: `${width}px`,
    border: `${GreyGrey402} 1px solid`,
    borderRadius: "8px",
    padding: "4px",
    background: GreyWhite2,
    boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.08)",
    zIndex: "1",
    "data-testid": testId,
    ...rest,
    children
  }
);

// src/components/SequenceMenu/MenuItemButton.tsx
import { Button, Text as Text3, Center } from "@chakra-ui/react";
import {
  GreyGrey20 as GreyGrey202,
  GreyGrey60,
  GreyGrey80,
  GreyWhite as GreyWhite3,
  IndigoIndigo50,
  Lato13Regular
} from "@sequencehq/design-tokens";
import ChevronRightIcon from "@heroicons/react/16/solid/ChevronRightIcon";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var MenuItemButton = ({
  background,
  setIsOpen,
  isDisabled,
  ...itemProps
}) => {
  const hasSubItems = Boolean(itemProps.subItems?.length);
  return /* @__PURE__ */ jsxs5(
    Button,
    {
      variant: "unstyled",
      width: "100%",
      textAlign: "left",
      padding: "8px",
      color: GreyGrey80,
      isDisabled,
      _hover: isDisabled ? void 0 : { background: GreyGrey202 },
      _active: !(hasSubItems || isDisabled) ? { background: IndigoIndigo50, color: GreyWhite3 } : void 0,
      background,
      transition: "none",
      cursor: hasSubItems ? "default" : "pointer",
      alignItems: "center",
      gap: "8px",
      display: "flex",
      flexDirection: "row",
      height: "initial",
      ...itemProps.style,
      onClick: (e) => {
        if (hasSubItems || isDisabled) {
          return;
        }
        itemProps.onClick?.(e);
        setIsOpen?.(false);
      },
      children: [
        itemProps.iconLeft && /* @__PURE__ */ jsx6(Center, { flexShrink: "0", children: itemProps.iconLeft }),
        /* @__PURE__ */ jsx6(
          Text3,
          {
            ...Lato13Regular,
            flex: "1",
            color: "inherit",
            textOverflow: "ellipsis",
            overflow: "hidden",
            children: itemProps.label
          }
        ),
        itemProps.iconRight && /* @__PURE__ */ jsx6(Center, { flexShrink: "0", children: itemProps.iconRight }),
        hasSubItems && /* @__PURE__ */ jsx6(Center, { flexShrink: "0", children: /* @__PURE__ */ jsx6(ChevronRightIcon, { width: "16px", height: "16px", color: GreyGrey60 }) })
      ]
    }
  );
};

// src/components/SequenceMenu/MenuDivider.tsx
import { Box as Box3 } from "@chakra-ui/react";
import { GreyGrey30 as GreyGrey303 } from "@sequencehq/design-tokens";
import { jsx as jsx7 } from "react/jsx-runtime";
var MenuDivider = () => /* @__PURE__ */ jsx7(
  Box3,
  {
    width: "calc(100% + 8px)",
    position: "relative",
    left: "-4px",
    height: "1px",
    background: GreyGrey303,
    my: "4px"
  }
);

// src/components/SequenceMenu/MenuItem.tsx
import { Fragment as Fragment3, jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var MenuItem = ({
  visibleSubMenuUuid,
  setVisibleSubMenuUuid,
  width,
  setIsOpen,
  subItems: subItemsProp,
  "data-testid": testId,
  ...itemProps
}) => {
  const refSubMenu = useRef2(null);
  const refMenu = useRef2(null);
  const [showSubmenuOnLeftSide, setShowSubmenuOnLeftSide] = useState3(false);
  const isSubMenuVisible = subItemsProp && visibleSubMenuUuid === itemProps.uuid;
  useLayoutEffect(() => {
    const windowWidth = window.innerWidth;
    const rightEdgeOfMenu = refMenu.current?.getBoundingClientRect()?.right || 0;
    const isThereEnoughRoomOnRightSide = windowWidth > rightEdgeOfMenu + (width || 0);
    setShowSubmenuOnLeftSide(!isThereEnoughRoomOnRightSide);
  });
  const subItems = useMemo(() => {
    if (typeof subItemsProp === "undefined") {
      return void 0;
    }
    return subItemsProp.map((subItem) => ({
      ...subItem,
      uuid: uuidv4()
    })).filter((item) => !item.hidden);
  }, [subItemsProp]);
  const Wrapper = itemProps.tooltip ? Tooltip : Fragment2;
  const wrapperProps = itemProps.tooltip ? { label: itemProps.tooltip, placement: "right" } : {};
  return /* @__PURE__ */ jsx8(Wrapper, { ...wrapperProps, children: /* @__PURE__ */ jsxs6(
    Box4,
    {
      ref: refMenu,
      position: "relative",
      onMouseEnter: () => setVisibleSubMenuUuid?.(itemProps.uuid),
      onMouseLeave: () => setVisibleSubMenuUuid?.(void 0),
      "data-testid": testId,
      children: [
        /* @__PURE__ */ jsx8(
          MenuItemButton,
          {
            ...itemProps,
            subItems,
            background: isSubMenuVisible ? GreyGrey203 : void 0,
            setIsOpen
          }
        ),
        isSubMenuVisible && refSubMenu.current && /* @__PURE__ */ jsx8(
          MenuContainerSafeArea,
          {
            subMenu: refSubMenu.current,
            showSubmenuOnLeft: showSubmenuOnLeftSide
          }
        ),
        subItems && width && /* @__PURE__ */ jsxs6(
          MenuContainer,
          {
            refMenu: refSubMenu,
            visibility: isSubMenuVisible ? "visible" : "hidden",
            position: "absolute",
            width,
            left: showSubmenuOnLeftSide ? `-${width + 8}px` : `${width - 2}px`,
            top: "-5px",
            children: [
              itemProps.subItemsHeader && /* @__PURE__ */ jsxs6(Fragment3, { children: [
                itemProps.subItemsHeader,
                /* @__PURE__ */ jsx8(MenuDivider, {})
              ] }),
              subItems.map((subItem) => /* @__PURE__ */ jsxs6(Fragment2, { children: [
                /* @__PURE__ */ jsx8(MenuItemButton, { ...subItem, setIsOpen }),
                subItem.divider && /* @__PURE__ */ jsx8(MenuDivider, {})
              ] }, subItem.uuid))
            ]
          }
        )
      ]
    }
  ) });
};

// src/components/SequenceMenu/MenuPopover.tsx
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var MenuPopover = ({
  items: itemsProp,
  refMenuButton,
  setIsOpen,
  "data-testid": testId,
  ...menuStyleProps
}) => {
  const [visibleSubMenuUuid, setVisibleSubMenuUuid] = useState4();
  const items = useMemo2(
    () => itemsProp.reduce((memo2, item) => {
      if (!isMenuItemConfig(item)) {
        return [...memo2, item];
      }
      if (item.hidden) {
        return memo2;
      }
      return [
        ...memo2,
        {
          ...item,
          uuid: uuidv42()
        }
      ];
    }, []),
    [itemsProp]
  );
  const [menuPosition, setMenuPosition] = useState4();
  const refMenu = useRef3(null);
  useEffect2(() => {
    if (!refMenuButton.current || !refMenu.current) {
      return;
    }
    const [offsetX, offsetY] = menuStyleProps.offset;
    const {
      x: menuButtonX,
      y: menuButtonY,
      height: menuButtonHeight,
      width: menuButtonWidth
    } = refMenuButton.current.getBoundingClientRect();
    const { height: menuHeight, width: menuWidth } = refMenu.current.getBoundingClientRect();
    match(menuStyleProps.alignment).with("bottom-left", () => {
      setMenuPosition({
        left: `${menuButtonX + offsetX + window.scrollX}px`,
        top: `${menuButtonY + menuButtonHeight + offsetY + window.scrollY}px`
      });
    }).with("bottom-right", () => {
      setMenuPosition({
        left: `${menuButtonX + offsetX + menuButtonWidth - menuWidth + window.scrollX}px`,
        top: `${menuButtonY + menuButtonHeight + offsetY + window.scrollY}px`
      });
    }).with("top-left", () => {
      setMenuPosition({
        left: `${menuButtonX + offsetX + window.scrollX}px`,
        top: `${menuButtonY - menuHeight - offsetY + window.scrollY}px`
      });
    }).with("top-right", () => {
      setMenuPosition({
        left: `${menuButtonX + offsetX + menuButtonWidth - menuWidth + window.scrollX}px`,
        top: `${menuButtonY - menuHeight - offsetY + window.scrollY}px`
      });
    }).exhaustive();
  }, [menuStyleProps.alignment, menuStyleProps.offset, refMenuButton]);
  const renderItem = (item) => {
    if (isMenuItemConfigWithUuid(item)) {
      return /* @__PURE__ */ jsxs7(Fragment4, { children: [
        /* @__PURE__ */ jsx9(
          MenuItem,
          {
            ...item,
            visibleSubMenuUuid,
            setVisibleSubMenuUuid,
            setIsOpen,
            width: menuStyleProps.width
          }
        ),
        item.divider && /* @__PURE__ */ jsx9(MenuDivider, {})
      ] }, item.uuid);
    }
    if (typeof item === "function") {
      return item({ ...menuStyleProps, setIsOpen });
    }
    if (isValidElement(item)) {
      return item;
    }
    return null;
  };
  return /* @__PURE__ */ jsx9(
    MenuContainer,
    {
      refMenu,
      position: "absolute",
      left: menuPosition?.left,
      top: menuPosition?.top,
      visibility: menuPosition ? void 0 : "hidden",
      width: menuStyleProps.width,
      "data-testid": `${testId}.menuItems`,
      children: items.map(renderItem)
    }
  );
};
var isMenuItemConfig = (item) => item.label !== void 0;
var isMenuItemConfigWithUuid = (item) => item.uuid !== void 0;

// src/components/SequenceMenu/MenuDismiss.tsx
import { Box as Box5 } from "@chakra-ui/react";
import { jsx as jsx10 } from "react/jsx-runtime";
var MenuDismiss = ({ isOpen, setIsOpen }) => /* @__PURE__ */ jsx10(
  Box5,
  {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    onClick: (e) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  }
);

// src/components/SequenceMenu/SequenceMenu.tsx
import { Fragment as Fragment5, jsx as jsx11, jsxs as jsxs8 } from "react/jsx-runtime";
var SequenceMenu = ({
  menuButton,
  items,
  offset = [0, 4],
  alignment = "bottom-left",
  width = 220,
  "data-testid": testId
}) => {
  const refMenuButton = useRef4(null);
  const [isOpen, setIsOpen] = useState5(false);
  const menuButtonOnClick = useCallback2(
    (event) => {
      setIsOpen(!isOpen);
      event.stopPropagation();
    },
    [isOpen]
  );
  const menuButtonElement = cloneElement(menuButton, {
    ref: refMenuButton,
    onClick: menuButtonOnClick,
    ...testId ? { "data-testid": testId } : {}
  });
  return /* @__PURE__ */ jsxs8(Fragment5, { children: [
    menuButtonElement,
    isOpen && /* @__PURE__ */ jsxs8(Portal, { children: [
      /* @__PURE__ */ jsx11(
        MenuPopover,
        {
          items,
          refMenuButton,
          setIsOpen,
          alignment,
          offset,
          width,
          "data-testid": testId
        }
      ),
      /* @__PURE__ */ jsx11(MenuDismiss, { isOpen, setIsOpen })
    ] })
  ] });
};

// src/components/Button/Button.tsx
import {
  Button as ChakraButton,
  forwardRef,
  IconButton as IconButton2
} from "@chakra-ui/react";
import { jsx as jsx12 } from "react/jsx-runtime";
var Button2 = forwardRef((props, ref) => {
  const {
    children,
    variant,
    disabled,
    active,
    size,
    fullWidth,
    ...otherProps
  } = props;
  const sharedProps = {
    disabled: false,
    active: false,
    type: "button",
    isDisabled: disabled,
    isActive: active,
    width: fullWidth ? "100%" : size,
    height: size,
    variant: `component-library-${variant === "destructive" ? "warning" : variant ?? "primary"}`,
    ref,
    ...otherProps
  };
  if (isIconButtonProps(props)) {
    return /* @__PURE__ */ jsx12(
      IconButton2,
      {
        ...sharedProps,
        "aria-label": props["aria-label"],
        width: size ?? "32px",
        height: size ?? "32px"
      }
    );
  }
  return /* @__PURE__ */ jsx12(
    ChakraButton,
    {
      ...sharedProps,
      leftIcon: props.leadingIcon,
      rightIcon: props.trailingIcon,
      children
    }
  );
});
var isIconButtonProps = (props) => "icon" in props;

// src/components/Button/ClearButton.tsx
import { Button as Button3 } from "@chakra-ui/react";
import { XMarkIcon as XMarkIcon2 } from "@heroicons/react/16/solid";
import { GreyGrey30 as GreyGrey304, GreyGrey60 as GreyGrey602, GreyGrey80 as GreyGrey802 } from "@sequencehq/design-tokens";
import { jsx as jsx13 } from "react/jsx-runtime";
var ClearButton = ({ onClick, ...props }) => {
  return /* @__PURE__ */ jsx13(
    Button3,
    {
      onClick,
      variant: "unstyled",
      minWidth: "initial",
      height: "initial",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "4px",
      color: GreyGrey602,
      _hover: { color: GreyGrey802, background: GreyGrey304 },
      ...props,
      children: /* @__PURE__ */ jsx13(XMarkIcon2, { width: "16px", height: "16px", color: "inherit" })
    }
  );
};

// src/components/SequenceMenu/SequenceKebabMenu.tsx
import { EllipsisHorizontalIcon } from "@heroicons/react/16/solid";
import { GreyGrey60 as GreyGrey603 } from "@sequencehq/design-tokens";
import { jsx as jsx14 } from "react/jsx-runtime";
var SequenceKebabMenu = ({
  "aria-label": ariaLabel,
  variant,
  disabled = false,
  buttonSize,
  ...props
}) => {
  return /* @__PURE__ */ jsx14(
    SequenceMenu,
    {
      ...props,
      menuButton: /* @__PURE__ */ jsx14(
        Button2,
        {
          variant: variant ?? "secondary",
          "aria-label": ariaLabel ?? "menu",
          size: buttonSize === "small" ? "24px" : "32px",
          icon: /* @__PURE__ */ jsx14(
            EllipsisHorizontalIcon,
            {
              width: "16px",
              height: "16px",
              color: GreyGrey603
            }
          ),
          disabled
        }
      )
    }
  );
};

// src/components/BulkActions/BulkActionBar.tsx
import { XMarkIcon as XMarkIcon3 } from "@heroicons/react/16/solid";
import { Fragment as Fragment6, jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var BulkActionBarContainer = ({ count, children, onDismiss }) => {
  return /* @__PURE__ */ jsxs9(Flex4, { alignItems: "center", gap: 1, "data-testid": "bulkActions.container", children: [
    /* @__PURE__ */ jsx15(Badge, { sentiment: "infoStrong", children: count }),
    /* @__PURE__ */ jsx15(Text4, { ...Lato13Bold3, ml: 1, children: "selected" }),
    /* @__PURE__ */ jsx15(Center2, { height: "16px", px: 2, children: /* @__PURE__ */ jsx15(Divider2, { orientation: "vertical", color: GreyGrey403 }) }),
    children,
    /* @__PURE__ */ jsx15(
      Button2,
      {
        onClick: onDismiss,
        variant: "ghost",
        icon: /* @__PURE__ */ jsx15(XMarkIcon3, { width: "16px", color: GreyGrey604 }),
        "data-testid": "bulkActions.dismiss",
        "aria-label": "dismiss bulk actions"
      }
    )
  ] });
};
var BulkActionBarButton = ({
  count,
  showCount,
  children,
  icon,
  onClick,
  "data-testid": dataTestId,
  showCaret = false
}) => {
  const disabled = count === 0;
  return /* @__PURE__ */ jsx15(
    Button2,
    {
      variant: "secondary",
      "data-testid": dataTestId,
      leadingIcon: icon,
      disabled,
      onClick: disabled ? void 0 : onClick,
      trailingIcon: /* @__PURE__ */ jsxs9(Fragment6, { children: [
        showCount && /* @__PURE__ */ jsx15(Badge, { children: count }),
        showCaret && /* @__PURE__ */ jsx15(ChevronDownIcon, { style: { marginLeft: 4 }, width: "20px" })
      ] }),
      children
    }
  );
};
var BulkActionBarDropdown = ({
  menuItems,
  disabled,
  "data-testid": dataTestId,
  ...buttonProps
}) => {
  return /* @__PURE__ */ jsx15(ControlContextProvider, { placement: "top-end", menuWidth: 172, children: /* @__PURE__ */ jsx15(
    Control,
    {
      "data-testid": `${dataTestId}.dropdown`,
      disabled: buttonProps.count === 0,
      trigger: /* @__PURE__ */ jsx15(
        BulkActionBarButton,
        {
          ...buttonProps,
          "data-testid": dataTestId,
          showCaret: true
        }
      ),
      menuItems
    }
  ) });
};
var KebabMenu = ({ items }) => {
  return /* @__PURE__ */ jsx15("div", { style: { marginRight: "-8px" }, children: /* @__PURE__ */ jsx15(
    SequenceKebabMenu,
    {
      "data-testid": "bulkActions.menu",
      variant: "ghost",
      items,
      alignment: "top-right"
    }
  ) });
};
var BulkActionBar = Object.assign(BulkActionBarContainer, {
  Button: BulkActionBarButton,
  Dropdown: Object.assign(BulkActionBarDropdown, {
    Item: MenuItem2
  }),
  KebabMenu
});

// src/components/BulkActions/BulkActionsSummary.tsx
import { Box as Box6, Flex as Flex5, Spinner, Text as Text5 } from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import {
  GreyGrey50,
  GreyGrey60 as GreyGrey605,
  GreyGrey70 as GreyGrey703,
  GreyGrey90,
  Lato13Regular as Lato13Regular2,
  Lato14Bold
} from "@sequencehq/design-tokens";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { XMarkIcon as XMarkIcon4 } from "@heroicons/react/20/solid";
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var BulkActionsSummary = ({
  title,
  status,
  counts,
  onDismiss,
  isReviewing = false,
  onChangeReviewing
}) => {
  const progressString = [
    `${counts.success} successful`,
    counts.skipped ? `${counts.skipped} skipped` : "",
    `${counts.failure} failed`
  ].filter(Boolean).join(", ");
  return /* @__PURE__ */ jsxs10(Flex5, { gap: 4, py: 4, alignItems: "center", children: [
    /* @__PURE__ */ jsx16(Flex5, { w: 5, justifyContent: "center", children: status === "PROCESSING" ? /* @__PURE__ */ jsx16(Spinner, { height: "16px", width: "16px", color: GreyGrey50 }) : status === "COMPLETED" ? /* @__PURE__ */ jsx16(Badge, { sentiment: "success", icon: CheckCircleIcon }) : /* @__PURE__ */ jsx16(Badge, { sentiment: "highlight", icon: ExclamationTriangleIcon }) }),
    /* @__PURE__ */ jsxs10(Box6, { flex: 1, children: [
      /* @__PURE__ */ jsx16(Text5, { color: GreyGrey90, ...Lato14Bold, mb: 1, children: title }),
      /* @__PURE__ */ jsx16(Text5, { color: GreyGrey703, ...Lato13Regular2, children: progressString })
    ] }),
    /* @__PURE__ */ jsxs10(Flex5, { gap: "4px", children: [
      status === "PROCESSING" ? null : /* @__PURE__ */ jsx16(
        Button2,
        {
          variant: "secondary",
          onClick: () => onChangeReviewing(!isReviewing),
          disabled: isReviewing,
          children: "Review"
        }
      ),
      status !== "PROCESSING" && /* @__PURE__ */ jsx16(
        Button2,
        {
          onClick: onDismiss,
          variant: "ghost",
          icon: /* @__PURE__ */ jsx16(XMarkIcon4, { width: "20px", color: GreyGrey605 }),
          "data-testid": "bulkActions.dismiss",
          "aria-label": "dismiss bulk actions"
        }
      )
    ] })
  ] });
};

// src/components/icons/AmazonRedshiftIcon.tsx
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
var AmazonRedshiftIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs11(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M31.9735 48.9338L56.3112 54.7285V9.14233L31.9735 14.937V48.9338Z",
          fill: "#205B97"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M56.3113 9.14233L60.947 11.4602V52.4106L56.3113 54.7285V9.14233ZM31.9735 48.9338L7.6358 54.7285V9.14233L31.9735 14.937V48.9338Z",
          fill: "#5193CE"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M7.63576 9.14233L3 11.4602V52.4106L7.63576 54.7285V9.14233Z",
          fill: "#205B97"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M38.1557 64L47.4272 59.3642V4.63576L38.1557 0L35.3211 30.3908L38.1557 64Z",
          fill: "#5193CE"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M25.7913 64L16.5198 59.3642V4.63576L25.7913 0L28.6258 30.3908L25.7913 64Z",
          fill: "#205B97"
        }
      ),
      /* @__PURE__ */ jsx17("path", { d: "M25.7913 0H38.2813V63.8708H25.7913V0Z", fill: "#2E73B7" })
    ]
  }
);

// src/components/icons/ArrowLeftIcon.tsx
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var ArrowLeftIcon = ({
  width = 24,
  height = 24
}) => /* @__PURE__ */ jsxs12(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx18(
        "path",
        {
          d: "M19 12H5",
          stroke: "#485972",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx18(
        "path",
        {
          d: "M12 19L5 12L12 5",
          stroke: "#485972",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/AvalaraIcon.tsx
import { jsx as jsx19, jsxs as jsxs13 } from "react/jsx-runtime";
var AvalaraIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs13(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 30 30",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx19(
        "path",
        {
          "fill-rule": "evenodd",
          "clip-rule": "evenodd",
          d: "M24.4622 30H29.6911L25.0787 18.9628C23.8284 20.2929 22.6528 21.6914 21.5574 23.1517L24.4622 30Z",
          fill: "#FF6600"
        }
      ),
      /* @__PURE__ */ jsx19(
        "path",
        {
          "fill-rule": "evenodd",
          "clip-rule": "evenodd",
          d: "M12.4009 19.8491L11.1204 22.871C12.9621 25.6137 14.1794 27.975 14.8491 29.1056C16.1536 26.8945 19.3385 20.3276 25.9388 14.1842L25.2109 12.4678C20.9256 15.6324 17.2769 19.6802 14.8561 22.767C14.8537 22.7716 13.3076 20.8729 12.4009 19.8491Z",
          fill: "#059BD2"
        }
      ),
      /* @__PURE__ */ jsx19(
        "path",
        {
          "fill-rule": "evenodd",
          "clip-rule": "evenodd",
          d: "M17.1541 0H12.538L0 29.9999H5.22832L14.2935 8.61958L14.7242 7.45916H14.9673L15.3968 8.61958L18.0827 14.9565C19.3114 13.7318 20.6023 12.5711 21.9502 11.4791L17.1541 0Z",
          fill: "#FF6600"
        }
      )
    ]
  }
);

// src/components/icons/CalendarIcon.tsx
import { jsx as jsx20, jsxs as jsxs14 } from "react/jsx-runtime";
var CalendarIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs14(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z",
          stroke: "#344054",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M16 2V6",
          stroke: "#344054",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M8 2V6",
          stroke: "#344054",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M3 10H21",
          stroke: "#344054",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/CheckboxIcon.tsx
import { jsx as jsx21, jsxs as jsxs15 } from "react/jsx-runtime";
var CheckboxIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs15(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M7.5 9.16683L10 11.6668L18.3333 3.3335",
          stroke: "white",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M17.5 10V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H13.3333",
          stroke: "white",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/CheckCircleIcon.tsx
import { jsx as jsx22, jsxs as jsxs16 } from "react/jsx-runtime";
var CheckCircleIcon2 = ({
  color = "black",
  width = 24,
  height = 24,
  title
}) => /* @__PURE__ */ jsxs16("svg", { width, height, viewBox: "0 0 24 24", focusable: "false", children: [
  title ?? /* @__PURE__ */ jsx22("title", { children: title }),
  /* @__PURE__ */ jsx22(
    "path",
    {
      fill: color,
      d: "M12,0A12,12,0,1,0,24,12,12.014,12.014,0,0,0,12,0Zm6.927,8.2-6.845,9.289a1.011,1.011,0,0,1-1.43.188L5.764,13.769a1,1,0,1,1,1.25-1.562l4.076,3.261,6.227-8.451A1,1,0,1,1,18.927,8.2Z"
    }
  )
] });

// src/components/icons/ChevronDownIcon.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var ChevronDownIcon2 = ({
  width,
  height,
  color = "black"
}) => /* @__PURE__ */ jsx23(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 8 5",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx23(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M7.80474 1.47139C8.06509 1.21103 8.06509 0.788925 7.80474 0.528575C7.54439 0.268226 7.12228 0.268226 6.86193 0.528575L4 3.3905L1.13807 0.528576C0.877722 0.268226 0.455612 0.268226 0.195262 0.528576C-0.0650875 0.788925 -0.0650875 1.21104 0.195262 1.47139L3.5286 4.80472C3.78895 5.06507 4.21106 5.06507 4.47141 4.80472L7.80474 1.47139Z",
        fill: color
      }
    )
  }
);

// src/components/icons/ChevronLeftIcon.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var ChevronLeftIcon = ({
  color = "#2D3748"
}) => /* @__PURE__ */ jsx24(
  "svg",
  {
    width: "7",
    height: "12",
    viewBox: "0 0 7 12",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx24(
      "path",
      {
        d: "M2.47531 5.83496L6.75328 10.1129L5.53125 11.335L0.03125 5.83496L5.53125 0.334961L6.75328 1.55699L2.47531 5.83496Z",
        fill: color
      }
    )
  }
);

// src/components/icons/ChevronRightIcon.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var ChevronRightIcon2 = ({
  color = "#2D3748"
}) => /* @__PURE__ */ jsx25(
  "svg",
  {
    width: "8",
    height: "12",
    viewBox: "0 0 8 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M5.01625 5.83496L0.738281 1.55699L1.96031 0.334961L7.46031 5.83496L1.96031 11.335L0.738281 10.1129L5.01625 5.83496Z",
        fill: color
      }
    )
  }
);

// src/components/icons/ChevronUpIcon.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var ChevronUpIcon = ({
  width,
  height,
  color = "black"
}) => /* @__PURE__ */ jsx26(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 8 5",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx26(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M0.195262 3.5286C-0.0650874 3.78895 -0.0650874 4.21106 0.195262 4.47141C0.455612 4.73175 0.877722 4.73175 1.13807 4.47141L4 1.60948L6.86193 4.47141C7.12228 4.73175 7.54439 4.73176 7.80474 4.47141C8.06509 4.21106 8.06509 3.78895 7.80474 3.5286L4.47141 0.195262C4.34638 0.0702378 4.17681 0 4 0C3.82319 0 3.65362 0.0702378 3.5286 0.195262L0.195262 3.5286Z",
        fill: color
      }
    )
  }
);

// src/components/icons/CreditNoteIcon.tsx
import { jsx as jsx27, jsxs as jsxs17 } from "react/jsx-runtime";
var CreditNoteIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs17(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx27("circle", { cx: "10", cy: "10", r: "10", fill: "#ebfbc9" }),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M11.1654 4.16699H6.4987C6.18928 4.16699 5.89253 4.28991 5.67374 4.5087C5.45495 4.72749 5.33203 5.02424 5.33203 5.33366V14.667C5.33203 14.9764 5.45495 15.2732 5.67374 15.492C5.89253 15.7107 6.18928 15.8337 6.4987 15.8337H13.4987C13.8081 15.8337 14.1049 15.7107 14.3237 15.492C14.5424 15.2732 14.6654 14.9764 14.6654 14.667V7.66699L11.1654 4.16699Z",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M11.168 4.16699V7.66699H14.668",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M12.3346 10.583H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M12.3346 12.917H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M8.83464 8.25H8.2513H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/CrossIcon.tsx
import { jsx as jsx28, jsxs as jsxs18 } from "react/jsx-runtime";
var CrossIcon = ({
  width = 14,
  height = 14,
  color = "currentColor"
}) => /* @__PURE__ */ jsxs18(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 14 14",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M1 1L13 13",
          stroke: color,
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M13 1L1 13",
          stroke: color,
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/CustomerIcon.tsx
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
var CustomerIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs19(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx29("circle", { cx: "10.0002", cy: "10", r: "10", fill: "#FFE9EC" }),
      /* @__PURE__ */ jsx29(
        "path",
        {
          d: "M12.3137 14.1669V13.241C12.3137 12.7499 12.1186 12.2788 11.7713 11.9316C11.424 11.5843 10.9529 11.3892 10.4618 11.3892H6.7581C6.26696 11.3892 5.79593 11.5843 5.44864 11.9316C5.10136 12.2788 4.90625 12.7499 4.90625 13.241V14.1669",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx29(
        "path",
        {
          d: "M8.61015 9.5372C9.6329 9.5372 10.462 8.7081 10.462 7.68535C10.462 6.6626 9.6329 5.8335 8.61015 5.8335C7.5874 5.8335 6.7583 6.6626 6.7583 7.68535C6.7583 8.7081 7.5874 9.5372 8.61015 9.5372Z",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx29(
        "path",
        {
          d: "M15.0923 14.1668V13.2409C15.092 12.8306 14.9554 12.432 14.704 12.1077C14.4526 11.7834 14.1007 11.5518 13.7034 11.4492",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx29(
        "path",
        {
          d: "M11.8518 5.89355C12.2501 5.99555 12.6032 6.22721 12.8553 6.55203C13.1075 6.87685 13.2443 7.27635 13.2443 7.68754C13.2443 8.09873 13.1075 8.49822 12.8553 8.82304C12.6032 9.14786 12.2501 9.37953 11.8518 9.48152",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/GcpLogsIcon.tsx
import { jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
var GcpLogsIcon = ({
  width = 16,
  height = 16
}) => /* @__PURE__ */ jsx30(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 81 76",
    fill: "#fff",
    fillRule: "evenodd",
    stroke: "#000",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: /* @__PURE__ */ jsxs20("g", { stroke: "none", children: [
      /* @__PURE__ */ jsx30("path", { fill: "white", d: "M0 0h24.127v23.492H0z" }),
      /* @__PURE__ */ jsx30(
        "path",
        {
          d: "M27.937 3.81H80v15.873H27.937zm0 27.936H80v15.873H27.937zm0 27.302H80v15.873H27.937z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx30(
        "path",
        {
          fill: "white",
          d: "M16.508 43.175v20.317h11.429v7.619H16.508h-3.81-4.444V23.492h8.254v12.063h11.429v7.619z"
        }
      )
    ] })
  }
);

// src/components/icons/GoogleBigQueryIcon.tsx
import { jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
var GoogleBigQueryIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs21(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M18.7598 50.6007L9.161 33.9744C8.79195 33.3352 8.59766 32.6101 8.59766 31.872C8.59766 31.1339 8.79195 30.4088 9.161 29.7696L18.7598 13.1436C19.129 12.5043 19.6599 11.9734 20.2992 11.6043C20.9385 11.2353 21.6637 11.041 22.4019 11.041H41.5996C42.3378 11.041 43.0629 11.2354 43.7022 11.6044C44.3414 11.9735 44.8723 12.5044 45.2413 13.1436L54.8402 29.7696C55.2093 30.4088 55.4036 31.134 55.4036 31.8722C55.4036 32.6103 55.2093 33.3355 54.8402 33.9748L45.2413 50.6007C44.8722 51.2399 44.3414 51.7707 43.7021 52.1397C43.0629 52.5087 42.3377 52.703 41.5996 52.7029H22.4019C21.6637 52.703 20.9385 52.5088 20.2991 52.1397C19.6598 51.7706 19.1289 51.24 18.7598 50.6007Z",
          fill: "url(#paint0_linear_6101_13262)"
        }
      ),
      /* @__PURE__ */ jsx31(
        "path",
        {
          opacity: "0.07",
          d: "M52.1966 40.0923L38.0803 25.9753L32.001 24.3484L26.5405 26.3779L24.4316 31.8721L26.1273 37.9894L41.0971 52.9592L44.2736 52.818L52.1966 40.0923Z",
          fill: "black"
        }
      ),
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M31.9993 23.3899C27.3151 23.3899 23.5176 27.1874 23.5176 31.872C23.5176 36.5562 27.3151 40.3537 31.9993 40.3537C36.6835 40.3537 40.4807 36.5562 40.4807 31.872C40.4807 27.1874 36.6832 23.3899 31.9993 23.3899ZM31.9993 38.3074C28.445 38.3074 25.5635 35.4259 25.5635 31.8716C25.5635 28.3173 28.445 25.4362 31.9993 25.4362C35.5536 25.4362 38.4351 28.3177 38.4351 31.872C38.4351 35.4263 35.5536 38.3074 31.9993 38.3074Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M27.9727 31.5443V34.1808C28.3628 34.8505 28.9131 35.4128 29.5743 35.8172V31.5443H27.9727ZM31.1485 29.3218V36.4198C31.4209 36.4699 31.6996 36.5003 31.9859 36.5003C32.247 36.5003 32.5015 36.4728 32.7512 36.4311V29.3218H31.1485ZM34.4728 32.6399V35.7707C35.1445 35.3436 35.6961 34.752 36.0752 34.0521V32.6402L34.4728 32.6399ZM38.1021 37.0963L37.2234 37.9757C37.149 38.0503 37.1072 38.1514 37.1072 38.2568C37.1072 38.3621 37.149 38.4632 37.2234 38.5378L40.5565 41.8698C40.6311 41.9442 40.7321 41.986 40.8375 41.986C40.9429 41.986 41.0439 41.9442 41.1185 41.8698L41.9972 40.9914C42.0712 40.9167 42.1127 40.8157 42.1127 40.7106C42.1127 40.6054 42.0712 40.5045 41.9972 40.4297L38.6638 37.0963C38.5891 37.0222 38.4881 36.9806 38.3829 36.9806C38.2777 36.9806 38.1768 37.0222 38.1021 37.0963Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx31("rect", { x: "0.5", y: "0.5", width: "63", height: "63", rx: "31.5", stroke: "#F1F4F8" }),
      /* @__PURE__ */ jsx31("defs", { children: /* @__PURE__ */ jsxs21(
        "linearGradient",
        {
          id: "paint0_linear_6101_13262",
          x1: "32.0008",
          y1: "11.0414",
          x2: "32.0008",
          y2: "52.6382",
          gradientUnits: "userSpaceOnUse",
          children: [
            /* @__PURE__ */ jsx31("stop", { stopColor: "#4387FD" }),
            /* @__PURE__ */ jsx31("stop", { offset: "1", stopColor: "#4683EA" })
          ]
        }
      ) })
    ]
  }
);

// src/components/icons/GoogleIcon.tsx
import { jsx as jsx32, jsxs as jsxs22 } from "react/jsx-runtime";
var GoogleIcon = ({
  width = 20,
  height = 20,
  ...props
}) => {
  return /* @__PURE__ */ jsxs22(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsx32(
          "path",
          {
            d: "M19.3 10.2083C19.3 9.55834 19.2417 8.93334 19.1333 8.33334H10.5V11.8833H15.4333C15.2167 13.025 14.5667 13.9917 13.5917 14.6417V16.95H16.5667C18.3 15.35 19.3 13 19.3 10.2083Z",
            fill: "#4285F4"
          }
        ),
        /* @__PURE__ */ jsx32(
          "path",
          {
            d: "M10.5 19.1667C12.975 19.1667 15.05 18.35 16.5667 16.95L13.5917 14.6417C12.775 15.1917 11.7333 15.525 10.5 15.525C8.11665 15.525 6.09165 13.9167 5.36665 11.75H2.31665V14.1167C3.82498 17.1083 6.91665 19.1667 10.5 19.1667Z",
            fill: "#34A853"
          }
        ),
        /* @__PURE__ */ jsx32(
          "path",
          {
            d: "M5.36671 11.7417C5.18337 11.1917 5.07504 10.6083 5.07504 10C5.07504 9.39166 5.18337 8.80833 5.36671 8.25833V5.89166H2.31671C1.69171 7.125 1.33337 8.51666 1.33337 10C1.33337 11.4833 1.69171 12.875 2.31671 14.1083L4.69171 12.2583L5.36671 11.7417Z",
            fill: "#FBBC05"
          }
        ),
        /* @__PURE__ */ jsx32(
          "path",
          {
            d: "M10.5 4.48334C11.85 4.48334 13.05 4.95001 14.0083 5.85001L16.6333 3.22501C15.0416 1.74168 12.975 0.833344 10.5 0.833344C6.91665 0.833344 3.82498 2.89168 2.31665 5.89168L5.36665 8.25834C6.09165 6.09168 8.11665 4.48334 10.5 4.48334Z",
            fill: "#EA4335"
          }
        )
      ]
    }
  );
};

// src/components/icons/GoogleMailIcon.tsx
import { jsx as jsx33, jsxs as jsxs23 } from "react/jsx-runtime";
var GoogleMailIcon = ({
  height = 16,
  width = 22,
  ...props
}) => {
  return /* @__PURE__ */ jsxs23(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 22 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsxs23("g", { clipPath: "url(#clip0_164_23823)", children: [
          /* @__PURE__ */ jsx33(
            "path",
            {
              d: "M1.78786 16H5.1818V7.75758L0.333313 4.12121V14.5455C0.333313 15.3503 0.985434 16 1.78786 16Z",
              fill: "#4285F4"
            }
          ),
          /* @__PURE__ */ jsx33(
            "path",
            {
              d: "M16.8182 16H20.2121C21.017 16 21.6667 15.3479 21.6667 14.5455V4.12121L16.8182 7.75758",
              fill: "#34A853"
            }
          ),
          /* @__PURE__ */ jsx33(
            "path",
            {
              d: "M16.8182 1.45454V7.75757L21.6667 4.12121V2.18182C21.6667 0.383029 19.6133 -0.642425 18.1758 0.436362",
              fill: "#FBBC04"
            }
          ),
          /* @__PURE__ */ jsx33(
            "path",
            {
              d: "M5.18182 7.75757V1.45454L11 5.81818L16.8182 1.45454V7.75757L11 12.1212",
              fill: "#EA4335"
            }
          ),
          /* @__PURE__ */ jsx33(
            "path",
            {
              d: "M0.333313 2.18182V4.12121L5.1818 7.75757V1.45454L3.82422 0.436362C2.38422 -0.642425 0.333313 0.383029 0.333313 2.18182Z",
              fill: "#C5221F"
            }
          )
        ] }),
        /* @__PURE__ */ jsx33("defs", { children: /* @__PURE__ */ jsx33("clipPath", { id: "clip0_164_23823", children: /* @__PURE__ */ jsx33(
          "rect",
          {
            width: "21.3333",
            height: "16",
            fill: "white",
            transform: "translate(0.333313)"
          }
        ) }) })
      ]
    }
  );
};

// src/components/icons/GoogleSheetsIcon.tsx
import { jsx as jsx34, jsxs as jsxs24 } from "react/jsx-runtime";
var GoogleSheetsIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs24(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsxs24("g", { clipPath: "url(#clip0_6101_14084)", children: [
        /* @__PURE__ */ jsx34(
          "path",
          {
            d: "M46.3236 49.6805C46.3231 50.2803 46.0846 50.8553 45.6605 51.2794C45.2364 51.7035 44.6614 51.942 44.0616 51.9425H19.9259C19.3261 51.9425 18.7509 51.7045 18.3264 51.2807C17.902 50.8569 17.6631 50.2821 17.6621 49.6823V14.3556C17.6619 14.0582 17.7203 13.7638 17.8339 13.489C17.9476 13.2142 18.1144 12.9646 18.3246 12.7543C18.5349 12.5441 18.7845 12.3773 19.0593 12.2636C19.3341 12.15 19.6285 12.0916 19.9259 12.0918H35.4156L46.3236 23.2161V49.6805Z",
            fill: "#23A566"
          }
        ),
        /* @__PURE__ */ jsx34(
          "path",
          {
            d: "M36.6152 22.9259L46.3229 32.2046V23.1512H37.7237C37.3483 23.1555 36.9782 23.0623 36.6495 22.8809L36.6152 22.9259Z",
            fill: "#1C8F5A"
          }
        ),
        /* @__PURE__ */ jsx34(
          "path",
          {
            d: "M46.3221 23.1981H37.6995C37.0997 23.1976 36.5246 22.9591 36.1005 22.535C35.6764 22.1109 35.438 21.5359 35.4375 20.9361V12.0305L46.3221 23.1981Z",
            fill: "#8ED1B1"
          }
        ),
        /* @__PURE__ */ jsx34(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M39.4206 44.5959H24.5059V31.6638H39.4206V44.5959ZM32.8905 33.5293V35.7931H37.5876V33.5329L32.8905 33.5293ZM32.8905 37.2188V39.3816H37.5876V37.2079L32.8905 37.2188ZM32.8905 40.7604V42.7629H37.5876V40.7496L32.8905 40.7604ZM31.0467 42.7521V40.7496H26.3515V42.7521H31.0467ZM31.0467 39.3582V37.2079H26.3515V39.369L31.0467 39.3582ZM31.0467 35.8165V33.5293H26.3515V35.7931L31.0467 35.8165Z",
            fill: "white"
          }
        )
      ] }),
      /* @__PURE__ */ jsx34("rect", { x: "0.5", y: "0.5", width: "63", height: "63", rx: "31.5", stroke: "#F1F4F8" }),
      /* @__PURE__ */ jsx34("defs", { children: /* @__PURE__ */ jsx34("clipPath", { id: "clip0_6101_14084", children: /* @__PURE__ */ jsx34(
        "rect",
        {
          width: "28.7442",
          height: "40",
          fill: "white",
          transform: "translate(17.627 12)"
        }
      ) }) })
    ]
  }
);

// src/components/icons/GraphIcon.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var GraphIcon = ({ width = 21, height = 20 }) => /* @__PURE__ */ jsx35(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 21 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx35("g", { id: "Frame", children: /* @__PURE__ */ jsx35(
      "path",
      {
        id: "Vector",
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M4.75 2C4.15326 2 3.58097 2.23705 3.15901 2.65901C2.73705 3.08097 2.5 3.65326 2.5 4.25V15.75C2.5 16.3467 2.73705 16.919 3.15901 17.341C3.58097 17.7629 4.15326 18 4.75 18H16.25C16.8467 18 17.419 17.7629 17.841 17.341C18.2629 16.919 18.5 16.3467 18.5 15.75V4.25C18.5 3.65326 18.2629 3.08097 17.841 2.65901C17.419 2.23705 16.8467 2 16.25 2H4.75ZM15.5 5.75C15.5 5.55109 15.421 5.36032 15.2803 5.21967C15.1397 5.07902 14.9489 5 14.75 5C14.5511 5 14.3603 5.07902 14.2197 5.21967C14.079 5.36032 14 5.55109 14 5.75V14.25C14 14.4489 14.079 14.6397 14.2197 14.7803C14.3603 14.921 14.5511 15 14.75 15C14.9489 15 15.1397 14.921 15.2803 14.7803C15.421 14.6397 15.5 14.4489 15.5 14.25V5.75ZM7 11.75C7 11.5511 6.92098 11.3603 6.78033 11.2197C6.63968 11.079 6.44891 11 6.25 11C6.05109 11 5.86032 11.079 5.71967 11.2197C5.57902 11.3603 5.5 11.5511 5.5 11.75V14.25C5.5 14.4489 5.57902 14.6397 5.71967 14.7803C5.86032 14.921 6.05109 15 6.25 15C6.44891 15 6.63968 14.921 6.78033 14.7803C6.92098 14.6397 7 14.4489 7 14.25V11.75ZM9.084 9C9.28291 9 9.47368 9.07902 9.61433 9.21967C9.75498 9.36032 9.834 9.55109 9.834 9.75V14.25C9.834 14.4489 9.75498 14.6397 9.61433 14.7803C9.47368 14.921 9.28291 15 9.084 15C8.88509 15 8.69432 14.921 8.55367 14.7803C8.41302 14.6397 8.334 14.4489 8.334 14.25V9.75C8.334 9.55109 8.41302 9.36032 8.55367 9.21967C8.69432 9.07902 8.88509 9 9.084 9ZM12.664 7.75C12.664 7.55109 12.585 7.36032 12.4443 7.21967C12.3037 7.07902 12.1129 7 11.914 7C11.7151 7 11.5243 7.07902 11.3837 7.21967C11.243 7.36032 11.164 7.55109 11.164 7.75V14.25C11.164 14.4489 11.243 14.6397 11.3837 14.7803C11.5243 14.921 11.7151 15 11.914 15C12.1129 15 12.3037 14.921 12.4443 14.7803C12.585 14.6397 12.664 14.4489 12.664 14.25V7.75Z",
        fill: "#7483A0"
      }
    ) })
  }
);

// src/components/icons/HubspotIcon.tsx
import { jsx as jsx36, jsxs as jsxs25 } from "react/jsx-runtime";
var HubspotIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs25(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx36(
        "path",
        {
          d: "M64 32C64 14.3269 49.6731 0 32 0C14.3269 0 0 14.3269 0 32C0 49.6731 14.3269 64 32 64C49.6731 64 64 49.6731 64 32Z",
          fill: "#FF7A59"
        }
      ),
      /* @__PURE__ */ jsx36(
        "path",
        {
          d: "M39.8603 39.9411C37.1723 39.9411 34.9919 37.7846 34.9919 35.124C34.9919 32.4634 37.1716 30.3062 39.8603 30.3062C42.5491 30.3062 44.7302 32.4627 44.7302 35.124C44.7302 37.7853 42.5505 39.9411 39.8603 39.9411ZM41.3186 25.8491V21.56C41.8888 21.2989 42.3726 20.8803 42.7131 20.3535C43.0535 19.8267 43.2364 19.2137 43.2402 18.5865V18.4874C43.2402 16.6747 41.7412 15.1925 39.9109 15.1925H39.8104C37.9823 15.1925 36.4811 16.6754 36.4811 18.4874V18.5865C36.4851 19.2136 36.6681 19.8266 37.0085 20.3534C37.3489 20.8801 37.8326 21.2987 38.4027 21.56V25.8491C36.761 26.0967 35.2128 26.7705 33.9126 27.8031L22.0227 18.6505C22.106 18.3494 22.1506 18.0389 22.1556 17.7266C22.1556 15.6727 20.4759 14.0056 18.4009 14C16.326 13.9944 14.6427 15.6587 14.6406 17.7132C14.6385 19.7677 16.3176 21.4313 18.3925 21.4349C19.0424 21.4321 19.6804 21.2603 20.2438 20.9363L31.9424 29.9434C30.89 31.511 30.3402 33.3621 30.3662 35.2501C30.3922 37.138 30.9928 38.9733 32.088 40.5113L28.5288 44.034C28.1077 43.902 27.663 43.8629 27.2253 43.9193C26.7876 43.9757 26.3674 44.1264 25.9935 44.3608C25.6196 44.5953 25.3011 44.908 25.0597 45.2775C24.8184 45.6469 24.66 46.0643 24.5954 46.5009C24.5309 46.9374 24.5618 47.3828 24.686 47.8062C24.8102 48.2297 25.0247 48.6212 25.3147 48.9538C25.6048 49.2864 25.9635 49.5522 26.3661 49.7328C26.7687 49.9134 27.2058 50.0046 27.647 50C29.3514 50 30.7338 48.6317 30.7338 46.9449C30.7307 46.6485 30.6833 46.3541 30.5931 46.0717L34.1088 42.5898C35.7637 43.8442 37.7837 44.5225 39.8603 44.5213C45.1063 44.5213 49.3567 40.3145 49.3567 35.124C49.3567 30.4257 45.8685 26.5459 41.3186 25.8491Z",
          fill: "white"
        }
      )
    ]
  }
);

// src/components/icons/InfoIcon.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var InfoIcon = ({ width = 17, height = 17 }) => /* @__PURE__ */ jsx37(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 17 17",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx37(
      "path",
      {
        d: "M8.49805 16.856C4.07965 16.856 0.498047 13.2744 0.498047 8.85596C0.498047 4.43756 4.07965 0.855957 8.49805 0.855957C12.9164 0.855957 16.498 4.43756 16.498 8.85596C16.498 13.2744 12.9164 16.856 8.49805 16.856ZM7.69805 8.05596V12.856H9.29805V8.05596H7.69805ZM7.69805 4.85596V6.45596H9.29805V4.85596H7.69805Z",
        fill: "#75808F"
      }
    )
  }
);

// src/components/icons/InfoIcon2.tsx
import { jsx as jsx38, jsxs as jsxs26 } from "react/jsx-runtime";
var InfoIcon2 = ({
  width = 20,
  height = 20,
  color = "#4745BA"
}) => /* @__PURE__ */ jsxs26(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsxs26("g", { clipPath: "url(#clip0_5476_8538)", children: [
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M9.99984 18.3327C14.6022 18.3327 18.3332 14.6017 18.3332 9.99935C18.3332 5.39698 14.6022 1.66602 9.99984 1.66602C5.39746 1.66602 1.6665 5.39698 1.6665 9.99935C1.6665 14.6017 5.39746 18.3327 9.99984 18.3327Z",
            stroke: color,
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M10 13.3333V10",
            stroke: color,
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M10 6.66602H10.0083",
            stroke: color,
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ] }),
      /* @__PURE__ */ jsx38("defs", { children: /* @__PURE__ */ jsx38("clipPath", { id: "clip0_5476_8538", children: /* @__PURE__ */ jsx38("rect", { width: "20", height: "20", fill: "white" }) }) })
    ]
  }
);

// src/components/icons/InfoPopoverIcon.tsx
import { jsx as jsx39, jsxs as jsxs27 } from "react/jsx-runtime";
var InfoPopoverIcon = ({
  width = 16,
  height = 16
}) => /* @__PURE__ */ jsxs27(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsxs27("g", { clipPath: "url(#clip0_188_16343)", children: [
        /* @__PURE__ */ jsx39(
          "path",
          {
            d: "M7.99998 14.6667C11.6819 14.6667 14.6666 11.6819 14.6666 8.00004C14.6666 4.31814 11.6819 1.33337 7.99998 1.33337C4.31808 1.33337 1.33331 4.31814 1.33331 8.00004C1.33331 11.6819 4.31808 14.6667 7.99998 14.6667Z",
            stroke: "#344054",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx39(
          "path",
          {
            d: "M8 10.6667V8",
            stroke: "#344054",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx39(
          "path",
          {
            d: "M8 5.33337H8.00667",
            stroke: "#344054",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ] }),
      /* @__PURE__ */ jsx39("defs", { children: /* @__PURE__ */ jsx39("clipPath", { id: "clip0_188_16343", children: /* @__PURE__ */ jsx39("rect", { width: "16", height: "16", fill: "white" }) }) })
    ]
  }
);

// src/components/icons/InvoiceIcon.tsx
import { jsx as jsx40, jsxs as jsxs28 } from "react/jsx-runtime";
var InvoiceIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs28(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx40("circle", { cx: "10", cy: "10", r: "10", fill: "#FFF3D4" }),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M11.1654 4.16699H6.4987C6.18928 4.16699 5.89253 4.28991 5.67374 4.5087C5.45495 4.72749 5.33203 5.02424 5.33203 5.33366V14.667C5.33203 14.9764 5.45495 15.2732 5.67374 15.492C5.89253 15.7107 6.18928 15.8337 6.4987 15.8337H13.4987C13.8081 15.8337 14.1049 15.7107 14.3237 15.492C14.5424 15.2732 14.6654 14.9764 14.6654 14.667V7.66699L11.1654 4.16699Z",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M11.168 4.16699V7.66699H14.668",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M12.3346 10.583H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M12.3346 12.917H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M8.83464 8.25H8.2513H7.66797",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/KebabIcon.tsx
import { jsx as jsx41 } from "react/jsx-runtime";
var KebabIcon = ({ width = 4, height = 14 }) => /* @__PURE__ */ jsx41(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 4 14",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx41(
      "path",
      {
        d: "M2.10754 3.57885C3.04674 3.57885 3.81518 2.81042 3.81518 1.87121C3.81518 0.932012 3.04674 0.163574 2.10754 0.163574C1.16834 0.163574 0.399902 0.932012 0.399902 1.87121C0.399902 2.81042 1.16834 3.57885 2.10754 3.57885ZM2.10754 5.28649C1.16834 5.28649 0.399902 6.05493 0.399902 6.99413C0.399902 7.93334 1.16834 8.70177 2.10754 8.70177C3.04674 8.70177 3.81518 7.93334 3.81518 6.99413C3.81518 6.05493 3.04674 5.28649 2.10754 5.28649ZM2.10754 10.4094C1.16834 10.4094 0.399902 11.1779 0.399902 12.1171C0.399902 13.0563 1.16834 13.8247 2.10754 13.8247C3.04674 13.8247 3.81518 13.0563 3.81518 12.1171C3.81518 11.1779 3.04674 10.4094 2.10754 10.4094Z",
        fill: "#283543"
      }
    )
  }
);

// src/components/icons/LightningIcon.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var LightningIcon = ({
  width = 24,
  height = 24,
  ...props
}) => {
  return /* @__PURE__ */ jsx42(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: /* @__PURE__ */ jsx42(
        "path",
        {
          d: "M3.75 13.5L14.25 2.25L12 10.5H20.25L9.75 21.75L12 13.5H3.75Z",
          stroke: "black",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};

// src/components/icons/MerchantIcon.tsx
import { jsx as jsx43, jsxs as jsxs29 } from "react/jsx-runtime";
var MerchantIcon = ({
  width = 37,
  height = 37
}) => /* @__PURE__ */ jsxs29(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 37 37",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx43("circle", { cx: "18.5", cy: "18.5", r: "18.5", fill: "#F0F4F7" }),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M8.84025 12.7846L8.84027 12.7846L16.3119 8.16478C16.4367 8.08786 16.5766 8.05 16.717 8.05C16.8574 8.05 16.9973 8.08786 17.122 8.16478L24.5937 12.7846L24.5937 12.7846C24.9644 13.0136 25.0787 13.4996 24.8561 13.8712C24.7076 14.1162 24.4497 14.2506 24.1887 14.2506H9.24528C8.98021 14.2506 8.72642 14.1163 8.57791 13.8712C8.35526 13.4996 8.46952 13.0136 8.84025 12.7846ZM16.7433 9.78096L16.717 9.76469L16.6907 9.78096L12.17 12.5781L12.0205 12.6706H12.1963H21.2376H21.4135L21.2639 12.5781L16.7433 9.78096ZM10.0755 22.3303H10.1255V22.2803V15.6105H11.6858V22.2803V22.3303H11.7358H12.9811H13.0311V22.2803V15.6105H14.5915V22.2803V22.3303H14.6415H15.8868H15.9368V22.2803V15.6105H17.4972V20.1679C16.9739 20.8754 16.667 21.7502 16.667 22.7002V23.9102H8.83019C8.40175 23.9102 8.05 23.5552 8.05 23.1202C8.05 22.6853 8.40175 22.3303 8.83019 22.3303H10.0755ZM20.4028 18.4766C19.8404 18.538 19.3147 18.7148 18.8425 18.9772V15.6105H20.4028V18.4766ZM23.3085 18.4504H21.7481V15.6105H23.3085V18.4504ZM18.0123 22.7002C18.0123 21.1062 19.2942 19.8104 20.8679 19.8104H27.0943C28.668 19.8104 29.95 21.1062 29.95 22.7002V26.0601C29.95 27.6542 28.668 28.95 27.0943 28.95H20.8679C19.2942 28.95 18.0123 27.6542 18.0123 26.0601V22.7002ZM27.0943 21.3903H20.8679C20.1532 21.3903 19.5726 21.9785 19.5726 22.7002V26.0601C19.5726 26.7818 20.1532 27.3701 20.8679 27.3701H27.0943C27.8091 27.3701 28.3896 26.7818 28.3896 26.0601V22.7002C28.3896 21.9785 27.8091 21.3903 27.0943 21.3903ZM25.484 22.9102C25.484 22.5896 25.7411 22.3303 26.0566 22.3303H27.3019C27.6174 22.3303 27.8745 22.5896 27.8745 22.9102V23.5402C27.8745 23.8609 27.6174 24.1202 27.3019 24.1202H26.0566C25.7411 24.1202 25.484 23.8609 25.484 23.5402V22.9102ZM20.0877 25.8501C20.0877 25.5294 20.3448 25.2701 20.6604 25.2701H21.0755C21.391 25.2701 21.6481 25.5294 21.6481 25.8501C21.6481 26.1708 21.391 26.4301 21.0755 26.4301H20.6604C20.3448 26.4301 20.0877 26.1708 20.0877 25.8501ZM22.1632 25.8501C22.1632 25.5294 22.4203 25.2701 22.7358 25.2701H23.1509C23.4665 25.2701 23.7236 25.5294 23.7236 25.8501C23.7236 26.1708 23.4665 26.4301 23.1509 26.4301H22.7358C22.4203 26.4301 22.1632 26.1708 22.1632 25.8501ZM24.2387 25.8501C24.2387 25.5294 24.4958 25.2701 24.8113 25.2701H25.2264C25.542 25.2701 25.7991 25.5294 25.7991 25.8501C25.7991 26.1708 25.542 26.4301 25.2264 26.4301H24.8113C24.4958 26.4301 24.2387 26.1708 24.2387 25.8501ZM26.3141 25.8501C26.3141 25.5294 26.5713 25.2701 26.8868 25.2701H27.3019C27.6174 25.2701 27.8745 25.5294 27.8745 25.8501C27.8745 26.1708 27.6174 26.4301 27.3019 26.4301H26.8868C26.5713 26.4301 26.3141 26.1708 26.3141 25.8501Z",
          fill: "#667085",
          stroke: "#F0F4F7",
          strokeWidth: "0.1"
        }
      )
    ]
  }
);

// src/components/icons/MicrosoftIcon.tsx
import { jsx as jsx44, jsxs as jsxs30 } from "react/jsx-runtime";
var MicrosoftIcon = ({
  width = 20,
  height = 20,
  ...props
}) => {
  return /* @__PURE__ */ jsxs30(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsx44("path", { d: "M10 0H0.5V9.5H10V0Z", fill: "#F25022" }),
        /* @__PURE__ */ jsx44("path", { d: "M20.5 0H11V9.5H20.5V0Z", fill: "#7FBA00" }),
        /* @__PURE__ */ jsx44("path", { d: "M10 10.5H0.5V20H10V10.5Z", fill: "#00A4EF" }),
        /* @__PURE__ */ jsx44("path", { d: "M20.5 10.5H11V20H20.5V10.5Z", fill: "#FFB900" })
      ]
    }
  );
};

// src/components/icons/NetsuiteIcon.tsx
import { jsx as jsx45, jsxs as jsxs31 } from "react/jsx-runtime";
var NetsuiteIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs31(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx45(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M12 12.8159H36.9209V34.155L27.2159 21.8498H12",
          fill: "#00467F"
        }
      ),
      /* @__PURE__ */ jsx45(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M27.1621 29.9644C30.3974 33.9664 33.5031 38.099 36.7024 42.137H52.0006V51.1842H27.1621",
          fill: "#00467F"
        }
      ),
      /* @__PURE__ */ jsx45(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M38.4805 21.8143V14.647H50.2423V40.5551H42.4295V21.8143",
          fill: "#B9C7D4"
        }
      ),
      /* @__PURE__ */ jsx45(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M21.6654 23.3853V41.9737C22.9006 41.9737 24.2514 41.9737 25.5311 41.9737V49.2934H13.8516V23.3853",
          fill: "#B9C7D4"
        }
      ),
      /* @__PURE__ */ jsx45("rect", { x: "0.5", y: "0.5", width: "63", height: "63", rx: "31.5", stroke: "#F1F4F8" })
    ]
  }
);

// src/components/icons/OutlookIcon.tsx
import { jsx as jsx46, jsxs as jsxs32 } from "react/jsx-runtime";
var OutlookIcon = ({
  height = 20,
  width = 20,
  ...props
}) => {
  return /* @__PURE__ */ jsxs32(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 21 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsxs32("g", { clipPath: "url(#clip0_164_23831)", children: [
          /* @__PURE__ */ jsx46(
            "path",
            {
              d: "M12.9886 4.24038V8.15249L14.3558 9.01333C14.3918 9.02385 14.47 9.0246 14.506 9.01333L20.39 5.04639C20.39 4.5769 19.952 4.24038 19.7049 4.24038H12.9886Z",
              fill: "#0072C6"
            }
          ),
          /* @__PURE__ */ jsx46(
            "path",
            {
              d: "M12.9887 9.61202L14.2364 10.4691C14.4121 10.5983 14.624 10.4691 14.624 10.4691C14.4129 10.5983 20.3907 6.62761 20.3907 6.62761V13.8178C20.3907 14.6006 19.8897 14.9288 19.3263 14.9288H12.9879V9.61202H12.9887Z",
              fill: "#0072C6"
            }
          ),
          /* @__PURE__ */ jsx46(
            "path",
            {
              d: "M6.52859 7.80845C6.10342 7.80845 5.76464 8.00826 5.5145 8.40714C5.26436 8.80601 5.13892 9.33408 5.13892 9.99136C5.13892 10.6584 5.26436 11.1857 5.5145 11.5733C5.76464 11.9617 6.09291 12.1547 6.49854 12.1547C6.91694 12.1547 7.24896 11.9662 7.49385 11.5891C7.73873 11.212 7.86192 10.6884 7.86192 10.0192C7.86192 9.32131 7.74324 8.77822 7.50511 8.38986C7.26699 8.00225 6.94173 7.80845 6.52859 7.80845Z",
              fill: "#0072C6"
            }
          ),
          /* @__PURE__ */ jsx46(
            "path",
            {
              d: "M0.609314 2.25352V17.5587L12.2525 20V0L0.609314 2.25352ZM8.40049 12.4935C7.90847 13.141 7.26697 13.4655 6.47523 13.4655C5.70377 13.4655 5.07579 13.1516 4.58978 12.5243C4.10453 11.8963 3.86115 11.0791 3.86115 10.071C3.86115 9.00657 4.10753 8.14573 4.60105 7.48845C5.09457 6.83117 5.74809 6.50216 6.56161 6.50216C7.33007 6.50216 7.95204 6.81615 8.42603 7.44564C8.90077 8.07512 9.13814 8.90441 9.13814 9.93427C9.13889 10.9927 8.89251 11.846 8.40049 12.4935Z",
              fill: "#0072C6"
            }
          )
        ] }),
        /* @__PURE__ */ jsx46("defs", { children: /* @__PURE__ */ jsx46("clipPath", { id: "clip0_164_23831", children: /* @__PURE__ */ jsx46(
          "rect",
          {
            width: "19.7814",
            height: "20",
            fill: "white",
            transform: "translate(0.609314)"
          }
        ) }) })
      ]
    }
  );
};

// src/components/icons/PaidIcon.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var PaidIcon = ({ height = 20, width = 20 }) => {
  return /* @__PURE__ */ jsx47(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx47("g", { id: "Frame", children: /* @__PURE__ */ jsx47(
        "circle",
        {
          id: "Ellipse 219",
          cx: "10",
          cy: "10",
          r: "6",
          stroke: "#716FFF",
          strokeWidth: "2"
        }
      ) })
    }
  );
};

// src/components/icons/PartiallyPaidIcon.tsx
import { jsx as jsx48, jsxs as jsxs33 } from "react/jsx-runtime";
var PartiallyPaidIcon = ({
  height = 20,
  width = 20
}) => {
  return /* @__PURE__ */ jsx48(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsxs33("g", { id: "Frame", children: [
        /* @__PURE__ */ jsxs33("g", { id: "Ellipse 219", children: [
          /* @__PURE__ */ jsx48("mask", { id: "path-1-inside-1_291_602", fill: "white", children: /* @__PURE__ */ jsx48("path", { d: "M10 17C8.14348 17 6.36301 16.2625 5.05025 14.9497C3.7375 13.637 3 11.8565 3 10C3 8.14348 3.7375 6.36301 5.05025 5.05025C6.36301 3.7375 8.14348 3 10 3V4.99872C8.67358 4.99872 7.40149 5.52564 6.46356 6.46356C5.52564 7.40148 4.99872 8.67358 4.99872 10C4.99872 11.3264 5.52564 12.5985 6.46356 13.5364C7.40148 14.4744 8.67358 15.0013 10 15.0013L10 17Z" }) }),
          /* @__PURE__ */ jsx48(
            "path",
            {
              d: "M10 17C8.14348 17 6.36301 16.2625 5.05025 14.9497C3.7375 13.637 3 11.8565 3 10C3 8.14348 3.7375 6.36301 5.05025 5.05025C6.36301 3.7375 8.14348 3 10 3V4.99872C8.67358 4.99872 7.40149 5.52564 6.46356 6.46356C5.52564 7.40148 4.99872 8.67358 4.99872 10C4.99872 11.3264 5.52564 12.5985 6.46356 13.5364C7.40148 14.4744 8.67358 15.0013 10 15.0013L10 17Z",
              fill: "#DEDAFF",
              stroke: "#716FFF",
              strokeWidth: "4",
              mask: "url(#path-1-inside-1_291_602)"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs33("g", { id: "Ellipse 220", children: [
          /* @__PURE__ */ jsx48("mask", { id: "path-2-inside-2_291_602", fill: "white", children: /* @__PURE__ */ jsx48("path", { d: "M5.45859 4.67313C6.47755 3.80441 7.72429 3.24618 9.05094 3.06464C10.3776 2.88309 11.7285 3.08586 12.9434 3.64888C14.1582 4.21191 15.1862 5.11157 15.9052 6.24116C16.6242 7.37074 17.0041 8.68285 17 10.0219C16.9958 11.3609 16.6076 12.6706 15.8816 13.7956C15.1555 14.9207 14.122 15.8139 12.9036 16.3694C11.6853 16.9248 10.3331 17.1191 9.00766 16.9293C7.68217 16.7395 6.43894 16.1735 5.42542 15.2984L6.71208 13.8082C7.44054 14.4371 8.33409 14.8439 9.28677 14.9803C10.2394 15.1168 11.2113 14.9771 12.087 14.5779C12.9626 14.1787 13.7055 13.5367 14.2273 12.7281C14.7492 11.9194 15.0281 10.9781 15.0311 10.0157C15.0341 9.05332 14.761 8.11026 14.2443 7.29838C13.7275 6.48651 12.9887 5.83989 12.1155 5.43522C11.2423 5.03056 10.2714 4.88482 9.31788 5.0153C8.36437 5.14578 7.46829 5.54701 6.73592 6.17138L5.45859 4.67313Z" }) }),
          /* @__PURE__ */ jsx48(
            "path",
            {
              d: "M5.45859 4.67313C6.47755 3.80441 7.72429 3.24618 9.05094 3.06464C10.3776 2.88309 11.7285 3.08586 12.9434 3.64888C14.1582 4.21191 15.1862 5.11157 15.9052 6.24116C16.6242 7.37074 17.0041 8.68285 17 10.0219C16.9958 11.3609 16.6076 12.6706 15.8816 13.7956C15.1555 14.9207 14.122 15.8139 12.9036 16.3694C11.6853 16.9248 10.3331 17.1191 9.00766 16.9293C7.68217 16.7395 6.43894 16.1735 5.42542 15.2984L6.71208 13.8082C7.44054 14.4371 8.33409 14.8439 9.28677 14.9803C10.2394 15.1168 11.2113 14.9771 12.087 14.5779C12.9626 14.1787 13.7055 13.5367 14.2273 12.7281C14.7492 11.9194 15.0281 10.9781 15.0311 10.0157C15.0341 9.05332 14.761 8.11026 14.2443 7.29838C13.7275 6.48651 12.9887 5.83989 12.1155 5.43522C11.2423 5.03056 10.2714 4.88482 9.31788 5.0153C8.36437 5.14578 7.46829 5.54701 6.73592 6.17138L5.45859 4.67313Z",
              stroke: "#716FFF",
              strokeWidth: "4",
              strokeDasharray: "4 4",
              mask: "url(#path-2-inside-2_291_602)"
            }
          )
        ] })
      ] })
    }
  );
};

// src/components/icons/PlanIcon.tsx
import { jsx as jsx49, jsxs as jsxs34 } from "react/jsx-runtime";
var PlanIcon = ({ width = 20, height = 20 }) => /* @__PURE__ */ jsxs34(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx49("circle", { cx: "10.0002", cy: "10", r: "10", fill: "#EAF7FC" }),
      /* @__PURE__ */ jsxs34("g", { clipPath: "url(#clip0_492_51795)", children: [
        /* @__PURE__ */ jsx49(
          "path",
          {
            d: "M11.0002 14.2V11.3C11.0002 11.1343 11.1345 11 11.3002 11H14.2002C14.3659 11 14.5002 11.1343 14.5002 11.3V14.2C14.5002 14.3657 14.3659 14.5 14.2002 14.5H11.3002C11.1345 14.5 11.0002 14.3657 11.0002 14.2Z",
            stroke: "#485972",
            strokeWidth: "1.5"
          }
        ),
        /* @__PURE__ */ jsx49(
          "path",
          {
            d: "M5.50024 14.2V11.3C5.50024 11.1343 5.63456 11 5.80024 11H8.70024C8.86593 11 9.00024 11.1343 9.00024 11.3V14.2C9.00024 14.3657 8.86593 14.5 8.70024 14.5H5.80024C5.63456 14.5 5.50024 14.3657 5.50024 14.2Z",
            stroke: "#485972",
            strokeWidth: "1.5"
          }
        ),
        /* @__PURE__ */ jsx49(
          "path",
          {
            d: "M11.0002 8.7V5.8C11.0002 5.63431 11.1345 5.5 11.3002 5.5H14.2002C14.3659 5.5 14.5002 5.63431 14.5002 5.8V8.7C14.5002 8.86568 14.3659 9 14.2002 9H11.3002C11.1345 9 11.0002 8.86568 11.0002 8.7Z",
            stroke: "#485972",
            strokeWidth: "1.5"
          }
        ),
        /* @__PURE__ */ jsx49(
          "path",
          {
            d: "M5.50024 8.7V5.8C5.50024 5.63431 5.63456 5.5 5.80024 5.5H8.70024C8.86593 5.5 9.00024 5.63431 9.00024 5.8V8.7C9.00024 8.86568 8.86593 9 8.70024 9H5.80024C5.63456 9 5.50024 8.86568 5.50024 8.7Z",
            stroke: "#485972",
            strokeWidth: "1.5"
          }
        )
      ] }),
      /* @__PURE__ */ jsx49("defs", { children: /* @__PURE__ */ jsx49("clipPath", { id: "clip0_492_51795", children: /* @__PURE__ */ jsx49(
        "rect",
        {
          width: "12",
          height: "12",
          fill: "white",
          transform: "translate(4.00024 4)"
        }
      ) }) })
    ]
  }
);

// src/components/icons/PlanMinimumIcon.tsx
import { jsx as jsx50, jsxs as jsxs35 } from "react/jsx-runtime";
var PlanMinimumIcon = ({
  width = 24,
  height = 24
}) => /* @__PURE__ */ jsxs35(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx50("circle", { cx: "12", cy: "12", r: "12", fill: "#F1F4F8" }),
      /* @__PURE__ */ jsx50(
        "rect",
        {
          x: "6.75",
          y: "6.75",
          width: "10.5",
          height: "10.5",
          stroke: "#4A9000",
          strokeWidth: "1.5",
          strokeDasharray: "2 2"
        }
      ),
      /* @__PURE__ */ jsx50("rect", { x: "6", y: "12", width: "6", height: "6", fill: "#4A9000" }),
      /* @__PURE__ */ jsx50("rect", { x: "6.5", y: "6.5", width: "1", height: "1", fill: "#4A9000" }),
      /* @__PURE__ */ jsx50("rect", { x: "16.5", y: "6.5", width: "1", height: "1", fill: "#4A9000" }),
      /* @__PURE__ */ jsx50("rect", { x: "16.5", y: "16.5", width: "1", height: "1", fill: "#4A9000" })
    ]
  }
);

// src/components/icons/PlusIcon.tsx
import { jsx as jsx51 } from "react/jsx-runtime";
var PlusIcon = ({
  width = 15,
  height = 15,
  color = "#2D3748"
}) => /* @__PURE__ */ jsx51(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 15 15",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx51(
      "path",
      {
        d: "M6.29199 6.02136V0.0213623H8.29199V6.02136H14.292V8.02136H8.29199V14.0214H6.29199V8.02136H0.291992V6.02136H6.29199Z",
        fill: color
      }
    )
  }
);

// src/components/icons/QuickbooksIcon.tsx
import { jsx as jsx52 } from "react/jsx-runtime";
var QuickbooksIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsx52(
  "svg",
  {
    width,
    height,
    viewBox: `0 0 64 64`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx52(
      "path",
      {
        d: "M28.5711 63.8953C13.7833 62.2942 2.07674 50.7531 0.165973 35.8914C-0.0553243 34.1703 -0.0553243 29.8689 0.165973 28.1472C1.56325 17.2792 8.05122 8.14728 17.818 3.30076C25.566 -0.543595 34.9306 -1.06041 43.0107 1.91086C53.3177 5.70135 60.9565 14.4722 63.3471 25.2608C64.2176 29.1899 64.2176 34.8487 63.3471 38.7779C61.2134 48.4069 54.9041 56.4807 46.1844 60.7389C43.0834 62.2536 40.5676 63.0633 37.1645 63.6425C35.1887 63.9785 30.5893 64.1139 28.5711 63.8953ZM30.5434 35.0466V19.2067L26.2467 19.1534C21.3229 19.1001 19.8573 19.2281 17.9678 19.8558C13.8268 21.2281 10.7724 24.5135 9.68074 28.7696C9.23745 30.4977 9.174 33.0839 9.53484 34.7175C10.5101 39.1325 13.674 42.6729 17.9779 44.1647C19.2754 44.6127 19.5948 44.666 21.2809 44.7225L23.1475 44.7812V42.5305V40.2803L21.7035 40.211C19.4072 40.099 17.7926 39.3854 16.1725 37.7618C14.2143 35.7997 13.4394 32.9799 14.0572 30.0651C14.5532 27.7264 16.1727 25.6858 18.3577 24.6463C19.9645 23.8821 20.624 23.7701 23.5066 23.7701H25.965V37.2632C25.965 44.6841 26.0087 50.8 26.0623 50.8534C26.1159 50.9067 27.146 50.9334 28.3515 50.912L30.5434 50.8587V35.0188L30.5434 35.0466ZM43.856 44.6111C48.2499 43.8996 52.0872 40.931 53.6676 37.0205C54.398 35.213 54.6011 34.1234 54.6126 31.9489C54.6217 30.2454 54.5705 29.711 54.3036 28.72C53.2901 24.9583 50.8228 21.9791 47.3603 20.3353C45.6784 19.5374 44.5602 19.2681 42.5493 19.1769L40.7496 19.0969L40.7885 21.4052L40.8275 23.7135L42.4476 23.7989C44.1912 23.8895 44.8642 24.0762 46.0269 24.7893C47.7461 25.8426 49.0331 27.3653 49.6683 29.0982C50.078 30.2161 50.1755 32.8033 49.8558 34.0738C49.2823 36.3533 47.781 38.2163 45.6788 39.2574C44.1469 40.0158 42.8546 40.2462 40.0879 40.252L37.9396 40.2574V26.6698V13.0829H35.6151H33.2907V28.9227V44.7625L38.1157 44.7615C40.8151 44.7615 43.3437 44.6964 43.856 44.6111Z",
        fill: "#2C9F1C"
      }
    )
  }
);

// src/components/icons/RedirectIcon.tsx
import { jsx as jsx53, jsxs as jsxs36 } from "react/jsx-runtime";
var RedirectIcon = ({
  width = 16,
  height = 16,
  color = "#344054"
}) => {
  return /* @__PURE__ */ jsxs36(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx53(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M3.4001 4.4001C3.06873 4.4001 2.8001 4.66873 2.8001 5.0001V11.8001C2.8001 12.1315 3.06873 12.4001 3.4001 12.4001H10.2001C10.5315 12.4001 10.8001 12.1315 10.8001 11.8001V8.6001C10.8001 8.26873 11.0687 8.0001 11.4001 8.0001C11.7315 8.0001 12.0001 8.26873 12.0001 8.6001V11.8001C12.0001 12.7942 11.1942 13.6001 10.2001 13.6001H3.4001C2.40599 13.6001 1.6001 12.7942 1.6001 11.8001V5.0001C1.6001 4.00599 2.40598 3.2001 3.4001 3.2001H7.4001C7.73147 3.2001 8.0001 3.46873 8.0001 3.8001C8.0001 4.13147 7.73147 4.4001 7.4001 4.4001H3.4001Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx53(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M4.95518 10.2026C5.1775 10.4484 5.55692 10.4673 5.80265 10.245L13.2001 3.55209V5.8001C13.2001 6.13147 13.4687 6.4001 13.8001 6.4001C14.1315 6.4001 14.4001 6.13147 14.4001 5.8001V2.2001C14.4001 1.86873 14.1315 1.6001 13.8001 1.6001H10.2001C9.86873 1.6001 9.6001 1.86873 9.6001 2.2001C9.6001 2.53147 9.86873 2.8001 10.2001 2.8001H12.2426L4.99755 9.35518C4.75183 9.5775 4.73285 9.95692 4.95518 10.2026Z",
            fill: color
          }
        )
      ]
    }
  );
};

// src/components/icons/SalesforceIcon.tsx
import { jsx as jsx54, jsxs as jsxs37 } from "react/jsx-runtime";
var SalesforceIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs37(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M0 28.9945V27.1223C0.296426 24.5188 1.81365 22.2947 3.95528 21.0312C3.44732 19.861 3.18585 18.5988 3.18718 17.3231C3.18718 12.1672 7.372 8 12.528 8C15.5474 8 18.249 9.43908 19.9441 11.6727L19.9424 11.6636C21.4927 10.0497 23.6504 9.05026 26.0342 9.05026C29.2125 9.05026 31.9671 10.816 33.4503 13.447C34.7669 12.8584 36.1929 12.5545 37.6351 12.5553C43.3561 12.5553 48 17.2345 48 23.0085C48 28.7825 43.3561 33.4616 37.6351 33.4616C36.9359 33.4616 36.2543 33.3914 35.5869 33.2586C34.289 35.5717 31.8081 37.1433 28.983 37.1433C27.8327 37.1455 26.6972 36.8846 25.6634 36.3804C24.5148 39.0783 22.0338 41.0763 19.0487 41.54H16.3812C13.2492 41.055 10.6824 38.891 9.61273 35.992C9.07047 36.1065 8.51773 36.1639 7.96352 36.1636C3.84537 36.1636 0.458986 33.0232 0 28.9945Z",
          fill: "#00A1E0"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M6.94664 25.4365C6.91645 25.5153 6.95741 25.5316 6.96713 25.5456C7.05735 25.611 7.14899 25.6583 7.24081 25.7114C7.73169 25.971 8.19432 26.0469 8.67813 26.0469C9.66342 26.0469 10.2761 25.5225 10.2761 24.6784V24.6618C10.2761 23.8814 9.58396 23.5971 8.93769 23.3941L8.85311 23.3666C8.36401 23.2082 7.94199 23.0701 7.94199 22.7487V22.7322C7.94199 22.4568 8.18919 22.2537 8.57059 22.2537C8.99437 22.2537 9.49938 22.3948 9.82251 22.5734C9.82251 22.5734 9.91821 22.6351 9.953 22.5427C9.97189 22.4928 10.1366 22.052 10.1543 22.0024C10.173 21.9507 10.1401 21.9117 10.1065 21.8916C9.73564 21.6656 9.22357 21.512 8.69385 21.512L8.5955 21.5123C7.6932 21.5123 7.06283 22.0579 7.06283 22.8384V22.8552C7.06283 23.678 7.75853 23.9464 8.40833 24.1318L8.51286 24.1642C8.98608 24.3098 9.39573 24.4362 9.39573 24.7682V24.7848C9.39573 25.0903 9.12911 25.318 8.70179 25.318C8.53564 25.318 8.00609 25.3152 7.43223 24.9525C7.36283 24.9121 7.32327 24.8829 7.26978 24.8503C7.24153 24.8332 7.1709 24.8022 7.14017 24.8947L6.94594 25.435M21.3721 25.435C21.3419 25.5139 21.3828 25.5302 21.3929 25.5442C21.4828 25.6096 21.5748 25.6569 21.6666 25.71C22.1575 25.9696 22.6201 26.0455 23.1039 26.0455C24.0892 26.0455 24.7019 25.5211 24.7019 24.677V24.6604C24.7019 23.88 24.0115 23.5957 23.3635 23.3927L23.2789 23.3652C22.7898 23.2067 22.3678 23.0686 22.3678 22.7472V22.7308C22.3678 22.4554 22.6149 22.2523 22.9964 22.2523C23.4202 22.2523 23.9234 22.3934 24.2483 22.572C24.2483 22.572 24.344 22.6337 24.3789 22.5413C24.3977 22.4914 24.5626 22.0505 24.5785 22.001C24.5974 21.9493 24.5643 21.9103 24.5308 21.8902C24.16 21.6641 23.6479 21.5106 23.1182 21.5106L23.0197 21.5109C22.1174 21.5109 21.487 22.0565 21.487 22.837V22.8538C21.487 23.6766 22.1827 23.945 22.8326 24.1304L22.9369 24.1628C23.4118 24.3083 23.8198 24.4348 23.8198 24.7668V24.7834C23.8198 25.0889 23.5531 25.3166 23.1258 25.3166C22.9593 25.3166 22.4283 25.3138 21.8562 24.9511C21.7868 24.9106 21.7462 24.8828 21.6936 24.8488C21.6757 24.8376 21.5926 24.8051 21.5642 24.8933L21.3699 25.4336M31.2228 23.7809C31.2228 24.2577 31.1338 24.6338 30.9597 24.9004C30.786 25.1635 30.5235 25.2924 30.1581 25.2924C29.7925 25.2924 29.5312 25.1646 29.36 24.9022C29.1875 24.6373 29.1004 24.2594 29.1004 23.7827C29.1004 23.306 29.188 22.9316 29.36 22.6685C29.5309 22.4072 29.7908 22.2818 30.1581 22.2818C30.5253 22.2818 30.7867 22.4084 30.9597 22.6685C31.1348 22.9316 31.2228 23.306 31.2228 23.7827M32.0456 22.8981C31.9646 22.6244 31.839 22.3843 31.6713 22.1829C31.5034 21.9817 31.2917 21.8192 31.0391 21.7027C30.7885 21.5853 30.4918 21.526 30.1563 21.526C29.8209 21.526 29.5259 21.5856 29.2734 21.7027C29.0227 21.8199 28.8091 21.9817 28.6413 22.1829C28.4739 22.3843 28.3482 22.6244 28.267 22.8981C28.1866 23.17 28.1458 23.4667 28.1458 23.7827C28.1458 24.0988 28.1866 24.3954 28.267 24.6674C28.3477 24.941 28.4735 25.1812 28.6413 25.3824C28.8093 25.5838 29.0227 25.7444 29.2734 25.8592C29.5259 25.9736 29.8226 26.0319 30.1563 26.0319C30.49 26.0319 30.7867 25.9736 31.0374 25.8592C31.2882 25.7448 31.5018 25.5838 31.6695 25.3824C31.8371 25.1812 31.9627 24.941 32.0439 24.6674C32.124 24.3954 32.1648 24.0988 32.1648 23.7827C32.1648 23.4684 32.124 23.17 32.0439 22.8981M38.8066 25.1583C38.7796 25.0782 38.7016 25.1084 38.7016 25.1084C38.5821 25.1541 38.4544 25.1965 38.3185 25.2177C38.1815 25.2393 38.0288 25.25 37.8681 25.25C37.4709 25.25 37.1531 25.1315 36.927 24.8968C36.6993 24.6621 36.5721 24.2841 36.5739 23.7721C36.5752 23.306 36.6878 22.9563 36.89 22.688C37.0895 22.423 37.3967 22.2854 37.8029 22.2854C38.1419 22.2854 38.4014 22.3247 38.6734 22.4098C38.6734 22.4098 38.7378 22.4379 38.7687 22.3529C38.841 22.1535 38.8943 22.0104 38.9717 21.7915C38.9936 21.7288 38.94 21.7023 38.9204 21.6947C38.8138 21.6531 38.562 21.5847 38.3713 21.556C38.1929 21.5287 37.9864 21.5146 37.7533 21.5146C37.4072 21.5146 37.0999 21.5737 36.8369 21.691C36.5737 21.808 36.3512 21.97 36.1747 22.1713C35.9981 22.3726 35.8639 22.6127 35.7738 22.8864C35.6847 23.1584 35.6397 23.4567 35.6397 23.7728C35.6397 24.4544 35.8233 25.007 36.187 25.4114C36.5507 25.8176 37.0982 26.0223 37.8115 26.0223C38.2335 26.0223 38.6661 25.9371 38.9769 25.814C38.9769 25.814 39.0362 25.7854 39.0105 25.7162L38.8066 25.1583ZM40.2465 23.3219C40.2858 23.0569 40.3584 22.8363 40.4725 22.665C40.6432 22.4037 40.9033 22.2606 41.2688 22.2606C41.6344 22.2606 41.8763 22.4044 42.0493 22.665C42.1641 22.8371 42.2142 23.0658 42.2329 23.3219L40.2376 23.3215L40.2465 23.3219ZM43.0187 22.7392C42.9486 22.4761 42.775 22.2094 42.662 22.0876C42.482 21.8951 42.3071 21.7591 42.1323 21.685C41.8799 21.5776 41.6084 21.5226 41.3342 21.5231C40.9863 21.5231 40.6703 21.5819 40.4143 21.7014C40.1582 21.8219 39.9428 21.9857 39.7732 22.1905C39.6039 22.3953 39.4766 22.6372 39.3953 22.9145C39.3142 23.1882 39.2732 23.4884 39.2732 23.8027C39.2732 24.124 39.3157 24.4224 39.3995 24.6926C39.484 24.9645 39.6202 25.2028 39.8038 25.4007C39.9875 25.6001 40.2223 25.7556 40.5048 25.8651C40.7855 25.9736 41.1263 26.03 41.5166 26.0288C42.3218 26.026 42.7456 25.8469 42.9186 25.7498C42.9494 25.7324 42.9786 25.7026 42.9422 25.6166L42.7603 25.1063C42.7324 25.0302 42.6555 25.0577 42.6555 25.0577C42.4559 25.1322 42.1734 25.2661 41.5112 25.2643C41.0787 25.2636 40.759 25.136 40.5578 24.9359C40.3529 24.731 40.2506 24.4327 40.2346 24.0089L43.0246 24.011C43.0246 24.011 43.098 24.0103 43.1056 23.9386C43.1086 23.909 43.2011 23.3665 43.0224 22.7397L43.0187 22.7392ZM17.9451 23.3219C17.9845 23.0569 18.0573 22.8363 18.1712 22.665C18.3421 22.4037 18.602 22.2606 18.9674 22.2606C19.333 22.2606 19.5749 22.4044 19.7479 22.665C19.8625 22.8371 19.9126 23.0658 19.9315 23.3219L17.9363 23.3215L17.9451 23.3219ZM20.7174 22.7392C20.6474 22.4761 20.4736 22.2094 20.3607 22.0876C20.1806 21.8951 20.0057 21.7591 19.8309 21.685C19.5785 21.5776 19.3071 21.5226 19.0328 21.5231C18.6849 21.5231 18.3689 21.5819 18.1129 21.7014C17.8568 21.8219 17.6414 21.9857 17.4719 22.1905C17.303 22.3953 17.1752 22.6372 17.0941 22.9145C17.0129 23.1882 16.9722 23.4884 16.9722 23.8027C16.9722 24.124 17.0144 24.4224 17.0986 24.6926C17.1831 24.9645 17.3193 25.2028 17.5012 25.4007C17.6849 25.6001 17.9197 25.7556 18.2022 25.8651C18.483 25.9736 18.822 26.03 19.214 26.0288C20.0192 26.026 20.4429 25.8469 20.6159 25.7498C20.6467 25.7324 20.676 25.7026 20.6394 25.6166L20.4576 25.1063C20.4295 25.0302 20.3525 25.0577 20.3525 25.0577C20.153 25.1322 19.8705 25.2661 19.2084 25.2643C18.7775 25.2636 18.4562 25.136 18.2548 24.9359C18.05 24.731 17.9476 24.4327 17.9317 24.0089L20.7216 24.011C20.7216 24.011 20.795 24.0103 20.8026 23.9386C20.8056 23.909 20.8981 23.3665 20.7193 22.7397L20.7174 22.7392ZM11.9239 25.1406C11.8146 25.0533 11.7995 25.0319 11.7632 24.9753C11.708 24.89 11.6798 24.7687 11.6798 24.6133C11.6798 24.3696 11.7609 24.1931 11.9287 24.0748C11.9269 24.0751 12.1688 23.8664 12.7374 23.8735C12.9904 23.878 13.2429 23.8995 13.4932 23.9379V25.204C13.4932 25.204 13.1404 25.2801 12.7413 25.3041C12.1745 25.3381 11.9237 25.1409 11.9255 25.1414L11.9239 25.1406ZM13.0328 23.1806C12.9198 23.1723 12.7732 23.1682 12.5985 23.1682C12.3601 23.1682 12.1288 23.1979 11.9134 23.2562C11.6961 23.3147 11.5001 23.4055 11.3324 23.5263C11.1642 23.6463 11.0259 23.8034 10.9281 23.9854C10.8294 24.1691 10.779 24.3845 10.779 24.6281C10.779 24.8754 10.8219 25.089 10.9067 25.2638C10.9897 25.4372 11.1148 25.5871 11.2704 25.7C11.4253 25.8126 11.6165 25.8959 11.8372 25.9454C12.0562 25.9953 12.3034 26.0206 12.5753 26.0206C12.8614 26.0206 13.1456 25.9966 13.4211 25.9501C13.6557 25.9097 13.8895 25.8643 14.1221 25.8138C14.2149 25.7925 14.3181 25.7644 14.3181 25.7644C14.387 25.747 14.3816 25.6732 14.3816 25.6732L14.38 23.1306C14.38 22.5727 14.231 22.1577 13.9369 21.9016C13.6437 21.6456 13.2147 21.5167 12.6584 21.5167C12.4501 21.5167 12.1129 21.545 11.9116 21.5854C11.9116 21.5854 11.3042 21.7034 11.0534 21.8997C11.0534 21.8997 10.9984 21.9337 11.0283 22.0104L11.2261 22.5401C11.2506 22.6088 11.3175 22.5853 11.3175 22.5853C11.3175 22.5853 11.3386 22.5771 11.3633 22.5624C11.8983 22.2711 12.5764 22.2798 12.5764 22.2798C12.8766 22.2798 13.1096 22.3408 13.265 22.46C13.417 22.5767 13.4946 22.7531 13.4946 23.1239V23.2417C13.2562 23.2071 13.0354 23.1871 13.0354 23.1871L13.0328 23.1806ZM35.4578 21.7451C35.4618 21.7357 35.4639 21.7257 35.4639 21.7155C35.464 21.7054 35.462 21.6953 35.4581 21.686C35.4542 21.6767 35.4485 21.6681 35.4412 21.6611C35.434 21.654 35.4254 21.6484 35.4159 21.6447C35.3684 21.6268 35.1316 21.5767 34.9497 21.5655C34.6002 21.5436 34.4059 21.6025 34.2311 21.6809C34.0585 21.7588 33.8673 21.8839 33.7614 22.0288L33.7611 21.6897C33.7611 21.6431 33.7281 21.6056 33.6811 21.6056H32.9677C32.9214 21.6056 32.8879 21.6431 32.8879 21.6897V25.8393C32.888 25.8616 32.8969 25.8831 32.9127 25.899C32.9286 25.9149 32.95 25.9238 32.9724 25.9238H33.7036C33.7259 25.9238 33.7473 25.9149 33.7632 25.899C33.779 25.8831 33.7879 25.8616 33.7879 25.8393V23.7557C33.7879 23.4767 33.8186 23.1994 33.8799 23.0247C33.9403 22.8518 34.0224 22.7139 34.1235 22.6151C34.2201 22.5195 34.3382 22.4486 34.4679 22.4084C34.5896 22.3735 34.7157 22.3557 34.8422 22.3557C34.9879 22.3557 35.1477 22.3932 35.1477 22.3932C35.2014 22.3992 35.2312 22.3664 35.2494 22.318C35.2972 22.1907 35.4331 21.8094 35.4595 21.7335",
          fill: "#FFFFFE"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M28.6299 19.8297C28.5396 19.8024 28.4476 19.7806 28.3546 19.7643C28.2296 19.7434 28.1029 19.7337 27.9761 19.735C27.4723 19.735 27.0752 19.8774 26.7964 20.1585C26.5195 20.4375 26.3311 20.8625 26.2366 21.4217L26.2026 21.6105H25.5703C25.5703 21.6105 25.4931 21.6073 25.4769 21.6915L25.373 22.2707C25.3658 22.3261 25.3896 22.3607 25.4637 22.3604H26.0794L25.4548 25.8465C25.4059 26.1272 25.3499 26.3582 25.2879 26.5332C25.2268 26.7058 25.1672 26.8353 25.0937 26.9292C25.0226 27.0202 24.9551 27.0871 24.8387 27.1261C24.7427 27.1585 24.6321 27.1732 24.511 27.1732C24.4435 27.1732 24.3538 27.162 24.2876 27.1487C24.2214 27.1357 24.187 27.1208 24.1374 27.1C24.1374 27.1 24.0651 27.0724 24.0367 27.1448C24.0136 27.204 23.8496 27.6552 23.8301 27.7109C23.8103 27.766 23.838 27.8094 23.873 27.822C23.9549 27.8513 24.0159 27.87 24.1275 27.8963C24.2825 27.9328 24.4132 27.9352 24.5355 27.9352C24.7919 27.9352 25.0255 27.8991 25.2192 27.8292C25.4141 27.7588 25.5838 27.6361 25.734 27.4699C25.8962 27.2907 25.9983 27.103 26.0959 26.8469C26.1925 26.5932 26.2748 26.2782 26.3407 25.9111L26.9682 22.3604H27.8856C27.8856 22.3604 27.9631 22.3632 27.9791 22.2793L28.0829 21.7002C28.0902 21.6447 28.0665 21.6101 27.992 21.6105H27.1015C27.1059 21.5903 27.1464 21.2771 27.2486 20.9822C27.2922 20.8563 27.3744 20.7547 27.4438 20.6851C27.5088 20.6186 27.5889 20.5689 27.6771 20.5398C27.774 20.51 27.8748 20.4954 27.9761 20.4968C28.06 20.4968 28.1422 20.5069 28.2049 20.5199C28.2913 20.5383 28.3248 20.548 28.3474 20.5547C28.4382 20.5824 28.4503 20.5556 28.4682 20.5116L28.6811 19.9268C28.703 19.864 28.6497 19.8374 28.6299 19.8297ZM16.186 25.8539C16.186 25.9005 16.1529 25.9385 16.1062 25.9385H15.3676C15.3208 25.9385 15.2876 25.9005 15.2876 25.8539V19.909C15.2876 19.8626 15.3208 19.8249 15.3676 19.8249H16.1062C16.1529 19.8249 16.186 19.8626 16.186 19.909V25.8539Z",
          fill: "#FFFFFE"
        }
      )
    ]
  }
);

// src/components/icons/ScheduleIcon.tsx
import { jsx as jsx55, jsxs as jsxs38 } from "react/jsx-runtime";
var ScheduleIcon = ({
  width = 20,
  height = 20
}) => /* @__PURE__ */ jsxs38(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx55("circle", { cx: "10.0002", cy: "10", r: "10", fill: "#F6E9FB" }),
      /* @__PURE__ */ jsx55(
        "path",
        {
          d: "M8.02867 5.6466C7.79536 5.50834 7.50024 5.6765 7.50024 5.9477V14.0525C7.50024 14.3237 7.79536 14.4919 8.02867 14.3537L14.8672 10.3012C15.0959 10.1657 15.0959 9.83457 14.8672 9.69901L8.02867 5.6466Z",
          stroke: "#485972",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/SelectIcon.tsx
import { ChevronDownIcon as ChevronDownIcon3 } from "@chakra-ui/icons";
import { chakra } from "@chakra-ui/react";
import { cloneElement as cloneElement2, isValidElement as isValidElement2 } from "react";
import { jsx as jsx56 } from "react/jsx-runtime";
var IconWrapper = chakra("div", {
  baseStyle: {
    position: "absolute",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    top: "50%",
    transform: "translateY(-50%)"
  }
});
var SelectIcon = (props) => {
  const { children = /* @__PURE__ */ jsx56(ChevronDownIcon3, {}), ...rest } = props;
  const clone = cloneElement2(children, {
    role: "presentation",
    className: "chakra-select__icon",
    focusable: false,
    "aria-hidden": true,
    // force icon to adhere to `IconWrapper` styles
    style: {
      width: "1em",
      height: "1em",
      color: "currentColor"
    }
  });
  return /* @__PURE__ */ jsx56(IconWrapper, { ...rest, className: "chakra-select__icon-wrapper", children: isValidElement2(children) ? clone : null });
};

// src/components/icons/SentryIcon.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var SentryIcon = ({
  width = 28,
  height = 28
}) => /* @__PURE__ */ jsx57("svg", { width, height, viewBox: "0 0 72 66", children: /* @__PURE__ */ jsx57(
  "path",
  {
    d: "M29,2.26a4.67,4.67,0,0,0-8,0L14.42,13.53A32.21,32.21,0,0,1,32.17,40.19H27.55A27.68,27.68,0,0,0,12.09,17.47L6,28a15.92,15.92,0,0,1,9.23,12.17H4.62A.76.76,0,0,1,4,39.06l2.94-5a10.74,10.74,0,0,0-3.36-1.9l-2.91,5a4.54,4.54,0,0,0,1.69,6.24A4.66,4.66,0,0,0,4.62,44H19.15a19.4,19.4,0,0,0-8-17.31l2.31-4A23.87,23.87,0,0,1,23.76,44H36.07a35.88,35.88,0,0,0-16.41-31.8l4.67-8a.77.77,0,0,1,1.05-.27c.53.29,20.29,34.77,20.66,35.17a.76.76,0,0,1-.68,1.13H40.6q.09,1.91,0,3.81h4.78A4.59,4.59,0,0,0,50,39.43a4.49,4.49,0,0,0-.62-2.28Z",
    transform: "translate(11, 11)",
    fill: "white"
  }
) });

// src/components/icons/SlackIcon.tsx
import { jsx as jsx58, jsxs as jsxs39 } from "react/jsx-runtime";
var SlackIcon = ({ width = 48, height = 48 }) => /* @__PURE__ */ jsxs39(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsxs39("g", { clipPath: "url(#clip0_6101_13798)", children: [
        /* @__PURE__ */ jsx58(
          "path",
          {
            d: "M18.2807 38.2363C18.2807 40.9953 16.0508 43.2253 13.2917 43.2253C10.5327 43.2253 8.30273 40.9953 8.30273 38.2363C8.30273 35.4772 10.5327 33.2473 13.2917 33.2473H18.2807V38.2363ZM20.7752 38.2363C20.7752 35.4772 23.0051 33.2473 25.7642 33.2473C28.5232 33.2473 30.7531 35.4772 30.7531 38.2363V50.7087C30.7531 53.4678 28.5232 55.6977 25.7642 55.6977C23.0051 55.6977 20.7752 53.4678 20.7752 50.7087V38.2363Z",
            fill: "#E01E5A"
          }
        ),
        /* @__PURE__ */ jsx58(
          "path",
          {
            d: "M25.7629 18.2048C23.0038 18.2048 20.7739 15.9748 20.7739 13.2158C20.7739 10.4567 23.0038 8.22681 25.7629 8.22681C28.5219 8.22681 30.7519 10.4567 30.7519 13.2158V18.2048H25.7629ZM25.7629 20.737C28.5219 20.737 30.7519 22.967 30.7519 25.726C30.7519 28.4851 28.5219 30.715 25.7629 30.715H13.2526C10.4936 30.715 8.26367 28.4851 8.26367 25.726C8.26367 22.967 10.4936 20.737 13.2526 20.737H25.7629Z",
            fill: "#36C5F0"
          }
        ),
        /* @__PURE__ */ jsx58(
          "path",
          {
            d: "M45.7576 25.726C45.7576 22.967 47.9875 20.737 50.7466 20.737C53.5056 20.737 55.7355 22.967 55.7355 25.726C55.7355 28.4851 53.5056 30.715 50.7466 30.715H45.7576V25.726ZM43.2631 25.726C43.2631 28.4851 41.0332 30.715 38.2741 30.715C35.5151 30.715 33.2852 28.4851 33.2852 25.726V13.2158C33.2852 10.4567 35.5151 8.22681 38.2741 8.22681C41.0332 8.22681 43.2631 10.4567 43.2631 13.2158V25.726Z",
            fill: "#2EB67D"
          }
        ),
        /* @__PURE__ */ jsx58(
          "path",
          {
            d: "M38.2741 45.7198C41.0332 45.7198 43.2631 47.9497 43.2631 50.7087C43.2631 53.4678 41.0332 55.6977 38.2741 55.6977C35.5151 55.6977 33.2852 53.4678 33.2852 50.7087V45.7198H38.2741ZM38.2741 43.2253C35.5151 43.2253 33.2852 40.9953 33.2852 38.2363C33.2852 35.4772 35.5151 33.2473 38.2741 33.2473H50.7844C53.5434 33.2473 55.7733 35.4772 55.7733 38.2363C55.7733 40.9953 53.5434 43.2253 50.7844 43.2253H38.2741Z",
            fill: "#ECB22E"
          }
        )
      ] }),
      /* @__PURE__ */ jsx58("rect", { x: "0.5", y: "0.5", width: "63", height: "63", rx: "31.5", stroke: "#F1F4F8" }),
      /* @__PURE__ */ jsx58("defs", { children: /* @__PURE__ */ jsx58("clipPath", { id: "clip0_6101_13798", children: /* @__PURE__ */ jsx58("rect", { width: "48", height: "48", fill: "white", transform: "translate(8 8)" }) }) })
    ]
  }
);

// src/components/icons/SnowflakeIcon.tsx
import { jsx as jsx59, jsxs as jsxs40 } from "react/jsx-runtime";
var SnowflakeIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs40(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsxs40("g", { clipPath: "url(#clip0_6101_13204)", children: [
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M48.8126 44.4375L39.0251 38.7833C37.6501 37.9917 35.8918 38.4625 35.096 39.8375C34.7876 40.3792 34.6668 40.975 34.7251 41.5542V52.6042C34.7251 54.1833 36.0085 55.4667 37.5918 55.4667C39.171 55.4667 40.4543 54.1833 40.4543 52.6042V46.25L45.9376 49.4167C47.3126 50.2125 49.0751 49.7375 49.8668 48.3625C50.6626 46.9875 50.1918 45.2292 48.8126 44.4375Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M24.4708 32.3208C24.4874 31.3 23.95 30.35 23.0666 29.8375L13.2791 24.1875C12.8541 23.9417 12.3666 23.8125 11.8791 23.8125C10.8749 23.8125 9.94162 24.35 9.44162 25.2167C8.66662 26.5583 9.12912 28.2792 10.4708 29.0542L16.1124 32.3083L10.4708 35.5667C9.82078 35.9417 9.35412 36.5458 9.16245 37.2708C8.96662 37.9958 9.06662 38.7542 9.44162 39.4042C9.94162 40.2708 10.8749 40.8083 11.8749 40.8083C12.3666 40.8083 12.8541 40.6792 13.2791 40.4333L23.0666 34.7833C23.9416 34.275 24.4791 33.3333 24.4708 32.3208Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M15.5007 20.1834L25.2882 25.8334C26.4299 26.4959 27.8382 26.2793 28.7382 25.4001C29.3091 24.8751 29.6632 24.1251 29.6632 23.2918V11.9668C29.6632 10.3834 28.3799 9.1001 26.8007 9.1001C25.2174 9.1001 23.9341 10.3834 23.9341 11.9668V18.4126L18.3757 15.2043C17.0007 14.4084 15.2424 14.8793 14.4466 16.2543C13.6507 17.6293 14.1257 19.3876 15.5007 20.1834Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M37.5079 32.7793C37.5079 32.996 37.3829 33.296 37.2288 33.4543L33.3496 37.3335C33.1954 37.4877 32.8913 37.6127 32.6746 37.6127H31.6871C31.4704 37.6127 31.1663 37.4877 31.0121 37.3335L27.1288 33.4543C26.9746 33.296 26.8496 32.996 26.8496 32.7793V31.7918C26.8496 31.571 26.9746 31.271 27.1288 31.1168L31.0121 27.2335C31.1663 27.0793 31.4704 26.9543 31.6871 26.9543H32.6746C32.8913 26.9543 33.1954 27.0793 33.3496 27.2335L37.2288 31.1168C37.3829 31.271 37.5079 31.571 37.5079 31.7918V32.7793ZM34.0454 32.3043V32.2627C34.0454 32.1043 33.9538 31.8835 33.8413 31.7668L32.6954 30.6252C32.5829 30.5085 32.3621 30.4168 32.1996 30.4168H32.1579C31.9996 30.4168 31.7788 30.5085 31.6621 30.6252L30.5204 31.7668C30.4079 31.8793 30.3163 32.1002 30.3163 32.2627V32.3043C30.3163 32.4668 30.4079 32.6877 30.5204 32.8002L31.6621 33.946C31.7788 34.0585 31.9996 34.1502 32.1579 34.1502H32.1996C32.3621 34.1502 32.5829 34.0585 32.6954 33.946L33.8413 32.8002C33.9538 32.6877 34.0454 32.4668 34.0454 32.3043Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M39.026 25.8334L48.8135 20.1834C50.1885 19.3918 50.6635 17.6293 49.8676 16.2543C49.0718 14.8793 47.3135 14.4084 45.9385 15.2043L40.4551 18.3709V11.9668C40.4551 10.3834 39.1718 9.1001 37.5926 9.1001C36.0093 9.1001 34.726 10.3834 34.726 11.9668V23.0668C34.6718 23.6418 34.7843 24.2418 35.0968 24.7834C35.8926 26.1584 37.651 26.6293 39.026 25.8334Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M27.2507 38.4501C26.6007 38.3251 25.9049 38.4293 25.2882 38.7835L15.5007 44.4376C14.1257 45.2293 13.6507 46.9876 14.4466 48.3626C15.2424 49.7418 17.0007 50.2126 18.3757 49.4168L23.9341 46.2085V52.6043C23.9341 54.1835 25.2174 55.4668 26.8007 55.4668C28.3799 55.4668 29.6632 54.1835 29.6632 52.6043V41.2793C29.6632 39.8501 28.6174 38.6668 27.2507 38.4501Z",
            fill: "#29B5E8"
          }
        ),
        /* @__PURE__ */ jsx59(
          "path",
          {
            d: "M54.9299 25.1835C54.1383 23.8044 52.3758 23.3335 51.0008 24.1294L41.2133 29.7794C40.2758 30.321 39.7591 31.3127 39.7758 32.321C39.7674 33.3252 40.2841 34.3044 41.2133 34.8377L51.0008 40.4919C52.3758 41.2835 54.1341 40.8127 54.9299 39.4377C55.7258 38.0627 55.2508 36.3044 53.8758 35.5085L48.3341 32.3085L53.8758 29.1085C55.2549 28.3169 55.7258 26.5585 54.9299 25.1835Z",
            fill: "#29B5E8"
          }
        )
      ] }),
      /* @__PURE__ */ jsx59("rect", { x: "0.5", y: "0.5", width: "63", height: "63", rx: "31.5", stroke: "#F1F4F8" }),
      /* @__PURE__ */ jsx59("defs", { children: /* @__PURE__ */ jsx59("clipPath", { id: "clip0_6101_13204", children: /* @__PURE__ */ jsx59(
        "rect",
        {
          width: "48",
          height: "46.9333",
          fill: "white",
          transform: "translate(8 8.53345)"
        }
      ) }) })
    ]
  }
);

// src/components/icons/StripeIcon.tsx
import { jsx as jsx60, jsxs as jsxs41 } from "react/jsx-runtime";
var StripeIcon = ({
  width = 48,
  height = 48
}) => /* @__PURE__ */ jsxs41(
  "svg",
  {
    height,
    width,
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    viewBox: "54 36 360 149.8",
    children: [
      /* @__PURE__ */ jsx60("style", { children: ".st0{fill-rule:evenodd;clip-rule:evenodd;fill:#635bff}" }),
      /* @__PURE__ */ jsx60(
        "path",
        {
          className: "st0",
          d: "M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.9-2.7 27.7-6.5v-20c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-8.9zm-49.4-9.5c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8zM301.1 67.6c-9.8 0-16.1 4.6-19.6 7.8l-1.3-6.2h-22v116.6l25-5.3.1-28.3c3.6 2.6 8.9 6.3 17.7 6.3 17.9 0 34.2-14.4 34.2-46.1-.1-29-16.6-44.8-34.1-44.8zm-6 68.9c-5.9 0-9.4-2.1-11.8-4.7l-.1-37.1c2.6-2.9 6.2-4.9 11.9-4.9 9.1 0 15.4 10.2 15.4 23.3 0 13.4-6.2 23.4-15.4 23.4zM223.8 61.7l25.1-5.4V36l-25.1 5.3zM223.8 69.3h25.1v87.5h-25.1zM196.9 76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7 15.9-6.3 19-5.2v-23c-3.2-1.2-14.9-3.4-20.8 7.4zM146.9 47.6l-24.4 5.2-.1 80.1c0 14.8 11.1 25.7 25.9 25.7 8.2 0 14.2-1.5 17.5-3.3V135c-3.2 1.3-19 5.9-19-8.9V90.6h19V69.3h-19l.1-21.7zM79.3 94.7c0-3.9 3.2-5.4 8.5-5.4 7.6 0 17.2 2.3 24.8 6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5 67.6 54 78.2 54 95.9c0 27.6 38 23.2 38 35.1 0 4.6-4 6.1-9.6 6.1-8.3 0-18.9-3.4-27.3-8v23.8c9.3 4 18.7 5.7 27.3 5.7 20.8 0 35.1-10.3 35.1-28.2-.1-29.8-38.2-24.5-38.2-35.7z"
        }
      )
    ]
  }
);

// src/components/icons/SubscriptionIcon.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
var SubscriptionIcon = ({
  width = 20,
  height = 20,
  color = "gray.70"
}) => /* @__PURE__ */ jsx61(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M1.89167 1.02427C1.60836 0.866256 1.25 1.05844 1.25 1.36838V10.6311C1.25 10.941 1.60836 11.1332 1.89167 10.9752L10.1955 6.34386C10.4733 6.18892 10.4733 5.81052 10.1955 5.65559L1.89167 1.02427Z",
        stroke: color,
        strokeWidth: "1.5",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);

// src/components/icons/TaxRateIcon.tsx
import { jsx as jsx62, jsxs as jsxs42 } from "react/jsx-runtime";
var TaxRateIcon = ({
  width = "37px",
  height = "37px"
}) => /* @__PURE__ */ jsxs42(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 37 37",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx62("circle", { cx: "18.5", cy: "19.3604", r: "18.5", fill: "#F0F4F7" }),
      /* @__PURE__ */ jsx62(
        "path",
        {
          d: "M18.795 9.86228C16.667 9.90522 14.7217 10.6856 13.2003 11.9556L11.9624 10.5782C11.6483 10.2288 11.0694 10.3824 10.9719 10.8418L10.0198 15.3178C9.93904 15.6983 10.2521 16.0462 10.6396 16.0073L15.2013 15.5495C15.6695 15.5025 15.8861 14.9449 15.5721 14.5955L14.2989 13.1789C15.7297 12.0012 17.6074 11.3476 19.6454 11.5266C23.1031 11.8301 25.9359 14.5847 26.3147 18.0272C26.8034 22.4674 23.3318 26.2241 18.9808 26.2241C15.1586 26.2241 12.0148 23.3241 11.6389 19.6092C11.5962 19.1862 11.2538 18.8605 10.8237 18.8605C10.3424 18.8605 9.95746 19.2673 10.0038 19.7418C10.4478 24.2986 14.2976 27.8604 18.9808 27.8604C24.2998 27.8604 28.5432 23.2664 27.9434 17.8378C27.4809 13.6529 24.0698 10.3043 19.8696 9.90303C19.5073 9.86841 19.1484 9.85515 18.795 9.86228ZM18.9504 13.9507C18.7827 13.9507 18.6701 14.0567 18.6701 14.2408V14.9383C17.1101 15.0164 15.9925 15.8871 15.9925 17.2314C15.9925 18.4362 16.7921 19.0889 18.2569 19.4182L18.6701 19.5133V21.5436C17.9654 21.4822 17.6133 21.1647 17.4346 20.7294C17.278 20.4058 17.0435 20.2276 16.6971 20.2276C16.2387 20.2276 15.9644 20.5003 15.9644 20.902C15.9644 21.0079 15.987 21.1251 16.0317 21.2535C16.3056 22.068 17.2331 22.7317 18.6701 22.7988V23.4795C18.6701 23.6636 18.7827 23.7696 18.9504 23.7696C19.118 23.7696 19.2298 23.6636 19.2298 23.4795V22.7988C20.8398 22.7039 21.9971 21.8507 21.9971 20.4394C21.9971 19.2174 21.1979 18.6038 19.6662 18.2581L19.2298 18.163V16.1991C19.7665 16.2719 20.1183 16.5455 20.3364 17.0029C20.5152 17.3265 20.7563 17.4879 21.1027 17.4879C21.5275 17.4879 21.8066 17.248 21.8066 16.8798C21.8066 16.7624 21.7849 16.6512 21.7513 16.5395C21.4828 15.6804 20.5602 15.0275 19.2298 14.9383V14.2408C19.2298 14.0567 19.118 13.9507 18.9504 13.9507ZM18.6701 16.1943V18.0352C17.8875 17.8343 17.5579 17.5388 17.5579 17.0868C17.5579 16.6347 17.9379 16.2614 18.6701 16.1943ZM19.2298 19.6412C20.1244 19.8645 20.4369 20.1153 20.4373 20.6119C20.4373 21.1249 20.035 21.4822 19.2298 21.5436V19.6412Z",
          fill: "#667085"
        }
      )
    ]
  }
);

// src/components/icons/TrashIcon.tsx
import { jsx as jsx63, jsxs as jsxs43 } from "react/jsx-runtime";
var TrashIcon = ({
  width = 16,
  height = 16,
  color = "#485972"
}) => /* @__PURE__ */ jsxs43(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx63(
        "path",
        {
          d: "M2.66797 4.66406H3.85315H13.3346",
          stroke: color,
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx63(
        "path",
        {
          d: "M12 4.66667V8.6V12.8C12 13.1183 11.8796 13.4235 11.6653 13.6485C11.4509 13.8736 11.1602 14 10.8571 14H5.14286C4.83975 14 4.54906 13.8736 4.33474 13.6485C4.12041 13.4235 4 13.1183 4 12.8V4.66667M5.71429 4.4V3.2C5.71429 2.88174 5.83469 2.57652 6.04902 2.35147C6.26335 2.12643 6.55404 2 6.85714 2H9.14286C9.44596 2 9.73665 2.12643 9.95098 2.35147C10.1653 2.57652 10.2857 2.88174 10.2857 3.2V4.4",
          stroke: color,
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);

// src/components/icons/TriangleDownIcon.tsx
import { jsx as jsx64 } from "react/jsx-runtime";
var TriangleDownIcon = ({
  width = 10,
  height = 8,
  color
}) => /* @__PURE__ */ jsx64(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 10 8",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M4.17801 6.81355C4.57569 7.38756 5.42431 7.38756 5.82199 6.81355L9.10877 2.06949C9.56825 1.40629 9.0936 0.5 8.28678 0.5H1.71322C0.906401 0.5 0.431746 1.40629 0.891226 2.06949L4.17801 6.81355Z",
        fill: color
      }
    )
  }
);

// src/components/icons/TriangleUpIcon.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var TriangleUpIcon = ({
  width = 10,
  height = 8,
  color
}) => /* @__PURE__ */ jsx65(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 10 8",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx65(
      "path",
      {
        d: "M4.17801 1.18645C4.57569 0.61244 5.42431 0.612441 5.82199 1.18645L9.10877 5.93051C9.56825 6.59371 9.0936 7.5 8.28678 7.5H1.71322C0.906401 7.5 0.431746 6.59371 0.891226 5.93051L4.17801 1.18645Z",
        fill: color
      }
    )
  }
);

// src/components/icons/UncollectibleIcon.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var UncollectibleIcon = ({
  height = 20,
  width = 20
}) => {
  return /* @__PURE__ */ jsx66(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx66("g", { id: "Frame", children: /* @__PURE__ */ jsx66(
        "circle",
        {
          id: "Ellipse 219",
          cx: "10",
          cy: "10",
          r: "6",
          stroke: "#FC7182",
          strokeWidth: "2"
        }
      ) })
    }
  );
};

// src/components/icons/UnpaidIcon.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var UnpaidIcon = ({
  height = 20,
  width = 20
}) => {
  return /* @__PURE__ */ jsx67(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx67("g", { id: "Frame", children: /* @__PURE__ */ jsx67(
        "circle",
        {
          id: "Ellipse 219",
          cx: "10",
          cy: "10",
          r: "6",
          stroke: "#A9B3C5",
          strokeWidth: "2"
        }
      ) })
    }
  );
};

// src/components/icons/Wordmark.tsx
import { jsx as jsx68, jsxs as jsxs44 } from "react/jsx-runtime";
var Wordmark = ({
  height = "23",
  width = "107",
  color = "#241F25"
}) => /* @__PURE__ */ jsxs44(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 107 23",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M8.23277 17.7498C10.339 17.7498 12.0043 17.2973 13.2044 16.3924C14.4044 15.4875 15.0167 14.2492 15.0167 12.6775C15.0167 11.3678 14.5759 10.32 13.6942 9.53413C12.8125 8.77209 11.3921 8.17676 9.40833 7.74812L7.8899 7.43854C6.42045 7.15278 5.44082 6.77177 4.90203 6.34312C4.36323 5.96211 4.09383 5.36677 4.09383 4.58093C4.09383 3.8189 4.38772 3.24738 4.99999 2.81874C5.58777 2.41391 6.46943 2.19959 7.6205 2.19959C9.08994 2.19959 10.143 2.46153 10.8043 2.98543C11.4655 3.50933 11.8329 4.29517 11.9309 5.39059H14.5269C14.4779 3.72365 13.8901 2.41391 12.7391 1.46137C11.588 0.508832 9.87365 0.0325623 7.59601 0.0325623C5.68573 0.0325623 4.19179 0.461204 3.08971 1.29468C1.96314 2.12814 1.42434 3.24738 1.42434 4.62856C1.42434 6.03355 1.86517 7.12897 2.79582 7.91481C3.70198 8.72447 5.04897 9.29599 6.86129 9.62938L8.35522 9.93895C9.82467 10.2723 10.8533 10.6295 11.4411 11.0344C12.0043 11.4392 12.2982 12.0107 12.2982 12.7489C12.2982 13.6777 11.9309 14.3921 11.2451 14.8683C10.5594 15.3446 9.55527 15.5827 8.28175 15.5827C5.14693 15.5827 3.57953 14.3206 3.53054 11.7726H0.910034C0.910034 13.7253 1.57128 15.2255 2.8448 16.2257C4.11832 17.2497 5.93064 17.7498 8.23277 17.7498Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M28.2376 11.3201C28.2376 9.41506 27.6988 7.86718 26.6457 6.62889C25.5681 5.4144 24.0987 4.79525 22.2374 4.79525C20.4496 4.79525 19.0046 5.39059 17.9025 6.55744C16.776 7.74812 16.2372 9.29599 16.2372 11.2249C16.2372 13.1776 16.776 14.7255 17.9025 15.8923C19.0046 17.083 20.4741 17.6545 22.2864 17.6545C23.7313 17.6545 24.9559 17.2973 25.9355 16.5829C26.9151 15.8685 27.6009 14.8683 27.9437 13.6062H25.4702C24.9804 14.9874 23.9273 15.6542 22.3354 15.6542C21.2578 15.6542 20.4251 15.3684 19.8128 14.7493C19.176 14.1301 18.8087 13.2252 18.7107 12.0107H28.2376V11.3201ZM22.2374 6.79558C23.217 6.79558 24.0007 7.10515 24.613 7.7243C25.2253 8.34345 25.5926 9.1293 25.7151 10.1295H18.7352C18.8822 9.05786 19.2495 8.22439 19.8618 7.65286C20.4741 7.08134 21.2578 6.79558 22.2374 6.79558Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M39.2815 6.89083C38.3264 5.50965 36.9304 4.79525 35.0936 4.79525C33.3303 4.79525 31.9098 5.39059 30.8812 6.53363C29.8281 7.70049 29.3138 9.27218 29.3138 11.2249C29.3138 13.2014 29.8281 14.7731 30.8812 15.9161C31.9098 17.083 33.3303 17.6545 35.0936 17.6545C36.857 17.6545 38.2284 17.0115 39.1836 15.7018V22.0362H41.6327V5.08101H39.2815V6.89083ZM32.865 14.4873C32.1792 13.7491 31.8608 12.7013 31.8608 11.3201C31.8608 9.93895 32.1792 8.84354 32.865 8.03388C33.5262 7.24803 34.4324 6.84321 35.559 6.84321C36.71 6.84321 37.6162 7.22422 38.2774 7.98625C38.9142 8.77209 39.2571 9.8437 39.2571 11.2249C39.2571 12.6299 38.9142 13.7015 38.2774 14.4397C37.6162 15.2255 36.71 15.6066 35.559 15.6066C34.4324 15.6066 33.5262 15.2493 32.865 14.4873Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M52.1894 12.0107C52.1894 13.13 51.8711 14.0111 51.2588 14.6302C50.6465 15.2494 49.8873 15.5589 49.0056 15.5589C47.2178 15.5589 46.3362 14.7016 46.3362 12.9871V5.08101H43.8871V13.3205C43.8871 14.7016 44.2789 15.7494 45.0626 16.5115C45.8463 17.2735 46.9239 17.6545 48.3199 17.6545C49.9853 17.6545 51.3078 16.9639 52.2874 15.5351V17.3687H54.6385V5.08101H52.1894V12.0107Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M68.3047 11.3201C68.3047 9.41506 67.7659 7.86718 66.7128 6.62889C65.6352 5.4144 64.1657 4.79525 62.3044 4.79525C60.5166 4.79525 59.0717 5.39059 57.9696 6.55744C56.843 7.74812 56.3042 9.29599 56.3042 11.2249C56.3042 13.1776 56.843 14.7255 57.9696 15.8923C59.0717 17.083 60.5411 17.6545 62.3534 17.6545C63.7984 17.6545 65.0229 17.2973 66.0025 16.5829C66.9822 15.8685 67.6679 14.8683 68.0108 13.6062H65.5372C65.0474 14.9874 63.9943 15.6542 62.4024 15.6542C61.3248 15.6542 60.4921 15.3684 59.8799 14.7493C59.2431 14.1301 58.8757 13.2252 58.7778 12.0107H68.3047V11.3201ZM62.3044 6.79558C63.2841 6.79558 64.0678 7.10515 64.68 7.7243C65.2923 8.34345 65.6597 9.1293 65.7821 10.1295H58.8023C58.9492 9.05786 59.3166 8.22439 59.9288 7.65286C60.5411 7.08134 61.3248 6.79558 62.3044 6.79558Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M76.2873 4.79525C74.5974 4.79525 73.2749 5.50965 72.3197 6.91465V5.08101H69.9686V17.3687H72.4177V10.439C72.4177 9.29599 72.7361 8.41489 73.3973 7.79574C74.0341 7.20041 74.7688 6.89083 75.6015 6.89083C77.3648 6.89083 78.271 7.74812 78.271 9.46268V17.3687H80.7201V9.1293C80.7201 7.74812 80.3282 6.70033 79.5445 5.9383C78.7608 5.17627 77.6587 4.79525 76.2873 4.79525Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M88.4105 17.6545C89.9289 17.6545 91.2024 17.2497 92.231 16.3924C93.2597 15.5589 93.8719 14.4159 94.0679 12.9871H91.5698C91.4229 13.8444 91.0555 14.4873 90.4922 14.916C89.9044 15.3684 89.2187 15.5827 88.4105 15.5827C87.3329 15.5827 86.5002 15.2255 85.8634 14.4635C85.2267 13.7015 84.9328 12.6299 84.9328 11.2249C84.9328 9.8437 85.2267 8.77209 85.8634 8.01007C86.5002 7.24803 87.3329 6.86702 88.4105 6.86702C89.1942 6.86702 89.8799 7.08134 90.4432 7.50998C91.0065 7.93862 91.3739 8.55777 91.5453 9.36743H94.0434C93.8474 7.98625 93.2352 6.86702 92.231 6.03355C91.2269 5.22389 89.9534 4.79525 88.4105 4.79525C86.5982 4.79525 85.1287 5.39059 84.0511 6.53363C82.9246 7.7243 82.3858 9.27218 82.3858 11.2249C82.3858 13.1776 82.9246 14.7255 84.0511 15.8923C85.1532 17.083 86.5982 17.6545 88.4105 17.6545Z",
          fill: color
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M106.913 11.3201C106.913 9.41506 106.374 7.86718 105.321 6.62889C104.243 5.4144 102.774 4.79525 100.913 4.79525C99.1247 4.79525 97.6798 5.39059 96.5777 6.55744C95.4511 7.74812 94.9123 9.29599 94.9123 11.2249C94.9123 13.1776 95.4511 14.7255 96.5777 15.8923C97.6798 17.083 99.1492 17.6545 100.962 17.6545C102.406 17.6545 103.631 17.2973 104.611 16.5829C105.59 15.8685 106.276 14.8683 106.619 13.6062H104.145C103.656 14.9874 102.602 15.6542 101.011 15.6542C99.9329 15.6542 99.1002 15.3684 98.488 14.7493C97.8512 14.1301 97.4839 13.2252 97.3859 12.0107H106.913V11.3201ZM100.913 6.79558C101.892 6.79558 102.676 7.10515 103.288 7.7243C103.9 8.34345 104.268 9.1293 104.39 10.1295H97.4104C97.5573 9.05786 97.9247 8.22439 98.537 7.65286C99.1492 7.08134 99.9329 6.79558 100.913 6.79558Z",
          fill: color
        }
      )
    ]
  }
);

// src/components/icons/XeroIcon.tsx
import { jsx as jsx69, jsxs as jsxs45 } from "react/jsx-runtime";
var XeroIcon = ({ width = 48, height = 48 }) => /* @__PURE__ */ jsxs45(
  "svg",
  {
    width,
    height,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M24 48C37.2549 48 48 37.2549 48 24C48 10.7452 37.2549 -3.8147e-06 24 -3.8147e-06C10.7452 -3.8147e-06 0 10.7452 0 24C0 37.2549 10.7452 48 24 48Z",
          fill: "#1AB4D7"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M11.4869 23.9302L15.5827 19.8243C15.7184 19.6859 15.7942 19.5027 15.7942 19.3083C15.7942 18.9033 15.465 18.5745 15.06 18.5745C14.8627 18.5745 14.6778 18.6518 14.5383 18.793C14.5379 18.7938 10.4462 22.8831 10.4462 22.8831L6.3358 18.7868C6.19691 18.6499 6.01287 18.5745 5.81772 18.5745C5.41322 18.5745 5.08392 18.9032 5.08392 19.3078C5.08392 19.5051 5.16272 19.6903 5.30414 19.8298L9.39936 23.9245L5.30626 28.0253C5.16239 28.1669 5.08392 28.3525 5.08392 28.5508C5.08392 28.9557 5.41322 29.2842 5.81772 29.2842C6.0132 29.2842 6.1974 29.2083 6.3358 29.0698L10.4398 24.9714L14.5283 29.0549C14.6733 29.2048 14.8604 29.2847 15.06 29.2847C15.4646 29.2847 15.7938 28.9557 15.7938 28.5508C15.7938 28.3554 15.7181 28.1718 15.5804 28.0334L11.4869 23.9302Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M35.8339 23.9297C35.8339 24.665 36.4319 25.2632 37.1681 25.2632C37.9026 25.2632 38.5008 24.665 38.5008 23.9297C38.5008 23.1941 37.9026 22.596 37.1681 22.596C36.4319 22.596 35.8339 23.1941 35.8339 23.9297Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M33.3085 23.9287C33.3085 21.7992 35.0408 20.0666 37.1705 20.0666C39.2992 20.0666 41.0319 21.7992 41.0319 23.9287C41.0319 26.0576 39.2992 27.7896 37.1705 27.7896C35.0408 27.7896 33.3085 26.0576 33.3085 23.9287ZM31.7893 23.9287C31.7893 26.8954 34.2033 29.3091 37.1705 29.3091C40.1377 29.3091 42.5529 26.8954 42.5529 23.9287C42.5529 20.9615 40.1377 18.5472 37.1705 18.5472C34.2033 18.5472 31.7893 20.9615 31.7893 23.9287Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M31.4048 18.6415L31.179 18.6408C30.5012 18.6408 29.8475 18.8546 29.3015 19.2753C29.2296 18.9461 28.9353 18.6984 28.5848 18.6984C28.1814 18.6984 27.8581 19.0216 27.8571 19.4259C27.8571 19.4274 27.8596 28.4892 27.8596 28.4892C27.8606 28.8927 28.1898 29.2207 28.5934 29.2207C28.9971 29.2207 29.3261 28.8927 29.3272 28.4883C29.3272 28.4865 29.3275 22.9155 29.3275 22.9155C29.3275 21.0581 29.4973 20.308 31.0884 20.1091C31.2354 20.0909 31.3954 20.0937 31.3961 20.0937C31.8314 20.0788 32.1408 19.7796 32.1408 19.3753C32.1408 18.9707 31.8105 18.6415 31.4048 18.6415Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M17.3157 23.0446C17.3157 23.0245 17.3172 23.0036 17.3182 22.983C17.7442 21.2988 19.2696 20.0524 21.0862 20.0524C22.9247 20.0524 24.464 21.3294 24.8682 23.0446H17.3157ZM26.371 22.9061C26.0548 21.4088 25.2351 20.179 23.9869 19.3892C22.1623 18.2308 19.7529 18.2949 17.9906 19.5482C16.553 20.5708 15.7232 22.2438 15.7232 23.9671C15.7232 24.3992 15.7753 24.8354 15.8837 25.2647C16.4266 27.4001 18.2622 29.0165 20.4508 29.2844C21.1003 29.3631 21.7324 29.3254 22.387 29.1559C22.9495 29.0191 23.4938 28.7909 23.9953 28.4696C24.5158 28.1349 24.9507 27.6937 25.3719 27.1655C25.3804 27.1558 25.3889 27.1472 25.3974 27.137C25.6897 26.7745 25.6355 26.2589 25.3143 26.0128C25.0434 25.8052 24.5886 25.721 24.2307 26.1793C24.1538 26.2889 24.068 26.4016 23.9735 26.5144C23.689 26.8288 23.3359 27.1335 22.9129 27.3698C22.3745 27.6574 21.7611 27.8217 21.1089 27.8254C18.9744 27.8016 17.8322 26.3117 17.4259 25.2482C17.3549 25.0496 17.3002 24.8436 17.2621 24.6314C17.2571 24.5918 17.2534 24.5535 17.252 24.5182C17.6933 24.5182 24.9118 24.517 24.9118 24.517C25.962 24.4949 26.5272 23.7537 26.371 22.9061Z",
          fill: "white"
        }
      )
    ]
  }
);

// src/components/BulkActions/InvoiceBulkActionBar.tsx
import {
  CalculatorIcon,
  EnvelopeIcon,
  LockClosedIcon
} from "@heroicons/react/16/solid";
import { IndigoIndigo60, RedRed60 } from "@sequencehq/design-tokens";
import { inflect } from "inflection";
import { Fragment as Fragment7, jsx as jsx70, jsxs as jsxs46 } from "react/jsx-runtime";
var MarkAsDropdown = ({
  count,
  showCount,
  onSelect
}) => {
  return /* @__PURE__ */ jsx70(
    BulkActionBar.Dropdown,
    {
      count,
      showCount,
      "data-testid": "invoices.bulkActions.markAs",
      menuItems: /* @__PURE__ */ jsxs46(Fragment7, { children: [
        /* @__PURE__ */ jsx70(
          BulkActionBar.Dropdown.Item,
          {
            icon: /* @__PURE__ */ jsx70(PaidIcon, {}),
            onClick: () => onSelect("PAID"),
            children: "Paid"
          }
        ),
        /* @__PURE__ */ jsx70(
          BulkActionBar.Dropdown.Item,
          {
            icon: /* @__PURE__ */ jsx70(PartiallyPaidIcon, {}),
            onClick: () => onSelect("PARTIALLY_PAID"),
            children: "Partially paid"
          }
        ),
        /* @__PURE__ */ jsx70(
          BulkActionBar.Dropdown.Item,
          {
            icon: /* @__PURE__ */ jsx70(UncollectibleIcon, {}),
            onClick: () => onSelect("UNCOLLECTIBLE"),
            children: "Uncollectable"
          }
        ),
        /* @__PURE__ */ jsx70(
          BulkActionBar.Dropdown.Item,
          {
            icon: /* @__PURE__ */ jsx70(UnpaidIcon, {}),
            onClick: () => onSelect("UNPAID"),
            children: "Unpaid"
          }
        )
      ] }),
      children: "Mark as..."
    }
  );
};
var RecalculateButton = (props) => {
  return /* @__PURE__ */ jsx70(
    BulkActionBar.Button,
    {
      ...props,
      "data-testid": "invoices.bulkActions.recalculate",
      icon: /* @__PURE__ */ jsx70(CalculatorIcon, { width: 16 }),
      children: "Recalculate"
    }
  );
};
var FinalizeButton = (props) => {
  return /* @__PURE__ */ jsx70(
    BulkActionBar.Button,
    {
      ...props,
      "data-testid": "invoices.bulkActions.finalize",
      icon: /* @__PURE__ */ jsx70(LockClosedIcon, { width: 16 }),
      children: "Finalize"
    }
  );
};
var SendButton = (props) => {
  return /* @__PURE__ */ jsx70(
    BulkActionBar.Button,
    {
      ...props,
      "data-testid": "invoices.bulkActions.send",
      icon: /* @__PURE__ */ jsx70(EnvelopeIcon, { width: 16 }),
      children: "Send"
    }
  );
};
var Void = ({ count, showCount, onClick }) => {
  const label = `Void ${showCount && count ? `${count} ` : ""}${inflect("invoice", count ?? 0)}`;
  return /* @__PURE__ */ jsx70(
    MenuItem,
    {
      label,
      uuid: "void",
      style: { color: RedRed60 },
      onClick,
      isDisabled: count === 0
    },
    "void"
  );
};
var Merge = ({ count, showCount, onClick }) => {
  const label = `Merge ${showCount && count ? `${count} ` : ""}${inflect("invoice", count ?? 0)}`;
  return /* @__PURE__ */ jsx70(
    MenuItem,
    {
      label,
      uuid: "merge",
      style: { color: IndigoIndigo60 },
      onClick,
      isDisabled: count === 0
    },
    "merge"
  );
};
var InvoiceBulkActionBar = Object.assign(BulkActionBar, {
  MarkAsDropdown,
  RecalculateButton,
  FinalizeButton,
  SendButton,
  KebabMenu: Object.assign(BulkActionBar.KebabMenu, { Merge, Void })
});

// src/components/BulkActions/InvoiceBulkActionDialogs.tsx
import { inflect as inflect2 } from "inflection";
import { jsxs as jsxs47 } from "react/jsx-runtime";
var RecalculateInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const title = `Recalculate ${count} ${inflect2("invoice", count)}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      "data-testid": "invoices.bulkActions.recalculate.dialog",
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Recalculate",
      children: [
        "You're about to recalculate",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflect2("invoice", count)
        ] }),
        "."
      ]
    }
  );
};
var SendInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Send ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      "data-testid": "invoices.bulkActions.send.dialog",
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Send",
      children: [
        "You're about to send",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] }),
        ". After sending you can no longer edit the ",
        inflected,
        "."
      ]
    }
  );
};
var FinalizeInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Finalize ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      "data-testid": "invoices.bulkActions.finalize.dialog",
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Finalize",
      children: [
        "You're about to finalize",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] }),
        ". This will lock the ",
        inflected,
        " and prevent editing."
      ]
    }
  );
};
var VoidInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Void ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      "data-testid": "invoices.bulkActions.void.dialog",
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Void",
      destructive: true,
      children: [
        "You're about to void",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] }),
        ". You won't be able to make any further changes to the ",
        inflected,
        " after voiding."
      ]
    }
  );
};
var SendPaymentReminderForInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Send payment reminder for ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Send reminder",
      children: [
        "You're about to send payment reminders for",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] })
      ]
    }
  );
};
var MergeInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Merge ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Merge",
      children: [
        "You're about to merge",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] }),
        ". This action can't be undone. The original invoices will be voided."
      ]
    }
  );
};
var DeleteInvoicesDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Delete ${count} ${inflected}`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Delete",
      destructive: true,
      children: [
        "You're about to delete",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " ",
          inflected
        ] }),
        ". This action can't be undone."
      ]
    }
  );
};
var ConvertInvoicesToDraftDialog = ({
  trigger,
  count,
  onConfirm
}) => {
  const inflected = inflect2("invoice", count);
  const title = `Convert ${count} upcoming ${inflected} to draft`;
  return /* @__PURE__ */ jsxs47(
    SimpleDialog,
    {
      trigger,
      onConfirm,
      title,
      confirmButtonText: "Convert",
      children: [
        "You're about to convert",
        " ",
        /* @__PURE__ */ jsxs47("strong", { children: [
          count,
          " upcoming ",
          inflected
        ] }),
        "to draft. This action can't be undone."
      ]
    }
  );
};

// src/components/Control/core/Control.tsx
import { useEffect as useEffect3 } from "react";
import {
  Menu,
  MenuButton,
  MenuItem as MenuItem3,
  MenuList,
  Divider as ChakraDivider,
  Box as Box7,
  useDisclosure
} from "@chakra-ui/react";
import { PlusIcon as PlusIcon2 } from "@heroicons/react/16/solid";

// src/components/Control/core/control.context.tsx
import { createContext, useContext, useState as useState6 } from "react";
import { jsx as jsx71 } from "react/jsx-runtime";
var defaults = {
  isLazy: true,
  placement: "bottom-start",
  menuHeight: 300,
  menuWidth: 300,
  maxWidth: void 0,
  matchWidth: false,
  showSearchInPopover: true,
  searchTerm: "",
  isOpen: false,
  setSearchTerm: () => {
    console.warn(
      "ControlContextProvider not found, setSearchTerm not implemented"
    );
  },
  setIsOpen: () => {
    console.warn("ControlContextProvider not found, setIsOpen not implemented");
  }
};
var ControlContext = createContext(defaults);
var useControlContext = () => {
  return useContext(ControlContext);
};
var ControlContextProvider = ({
  children,
  ...contextProps
}) => {
  const [searchTerm, setSearchTerm] = useState6("");
  const [isOpen, setIsOpen] = useState6(contextProps.isOpen ?? false);
  return /* @__PURE__ */ jsx71(
    ControlContext.Provider,
    {
      value: {
        ...defaults,
        ...contextProps,
        searchTerm,
        setSearchTerm,
        isOpen,
        setIsOpen
      },
      children
    }
  );
};

// src/components/Control/core/Control.tsx
import { Fragment as Fragment8, jsx as jsx72, jsxs as jsxs48 } from "react/jsx-runtime";
var Control = ({
  trigger,
  menuItems,
  closeOnSelect = true,
  onCreate,
  onToggle,
  createLabel = "New",
  disabled = false,
  style,
  "data-testid": dataTestId
}) => {
  const menuDisclosure = useDisclosure();
  const context = useControlContext();
  useEffect3(() => {
    if (onToggle) {
      onToggle(menuDisclosure.isOpen);
    }
  }, [menuDisclosure.isOpen, onToggle]);
  return /* @__PURE__ */ jsxs48(
    Menu,
    {
      computePositionOnMount: true,
      variant: "component-library",
      closeOnSelect,
      isOpen: menuDisclosure.isOpen,
      onOpen: menuDisclosure.onOpen,
      onClose: menuDisclosure.onClose,
      isLazy: context.isLazy,
      placement: context.placement,
      strategy: "fixed",
      matchWidth: true,
      children: [
        /* @__PURE__ */ jsx72(
          MenuButton,
          {
            type: "button",
            style,
            sx: { "& > span": { pointerEvents: "all" } },
            disabled,
            cursor: disabled ? "default" : "pointer",
            children: trigger
          }
        ),
        /* @__PURE__ */ jsxs48(
          MenuList,
          {
            maxWidth: `${context.menuWidth}px`,
            minWidth: `${context.menuWidth}px`,
            children: [
              /* @__PURE__ */ jsx72(
                Box7,
                {
                  maxHeight: `${context.menuHeight}px`,
                  width: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  style: { contentVisibility: "auto" },
                  "data-testid": dataTestId,
                  children: menuItems
                }
              ),
              onCreate && /* @__PURE__ */ jsxs48(Fragment8, { children: [
                /* @__PURE__ */ jsx72(Divider3, {}),
                /* @__PURE__ */ jsx72(CreateMenuItem, { onClick: onCreate, children: createLabel })
              ] })
            ]
          }
        )
      ]
    }
  );
};
var CreateMenuItem = ({ onClick, children }) => {
  return /* @__PURE__ */ jsxs48(MenuItem3, { as: "div", onClick, fontWeight: "bold", children: [
    /* @__PURE__ */ jsx72(PlusIcon2, { height: 16, width: 16 }),
    children
  ] });
};
var Divider3 = (props) => {
  return /* @__PURE__ */ jsx72(
    ChakraDivider,
    {
      variant: "component-library",
      position: "relative",
      left: "-4px",
      width: "calc(100% + 8px)",
      my: 1,
      ...props
    }
  );
};

// src/components/Control/core/DateControl.tsx
import { useState as useState8 } from "react";

// src/forms/inputs/dates/DateDurationInput/DateDurationPopover.tsx
import { Flex as Flex8 } from "@chakra-ui/react";

// src/forms/inputs/dates/DateDurationInput/useDateDurationPopover.ts
import { useEffect as useEffect4, useState as useState7 } from "react";
import {
  useDayzed
} from "dayzed";

// ../validation/dist/index.js
import { pluralize, singularize } from "inflection";
var isValidDate = (date) => {
  if (!(date instanceof Date)) {
    return false;
  }
  try {
    if (date.toString() === "Invalid Date") {
      return false;
    }
  } catch (_e) {
    return false;
  }
  return true;
};

// src/forms/inputs/dates/utils/dayzed.adapters.ts
import { format, parse, tz } from "@sequencehq/utils/dates";
var dayzedAdapter = {
  toDayzed: (date) => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return parse(format(date, "yyyy-MM-dd"), "yyyy-MM-dd", /* @__PURE__ */ new Date(), {
      in: tz(localTimezone)
    });
  },
  fromDayzed: (date) => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return parse(
      format(date, "yyyy-MM-dd", {
        in: tz(localTimezone)
      }),
      "yyyy-MM-dd",
      /* @__PURE__ */ new Date(),
      {
        in: tz("UTC")
      }
    );
  }
};

// src/forms/inputs/dates/DateDurationInput/useDateDurationPopover.ts
var useDateDurationPopover = (props) => {
  const [offset, setOffset] = useState7(0);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState7(
    isValidDate(props.selectedDate) ? props.selectedDate : /* @__PURE__ */ new Date()
  );
  useEffect4(() => {
    if (!isValidDate(props.selectedDate)) {
      return;
    }
    setSelectedCalendarDate(props.selectedDate);
    setOffset(0);
  }, [props.selectedDate]);
  const { calendars, getDateProps, getBackProps, getForwardProps } = useDayzed({
    selected: [dayzedAdapter.toDayzed(props.selectedDate)],
    onDateSelected: (newSelectedDate) => {
      props.onDateSelected(dayzedAdapter.fromDayzed(newSelectedDate.date));
    },
    date: dayzedAdapter.toDayzed(props.selectedDate),
    minDate: props.minDate && dayzedAdapter.toDayzed(props.minDate),
    maxDate: props.maxDate && dayzedAdapter.toDayzed(props.maxDate),
    monthsToDisplay: 1,
    offset,
    onOffsetChanged: (newOffset) => setOffset(newOffset),
    firstDayOfWeek: 1
  });
  return {
    calendarView: {
      calendar: calendars[0],
      getDateProps,
      getBackProps,
      getForwardProps,
      selectedDate: selectedCalendarDate
    }
  };
};

// src/forms/inputs/dates/CalendarView.tsx
import { monthNamesShort, weekdayNamesShort } from "@sequencehq/core-models";
import { Box as Box9, Flex as Flex7, SimpleGrid } from "@chakra-ui/react";
import { format as format2, add } from "@sequencehq/utils/dates";
import { match as match3 } from "ts-pattern";

// src/forms/inputs/dates/CalendarHeader.tsx
import { Button as Button4, Flex as Flex6, Spacer, Tooltip as Tooltip2, Box as Box8 } from "@chakra-ui/react";
import { ChevronRightIcon as ChevronRightIcon3, ChevronLeftIcon as ChevronLeftIcon2 } from "@chakra-ui/icons";
import { jsx as jsx73, jsxs as jsxs49 } from "react/jsx-runtime";
var DateSkipperButton = ({
  calendar,
  directionProps,
  offset,
  children,
  type
}) => {
  const tooltipLabel = offset === 1 && type === "back" ? "Previous month" : offset === 1 && type === "forward" ? "Next month" : offset === 12 && type === "back" ? "Previous year" : offset === 12 && type === "forward" ? "Next year" : "";
  return /* @__PURE__ */ jsx73(
    Tooltip2,
    {
      label: tooltipLabel,
      placement: "top",
      isDisabled: !tooltipLabel ? true : false,
      hasArrow: true,
      bg: "#171923",
      color: "white",
      px: "8px",
      py: "4px",
      fontSize: "14px",
      fontWeight: "400",
      borderRadius: "4px",
      children: /* @__PURE__ */ jsx73(
        Button4,
        {
          ...directionProps({
            calendars: [calendar],
            offset
          }),
          tabIndex: -1,
          variant: "dateSkipper",
          width: "24px",
          height: "24px",
          children
        }
      )
    }
  );
};
var CalendarHeader = ({
  getBackProps,
  getForwardProps,
  calendar,
  monthLabel,
  hideYearSkippers
}) => {
  return /* @__PURE__ */ jsxs49(Flex6, { w: "100%", onClick: (e) => e.stopPropagation(), children: [
    !hideYearSkippers && /* @__PURE__ */ jsxs49(
      DateSkipperButton,
      {
        calendar,
        directionProps: getBackProps,
        offset: 12,
        type: "back",
        children: [
          /* @__PURE__ */ jsx73(ChevronLeftIcon2, {}),
          /* @__PURE__ */ jsx73(ChevronLeftIcon2, {})
        ]
      }
    ),
    /* @__PURE__ */ jsx73(
      DateSkipperButton,
      {
        calendar,
        directionProps: getBackProps,
        offset: 1,
        type: "back",
        children: /* @__PURE__ */ jsx73(ChevronLeftIcon2, {})
      }
    ),
    /* @__PURE__ */ jsx73(Spacer, {}),
    /* @__PURE__ */ jsx73(Flex6, { justify: "center", align: "center", children: /* @__PURE__ */ jsx73(Box8, { fontWeight: 700, children: monthLabel }) }),
    /* @__PURE__ */ jsx73(Spacer, {}),
    /* @__PURE__ */ jsx73(
      DateSkipperButton,
      {
        calendar,
        directionProps: getForwardProps,
        offset: 1,
        type: "forward",
        children: /* @__PURE__ */ jsx73(ChevronRightIcon3, {})
      }
    ),
    !hideYearSkippers && /* @__PURE__ */ jsxs49(
      DateSkipperButton,
      {
        calendar,
        directionProps: getForwardProps,
        offset: 12,
        type: "forward",
        children: [
          /* @__PURE__ */ jsx73(ChevronRightIcon3, {}),
          /* @__PURE__ */ jsx73(ChevronRightIcon3, {})
        ]
      }
    )
  ] });
};

// src/forms/inputs/DayOfMonthInput/DateButton.tsx
import { Button as Button5 } from "@chakra-ui/react";
import { match as match2 } from "ts-pattern";
import {
  TurquoiseTurquoise80,
  IndigoIndigo50 as IndigoIndigo502,
  TurquoiseTurquoise70 as TurquoiseTurquoise702
} from "@sequencehq/design-tokens";
import { jsx as jsx74 } from "react/jsx-runtime";
var getKeyOffset = (number) => {
  const e = document.activeElement;
  const buttons = document.querySelectorAll("button.date-button");
  buttons.forEach((el, i) => {
    const newNodeKey = i + number;
    if (el == e) {
      if (newNodeKey <= buttons.length - 1 && newNodeKey >= 0) {
        buttons[newNodeKey].focus();
      } else {
        buttons[0].focus();
      }
    }
  });
};
var DateButton = ({
  selected,
  today,
  dateProps,
  inRange,
  selectableDate,
  label,
  position,
  initialRef,
  hasInitialFocus: hasInitialFocus2,
  focusedInput,
  "data-testid": testId
}) => {
  const styles = dateStyling({
    today,
    selected,
    selectable: selectableDate,
    inRange,
    position,
    focusedInput
  });
  return /* @__PURE__ */ jsx74(
    Button5,
    {
      ...dateProps,
      ...styles,
      "data-testid": testId,
      className: "date-button",
      ref: hasInitialFocus2 ? initialRef : null,
      tabIndex: -1,
      isDisabled: !selectableDate,
      _focus: {
        zIndex: 1,
        boxShadow: `0 0 0 2px ${IndigoIndigo502}`
      },
      onKeyDown: (event) => {
        match2(event.key).with("ArrowUp", () => {
          event.preventDefault();
          getKeyOffset(-7);
        }).with("ArrowRight", () => {
          event.preventDefault();
          return getKeyOffset(1);
        }).with("ArrowLeft", () => {
          event.preventDefault();
          return getKeyOffset(-1);
        }).with("ArrowDown", () => {
          event.preventDefault();
          return getKeyOffset(7);
        }).otherwise(() => {
        });
      },
      children: label
    }
  );
};
var dateStyling = ({
  today,
  selected,
  selectable,
  inRange,
  position,
  focusedInput
}) => {
  const styles = {};
  styles.border = "1px solid";
  styles.bg = "white";
  styles.borderColor = "white";
  styles.fontWeight = "400";
  styles.fontSize = "14px";
  styles.lineHeight = "16px";
  styles.width = "40px";
  styles.height = "40px";
  styles.borderRadius = "0";
  styles._hover = {};
  if (today) {
    styles.borderRadius = "8px";
  }
  if (selectable) {
    if (selected) {
      styles.bg = TurquoiseTurquoise80;
      styles.borderColor = TurquoiseTurquoise80;
      styles.color = "white";
      if (position === "start") {
        styles.borderRadius = "8px 0 0 8px";
        if (focusedInput === "start") {
          styles.bg = TurquoiseTurquoise702;
        }
      } else if (position === "end") {
        styles.borderRadius = "0 8px 8px 0";
        if (focusedInput === "end") {
          styles.bg = TurquoiseTurquoise702;
        }
      } else {
        styles.borderRadius = "8px";
      }
    } else if (inRange) {
      styles.bg = "gray.20";
      styles.borderColor = "gray.20";
    } else {
      styles.color = "gray.100";
      styles._hover = {
        bg: "gray.20",
        borderColor: "gray.20"
      };
    }
  }
  if (today) {
    styles.borderColor = TurquoiseTurquoise80;
  }
  return styles;
};

// src/forms/inputs/dates/CalendarView.tsx
import { jsx as jsx75, jsxs as jsxs50 } from "react/jsx-runtime";
var CalendarView = ({
  calendar,
  getDateProps,
  getBackProps,
  getForwardProps,
  initialRef,
  ...rest
}) => /* @__PURE__ */ jsxs50(Flex7, { flexDirection: "column", width: "max-content", children: [
  /* @__PURE__ */ jsx75(
    CalendarHeader,
    {
      getForwardProps,
      getBackProps,
      calendar,
      monthLabel: `${monthNamesShort[calendar.month]} ${calendar.year}`
    }
  ),
  /* @__PURE__ */ jsxs50(SimpleGrid, { columns: 7, spacingY: "1px", textAlign: "center", children: [
    weekdayNamesShort.map((weekday) => /* @__PURE__ */ jsx75(
      Box9,
      {
        color: "gray.60",
        fontSize: "12px",
        lineHeight: "16px",
        py: 4,
        children: weekday
      },
      weekday
    )),
    calendar.weeks.map(
      (week) => week.map((dateObj, index) => {
        if (typeof dateObj === "string") {
          return /* @__PURE__ */ jsx75(Box9, { outline: "none" }, index);
        }
        const { date, selectable, selected, today } = dateObj;
        return /* @__PURE__ */ jsx75(
          DateButton,
          {
            "data-testid": `datePicker.button.${dateObj.date.getDate()}`,
            initialRef,
            selected,
            selectableDate: selectable,
            today,
            label: dateObj.date.getDate(),
            dateProps: {
              ...getDateProps({
                dateObj
              })
            },
            ...getRangeProps({
              ...rest,
              date,
              selected,
              today
            })
          },
          format2(date, "yyyy-MM-dd")
        );
      })
    )
  ] })
] });
var getRangeProps = ({ selected, date, today, ...rest }) => {
  if (rest.type === "single") {
    return;
  }
  const { selectedDates, focusedInput } = rest;
  return {
    position: getPosition({ selected, date, selectedDates }),
    hasInitialFocus: hasInitialFocus({
      today,
      date,
      selectedDates,
      focusedInput
    }),
    inRange: Boolean(isInRange({ date, selectedDates })),
    focusedInput
  };
};
var getPosition = ({
  selected,
  date,
  selectedDates
}) => {
  if (!selected) {
    return void 0;
  }
  if (isValidDate(selectedDates[0]) && isValidDate(selectedDates[1]) && date.getTime() === selectedDates[0].getTime()) {
    return "start";
  }
  if (isValidDate(selectedDates[0]) && isValidDate(selectedDates[1]) && date.getTime() === selectedDates[1].getTime()) {
    return "end";
  }
};
var isInRange = ({ date, selectedDates }) => {
  if (!isValidDate(selectedDates[0]) || !isValidDate(selectedDates[1])) {
    return false;
  }
  const [startDate, endDate] = selectedDates;
  const normalizedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
  const normalizedFirst = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  ).getTime();
  const normalizedSecond = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  ).getTime();
  return normalizedFirst && normalizedSecond && normalizedFirst <= normalizedDate && normalizedSecond >= normalizedDate;
};
var hasInitialFocus = ({
  today,
  date,
  selectedDates,
  focusedInput
}) => {
  return match3(focusedInput).with("start", () => {
    if (selectedDates[0] && isValidDate(selectedDates[0])) {
      return date.getTime() === selectedDates[0].getTime();
    } else if (selectedDates[1] && isValidDate(selectedDates[1])) {
      return date.getTime() === add(selectedDates[1], { days: -1 }).getTime();
    } else {
      return today;
    }
  }).with("end", () => {
    if (selectedDates[1] && isValidDate(selectedDates[1])) {
      return date.getTime() === selectedDates[1].getTime();
    } else if (selectedDates[0] && isValidDate(selectedDates[0])) {
      return date.getTime() === add(selectedDates[0], { days: 0 }).getTime();
    } else {
      return date.getTime() === (/* @__PURE__ */ new Date()).getTime();
    }
  }).otherwise(() => false);
};

// src/forms/inputs/dates/DateDurationInput/DateDurationPopover.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var DateDurationPopover = ({
  selectedDate,
  minDate,
  maxDate,
  onDateSelected,
  initialRef,
  "data-testid": dataTestId
}) => {
  const { calendarView } = useDateDurationPopover({
    selectedDate,
    minDate,
    maxDate,
    onDateSelected
  });
  return /* @__PURE__ */ jsx76(Flex8, { children: /* @__PURE__ */ jsx76(Flex8, { height: "320px", "data-testid": dataTestId, children: /* @__PURE__ */ jsx76(CalendarView, { ...calendarView, initialRef, type: "single" }) }) });
};

// src/components/Control/core/DateControl.tsx
import { add as add2 } from "@sequencehq/utils/dates";
import { MenuItem as MenuItem4 } from "@chakra-ui/react";
import { jsx as jsx77 } from "react/jsx-runtime";
var defaultMinDate = add2(/* @__PURE__ */ new Date(), { months: -12 });
var defaultMaxDate = add2(/* @__PURE__ */ new Date(), { months: 12 });
var DateControl = ({
  trigger,
  minDate = defaultMinDate,
  maxDate = defaultMaxDate,
  initialValue = /* @__PURE__ */ new Date(),
  disabled = false,
  onChange,
  onToggle
}) => {
  const [selectedDate, setSelectedDate] = useState8(initialValue);
  return /* @__PURE__ */ jsx77(
    Control,
    {
      disabled,
      trigger,
      onToggle,
      menuItems: /* @__PURE__ */ jsx77(
        MenuItem4,
        {
          background: "none",
          _hover: { background: "none" },
          _focus: { background: "none" },
          _active: { background: "none" },
          children: /* @__PURE__ */ jsx77(
            DateDurationPopover,
            {
              selectedDate,
              minDate,
              maxDate,
              onDateSelected: (date) => {
                setSelectedDate(date);
                onChange(date);
              }
            }
          )
        }
      )
    }
  );
};

// src/components/Control/core/MultiSelectControlCore.tsx
import { useEffect as useEffect7, useMemo as useMemo3, useRef as useRef6, useState as useState10 } from "react";
import partition from "lodash/fp/partition";
import { Divider as Divider6 } from "@chakra-ui/react";

// src/components/Control/core/SearchableListPopover.tsx
import { useCallback as useCallback3, useEffect as useEffect5, useRef as useRef5 } from "react";
import {
  Flex as Flex10,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Checkbox,
  Divider as Divider5,
  Text as Text6,
  useOutsideClick,
  Portal as Portal2
} from "@chakra-ui/react";
import {
  GreyGrey20 as GreyGrey204,
  GreyGrey60 as GreyGrey607,
  Lato13Regular as Lato13Regular4
} from "@sequencehq/design-tokens";
import { CheckIcon, PlusIcon as PlusIcon3 } from "@heroicons/react/16/solid";

// src/components/Control/core/SimpleSearchMenuInput.tsx
import {
  Input,
  Flex as Flex9,
  Divider as Divider4,
  forwardRef as forwardRef2,
  FocusLock
} from "@chakra-ui/react";
import MagnifyingGlassIcon from "@heroicons/react/16/solid/MagnifyingGlassIcon";
import {
  GreyGrey30 as GreyGrey305,
  GreyGrey60 as GreyGrey606,
  GreyGrey80 as GreyGrey803,
  GreyWhite as GreyWhite4,
  Lato13Regular as Lato13Regular3
} from "@sequencehq/design-tokens";
import { jsx as jsx78, jsxs as jsxs51 } from "react/jsx-runtime";
var SimpleSearchMenuInput = forwardRef2(
  ({
    value,
    onChange,
    placeholder,
    showDivider = false,
    marginBottom = void 0
  }, ref) => {
    return /* @__PURE__ */ jsxs51(
      Flex9,
      {
        paddingTop: "4px",
        paddingBottom: showDivider ? "4px" : "0px",
        position: "sticky",
        top: "0",
        left: "0",
        background: GreyWhite4,
        zIndex: "1",
        marginBottom,
        children: [
          /* @__PURE__ */ jsx78(FocusLock, { children: /* @__PURE__ */ jsxs51(
            Flex9,
            {
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "100%",
              px: "8px",
              py: "6px",
              children: [
                /* @__PURE__ */ jsx78(
                  MagnifyingGlassIcon,
                  {
                    width: "16px",
                    height: "16px",
                    color: GreyGrey803
                  }
                ),
                /* @__PURE__ */ jsx78(
                  Input,
                  {
                    ref,
                    autoComplete: "off",
                    variant: "unstyled",
                    placeholder,
                    flex: "1",
                    ...Lato13Regular3,
                    color: GreyGrey803,
                    _placeholder: { color: GreyGrey606 },
                    spellCheck: false,
                    value,
                    onChange: (e) => onChange(e.target.value),
                    autoFocus: true,
                    "data-1p-ignore": true,
                    borderRadius: 0
                  }
                )
              ]
            }
          ) }),
          showDivider && /* @__PURE__ */ jsx78(
            Divider4,
            {
              position: "absolute",
              bottom: "0",
              left: "-4px",
              width: "calc(100% + 8px)",
              borderColor: GreyGrey305
            }
          )
        ]
      }
    );
  }
);

// src/components/Control/core/SearchableListPopover.tsx
import { Fragment as Fragment9, jsx as jsx79, jsxs as jsxs52 } from "react/jsx-runtime";
var MenuItem5 = ({
  option,
  isSelected,
  isFocused,
  isDisabled = false,
  onSelect,
  variant = "default",
  scrollIntoView = true,
  indented = false,
  "data-testid": dataTestId
}) => {
  const ref = useRef5(null);
  useEffect5(() => {
    if (scrollIntoView && (isFocused || isSelected)) {
      ref.current?.scrollIntoView({ block: "center" });
    }
  }, [scrollIntoView, isFocused, isSelected]);
  const showHoverState = !isDisabled && (variant === "checkbox" ? isFocused : isSelected || isFocused);
  return /* @__PURE__ */ jsxs52(Fragment9, { children: [
    /* @__PURE__ */ jsxs52(
      Flex10,
      {
        ref,
        "data-testid": dataTestId,
        onClick: isDisabled ? () => {
        } : onSelect,
        background: showHoverState ? GreyGrey204 : void 0,
        borderRadius: 6,
        _hover: { background: isDisabled ? "none" : GreyGrey204 },
        padding: 2,
        cursor: "pointer",
        alignItems: "center",
        maxWidth: "100%",
        role: "group",
        "aria-disabled": isDisabled ? "true" : "false",
        marginX: 1,
        _disabled: {
          background: "none",
          cursor: "not-allowed",
          opacity: "0.4"
        },
        children: [
          /* @__PURE__ */ jsxs52(
            Flex10,
            {
              ml: indented ? 2 : 0,
              grow: 1,
              shrink: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              gap: 2,
              alignItems: "flex-start",
              children: [
                variant === "checkbox" && /* @__PURE__ */ jsx79(
                  Checkbox,
                  {
                    isChecked: isSelected,
                    opacity: isSelected || isFocused ? 1 : 0,
                    _groupHover: { opacity: 1 },
                    pointerEvents: "none"
                  }
                ),
                /* @__PURE__ */ jsxs52(Flex10, { width: "100%", textAlign: "left", flexWrap: "wrap", columnGap: "6px", children: [
                  /* @__PURE__ */ jsx79(
                    Text6,
                    {
                      as: "span",
                      ...Lato13Regular4,
                      ...option.secondaryLabel ? {} : {
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      },
                      children: option.label
                    }
                  ),
                  /* @__PURE__ */ jsx79(Text6, { as: "span", ...Lato13Regular4, color: GreyGrey607, children: option.secondaryLabel })
                ] })
              ]
            }
          ),
          variant === "default" && isSelected && /* @__PURE__ */ jsx79(Flex10, { shrink: 0, children: /* @__PURE__ */ jsx79(CheckIcon, { width: 16, height: 16 }) })
        ]
      }
    ),
    option.dividerAfter && /* @__PURE__ */ jsx79(Divider5, { marginTop: 0, variant: "component-library" })
  ] });
};
var CreateMenuItem2 = ({ onClick, children, color, ...props }) => {
  return /* @__PURE__ */ jsxs52(
    Flex10,
    {
      ...props,
      onClick,
      color,
      fontWeight: "bold",
      borderRadius: 6,
      _hover: { background: GreyGrey204 },
      padding: 2,
      cursor: "pointer",
      alignItems: "center",
      m: 1,
      children: [
        /* @__PURE__ */ jsx79(Flex10, { shrink: 0, marginRight: 2, children: /* @__PURE__ */ jsx79(PlusIcon3, { height: 16, width: 16 }) }),
        children
      ]
    }
  );
};
var SearchableListPopover = ({
  trigger,
  children,
  placeholder,
  initialFocusRef,
  searchTerm,
  setSearchTerm,
  disabled = false,
  onToggle,
  onCreate,
  createLabel,
  flip = true
}) => {
  const context = useControlContext();
  const ref = useRef5(null);
  const { setIsOpen, isOpen } = context;
  const handleOutsideClick = useCallback3(() => {
    if (isOpen) {
      setIsOpen(false);
      onToggle?.(false);
    }
  }, [isOpen, setIsOpen, onToggle]);
  useOutsideClick({
    ref,
    handler: handleOutsideClick
  });
  useEffect5(() => {
    if (!isOpen) {
      initialFocusRef.current?.blur();
    }
  }, [initialFocusRef, isOpen]);
  const showSearch = context.showSearchInPopover && !!setSearchTerm;
  return /* @__PURE__ */ jsxs52(
    Popover,
    {
      placement: context.placement,
      initialFocusRef,
      isOpen: disabled ? false : context.isOpen,
      isLazy: context.isLazy,
      onOpen: () => {
        if (disabled) {
          return;
        }
        onToggle?.(true);
      },
      onClose: () => {
        if (disabled) {
          return;
        }
        onToggle?.(false);
      },
      computePositionOnMount: true,
      flip,
      matchWidth: context.matchWidth,
      children: [
        /* @__PURE__ */ jsx79(PopoverTrigger, { children: /* @__PURE__ */ jsx79(
          Flex10,
          {
            onClick: () => setIsOpen(!isOpen),
            cursor: disabled ? "default" : "pointer",
            height: "fit-content",
            children: trigger
          }
        ) }),
        context.isOpen && /* @__PURE__ */ jsx79(Portal2, { children: (context.showSearchInPopover || children || onCreate) && /* @__PURE__ */ jsxs52(
          PopoverContent,
          {
            opacity: !children && !onCreate && !context.showSearchInPopover ? 0 : 1,
            minWidth: context.matchWidth ? void 0 : 250,
            width: "inherit",
            maxHeight: context.menuHeight,
            maxWidth: context.maxWidth,
            overflowX: "hidden",
            style: { contentVisibility: "auto" },
            borderRadius: 8,
            paddingTop: 0,
            fontSize: "13px",
            ref,
            children: [
              showSearch && /* @__PURE__ */ jsx79(
                SimpleSearchMenuInput,
                {
                  ref: initialFocusRef,
                  value: searchTerm,
                  onChange: (term) => setSearchTerm(term),
                  showDivider: !!children,
                  placeholder
                }
              ),
              children && /* @__PURE__ */ jsx79(Flex10, { overflowY: "auto", paddingY: 1, gap: 1, direction: "column", children }),
              onCreate && /* @__PURE__ */ jsxs52(Fragment9, { children: [
                /* @__PURE__ */ jsx79(Divider5, { marginTop: 0, variant: "component-library" }),
                /* @__PURE__ */ jsx79(CreateMenuItem2, { onClick: onCreate, children: createLabel })
              ] })
            ]
          }
        ) })
      ]
    }
  );
};

// src/components/Control/hooks/useKeyboardNav.tsx
import { useCallback as useCallback4, useEffect as useEffect6, useState as useState9 } from "react";
import { HStack, Kbd, Text as Text7 } from "@chakra-ui/react";
import {
  GreyGrey40 as GreyGrey404,
  GreyGrey60 as GreyGrey608,
  GreyGrey80 as GreyGrey804,
  Lato13Regular as Lato13Regular5
} from "@sequencehq/design-tokens";
import { jsx as jsx80, jsxs as jsxs53 } from "react/jsx-runtime";
var useKeyboardNav = (options, selectOption) => {
  const [focusedOption, setFocusedOption] = useState9(null);
  useEffect6(() => {
    if (focusedOption && !options.includes(focusedOption)) {
      setFocusedOption(null);
    }
  }, [options, focusedOption]);
  const handleKeyDown = useCallback4(
    (e) => {
      if (options.length) {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setFocusedOption((prev) => {
              if (!prev) {
                return options[0];
              }
              const idx = options.indexOf(prev);
              return options[(idx + 1) % options.length];
            });
            break;
          case "ArrowUp":
            e.preventDefault();
            setFocusedOption((prev) => {
              if (!prev) {
                return options[options.length - 1];
              }
              const idx = options.indexOf(prev);
              return options[(idx - 1 + options.length) % options.length];
            });
            break;
          case "Enter":
            e.stopPropagation();
            e.preventDefault();
            if (focusedOption) {
              selectOption(focusedOption);
            }
            break;
        }
      }
    },
    [options, focusedOption, setFocusedOption, selectOption]
  );
  return { handleKeyDown, setFocusedOption, focusedOption };
};

// src/components/Control/core/MultiSelectControlCore.tsx
import difference from "lodash/difference";
import { Fragment as Fragment10, jsx as jsx81, jsxs as jsxs54 } from "react/jsx-runtime";
var MultiSelectControlCore = ({
  trigger,
  options,
  placeholder,
  filterOptions = genericFilterOptions,
  MenuItemComponent = MenuItem5,
  initialValues,
  onChange,
  onToggle,
  onCreate,
  createLabel,
  disabled = false
}) => {
  const refInitialised = useRef6(false);
  const { searchTerm, setSearchTerm, isOpen } = useControlContext();
  const [selectedOptionValues, setSelectedOptionValues] = useState10(
    initialValues ?? []
  );
  const initialFocusRef = useRef6(null);
  const isSelected = (opt) => selectedOptionValues.includes(opt.value);
  const partitionOptions = partition((opt) => isSelected(opt));
  const deselectOption = (opt) => {
    setSelectedOptionValues(
      selectedOptionValues.filter((value) => value != opt.value)
    );
  };
  const selectOption = (opt) => {
    setSelectedOptionValues([...selectedOptionValues, opt.value]);
  };
  const [openIndex, setOpenIndex] = useState10(0);
  useEffect7(() => {
    if (isOpen) {
      setOpenIndex((prev) => prev + 1);
    }
  }, [isOpen]);
  const [selectedOptions, remainingOptions] = useMemo3(
    () => partitionOptions(options),
    // eslint-disable-next-line
    [openIndex]
  );
  const filteredOptions = useMemo3(
    () => filterOptions(remainingOptions, searchTerm),
    [remainingOptions, searchTerm, filterOptions]
  );
  useEffect7(() => {
    if (!refInitialised.current) {
      return;
    }
    refInitialised.current = false;
    setSelectedOptionValues((prev) => {
      const selectedOptionsWithoutRemovedOptions = prev.filter(
        (selectedOptionValue) => options.map((option) => option.value).includes(selectedOptionValue)
      );
      const selectedOptionsAddedAfterInitialRender = difference(
        initialValues || [],
        selectedOptionsWithoutRemovedOptions
      );
      return [
        ...selectedOptionsWithoutRemovedOptions,
        ...selectedOptionsAddedAfterInitialRender
      ];
    });
  }, [JSON.stringify(options), openIndex]);
  useEffect7(() => {
    if (!refInitialised.current) {
      refInitialised.current = true;
      return;
    }
    onChange(selectedOptionValues);
  }, [selectedOptionValues]);
  const { handleKeyDown, focusedOption } = useKeyboardNav(
    filteredOptions,
    selectOption
  );
  useEffect7(() => {
    const element = initialFocusRef?.current;
    element?.addEventListener("keydown", handleKeyDown, false);
    return () => {
      element?.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown, isOpen]);
  return /* @__PURE__ */ jsx81(
    SearchableListPopover,
    {
      trigger,
      placeholder,
      initialFocusRef,
      searchTerm,
      setSearchTerm,
      disabled,
      onToggle,
      onCreate,
      createLabel,
      children: !selectedOptions.length && !filteredOptions.length ? null : /* @__PURE__ */ jsxs54(Fragment10, { children: [
        selectedOptions.map((opt) => /* @__PURE__ */ jsx81(
          MenuItemComponent,
          {
            option: opt,
            isSelected: isSelected(opt),
            isFocused: false,
            onSelect: () => {
              isSelected(opt) ? deselectOption(opt) : selectOption(opt);
            },
            variant: "checkbox",
            scrollIntoView: false
          },
          opt.value
        )),
        !!selectedOptions.length && !!filteredOptions.length && /* @__PURE__ */ jsx81(Divider6, { variant: "component-library", my: 1 }),
        filteredOptions.map((opt) => /* @__PURE__ */ jsx81(
          MenuItemComponent,
          {
            option: opt,
            isSelected: isSelected(opt),
            variant: "checkbox",
            isFocused: focusedOption === opt,
            onSelect: () => isSelected(opt) ? deselectOption(opt) : selectOption(opt),
            scrollIntoView: !isSelected(opt),
            isDisabled: opt.disabled
          },
          opt.value
        ))
      ] })
    }
  );
};
function genericFilterOptions(options, searchTerm) {
  return options.filter(
    (option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
}

// src/components/Control/core/SelectControl.tsx
import { useEffect as useEffect8, useMemo as useMemo4, useRef as useRef7, useState as useState11 } from "react";
import flatten from "lodash/flatten";
import { GreyGrey90 as GreyGrey902, Lato13Bold as Lato13Bold4 } from "@sequencehq/design-tokens";
import { Box as Box10, Flex as Flex11 } from "@chakra-ui/react";
import { match as match4 } from "ts-pattern";
import { Fragment as Fragment11, jsx as jsx82, jsxs as jsxs55 } from "react/jsx-runtime";
var SelectControl = ({
  trigger,
  onChange,
  onCreate,
  onToggle,
  options,
  filterOptionGroups = genericFilterOptionGroups,
  MenuItemComponent = MenuItem5,
  initialValue = null,
  placeholder,
  createLabel,
  disabled = false,
  name,
  externalFocusRef,
  emptyState = null,
  noResultsState = null,
  flip = true,
  "data-testid": dataTestId
}) => {
  const ctx = useControlContext();
  const [selectedOptionValue, setSelectedOptionValue] = useState11(
    initialValue
  );
  const initialFocusRef = useRef7(null);
  const inputRef = !ctx.showSearchInPopover && externalFocusRef ? externalFocusRef : initialFocusRef;
  const selectOption = (option) => {
    setSelectedOptionValue(option.value);
    onChange(option.value);
    ctx.setIsOpen(false);
  };
  const optionGroups = useMemo4(() => toOptionGroupArray(options), [options]);
  const filteredOptionGroups = useMemo4(
    () => filterOptionGroups ? filterOptionGroups(optionGroups, ctx.searchTerm) : optionGroups,
    [optionGroups, filterOptionGroups, ctx.searchTerm]
  );
  const filteredOptions = useMemo4(
    () => flatten(filteredOptionGroups.map((group) => group.options)),
    [filteredOptionGroups]
  );
  const { handleKeyDown, setFocusedOption, focusedOption } = useKeyboardNav(
    filteredOptions,
    selectOption
  );
  useEffect8(() => {
    const element = inputRef?.current;
    element?.addEventListener("keydown", handleKeyDown, false);
    return () => {
      element?.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [inputRef, handleKeyDown, ctx.isOpen]);
  useEffect8(() => {
    setSelectedOptionValue(initialValue);
    setFocusedOption(
      filteredOptions.find((opt) => opt.value === initialValue) ?? null
    );
  }, [initialValue, filteredOptions, setFocusedOption]);
  const shouldRenderEmptyState = useMemo4(
    () => optionGroups.length === 0,
    [optionGroups]
  );
  const shouldRenderNoResultsState = useMemo4(
    () => ctx.searchTerm !== "" && filteredOptionGroups.every(
      (group) => group.options.length === 0 && !group.emptyState
    ),
    [ctx.searchTerm, filteredOptionGroups]
  );
  const state = useMemo4(
    () => shouldRenderEmptyState ? "empty" : shouldRenderNoResultsState ? "noResults" : "default",
    [shouldRenderEmptyState, shouldRenderNoResultsState]
  );
  return /* @__PURE__ */ jsxs55(Fragment11, { children: [
    /* @__PURE__ */ jsx82(
      SearchableListPopover,
      {
        trigger,
        placeholder,
        initialFocusRef: inputRef,
        searchTerm: ctx.searchTerm,
        setSearchTerm: filterOptionGroups ? ctx.setSearchTerm : void 0,
        disabled,
        onToggle,
        onCreate,
        createLabel,
        flip,
        children: match4(state).with("empty", () => emptyState).with("noResults", () => noResultsState).otherwise(
          () => filteredOptionGroups.map((opt) => /* @__PURE__ */ jsx82(
            GroupedOptions,
            {
              optionGroup: opt,
              selectOption,
              focusedOption,
              selectedOptionValue,
              baseDataTestId: dataTestId,
              MenuItemComponent
            },
            opt.key ?? opt.label
          ))
        )
      }
    ),
    /* @__PURE__ */ jsx82("input", { name, type: "hidden", value: selectedOptionValue ?? "" })
  ] });
};
var GroupedOptions = ({
  optionGroup: { label, options, emptyState },
  MenuItemComponent,
  focusedOption,
  selectedOptionValue,
  selectOption,
  baseDataTestId
}) => {
  if (!options.length && !emptyState) {
    return null;
  }
  return /* @__PURE__ */ jsxs55("div", { children: [
    label && /* @__PURE__ */ jsx82(
      Box10,
      {
        borderRadius: 6,
        padding: 2,
        maxWidth: "100%",
        marginX: 1,
        color: GreyGrey902,
        ...Lato13Bold4,
        children: label
      }
    ),
    !options.length ? emptyState : /* @__PURE__ */ jsx82(Flex11, { gap: 1, direction: "column", children: options.map((option) => /* @__PURE__ */ jsx82(
      MenuItemComponent,
      {
        option,
        isSelected: selectedOptionValue == option.value,
        isFocused: focusedOption === option,
        isDisabled: option.disabled ?? false,
        onSelect: () => selectOption(option),
        indented: label != null,
        "data-testid": baseDataTestId ? `${baseDataTestId}.option.${option.value}` : `select.option.${option.value}`
      },
      option.value
    )) })
  ] });
};
function genericFilterOptionGroups(groups, searchTerm) {
  return groups.reduce((acc, group) => {
    if (group.label?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return [...acc, group];
    }
    const withFilteredOptions = {
      ...group,
      options: group.options.filter(
        (option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    };
    if (!withFilteredOptions.options.length) {
      return acc;
    }
    return [...acc, withFilteredOptions];
  }, []);
}
function filterOptionGroupsOnGroupNameOnly(groups, searchTerm) {
  return groups.reduce((acc, group) => {
    if (group.label?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return [...acc, group];
    }
    return acc;
  }, []);
}
function toOptionGroupArray(options) {
  if (isOptionGroupArray(options) || options.length === 0) {
    return options;
  }
  return [
    {
      label: null,
      options
    }
  ];
}
function isOptionGroupArray(options) {
  return options.length > 0 && "options" in options[0];
}

// src/components/Control/ControlEmptyState.tsx
import { Flex as Flex12, Text as Text8 } from "@chakra-ui/react";
import { PlusIcon as PlusIcon4 } from "@heroicons/react/16/solid";
import { GreyWhite as GreyWhite5, Lato13Bold as Lato13Bold5, Lato13Regular as Lato13Regular6 } from "@sequencehq/design-tokens";
import { jsx as jsx83, jsxs as jsxs56 } from "react/jsx-runtime";
var ControlEmptyState = ({ title, content, button }) => {
  return /* @__PURE__ */ jsxs56(
    Flex12,
    {
      justifyContent: "center",
      flexDirection: "column",
      gap: 1,
      background: GreyWhite5,
      p: 2,
      children: [
        /* @__PURE__ */ jsx83(Text8, { ...Lato13Bold5, textAlign: "center", children: title }),
        /* @__PURE__ */ jsx83(Text8, { ...Lato13Regular6, textAlign: "center", children: content }),
        button && /* @__PURE__ */ jsx83(
          Button2,
          {
            leadingIcon: /* @__PURE__ */ jsx83(PlusIcon4, { width: 16 }),
            style: { flex: 0, marginTop: "4px" },
            onClick: button.onClick,
            "data-testid": `control-empty-state-button-${button.text.toLowerCase().replaceAll(" ", "-")}`,
            children: button.text
          }
        )
      ]
    }
  );
};

// src/components/Control/PillSelectControl.tsx
import { useEffect as useEffect9, useState as useState12 } from "react";

// src/components/Pill/Pill.tsx
import { Box as Box11 } from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  GreyGrey10 as GreyGrey102,
  GreyGrey20 as GreyGrey205,
  GreyGrey30 as GreyGrey306,
  GreyGrey40 as GreyGrey405,
  GreyGrey50 as GreyGrey502,
  GreyGrey70 as GreyGrey704,
  GreyGrey80 as GreyGrey805,
  GreyWhite as GreyWhite6,
  RedRed50
} from "@sequencehq/design-tokens";
import { jsx as jsx84, jsxs as jsxs57 } from "react/jsx-runtime";
var defaultVariantStyles = `
  background: ${GreyWhite6};
  color: ${GreyGrey805};
  border: 1px solid ${GreyGrey405};
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.05), 0px 0px 2px rgba(0, 0, 0, 0.02);
`;
var PillWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 6px;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  user-select: none;

  height: 24px;
  border: 1px solid transparent;

  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;

  ${(props) => props.variant === "default" && defaultVariantStyles}

  border-radius: 6px;

  ${(props) => props.interactive && `
    cursor: pointer;
    &:hover {
      ${defaultVariantStyles}
      background: ${GreyGrey102};
    }
  `}

  ${(props) => props.optional && `
    box-shadow: none;
    border: none;
    background: ${GreyGrey205};
    color: ${GreyGrey704};
  `}

  ${(props) => props.disabled && `
    box-shadow: none;
    background: ${GreyWhite6};
    color: ${GreyGrey502};
    border: 1px solid ${GreyGrey306};
    cursor: not-allowed;
  `}

  ${(props) => props.error && `
    box-shadow: none;
    background: ${GreyWhite6};
    color: ${RedRed50};
    border: 1px solid ${RedRed50};
  `}

  > svg {
    min-width: 16px;
  }
`;
var MoreBadge = styled.div`
  background: ${GreyGrey205};
  color: ${GreyGrey704};
  padding: 1px 4px;
  border-radius: 4px;
  margin-right: -2px;

  ${(props) => props.disabled && `
    color: ${GreyGrey502};
  `}
`;
var Pill = ({
  variant = "default",
  icon,
  children,
  interactive = false,
  more = 0,
  optional = false,
  disabled = false,
  error = false,
  onClear,
  onClick,
  "data-testid": testId
}) => {
  return /* @__PURE__ */ jsxs57(
    PillWrapper,
    {
      error,
      disabled,
      interactive,
      optional,
      variant,
      onClick,
      "data-testid": testId,
      children: [
        icon ? /* @__PURE__ */ jsx84(Box11, { flex: 0, minWidth: "fit-content", children: icon }) : null,
        /* @__PURE__ */ jsx84(Box11, { overflow: "hidden", textOverflow: "ellipsis", children }),
        more > 0 && /* @__PURE__ */ jsxs57(MoreBadge, { disabled, children: [
          "+",
          more
        ] }),
        !!onClear && !disabled && /* @__PURE__ */ jsx84(
          ClearButton,
          {
            onClick: onClear,
            marginRight: "-3px",
            isDisabled: disabled,
            "data-testid": `${testId}.clear`
          }
        )
      ]
    }
  );
};
var PillRow = styled.div`
  display: flex;
  gap: 8px;
  padding-bottom: 2px;
  flex-wrap: wrap;
`;

// src/components/Popover/Popover.tsx
import {
  Box as Box12,
  Popover as ChakraPopover,
  PopoverContent as PopoverContent4,
  PopoverTrigger as PopoverTrigger2,
  Portal as Portal3
} from "@chakra-ui/react";
import styled2 from "@emotion/styled";
import {
  BorderRadius6,
  GreyGrey30 as GreyGrey307,
  GreyWhite as GreyWhite7,
  ShadowM
} from "@sequencehq/design-tokens";

// src/components/Popover/PopoverStandard.tsx
import {
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent as PopoverContent2,
  PopoverHeader,
  Text as Text9
} from "@chakra-ui/react";
import {
  GreyGrey90 as GreyGrey903,
  Lato13Bold as Lato13Bold6,
  Lato13Regular as Lato13Regular7,
  ShadowS
} from "@sequencehq/design-tokens";
import { jsx as jsx85, jsxs as jsxs58 } from "react/jsx-runtime";
var PopoverStandard = ({
  width = "unset",
  maxWidth = 250,
  showArrow = true,
  showCloseButton = false,
  header,
  content
}) => {
  return /* @__PURE__ */ jsxs58(
    PopoverContent2,
    {
      w: width,
      maxW: maxWidth,
      boxShadow: ShadowS,
      borderRadius: 6,
      p: 4,
      children: [
        showArrow && /* @__PURE__ */ jsx85(PopoverArrow, {}),
        showCloseButton && /* @__PURE__ */ jsx85(PopoverCloseButton, {}),
        header && /* @__PURE__ */ jsx85(PopoverHeader, { border: "none", px: 0, pt: 0, pb: 2, children: /* @__PURE__ */ jsx85(Text9, { ...Lato13Bold6, color: GreyGrey903, children: header }) }),
        /* @__PURE__ */ jsx85(PopoverBody, { p: 0, children: typeof content === "string" ? /* @__PURE__ */ jsx85(Text9, { ...Lato13Regular7, color: GreyGrey903, children: content }) : content })
      ]
    }
  );
};

// src/components/Popover/PopoverTable.tsx
import { PopoverContent as PopoverContent3, Table, Tbody, Th, Thead, Tr } from "@chakra-ui/react";
import { ShadowS as ShadowS2 } from "@sequencehq/design-tokens";
import { jsx as jsx86, jsxs as jsxs59 } from "react/jsx-runtime";
var PopoverTable = ({
  width = "unset",
  maxWidth = 250,
  maxHeight,
  header,
  content
}) => {
  return /* @__PURE__ */ jsx86(
    PopoverContent3,
    {
      w: width,
      maxW: maxWidth,
      boxShadow: ShadowS2,
      borderRadius: 8,
      maxHeight,
      overflowY: "auto",
      position: "relative",
      children: /* @__PURE__ */ jsxs59(Table, { variant: "v2", width: "100%", height: "100%", borderRadius: 8, children: [
        header && /* @__PURE__ */ jsx86(Thead, { position: "sticky", top: 0, zIndex: "docked", children: /* @__PURE__ */ jsx86(Tr, { children: /* @__PURE__ */ jsx86(Th, { children: header }) }) }),
        /* @__PURE__ */ jsx86(Tbody, { height: "100%", children: content })
      ] })
    }
  );
};

// src/components/Popover/Popover.tsx
import { jsx as jsx87, jsxs as jsxs60 } from "react/jsx-runtime";
var PopoverSeparator = styled2.div`
  width: 100%;
  height: 5px;
  background: transparent;
`;
var PopoverContainer = styled2.div`
  width: 100%;
  border-radius: ${BorderRadius6};
  background-color: ${GreyWhite7};
  box-shadow: ${ShadowM};
  border: ${GreyGrey307} 1px solid;
  overflow: hidden;
`;
var PopoverWrapper = ({
  children,
  popover,
  disabled = false,
  placement = "bottom-start",
  isOpen,
  offset = [0, 0],
  openDelay
}) => {
  return /* @__PURE__ */ jsxs60(
    ChakraPopover,
    {
      variant: "unstyled",
      trigger: "hover",
      placement,
      offset,
      isOpen,
      openDelay,
      children: [
        /* @__PURE__ */ jsx87(Box12, { maxWidth: "100%", children: /* @__PURE__ */ jsx87(PopoverTrigger2, { children: /* @__PURE__ */ jsx87(Box12, { children }) }) }),
        /* @__PURE__ */ jsx87(Portal3, { children: !disabled && /* @__PURE__ */ jsxs60(PopoverContent4, { children: [
          /* @__PURE__ */ jsx87(PopoverSeparator, {}),
          /* @__PURE__ */ jsx87(PopoverContainer, { children: popover })
        ] }) })
      ]
    }
  );
};
var Popover2 = ({
  variant = "standard",
  defaultIsOpen = false,
  closeOnEscape = true,
  closeOnBlur = true,
  gutter = 8,
  position = "bottom",
  trigger = "hover",
  offset,
  onClose,
  children,
  ...props
}) => {
  return /* @__PURE__ */ jsxs60(
    ChakraPopover,
    {
      trigger,
      arrowSize: 10,
      placement: position,
      onClose,
      gutter,
      defaultIsOpen,
      closeOnEsc: closeOnEscape,
      closeOnBlur,
      offset,
      children: [
        /* @__PURE__ */ jsx87(PopoverTrigger2, { children: /* @__PURE__ */ jsx87("span", { children }) }),
        /* @__PURE__ */ jsxs60(Portal3, { children: [
          variant === "standard" && /* @__PURE__ */ jsx87(PopoverStandard, { ...props }),
          variant === "table" && /* @__PURE__ */ jsx87(PopoverTable, { ...props })
        ] })
      ]
    }
  );
};

// src/components/Control/hooks/useSelectedOption.ts
import { useMemo as useMemo5 } from "react";
function useSelectedOption(options, value) {
  const optionGroups = useMemo5(() => toOptionGroupArray(options), [options]);
  const flattenedOptions = useMemo5(
    () => optionGroups.flatMap((group) => group.options),
    [optionGroups]
  );
  const selectedOption = useMemo5(
    () => flattenedOptions.find((opt) => opt.value === value),
    [flattenedOptions, value]
  );
  return selectedOption;
}

// src/components/Control/PillSelectControl.tsx
import { jsx as jsx88 } from "react/jsx-runtime";
var PillSelectControl = ({
  options,
  initialValue,
  onChange,
  onCreate,
  createLabel,
  icon,
  labelWhenNoSelection,
  labelOverride,
  placeholder,
  errorMessage,
  disabled = false,
  popover,
  name,
  "data-testid": dataTestId
}) => {
  const [value, setValue] = useState12(initialValue);
  const [isOpen, setIsOpen] = useState12(false);
  const handleChange = (val) => {
    setValue(val);
    onChange(val);
  };
  useEffect9(() => {
    setValue(initialValue);
  }, [initialValue]);
  const selectedOption = useSelectedOption(options, value);
  const pill = /* @__PURE__ */ jsx88(
    Pill,
    {
      disabled,
      icon,
      interactive: true,
      error: !!errorMessage,
      "data-testid": dataTestId,
      children: errorMessage ?? labelOverride ?? selectedOption?.label ?? labelWhenNoSelection
    }
  );
  const control = /* @__PURE__ */ jsx88(ControlContextProvider, { children: /* @__PURE__ */ jsx88(
    SelectControl,
    {
      name,
      initialValue,
      options,
      onChange: handleChange,
      placeholder,
      onCreate,
      createLabel,
      disabled,
      trigger: pill,
      onToggle: setIsOpen,
      "data-testid": dataTestId
    }
  ) });
  return !popover ? control : /* @__PURE__ */ jsx88(PopoverWrapper, { disabled: isOpen, popover, children: control });
};

// src/components/Control/InputSelectControl.tsx
import {
  useEffect as useEffect17,
  useRef as useRef20,
  useState as useState24
} from "react";
import { Box as Box33, Center as Center6, Flex as Flex29, forwardRef as forwardRef4, Text as Text10 } from "@chakra-ui/react";
import {
  GreyGrey0 as GreyGrey05,
  GreyGrey10 as GreyGrey108,
  GreyGrey30 as GreyGrey3014,
  GreyGrey50 as GreyGrey5010,
  GreyGrey60 as GreyGrey6019,
  GreyGrey90 as GreyGrey9010,
  IndigoIndigo50 as IndigoIndigo5019,
  Lato13Regular as Lato13Regular22,
  ShadowS as ShadowS5
} from "@sequencehq/design-tokens";

// src/forms/inputs/dates/DateDurationInput/DateDurationInput.tsx
import {
  Popover as Popover3,
  PopoverAnchor,
  PopoverBody as PopoverBody2,
  PopoverContent as PopoverContent5,
  InputGroup as InputGroup2,
  Box as Box13
} from "@chakra-ui/react";

// src/forms/inputs/dates/DateDurationInput/useDateDurationInput.ts
import {
  formatDuration,
  format as format3,
  durationsToDateAdapter
} from "@sequencehq/utils/dates";
import { useCallback as useCallback5, useMemo as useMemo6, useRef as useRef8, useState as useState13 } from "react";
import equals from "lodash/fp/equals";
import pick from "lodash/fp/pick";
var BASE_DURATION_OPTIONS = [
  {
    diff: { months: 1, days: -1 },
    label: "1 month",
    value: "1-month"
  },
  {
    diff: { months: 2, days: -1 },
    label: "2 months",
    value: "2-month"
  },
  {
    diff: { months: 3, days: -1 },
    label: "3 months",
    value: "3-month"
  },
  {
    diff: { months: 6, days: -1 },
    label: "6 months",
    value: "6-month"
  },
  {
    diff: { months: 9, days: -1 },
    label: "9 months",
    value: "9-month"
  },
  {
    diff: { years: 1, days: -1 },
    label: "1 year",
    value: "1-year"
  },
  {
    diff: { years: 2, days: -1 },
    label: "2 years",
    value: "2-year"
  },
  {
    diff: { years: 3, days: -1 },
    label: "3 years",
    value: "3-year"
  },
  {
    diff: "FOREVER",
    label: "Open ended",
    value: "FOREVER"
  },
  {
    diff: "CUSTOM",
    label: "Choose date...",
    value: "CUSTOM"
  },
  {
    diff: "CUSTOM_DURATION",
    label: "Choose duration...",
    value: "CUSTOM_DURATION"
  }
];
var useDateDurationInput = ({
  value,
  mode,
  fromDate,
  durations = BASE_DURATION_OPTIONS,
  onChange,
  showAbsoluteDate = false,
  durationFilterFn
}) => {
  const [datePickerPopoverOpen, setDatePickerPopoverOpen] = useState13(false);
  const [durationPickerPopoverOpen, setDurationPickerPopoverOpen] = useState13(false);
  const resolvedDurations = useMemo6(() => {
    const filteredDurations = durationFilterFn ? durations.filter((duration) => durationFilterFn(duration)) : durations;
    if (!fromDate) {
      return filteredDurations.filter((durationConfig) => typeof durationConfig.diff !== "string").map((durationConfig) => ({
        ...durationConfig,
        absolute: durationsToDateAdapter.toDate(fromDate)(durationConfig.diff)
      }));
    }
    return filteredDurations;
  }, [durationFilterFn, durations, fromDate]);
  const selectedOption = useMemo6(() => {
    if (!value) {
      return "FOREVER";
    }
    const matchingDuration = resolvedDurations.find((durationConfig) => {
      if (typeof durationConfig.diff === "string") {
        return;
      }
      const fromDateToTest = new Date(
        format3(fromDate ?? /* @__PURE__ */ new Date(), "yyyy-MM-dd")
      );
      if (mode === "absolute" && value) {
        return equals(value)(
          durationsToDateAdapter.toDate(fromDateToTest)({
            years: 0,
            months: 0,
            days: 0,
            ...durationConfig.diff
          })
        );
      }
      const currentDuration = durationsToDateAdapter.toDuration(
        fromDateToTest
      )(
        durationsToDateAdapter.toDate(fromDateToTest)({
          years: 0,
          months: 0,
          days: 0,
          ...pick(["years", "months", "days"])(value)
        })
      );
      const durationOfOption = durationsToDateAdapter.toDuration(
        fromDateToTest
      )(
        durationsToDateAdapter.toDate(fromDateToTest)({
          years: 0,
          months: 0,
          days: 0,
          ...durationConfig.diff
        })
      );
      return equals(currentDuration)(durationOfOption);
    }) ?? "";
    if (!matchingDuration) {
      return "CURRENT_CUSTOM";
    }
    return matchingDuration?.value;
  }, [resolvedDurations, fromDate, value, mode]);
  const availableOptions = useMemo6(() => {
    const baseOptions = resolvedDurations.map((durationConfig) => ({
      value: durationConfig.value,
      label: durationConfig.label,
      diff: durationConfig.diff
    }));
    if (!fromDate) {
      return baseOptions.filter((option) => typeof option.diff !== "string");
    }
    if (selectedOption === "CURRENT_CUSTOM" && value) {
      const customDurationLength = mode === "absolute" ? durationsToDateAdapter.toDuration(fromDate)(value) : value;
      if (!customDurationLength) {
        throw new Error("Invalid custom duration length");
      }
      return [
        {
          diff: "CURRENT_CUSTOM",
          label: showAbsoluteDate && mode === "absolute" ? format3(value, "d MMMM yyyy") : formatDuration(
            {
              ...customDurationLength,
              days: customDurationLength.days === -1 ? 0 : customDurationLength.days
            },
            {
              format: ["years", "months", "days"]
            }
          ),
          value: "CURRENT_CUSTOM"
        },
        ...baseOptions
      ];
    }
    return baseOptions;
  }, [
    value,
    fromDate,
    selectedOption,
    showAbsoluteDate,
    mode,
    resolvedDurations
  ]);
  const inputRef = useRef8(null);
  const popoverRef = useRef8(null);
  const handleInputSelection = useCallback5(
    (newDurationValue) => {
      if (newDurationValue === "CURRENT_CUSTOM") {
        setDurationPickerPopoverOpen(false);
        setDatePickerPopoverOpen(false);
        return;
      }
      if (newDurationValue === "CUSTOM") {
        setDurationPickerPopoverOpen(false);
        setDatePickerPopoverOpen(true);
        return;
      }
      if (newDurationValue === "CUSTOM_DURATION") {
        setDurationPickerPopoverOpen(true);
        setDatePickerPopoverOpen(false);
        return;
      }
      const matchingDuration = durations.find((d) => d.value === newDurationValue);
      const newDuration = availableOptions.find(
        (option) => option.value === newDurationValue
      )?.diff;
      if (!newDuration || newDurationValue === "FOREVER") {
        return onChange(void 0, matchingDuration);
      }
      if (mode === "absolute") {
        return onChange(
          durationsToDateAdapter.toDate(fromDate)(newDuration),
          matchingDuration
        );
      }
      return onChange(
        {
          years: 0,
          months: 0,
          days: 0,
          ...newDuration
        },
        matchingDuration
      );
    },
    [onChange, availableOptions, fromDate, mode, durations]
  );
  const handlePickerSelection = useCallback5(
    (newDate) => {
      if (mode === "relative") {
        onChange(durationsToDateAdapter.toDuration(fromDate)(newDate));
        setDatePickerPopoverOpen(false);
        return;
      }
      onChange(newDate);
      setDatePickerPopoverOpen(false);
    },
    [onChange, mode, fromDate]
  );
  const handleDurationPickerSelection = useCallback5(
    (newDuration) => {
      if (mode === "absolute") {
        onChange(durationsToDateAdapter.toDate(fromDate)(newDuration));
        setDurationPickerPopoverOpen(false);
        return;
      }
      onChange(newDuration);
      setDurationPickerPopoverOpen(false);
    },
    [onChange, mode, fromDate]
  );
  const handleInputKeydown = useCallback5(
    (event) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
        event.stopPropagation();
      }
    },
    []
  );
  const closePopover = useCallback5(() => {
    setDurationPickerPopoverOpen(false);
    setDatePickerPopoverOpen(false);
  }, []);
  const selectedDate = useMemo6(() => {
    if (!fromDate || !selectedOption || selectedOption === "FOREVER") {
      return;
    }
    if (mode === "absolute") {
      return value;
    }
    return durationsToDateAdapter.toDate(fromDate)(value);
  }, [fromDate, selectedOption, mode, value]);
  const selectedDuration = useMemo6(() => {
    if (!fromDate || !selectedOption || selectedOption === "FOREVER") {
      return;
    }
    if (mode === "absolute") {
      return durationsToDateAdapter.toDuration(fromDate)(value);
    }
    return value;
  }, [fromDate, selectedOption, mode, value]);
  return {
    handlePickerSelection,
    handleDurationPickerSelection,
    handleInputSelection,
    handleInputKeydown,
    datePickerPopoverOpen,
    durationPickerPopoverOpen,
    closePopover,
    selectedOption,
    availableOptions,
    selectedDate,
    selectedDuration,
    refs: {
      input: inputRef,
      popover: popoverRef
    }
  };
};

// src/forms/inputs/dates/DateDurationInput/DateDurationInput.tsx
import { Lato13Regular as Lato13Regular10 } from "@sequencehq/design-tokens";

// src/forms/inputs/dates/DateDurationInput/DurationPicker.tsx
import { Flex as Flex14 } from "@chakra-ui/react";
import { GreyGrey90 as GreyGrey904, Lato13Regular as Lato13Regular9 } from "@sequencehq/design-tokens";

// src/forms/inputs/TextInput.tsx
import {
  useEffect as useEffect10,
  useRef as useRef9
} from "react";
import {
  Input as Input2,
  forwardRef as forwardRef3,
  InputGroup,
  InputLeftAddon,
  Flex as Flex13,
  Center as Center3
} from "@chakra-ui/react";
import {
  GreyWhite as GreyWhite8,
  IndigoIndigo50 as IndigoIndigo503,
  GreyGrey50 as GreyGrey503,
  Lato14Regular,
  GreyGrey10 as GreyGrey103,
  GreyGrey30 as GreyGrey308,
  GreyGrey60 as GreyGrey609,
  Lato13Regular as Lato13Regular8,
  GreyGrey40 as GreyGrey406,
  ShadowS as ShadowS3,
  ShadowXs,
  RedRed50 as RedRed502
} from "@sequencehq/design-tokens";
import { jsx as jsx89, jsxs as jsxs61 } from "react/jsx-runtime";
var TextInput = forwardRef3(
  ({ onChange, autoHighlightText = false, trailingIcon, ...restProps }, ref) => {
    const refInput = useRef9(null);
    useEffect10(() => {
      if (!refInput.current || !autoHighlightText) {
        return;
      }
      refInput.current.select();
    }, []);
    return /* @__PURE__ */ jsxs61(Flex13, { position: "relative", height: "40px", width: "100%", alignItems: "center", children: [
      /* @__PURE__ */ jsx89(
        Input2,
        {
          ...Lato14Regular,
          ref: (r) => {
            setRef(refInput, r);
            if (ref) {
              setRef(ref, r);
            }
          },
          height: "40px",
          width: "100%",
          border: "1px solid var(--grey-grey-40, #D1D9E4)",
          boxShadow: ShadowS3,
          borderRadius: "6px",
          backgroundColor: GreyWhite8,
          _hover: {
            border: `1px solid ${GreyGrey503}`,
            boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.07)"
          },
          _focus: {
            outline: `2px solid ${IndigoIndigo503}`,
            outlineOffset: "-1px",
            boxShadow: `0px 1px 3px 0px rgba(20, 23, 28, 0.06)`
          },
          ...restProps,
          onChange: (e) => onChange?.(e.target.value)
        }
      ),
      trailingIcon && /* @__PURE__ */ jsx89(
        Flex13,
        {
          marginRight: 2,
          alignItems: "center",
          position: "absolute",
          right: 0,
          zIndex: 2,
          children: /* @__PURE__ */ jsx89(Center3, { width: "20px", color: GreyGrey609, children: trailingIcon })
        }
      )
    ] });
  }
);
function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ;
    ref.current = value;
  }
}
var disabledStyles = {
  backgroundColor: GreyGrey103,
  color: GreyGrey503,
  border: `1px solid ${GreyGrey308}`,
  cursor: "not-allowed",
  boxShadow: "none"
};
var invalidStyles = {
  border: `1px solid ${RedRed502}`
};
var focusStyles = {
  border: `1px solid ${IndigoIndigo503}`
};
var TextFieldInput = ({
  fullWidth = true,
  isInvalid = false,
  isDisabled = false,
  prefix,
  suffix,
  onBlur: onBlurProps,
  onFocus: onFocusProps,
  autoFocus,
  borderColor: borderColor4 = GreyGrey406,
  height = "32px",
  ...props
}) => {
  const inputRef = useRef9(null);
  const inputWidth = fullWidth ? "100%" : "unset";
  const focusOnInput = () => {
    inputRef?.current?.focus();
  };
  return /* @__PURE__ */ jsxs61(
    InputGroup,
    {
      borderRadius: "6px",
      boxShadow: ShadowXs,
      border: `1px solid ${borderColor4}`,
      backgroundColor: GreyWhite8,
      onClick: focusOnInput,
      onFocus: onFocusProps,
      onBlur: onBlurProps,
      ...isDisabled && disabledStyles,
      ...isInvalid && invalidStyles,
      height,
      width: props.width,
      display: "flex",
      alignItems: "center",
      _focusWithin: {
        ...focusStyles
      },
      children: [
        prefix && /* @__PURE__ */ jsx89(
          InputLeftAddon,
          {
            h: height,
            pl: "8px",
            pr: "4px",
            pointerEvents: "none",
            border: "none",
            color: GreyGrey609,
            backgroundColor: "transparent",
            ...Lato13Regular8,
            children: prefix
          }
        ),
        /* @__PURE__ */ jsx89(
          Input2,
          {
            ref: inputRef,
            border: "none",
            height,
            boxShadow: "none",
            pl: prefix ? "0" : "8px",
            backgroundColor: "transparent",
            width: inputWidth,
            isInvalid,
            isDisabled,
            ...Lato13Regular8,
            ...props,
            _placeholder: { color: GreyGrey503 },
            _focus: {
              border: "none",
              boxShadow: "none"
            },
            _invalid: {
              border: "none",
              boxShadow: "none"
            },
            _disabled: {
              border: "none",
              boxShadow: "none",
              cursor: "not-allowed"
            },
            autoFocus,
            maxLength: props.maxLength
          }
        ),
        suffix && /* @__PURE__ */ jsx89(
          InputLeftAddon,
          {
            h: height,
            pl: "4px",
            pr: "8px",
            pointerEvents: "none",
            border: "none",
            color: GreyGrey609,
            backgroundColor: "transparent",
            ...Lato13Regular8,
            children: suffix
          }
        )
      ]
    }
  );
};

// src/forms/inputs/dates/DateDurationInput/DurationPicker.tsx
import { inflect as inflect3 } from "inflection";
import { useState as useState14 } from "react";
import { jsx as jsx90, jsxs as jsxs62 } from "react/jsx-runtime";
var DurationPicker = (props) => {
  const [value, setValue] = useState14(props.selectedDuration.months || 1);
  return /* @__PURE__ */ jsxs62(
    Flex14,
    {
      gap: "8px",
      ...Lato13Regular9,
      color: GreyGrey904,
      alignItems: "center",
      width: "200px",
      children: [
        /* @__PURE__ */ jsx90(
          TextInput,
          {
            autoFocus: true,
            type: "number",
            "data-testid": props["data-testid"],
            height: "32px",
            width: "56px",
            value,
            onChange: (newValue) => {
              setValue(Math.max(parseInt(newValue), 1));
            },
            onBlur: () => {
              const newDuration = { years: 0, months: value || 1, days: -1 };
              props.onDurationSelected(newDuration);
            },
            onKeyDown: (event) => {
              if (event.key === "Enter") {
                const newDuration = { years: 0, months: value || 1, days: -1 };
                props.onDurationSelected(newDuration);
              }
            }
          }
        ),
        inflect3("month", value)
      ]
    }
  );
};

// src/forms/inputs/dates/DateDurationInput/DateDurationInput.tsx
import { jsx as jsx91, jsxs as jsxs63 } from "react/jsx-runtime";
var defaultDurationOptions = BASE_DURATION_OPTIONS;
var DateDurationInput = ({
  value,
  name,
  onChange,
  mode,
  isDisabled,
  fromDate = /* @__PURE__ */ new Date(),
  durations,
  minDate,
  width = "226px",
  height = "36px",
  showAbsoluteDate = false,
  durationFilterFn,
  "data-testid": dataTestId,
  trailingIcon
}) => {
  const inputHook = useDateDurationInput({
    value,
    mode,
    onChange,
    fromDate,
    durations,
    showAbsoluteDate,
    durationFilterFn
  });
  const overrideSelectedLabel = inputHook.selectedOption === "CURRENT_CUSTOM" ? inputHook.availableOptions.find(
    (option) => option.value === inputHook.selectedOption
  )?.label : void 0;
  return /* @__PURE__ */ jsxs63(
    Popover3,
    {
      variant: "dateRange",
      isOpen: inputHook.datePickerPopoverOpen || inputHook.durationPickerPopoverOpen,
      placement: "bottom-start",
      onClose: () => inputHook.closePopover(),
      autoFocus: true,
      returnFocusOnClose: false,
      closeOnBlur: true,
      isLazy: true,
      lazyBehavior: "unmount",
      children: [
        /* @__PURE__ */ jsx91(PopoverAnchor, { children: /* @__PURE__ */ jsx91(Box13, { children: /* @__PURE__ */ jsx91(
          InputGroup2,
          {
            variant: "input-in-group-2",
            width,
            height,
            tabIndex: -1,
            position: "relative",
            transition: "border-color 0.2s ease-in-out",
            isolation: "auto",
            children: /* @__PURE__ */ jsx91(
              InputSelectControl,
              {
                name,
                inputRef: inputHook.refs.input,
                initialValue: inputHook.selectedDate || inputHook.selectedOption === "FOREVER" ? inputHook.selectedOption : void 0,
                triggerStyle: {
                  width,
                  ...Lato13Regular10
                },
                overrideSelectedLabel,
                onChange: inputHook.handleInputSelection,
                options: inputHook.availableOptions,
                disabled: isDisabled,
                matchWidth: true,
                placeholder: "Select duration",
                "data-testid": dataTestId,
                trailingIcon
              }
            )
          }
        ) }) }),
        inputHook.datePickerPopoverOpen && /* @__PURE__ */ jsx91(PopoverContent5, { children: /* @__PURE__ */ jsx91(PopoverBody2, { ref: inputHook.refs.popover, children: /* @__PURE__ */ jsx91(
          DateDurationPopover,
          {
            selectedDate: inputHook.selectedDate ?? /* @__PURE__ */ new Date(),
            onDateSelected: inputHook.handlePickerSelection,
            minDate: minDate ?? fromDate,
            "data-testid": `${dataTestId}.calendar`
          }
        ) }) }),
        inputHook.durationPickerPopoverOpen && /* @__PURE__ */ jsx91(PopoverContent5, { children: /* @__PURE__ */ jsx91(PopoverBody2, { ref: inputHook.refs.popover, children: /* @__PURE__ */ jsx91(
          DurationPicker,
          {
            selectedDuration: inputHook.selectedDuration ?? { months: 1 },
            onDurationSelected: inputHook.handleDurationPickerSelection,
            "data-testid": `${dataTestId}.durationPicker`
          }
        ) }) })
      ]
    }
  );
};

// src/forms/inputs/dates/DateDurationInput/DaysDurationPicker.tsx
import { Flex as Flex15 } from "@chakra-ui/react";
import { GreyGrey90 as GreyGrey905, Lato13Regular as Lato13Regular11 } from "@sequencehq/design-tokens";
import { add as add3, format as format4 } from "@sequencehq/utils/dates";
import { inflect as inflect4 } from "inflection";
import { useState as useState15 } from "react";
import { jsx as jsx92, jsxs as jsxs64 } from "react/jsx-runtime";
var DaysDurationPicker = ({
  selectedDays,
  onDaysSelected,
  "data-testid": dataTestId
}) => {
  const [value, setValue] = useState15(String(selectedDays));
  function formatFutureDate(days) {
    const futureDate = add3(/* @__PURE__ */ new Date(), { days });
    const dayOfWeek = format4(futureDate, "EEE");
    const day = futureDate.getDate();
    const month = format4(futureDate, "MMM");
    const suffixes = ["th", "st", "nd", "rd"];
    const v = day % 100;
    const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
    return `(${dayOfWeek}, ${day}${suffix} ${month})`;
  }
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const numValue = Math.max(parseInt(value) || 1, 1);
      onDaysSelected(numValue);
    }
  };
  const handleChange = (newValue) => {
    const numValue = parseInt(newValue);
    if (numValue <= 0) {
      setValue("1");
      return;
    }
    setValue(newValue);
  };
  const handleBlur = () => {
    const numValue = Math.max(parseInt(value) || 1, 1);
    setValue(String(numValue));
    onDaysSelected(numValue);
  };
  return /* @__PURE__ */ jsxs64(
    Flex15,
    {
      gap: "8px",
      ...Lato13Regular11,
      color: GreyGrey905,
      alignItems: "center",
      width: "200px",
      children: [
        /* @__PURE__ */ jsx92(
          TextInput,
          {
            autoFocus: true,
            type: "number",
            "data-testid": dataTestId,
            height: "32px",
            width: "56px",
            value,
            min: "1",
            onChange: handleChange,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown
          }
        ),
        /* @__PURE__ */ jsxs64("span", { style: { whiteSpace: "nowrap" }, children: [
          inflect4("day", parseInt(value) || 1),
          " ",
          formatFutureDate(parseInt(value) || 1)
        ] })
      ]
    }
  );
};

// src/forms/inputs/dates/DatePickerInput/DatePickerInput.tsx
import { useRef as useRef11 } from "react";
import {
  Popover as Popover4,
  PopoverAnchor as PopoverAnchor2,
  PopoverBody as PopoverBody3,
  PopoverContent as PopoverContent6,
  InputGroup as InputGroup3,
  Box as Box14,
  Icon as Icon2,
  Center as Center4,
  Portal as Portal4
} from "@chakra-ui/react";
import { GreyGrey60 as GreyGrey6010 } from "@sequencehq/design-tokens";
import CalendarIcon2 from "@heroicons/react/24/outline/CalendarIcon";
import SolidCalendarIcon from "@heroicons/react/16/solid/CalendarIcon";

// src/forms/inputs/dates/DatePickerInput/useDatePickerInput.ts
import { useOutsideClick as useOutsideClick2 } from "@chakra-ui/react";
import { format as format5 } from "@sequencehq/utils/dates";
import { useCallback as useCallback6, useEffect as useEffect11, useRef as useRef10, useState as useState16 } from "react";
var dateAdapter = {
  dateToString: (dateToFormat) => {
    if (!dateToFormat) {
      return "";
    }
    try {
      return format5(dateToFormat, "yyyy-MM-dd") ?? "";
    } catch {
      return "";
    }
  },
  stringToDate: (potentialDate) => {
    try {
      return new Date(potentialDate);
    } catch {
      return void 0;
    }
  }
};
var useDatePickerInput = (props) => {
  const [formattedDateString, setFormattedDateString] = useState16(
    dateAdapter.dateToString(props.value)
  );
  const [popoverOpen, setPopoverOpen] = useState16(false);
  const inputRef = useRef10(null);
  const popoverRef = useRef10(null);
  const handleInputUpdate = useCallback6((newValue) => {
    setFormattedDateString(newValue);
  }, []);
  const handleInputBlur = useCallback6(() => {
    const parsedDate = dateAdapter.stringToDate(formattedDateString);
    props.onChange(formattedDateString ? parsedDate : void 0);
    setFormattedDateString(dateAdapter.dateToString(parsedDate));
  }, [formattedDateString, props]);
  const handlePickerSelection = useCallback6(
    (newDate) => {
      props.onChange(newDate);
      setFormattedDateString(dateAdapter.dateToString(newDate));
      setPopoverOpen(false);
    },
    [props]
  );
  const handleInputKeydown = useCallback6(
    (event) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
        event.stopPropagation();
      }
    },
    []
  );
  const updatePopoverOpen = useCallback6((newValue) => {
    setPopoverOpen(newValue);
  }, []);
  useEffect11(() => {
    setFormattedDateString(dateAdapter.dateToString(props.value));
  }, [props.value]);
  useOutsideClick2({
    ref: popoverRef,
    handler: (e) => {
      if (e.target === inputRef.current) {
        return;
      }
      setPopoverOpen(false);
    }
  });
  return {
    handleInputBlur,
    handlePickerSelection,
    handleInputUpdate,
    handleInputKeydown,
    popoverOpen,
    updatePopoverOpen,
    formattedDateString,
    refs: {
      input: inputRef,
      popover: popoverRef
    }
  };
};

// src/forms/inputs/dates/DatePickerInput/DatePickerPopover.tsx
import { Flex as Flex16 } from "@chakra-ui/react";

// src/forms/inputs/dates/DatePickerInput/useDatePickerPopover.ts
import { useEffect as useEffect12, useState as useState17 } from "react";
import {
  useDayzed as useDayzed2
} from "dayzed";
var useDatePickerPopover = (props) => {
  const [offset, setOffset] = useState17(0);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState17(
    props.selectedDate && isValidDate(props.selectedDate) ? props.selectedDate : void 0
  );
  useEffect12(() => {
    if (!isValidDate(props.selectedDate)) {
      return;
    }
    setSelectedCalendarDate(props.selectedDate);
    setOffset(0);
  }, [props.selectedDate]);
  const { calendars, getDateProps, getBackProps, getForwardProps } = useDayzed2({
    selected: [
      props.selectedDate && dayzedAdapter.toDayzed(props.selectedDate)
    ]?.filter(Boolean),
    onDateSelected: (newSelectedDate) => {
      props.onDateSelected(dayzedAdapter.fromDayzed(newSelectedDate.date));
    },
    date: props.selectedDate && dayzedAdapter.toDayzed(props.selectedDate),
    minDate: props.minDate && dayzedAdapter.toDayzed(props.minDate),
    maxDate: props.maxDate && dayzedAdapter.toDayzed(props.maxDate),
    monthsToDisplay: 1,
    offset,
    onOffsetChanged: (newOffset) => setOffset(newOffset),
    firstDayOfWeek: 1
  });
  return {
    calendarView: {
      calendar: calendars[0],
      getDateProps,
      getBackProps,
      getForwardProps,
      selectedDate: selectedCalendarDate
    }
  };
};

// src/forms/inputs/dates/DatePickerInput/DatePickerPopover.tsx
import { jsx as jsx93 } from "react/jsx-runtime";
var DatePickerPopover = ({
  selectedDate,
  minDate,
  maxDate,
  onDateSelected,
  initialRef
}) => {
  const { calendarView } = useDatePickerPopover({
    selectedDate,
    minDate,
    maxDate,
    onDateSelected
  });
  return /* @__PURE__ */ jsx93(Flex16, { children: /* @__PURE__ */ jsx93(Flex16, { height: "320px", children: /* @__PURE__ */ jsx93(CalendarView, { ...calendarView, initialRef, type: "single" }) }) });
};

// src/forms/inputs/dates/DatePickerInput/DatePickerInput.tsx
import { Fragment as Fragment12, jsx as jsx94, jsxs as jsxs65 } from "react/jsx-runtime";
var DatePickerInput = ({
  value,
  name,
  onChange,
  minDate,
  isDisabled,
  isInvalid,
  width = "226px",
  height,
  variant,
  trigger,
  placeholder = "Select date",
  "data-testid": dataTestId,
  suffix
}) => {
  const testId = dataTestId ?? "datePicker";
  const inputHook = useDatePickerInput({
    value,
    onChange
  });
  const inputRef = useRef11(null);
  return /* @__PURE__ */ jsxs65(
    Popover4,
    {
      variant: "dateRange",
      isOpen: inputHook.popoverOpen,
      placement: "bottom-start",
      onClose: () => inputHook.updatePopoverOpen(false),
      autoFocus: false,
      returnFocusOnClose: false,
      closeOnBlur: false,
      isLazy: true,
      lazyBehavior: "unmount",
      initialFocusRef: inputRef,
      children: [
        /* @__PURE__ */ jsx94(PopoverAnchor2, { children: /* @__PURE__ */ jsx94("div", { style: { cursor: isDisabled ? "not-allowed" : "auto" }, children: /* @__PURE__ */ jsx94(
          InputGroup3,
          {
            "data-testid": `${testId}`,
            ref: inputRef,
            height,
            width,
            onBlur: inputHook.handleInputBlur,
            onClick: () => inputHook.updatePopoverOpen(true),
            tabIndex: isDisabled ? void 0 : 0,
            pointerEvents: isDisabled ? "none" : "auto",
            children: trigger ?? /* @__PURE__ */ jsx94(
              TextFieldInput,
              {
                "data-testid": `${testId}.textInput`,
                name,
                isDisabled,
                isInvalid,
                width,
                placeholder,
                value: inputHook.formattedDateString,
                autoComplete: "off",
                suffix: /* @__PURE__ */ jsxs65(Fragment12, { children: [
                  suffix,
                  /* @__PURE__ */ jsx94(Center4, { width: "20px", children: variant === "new" ? /* @__PURE__ */ jsx94(
                    Icon2,
                    {
                      "data-testid": `${testId}.popover.iconInput`,
                      as: SolidCalendarIcon,
                      height: "16px",
                      width: "16px",
                      color: GreyGrey6010
                    }
                  ) : /* @__PURE__ */ jsx94(CalendarIcon2, { height: "20px" }) })
                ] })
              }
            )
          }
        ) }) }),
        inputHook.popoverOpen && /* @__PURE__ */ jsx94(Portal4, { children: /* @__PURE__ */ jsx94(PopoverContent6, { "data-testid": `${testId}.popover.content`, children: /* @__PURE__ */ jsx94(PopoverBody3, { ref: inputHook.refs.popover, children: /* @__PURE__ */ jsx94(Box14, { "data-testid": dataTestId, children: /* @__PURE__ */ jsx94(
          DatePickerPopover,
          {
            selectedDate: value,
            onDateSelected: inputHook.handlePickerSelection,
            minDate
          }
        ) }) }) }) })
      ]
    }
  );
};

// src/forms/inputs/dates/DateRangePicker/DateRangePicker.tsx
import { useEffect as useEffect15, useState as useState22 } from "react";
import { useDayzed as useDayzed3 } from "dayzed";
import {
  Flex as Flex26,
  Popover as Popover11,
  PopoverAnchor as PopoverAnchor6,
  PopoverArrow as PopoverArrow2,
  PopoverBody as PopoverBody9,
  PopoverContent as PopoverContent13,
  PopoverTrigger as PopoverTrigger6,
  Stack as Stack3,
  Divider as Divider7,
  Box as Box29
} from "@chakra-ui/react";
import { ShadowS as ShadowS4, GreyGrey10 as GreyGrey107 } from "@sequencehq/design-tokens";
import { borderColor as borderColor2 } from "@sequencehq/core-theme";

// src/forms/inputs/dates/DateRangePicker/DateRangePickerTextInput.tsx
import { CalendarIcon as CalendarIcon3 } from "@heroicons/react/16/solid";
import {
  GreyGrey40 as GreyGrey4013,
  IndigoIndigo50 as IndigoIndigo5017,
  Lato13Bold as Lato13Bold10
} from "@sequencehq/design-tokens";
import { dateTimeWithFormat } from "@sequencehq/formatters";

// src/forms/inputs/DayOfMonthInput/DayOfMonthInput.tsx
import {
  Popover as Popover5,
  PopoverBody as PopoverBody4,
  PopoverContent as PopoverContent7,
  InputGroup as InputGroup4,
  Icon as Icon3,
  Center as Center5,
  Box as Box16,
  PopoverTrigger as PopoverTrigger3
} from "@chakra-ui/react";
import { ChevronDownIcon as ChevronDownIcon4, ChevronUpIcon as ChevronUpIcon2 } from "@chakra-ui/icons";
import { nth } from "@sequencehq/formatters";

// src/forms/inputs/DayOfMonthInput/MonthlyPopover.tsx
import { Box as Box15, SimpleGrid as SimpleGrid2, Flex as Flex17, FormLabel } from "@chakra-ui/react";
import { jsx as jsx95, jsxs as jsxs66 } from "react/jsx-runtime";
var MonthlyPopover = ({
  onChange,
  value
}) => /* @__PURE__ */ jsxs66(Flex17, { flexDirection: "column", width: "max-content", children: [
  /* @__PURE__ */ jsx95(FormLabel, { textAlign: "center", children: "Choose a day of the month" }),
  /* @__PURE__ */ jsx95(Box15, { height: 4 }),
  /* @__PURE__ */ jsx95(SimpleGrid2, { columns: 7, spacing: "1px", textAlign: "center", children: Array.from(Array(31), (_, index) => /* @__PURE__ */ jsx95(
    DateButton,
    {
      selected: value === index + 1,
      label: index + 1,
      today: false,
      selectableDate: true,
      dateProps: {
        onClick: (event) => {
          event.preventDefault();
          onChange(index + 1);
        }
      }
    },
    index
  )) })
] });

// src/forms/inputs/DayOfMonthInput/DayOfMonthInput.tsx
import { Fragment as Fragment13, jsx as jsx96, jsxs as jsxs67 } from "react/jsx-runtime";
var DayOfMonthInput = ({
  isDisabled,
  isInvalid,
  value,
  onChange,
  width = "184px",
  "data-testid": dataTestId,
  suffix
}) => {
  return /* @__PURE__ */ jsx96(Popover5, { placement: "bottom-start", isLazy: true, closeOnBlur: true, lazyBehavior: "unmount", children: ({ onClose, isOpen }) => /* @__PURE__ */ jsxs67(Fragment13, { children: [
    /* @__PURE__ */ jsx96(PopoverTrigger3, { children: /* @__PURE__ */ jsx96(InputGroup4, { width, tabIndex: isDisabled ? void 0 : 0, children: /* @__PURE__ */ jsx96(
      TextFieldInput,
      {
        isDisabled,
        isInvalid,
        width,
        placeholder: "Select billing date",
        value: formatMonthly(value),
        suffix: /* @__PURE__ */ jsxs67(Fragment13, { children: [
          suffix,
          /* @__PURE__ */ jsx96(Center5, { width: "20px", children: isOpen ? /* @__PURE__ */ jsx96(Icon3, { as: ChevronUpIcon2, height: "20px", width: "20px" }) : /* @__PURE__ */ jsx96(Icon3, { as: ChevronDownIcon4, height: "20px", width: "20px" }) })
        ] })
      }
    ) }) }),
    /* @__PURE__ */ jsx96(PopoverContent7, { children: /* @__PURE__ */ jsx96(PopoverBody4, { children: /* @__PURE__ */ jsx96(Box16, { "data-testid": dataTestId, children: /* @__PURE__ */ jsx96(
      MonthlyPopover,
      {
        onChange: (newValue) => {
          onChange(newValue);
          onClose();
        },
        value
      }
    ) }) }) })
  ] }) });
};
var formatMonthly = (recurrenceDayOfMonth) => {
  return typeof recurrenceDayOfMonth === "number" ? `${nth(`${recurrenceDayOfMonth}`)} of the month` : "Select billing date";
};

// src/forms/inputs/HorizontalSelectorInput.tsx
import { Flex as Flex18 } from "@chakra-ui/react";
import {
  GreyGrey0,
  GreyGrey40 as GreyGrey407,
  GreyGrey60 as GreyGrey6011,
  GreyWhite as GreyWhite10,
  IndigoIndigo50 as IndigoIndigo504,
  Lato13Regular as Lato13Regular13,
  Lato13Bold as Lato13Bold7,
  RedRed50 as RedRed503
} from "@sequencehq/design-tokens";

// src/components/Tooltip.tsx
import { useCallback as useCallback7, useRef as useRef12 } from "react";
import {
  Box as Box17,
  Popover as Popover6,
  PopoverContent as PopoverContent8,
  PopoverTrigger as PopoverTrigger4,
  Portal as Portal5,
  useDisclosure as useDisclosure2,
  Tooltip as ChakraTooltip
} from "@chakra-ui/react";
import {
  GreyGrey100 as GreyGrey1002,
  GreyGrey30 as GreyGrey309,
  GreyGrey90 as GreyGrey906,
  GreyWhite as GreyWhite9,
  Lato13Regular as Lato13Regular12,
  Lato14Bold as Lato14Bold2,
  Lato14Regular as Lato14Regular2
} from "@sequencehq/design-tokens";
import { jsx as jsx97, jsxs as jsxs68 } from "react/jsx-runtime";
var Tooltip = ({ children, hasArrow = true, ...props }) => {
  return /* @__PURE__ */ jsx97(
    ChakraTooltip,
    {
      hasArrow,
      p: "8px",
      bg: GreyGrey906,
      borderRadius: "6px",
      arrowSize: 8,
      shouldWrapChildren: true,
      ...props,
      ...Lato13Regular12,
      children
    }
  );
};
var TooltipLegacy = (props) => {
  const { onOpen, onClose, isOpen } = useDisclosure2();
  const popoverContentRef = useRef12(null);
  const queuedClose = useRef12(null);
  const queuedOpen = useRef12(null);
  const handleOpen = useCallback7(() => {
    if (queuedOpen.current) {
      return;
    }
    if (queuedClose.current) {
      clearTimeout(queuedClose.current);
      queuedClose.current = null;
    }
    queuedOpen.current = setTimeout(onOpen, props.delay ?? 250);
  }, [onOpen, props.delay]);
  const handleClose = useCallback7(() => {
    queuedOpen.current && clearTimeout(queuedOpen.current);
    queuedOpen.current = null;
    queuedClose.current = setTimeout(() => onClose(), props.delay ?? 250);
  }, [onClose, props.delay]);
  return /* @__PURE__ */ jsxs68(
    Popover6,
    {
      isOpen: !props.disabled && isOpen,
      placement: props.placement ?? "top",
      children: [
        /* @__PURE__ */ jsx97(PopoverTrigger4, { children: /* @__PURE__ */ jsx97(
          Box17,
          {
            onMouseEnter: handleOpen,
            onMouseLeave: handleClose,
            ...props.style?.wrapper ?? {},
            children: props.children
          }
        ) }),
        /* @__PURE__ */ jsx97(Portal5, { children: /* @__PURE__ */ jsx97(
          PopoverContent8,
          {
            ref: popoverContentRef,
            padding: "12px",
            borderRadius: "8px",
            border: `1px solid ${GreyGrey309}`,
            boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
            backgroundColor: GreyWhite9,
            onMouseEnter: handleOpen,
            onMouseLeave: handleClose,
            overflow: "visible",
            ...props.style?.content ?? {},
            children: /* @__PURE__ */ jsxs68(Box17, { children: [
              props.title && /* @__PURE__ */ jsx97(Box17, { ...Lato14Bold2, color: GreyGrey1002, marginBottom: "8px", children: props.title }),
              /* @__PURE__ */ jsx97(Box17, { ...Lato14Regular2, color: GreyGrey1002, children: props.content })
            ] })
          }
        ) })
      ]
    }
  );
};

// src/forms/inputs/HorizontalSelectorInput.tsx
import { jsx as jsx98 } from "react/jsx-runtime";
var HorizontalSelectorInput = ({
  options,
  value,
  onChange,
  isDisabled,
  isInvalid,
  ...props
}) => {
  const inActiveDefaultBorderStyle = {
    ...Lato13Regular13,
    color: GreyGrey6011
  };
  const inActiveLeftBorderStyle = {
    borderLeft: `1px solid ${GreyGrey407}`,
    borderRight: "none"
  };
  const inActiveRightBorderStyle = {
    borderRight: `1px solid ${GreyGrey407}`,
    borderLeft: "none"
  };
  const activeTabStyle = {
    border: `1px solid ${GreyGrey407}`,
    borderRadius: "4px",
    boxShadow: "0px 2px 2px 0px rgba(20, 23, 28, 0.08), 0px -2px 2px 0px rgba(255, 255, 255, 0.24) inset",
    backgroundColor: GreyWhite10,
    ...Lato13Bold7,
    color: IndigoIndigo504
  };
  return /* @__PURE__ */ jsx98(
    Flex18,
    {
      borderRadius: "6px",
      background: GreyGrey0,
      padding: "3px",
      height: "40px",
      width: "100%",
      flex: "space-between",
      boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
      ...isDisabled ? { pointerEvents: "none", cursor: "not-allowed", opacity: 0.4 } : {},
      ...isInvalid ? { border: `1px solid ${RedRed503}` } : { border: `1px solid ${GreyGrey407}` },
      ...options.some((option) => option.disabled) && {
        cursor: "not-allowed"
      },
      ...props,
      children: options.map((option) => {
        const tabIndex = options.findIndex((tab) => tab.value === option.value);
        const activeTab = options.findIndex((tabb) => tabb.value === value);
        const isLessThanActiveTab = tabIndex < activeTab;
        const isGreaterThanActiveTab = tabIndex > activeTab;
        return /* @__PURE__ */ jsx98(
          TooltipLegacy,
          {
            disabled: !option.disabledReason,
            content: option.disabledReason,
            style: {
              wrapper: {
                width: "100%"
              }
            },
            children: /* @__PURE__ */ jsx98(
              Flex18,
              {
                ...option.value === value ? {
                  ...activeTabStyle
                } : {
                  ...inActiveDefaultBorderStyle,
                  ...isLessThanActiveTab && inActiveLeftBorderStyle,
                  ...isGreaterThanActiveTab && inActiveRightBorderStyle,
                  _first: {
                    borderLeft: "none"
                  },
                  _last: {
                    borderRight: "none"
                  }
                },
                alignItems: "center",
                height: "100%",
                width: "100%",
                justifyContent: "center",
                userSelect: "none",
                onClick: () => !option.disabled && onChange(option.value),
                ...option.disabled ? {
                  pointerEvents: "none",
                  opacity: 0.4
                } : { cursor: "pointer" },
                "data-active": option.value === value,
                "data-testid": props["data-testid"] && `${props["data-testid"]}.option.${option.value}`,
                children: option.label
              }
            )
          },
          option.value
        );
      })
    }
  );
};

// src/forms/inputs/NumberInput.tsx
import {
  NumberInput as ChakraNumberInput,
  NumberInputField
} from "@chakra-ui/react";
import { IndigoIndigo50 as IndigoIndigo505, Lato14Regular as Lato14Regular3 } from "@sequencehq/design-tokens";
import { jsx as jsx99 } from "react/jsx-runtime";
var NumberInput = ({
  onChange,
  onBlur,
  isDisabled,
  isInvalid,
  width = "100%",
  ...props
}) => {
  return /* @__PURE__ */ jsx99(
    ChakraNumberInput,
    {
      precision: props.precision,
      step: 0.01,
      value: props.value,
      onChange: (valueAsString, valueAsNumber) => {
        onChange(valueAsString, valueAsNumber);
      },
      clampValueOnBlur: Boolean(props.precision),
      onBlur,
      isDisabled,
      isInvalid,
      width,
      children: /* @__PURE__ */ jsx99(
        NumberInputField,
        {
          ...Lato14Regular3,
          _focus: {
            outline: `2px solid ${IndigoIndigo505}`,
            outlineOffset: "-1px",
            boxShadow: `0px 1px 3px 0px rgba(20, 23, 28, 0.06)`
          },
          height: "40px",
          width: "100%",
          border: "1px solid var(--grey-grey-40, #D1D9E4)",
          boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
          borderRadius: "6px",
          onBlur,
          ...props
        }
      )
    }
  );
};

// src/forms/inputs/PercentageInput.tsx
import {
  NumberInput as ChakraNumberInput2,
  NumberInputField as NumberInputField2,
  InputGroup as InputGroup5,
  InputLeftAddon as InputLeftAddon2
} from "@chakra-ui/react";
import {
  GreyGrey0 as GreyGrey02,
  Lato14Regular as Lato14Regular4,
  IndigoIndigo50 as IndigoIndigo506,
  ShadowXs as ShadowXs2,
  GreyGrey40 as GreyGrey408,
  GreyWhite as GreyWhite11,
  GreyGrey60 as GreyGrey6012,
  Lato13Regular as Lato13Regular14,
  GreyGrey50 as GreyGrey504,
  GreyGrey10 as GreyGrey104,
  GreyGrey30 as GreyGrey3010,
  RedRed50 as RedRed504
} from "@sequencehq/design-tokens";
import { useRef as useRef13 } from "react";
import { jsx as jsx100, jsxs as jsxs69 } from "react/jsx-runtime";
var PercentageInput = ({
  onChange,
  isDisabled,
  isInvalid,
  width = "100%",
  variant = "standard",
  ...props
}) => {
  if (variant === "v2") {
    return /* @__PURE__ */ jsx100(
      PercentageInputV2,
      {
        ...{ onChange, isDisabled, isInvalid, width, ...props }
      }
    );
  }
  return /* @__PURE__ */ jsxs69(
    InputGroup5,
    {
      ...variant === "table" ? {} : {
        boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)"
      },
      width,
      children: [
        /* @__PURE__ */ jsx100(
          InputLeftAddon2,
          {
            ...variant === "table" ? {
              ...Lato14Regular4,
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              borderRadius: "0",
              height: "40px",
              padding: "0 4px 0 12px",
              width: "fit-content",
              display: "flex",
              alignContent: "center"
            } : {
              backgroundColor: GreyGrey02
            },
            ...isDisabled ? { opacity: 0.4 } : {},
            children: "%"
          }
        ),
        /* @__PURE__ */ jsx100(
          ChakraNumberInput2,
          {
            precision: props.precision,
            step: 0.01,
            value: props.value,
            onChange,
            isDisabled,
            isInvalid,
            width: "100%",
            clampValueOnBlur: Boolean(props.precision),
            min: typeof props.min === "number" ? props.min : void 0,
            max: typeof props.max === "number" ? props.max : void 0,
            children: /* @__PURE__ */ jsx100(
              NumberInputField2,
              {
                ...Lato14Regular4,
                height: "40px",
                width: "100%",
                borderTopLeftRadius: "0",
                borderBottomLeftRadius: "0",
                _focus: {
                  outline: `2px solid ${IndigoIndigo506}`,
                  outlineOffset: "-1px",
                  boxShadow: `0px 1px 3px 0px rgba(20, 23, 28, 0.06)`
                },
                ...variant === "table" ? {
                  paddingLeft: "4px",
                  paddingRight: "4px"
                } : {},
                ...props
              }
            )
          }
        )
      ]
    }
  );
};
var disabledStyles2 = {
  backgroundColor: GreyGrey104,
  color: GreyGrey504,
  border: `1px solid ${GreyGrey3010}`,
  cursor: "not-allowed",
  boxShadow: "none"
};
var invalidStyles2 = {
  border: `1px solid ${RedRed504}`
};
var focusStyles2 = {
  border: `1px solid ${IndigoIndigo506}`
};
var PercentageInputV2 = ({
  isDisabled,
  isInvalid,
  width,
  precision,
  value,
  onChange,
  ...props
}) => {
  const inputRef = useRef13(null);
  const focusOnInput = () => {
    inputRef?.current?.focus();
  };
  return /* @__PURE__ */ jsxs69(
    InputGroup5,
    {
      height: "32px",
      borderRadius: "6px",
      boxShadow: ShadowXs2,
      border: `1px solid ${GreyGrey408}`,
      backgroundColor: GreyWhite11,
      onClick: focusOnInput,
      ...isDisabled && disabledStyles2,
      ...isInvalid && invalidStyles2,
      _focusWithin: {
        ...focusStyles2
      },
      children: [
        /* @__PURE__ */ jsx100(
          InputLeftAddon2,
          {
            h: "32px",
            pl: "8px",
            pr: "4px",
            pointerEvents: "none",
            border: "none",
            color: GreyGrey6012,
            backgroundColor: "transparent",
            ...Lato13Regular14,
            children: "%"
          }
        ),
        /* @__PURE__ */ jsx100(
          ChakraNumberInput2,
          {
            precision,
            step: 0.01,
            value,
            onChange,
            isDisabled,
            isInvalid,
            width: "100%",
            clampValueOnBlur: Boolean(precision),
            min: typeof props.min === "number" ? props.min : void 0,
            max: typeof props.max === "number" ? props.max : void 0,
            children: /* @__PURE__ */ jsx100(
              NumberInputField2,
              {
                ...Lato14Regular4,
                ref: inputRef,
                border: "none",
                height: "32px",
                boxShadow: "none",
                pl: "0",
                backgroundColor: "transparent",
                width,
                ...Lato13Regular14,
                ...props,
                _placeholder: { color: GreyGrey504 },
                _focus: {
                  border: "none",
                  boxShadow: "none"
                },
                _invalid: {
                  border: "none",
                  boxShadow: "none"
                },
                _disabled: {
                  border: "none",
                  boxShadow: "none",
                  cursor: "not-allowed"
                }
              }
            )
          }
        )
      ]
    }
  );
};

// src/forms/inputs/PriceInput.tsx
import {
  NumberInput as ChakraNumberInput3,
  NumberInputField as NumberInputField3,
  InputGroup as InputGroup6,
  InputLeftAddon as InputLeftAddon3
} from "@chakra-ui/react";
import { toCurrencySymbol } from "@sequencehq/core-models";
import {
  GreyGrey0 as GreyGrey03,
  IndigoIndigo50 as IndigoIndigo507,
  Lato14Regular as Lato14Regular5
} from "@sequencehq/design-tokens";
import { jsx as jsx101, jsxs as jsxs70 } from "react/jsx-runtime";
var PriceInput = ({
  onChange,
  onBlur,
  value,
  isDisabled,
  isInvalid,
  width = "100%",
  variant = "standard",
  "data-testid": testId,
  ...props
}) => {
  return /* @__PURE__ */ jsxs70(
    InputGroup6,
    {
      ...variant === "table" ? {} : {
        boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)"
      },
      width,
      borderRadius: "6px",
      children: [
        /* @__PURE__ */ jsx101(
          InputLeftAddon3,
          {
            ...variant === "table" ? {
              ...Lato14Regular5,
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              borderRadius: "0",
              height: "40px",
              padding: "0 4px 0 12px",
              width: "fit-content",
              display: "flex",
              alignContent: "center"
            } : {
              backgroundColor: GreyGrey03
            },
            ...isDisabled ? { opacity: 0.4 } : {},
            children: toCurrencySymbol(props.currency)
          }
        ),
        /* @__PURE__ */ jsx101(
          ChakraNumberInput3,
          {
            precision: props.precision,
            value,
            clampValueOnBlur: Boolean(props.precision),
            isDisabled,
            onChange: (valueAsString, valueAsNumber) => {
              onChange(valueAsString, valueAsNumber);
            },
            onBlur,
            isInvalid,
            width: "100%",
            children: /* @__PURE__ */ jsx101(
              NumberInputField3,
              {
                ...Lato14Regular5,
                height: "40px",
                width: "100%",
                position: "relative",
                zIndex: 1,
                borderTopLeftRadius: "0",
                borderBottomLeftRadius: "0",
                "data-testid": testId,
                _focus: {
                  outline: `2px solid ${IndigoIndigo507}`,
                  outlineOffset: "-1px",
                  boxShadow: `0px 1px 3px 0px rgba(20, 23, 28, 0.06)`
                },
                ...variant === "table" ? {
                  paddingLeft: "4px",
                  paddingRight: "4px"
                } : {},
                ...props
              }
            )
          }
        )
      ]
    }
  );
};

// src/forms/inputs/SelectInput/CustomSelect.tsx
import { useSelect } from "downshift";
import {
  useMultiStyleConfig as useMultiStyleConfig3,
  Box as Box22,
  Popover as Popover7,
  PopoverAnchor as PopoverAnchor3,
  PopoverContent as PopoverContent9,
  PopoverBody as PopoverBody5,
  Portal as Portal6,
  Grid,
  GridItem,
  Icon as Icon7,
  useOutsideClick as useOutsideClick3
} from "@chakra-ui/react";
import {
  useMemo as useMemo7,
  useRef as useRef14,
  useState as useState18
} from "react";
import { errorBoxShadow } from "@sequencehq/core-theme";
import {
  GreyGrey40 as GreyGrey409,
  IndigoIndigo50 as IndigoIndigo5012,
  GreyGrey50 as GreyGrey505,
  RedRed60 as RedRed602,
  GreyGrey20 as GreyGrey206,
  Lato13Regular as Lato13Regular17,
  GreyGrey90 as GreyGrey908,
  GreyGrey70 as GreyGrey705,
  GreyGrey60 as GreyGrey6015
} from "@sequencehq/design-tokens";

// src/forms/inputs/SelectInput/VirtualList/ListButton.tsx
import { Button as Button6, Flex as Flex19, Icon as Icon4, useMultiStyleConfig } from "@chakra-ui/react";
import { standardHoverBackground } from "@sequencehq/core-theme";
import {
  GreyGrey90 as GreyGrey907,
  IndigoIndigo50 as IndigoIndigo508,
  Lato13Bold as Lato13Bold8,
  Lato14Bold as Lato14Bold3
} from "@sequencehq/design-tokens";
import { jsx as jsx102, jsxs as jsxs71 } from "react/jsx-runtime";
var ListButton = ({
  label,
  leftIcon,
  onClick,
  px,
  variant
}) => {
  const styles = useMultiStyleConfig("CustomSelect");
  return /* @__PURE__ */ jsx102(
    Button6,
    {
      __css: styles.item,
      onClick,
      color: variant === "new" ? IndigoIndigo508 : GreyGrey907,
      display: "flex",
      px: px ? px : 4,
      py: 2,
      alignItems: "center",
      _hover: {
        bg: standardHoverBackground,
        cursor: "pointer"
      },
      ...variant === "new" ? {
        ...Lato13Bold8
      } : {
        ...Lato14Bold3
      },
      children: /* @__PURE__ */ jsxs71(Flex19, { gap: "8px", children: [
        leftIcon && /* @__PURE__ */ jsx102(Icon4, { as: leftIcon, height: "16px", width: "16px" }),
        label
      ] })
    }
  );
};

// src/forms/inputs/SelectInput/VirtualList/ListContent.tsx
import { HStack as HStack2, Box as Box18 } from "@chakra-ui/react";
import {
  GreyGrey60 as GreyGrey6013,
  IndigoIndigo50 as IndigoIndigo509,
  Lato13Regular as Lato13Regular15,
  Lato14Regular as Lato14Regular6
} from "@sequencehq/design-tokens";
import { jsx as jsx103, jsxs as jsxs72 } from "react/jsx-runtime";
var ListContent = (props) => {
  const { title, subTitle, icon, tag, px, selectedValue, variant } = props;
  return /* @__PURE__ */ jsxs72(
    HStack2,
    {
      spacing: 2,
      px: px ? px : 4,
      py: variant === "new" ? "8px" : 3,
      width: "100%",
      _hover: {
        cursor: "pointer"
      },
      children: [
        icon,
        /* @__PURE__ */ jsx103(
          Box18,
          {
            textOverflow: "ellipsis",
            overflow: "hidden",
            color: selectedValue ? IndigoIndigo509 : void 0,
            ...props.variant === "new" ? Lato13Regular15 : Lato14Regular6,
            children: title
          }
        ),
        /* @__PURE__ */ jsx103(Box18, { color: GreyGrey6013, children: subTitle }),
        tag
      ]
    }
  );
};

// src/forms/inputs/SelectInput/VirtualList/ListDivider.tsx
import { internalBorder } from "@sequencehq/core-theme";
import { Flex as Flex20 } from "@chakra-ui/react";
import { jsx as jsx104 } from "react/jsx-runtime";
var ListDivider = () => /* @__PURE__ */ jsx104(Flex20, { borderBottom: internalBorder, marginBottom: "4px", width: "100%" });

// src/forms/inputs/SelectInput/VirtualList/ListTitle.tsx
import { Box as Box19, useMultiStyleConfig as useMultiStyleConfig2 } from "@chakra-ui/react";
import {
  GreyGrey80 as GreyGrey806,
  Lato13Regular as Lato13Regular16,
  Lato13Bold as Lato13Bold9,
  GreyGrey60 as GreyGrey6014
} from "@sequencehq/design-tokens";
import { jsx as jsx105 } from "react/jsx-runtime";
var ListTitle = ({ title, px, variant }) => {
  const styles = useMultiStyleConfig2("CustomSelect");
  return /* @__PURE__ */ jsx105(
    Box19,
    {
      ...styles.groupTitle,
      px: px ? px : 4,
      pt: 3,
      pb: 2,
      pl: variant === "new" ? "8px" : "auto",
      backgroundColor: "white",
      color: variant === "new" ? GreyGrey806 : GreyGrey6014,
      ...variant === "new" ? Lato13Bold9 : Lato13Regular16,
      children: title
    }
  );
};

// src/forms/inputs/SelectInput/VirtualList/VirtualListWithActions.tsx
import { Box as Box20, Icon as Icon5, List, ListItem } from "@chakra-ui/react";
import { standardHoverBackground as standardHoverBackground2 } from "@sequencehq/core-theme";
import { useVirtualizer } from "@tanstack/react-virtual";

// src/forms/inputs/SelectInput/VirtualList/tokenToOption.tsx
import { match as match5 } from "ts-pattern";
import { jsx as jsx106 } from "react/jsx-runtime";
var tokenToOption = (variant = "default") => ({
  px,
  token,
  itemToContent,
  selectedValue
}) => {
  return match5(token).with({ type: "model" }, ({ model }) => /* @__PURE__ */ jsx106(
    ListContent,
    {
      ...itemToContent(model),
      px,
      selectedValue,
      variant
    }
  )).with({ type: "button" }, ({ label, leftIcon, onClick }) => /* @__PURE__ */ jsx106(
    ListButton,
    {
      label,
      leftIcon,
      onClick,
      px,
      variant
    }
  )).with({ type: "divider" }, () => /* @__PURE__ */ jsx106(ListDivider, {})).with({ type: "title" }, ({ title }) => /* @__PURE__ */ jsx106(ListTitle, { title, px, variant })).exhaustive();
};
var basicTokenToOption = (variant = "default") => ({
  px,
  token,
  itemToContent,
  selectedValue
}) => /* @__PURE__ */ jsx106(
  ListContent,
  {
    ...itemToContent(token.model),
    px,
    selectedValue,
    variant
  }
);

// src/forms/inputs/SelectInput/VirtualList/VirtualListWithActions.tsx
import { match as match6 } from "ts-pattern";
import { IndigoIndigo50 as IndigoIndigo5010 } from "@sequencehq/design-tokens";
import CheckIcon2 from "@heroicons/react/24/outline/CheckIcon";
import { jsx as jsx107, jsxs as jsxs73 } from "react/jsx-runtime";
var VirtualListWithActions = ({
  parentRef,
  menuProps,
  tokens,
  getItemProps,
  highlightedIndex,
  itemToContent,
  styles,
  tokenIsDisabled,
  px,
  getSizeByType: getSizeByType3,
  variant,
  tokenToOption: tokenToOption3 = tokenToOption(variant),
  currentValueIndex
}) => {
  const { header, body, footer } = groupTokensByLocation(tokens);
  const rowVirtualizer = useVirtualizer({
    count: body.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => getSizeByType3(body[index]),
    overscan: 5,
    getItemKey: getItemKey(body)
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
  return /* @__PURE__ */ jsx107(Box20, { ...menuProps, margin: "8px", children: /* @__PURE__ */ jsxs73(Box20, { children: [
    /* @__PURE__ */ jsx107(List, { children: header.map((token, index) => /* @__PURE__ */ jsx107(
      TokenItem,
      {
        "data-testid": token.dataTestId,
        token,
        tokenIsDisabled,
        highlightedIndex,
        getItemProps,
        itemToContent,
        styles,
        px,
        getSizeByType: getSizeByType3,
        tokenToOption: tokenToOption3
      },
      getItemKey(header)(index)
    )) }),
    /* @__PURE__ */ jsx107(
      Box20,
      {
        ref: parentRef,
        overflowY: "auto",
        maxHeight: "200px",
        position: "relative",
        children: /* @__PURE__ */ jsxs73(List, { height: `${rowVirtualizer.getTotalSize()}px`, children: [
          paddingTop > 0 && /* @__PURE__ */ jsx107("li", { style: { height: `${paddingTop}px` } }),
          virtualRows.map(({ key, index, size }) => /* @__PURE__ */ jsx107(
            TokenItem,
            {
              token: body[index],
              tokenIsDisabled,
              highlightedIndex,
              getItemProps,
              itemToContent,
              styles,
              size,
              px,
              getSizeByType: getSizeByType3,
              tokenToOption: tokenToOption3,
              currentValueIndex,
              "data-testid": body[index].dataTestId
            },
            key
          )),
          paddingBottom > 0 && /* @__PURE__ */ jsx107(
            "li",
            {
              style: { height: `${paddingBottom}px`, marginBottom: "8px" }
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx107(List, { children: footer.map((token, index) => /* @__PURE__ */ jsx107(
      TokenItem,
      {
        token,
        "data-testid": token.dataTestId,
        tokenIsDisabled,
        highlightedIndex,
        getItemProps,
        itemToContent,
        styles,
        px,
        getSizeByType: getSizeByType3,
        tokenToOption: tokenToOption3
      },
      getItemKey(footer)(index)
    )) })
  ] }) });
};
var TokenItem = ({
  token,
  tokenIsDisabled,
  highlightedIndex,
  getItemProps,
  itemToContent,
  styles,
  px,
  tokenToOption: tokenToOption3,
  currentValueIndex,
  "data-testid": dataTestId
}) => {
  const disabled = (token.type === "divider" || token.type === "title" || tokenIsDisabled?.(token)) ?? false;
  const highLighted = highlightedIndex === token.tokenIndex;
  const selectedValue = currentValueIndex === token.tokenIndex;
  const { onClick, ...itemProps } = getItemProps({
    item: token,
    index: token.tokenIndex,
    disabled
  });
  return /* @__PURE__ */ jsxs73(
    ListItem,
    {
      display: selectedValue ? "flex" : void 0,
      justifyContent: selectedValue ? "space-between" : void 0,
      opacity: disabled && !["divider", "title"].includes(token.type) ? 0.4 : 1,
      ...itemProps,
      onClick,
      ...styles.item,
      backgroundColor: highLighted ? standardHoverBackground2 : "white",
      borderRadius: token.type !== "divider" ? "6px" : "0",
      color: selectedValue ? IndigoIndigo5010 : void 0,
      "data-testid": dataTestId ?? `select.option.${token.model?.id}`,
      children: [
        tokenToOption3({ token, itemToContent, px, selectedValue }),
        selectedValue ? /* @__PURE__ */ jsx107(Box20, { paddingRight: "16px", children: /* @__PURE__ */ jsx107(
          Icon5,
          {
            as: CheckIcon2,
            width: "16px",
            height: "16px",
            color: IndigoIndigo5010
          }
        ) }) : null
      ]
    }
  );
};
var groupTokensByLocation = (tokens) => {
  return tokens.reduce(
    (acc, item, tokenIndex) => {
      return match6(item.location).with("header", () => {
        acc.header.push({ ...item, tokenIndex });
        return acc;
      }).with("body", () => {
        acc.body.push({ ...item, tokenIndex });
        return acc;
      }).with("footer", () => {
        acc.footer.push({ ...item, tokenIndex });
        return acc;
      }).exhaustive();
    },
    {
      header: [],
      body: [],
      footer: []
    }
  );
};
var getItemKey = (tokens) => (index) => {
  const key = match6(tokens[index]).with({ type: "model" }, (matched) => matched.model.id).with({ type: "divider" }, () => "divider").with({ type: "button" }, (matched) => matched.key).with({ type: "title" }, (matched) => matched.id ?? matched.title).exhaustive();
  return key;
};

// src/forms/inputs/SelectInput/VirtualList/VirtualList.tsx
import { Box as Box21, Icon as Icon6, List as List2, ListItem as ListItem2 } from "@chakra-ui/react";
import { standardHoverBackground as standardHoverBackground3 } from "@sequencehq/core-theme";
import { useVirtualizer as useVirtualizer2 } from "@tanstack/react-virtual";
import { IndigoIndigo50 as IndigoIndigo5011 } from "@sequencehq/design-tokens";
import CheckIcon3 from "@heroicons/react/24/outline/CheckIcon";
import { jsx as jsx108, jsxs as jsxs74 } from "react/jsx-runtime";
var VirtualList = ({
  parentRef,
  menuProps,
  tokens,
  getItemProps,
  highlightedIndex,
  itemToContent,
  styles,
  px,
  tokenSize,
  variant,
  tokenToOption: tokenToOption3 = basicTokenToOption(variant),
  currentValueIndex,
  baseDataTestId
}) => {
  const indexedTokens = indexTokens(tokens);
  const rowVirtualizer = useVirtualizer2({
    count: indexedTokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => tokenSize,
    overscan: 5,
    getItemKey: getItemKey2(indexedTokens)
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
  return /* @__PURE__ */ jsx108(Box21, { ...menuProps, margin: "8px", children: /* @__PURE__ */ jsx108(Box21, { children: /* @__PURE__ */ jsx108(
    Box21,
    {
      ref: parentRef,
      overflowY: "auto",
      maxHeight: "200px",
      position: "relative",
      children: /* @__PURE__ */ jsxs74(
        List2,
        {
          height: `${virtualRows.length > 1 ? rowVirtualizer.getTotalSize() + 12 : rowVirtualizer.getTotalSize()}px`,
          children: [
            paddingTop > 0 && /* @__PURE__ */ jsx108("li", { style: { height: `${paddingTop}px` } }),
            virtualRows.map(({ key, index, size }) => /* @__PURE__ */ jsx108(
              TokenItem2,
              {
                token: indexedTokens[index],
                highlightedIndex,
                getItemProps,
                itemToContent,
                styles,
                size,
                px,
                tokenSize,
                tokenToOption: tokenToOption3,
                currentValueIndex,
                parentDataTestId: baseDataTestId
              },
              key
            )),
            paddingBottom > 0 && /* @__PURE__ */ jsx108(
              "li",
              {
                style: { height: `${paddingBottom}px`, marginBottom: "8px" }
              }
            )
          ]
        }
      )
    }
  ) }) });
};
var TokenItem2 = ({
  token,
  highlightedIndex,
  getItemProps,
  itemToContent,
  styles,
  size,
  px,
  tokenSize,
  tokenToOption: tokenToOption3,
  currentValueIndex,
  parentDataTestId
}) => {
  const disabled = token.disabled ?? false;
  const highLighted = highlightedIndex === token.tokenIndex;
  const selectedValue = currentValueIndex === token.tokenIndex;
  const dataTestId = parentDataTestId ? `${parentDataTestId}.option.${token.model.id}` : "";
  return /* @__PURE__ */ jsxs74(
    ListItem2,
    {
      display: selectedValue ? "flex" : void 0,
      justifyContent: selectedValue ? "space-between" : void 0,
      height: `${size ?? tokenSize}px`,
      opacity: disabled ? 0.4 : 1,
      ...getItemProps({
        item: token,
        index: token.tokenIndex,
        disabled
      }),
      ...styles.item,
      backgroundColor: highLighted ? standardHoverBackground3 : "white",
      borderRadius: "0",
      color: selectedValue ? IndigoIndigo5011 : void 0,
      overflow: selectedValue ? "hidden" : void 0,
      _notLast: {
        marginBottom: "4px"
      },
      "data-testid": dataTestId,
      children: [
        tokenToOption3({ token, itemToContent, px, selectedValue }),
        selectedValue ? /* @__PURE__ */ jsx108(Box21, { paddingRight: "16px", children: /* @__PURE__ */ jsx108(
          Icon6,
          {
            as: CheckIcon3,
            width: "16px",
            height: "16px",
            color: IndigoIndigo5011
          }
        ) }) : null
      ]
    }
  );
};
var indexTokens = (tokens) => {
  return tokens.reduce((acc, item, tokenIndex) => {
    return [
      ...acc,
      {
        ...item,
        tokenIndex
      }
    ];
  }, []);
};
var getItemKey2 = (tokens) => (index) => tokens[index].model.id;

// src/forms/inputs/SelectInput/selectInput.utils.ts
var modelToToken = (model) => ({
  type: "model",
  location: "body",
  model
});
var modelToBasicToken = (model) => ({
  model,
  disabled: model.disabled
});

// src/forms/inputs/SelectInput/CustomSelect.tsx
import ChevronDownIcon5 from "@heroicons/react/16/solid/ChevronDownIcon";
import ChevronUpIcon3 from "@heroicons/react/16/solid/ChevronUpIcon";
import { jsx as jsx109, jsxs as jsxs75 } from "react/jsx-runtime";
var CustomSelect = ({
  placeholder,
  items,
  itemToString,
  itemToContent,
  disabled,
  rightIcon,
  onChange,
  width,
  height,
  isInvalid,
  postChange,
  value,
  popoverRef,
  inputRef,
  sx,
  variant = "default",
  inputStyle = {},
  "data-testid": dataTestId
}) => {
  const supplementedItems = items.map(modelToBasicToken);
  const actualModel = useMemo7(() => {
    return items.find((item) => item.id === value || item?.value === value) ?? null;
  }, [items, value]);
  const {
    isOpen = true,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect({
    items: supplementedItems,
    itemToString: (token) => {
      return token ? itemToString(token.model) : "";
    },
    onSelectedItemChange: ({ selectedItem: selectedValue }) => {
      onChange(selectedValue ? selectedValue.model.id : "");
      postChange?.();
    },
    selectedItem: actualModel ? modelToBasicToken(actualModel) : null
  });
  const [focused, setFocused] = useState18(false);
  const currentValueIndex = supplementedItems.findIndex(
    (item) => item.model?.id === selectedItem?.model?.id
  );
  const parentRef = useRef14(null);
  const controlRef = useRef14(null);
  const styles = useMultiStyleConfig3("CustomSelect");
  const toggleButtonProps = getToggleButtonProps({
    disabled,
    //@ts-expect-error The 'button' ref type is super weird from the perspective of the outside, so we ignore the error here (and expect div)
    ref: inputRef
  });
  const variantStyles = useMemo7(() => {
    switch (variant) {
      case "pill":
        return {
          backgroundColor: GreyGrey206,
          border: "none",
          borderColor: "none",
          boxShadow: "none",
          fontSize: "14px",
          fontWeight: "semibold"
        };
      case "default":
        return {
          height: height ?? "40px",
          border: `1px solid ${GreyGrey409}`,
          boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px -2px 2px 0px rgba(0, 0, 0, 0.04) inset"
        };
      case "new":
        return {
          ...Lato13Regular17,
          color: GreyGrey908,
          height: height ?? "32px"
        };
      default:
        return {};
    }
  }, [variant, height]);
  useOutsideClick3({
    ref: controlRef,
    handler: () => {
      setFocused(false);
    }
  });
  return /* @__PURE__ */ jsxs75(Popover7, { isOpen: isOpen || focused, autoFocus: false, variant: "downshift", children: [
    /* @__PURE__ */ jsx109(
      Box22,
      {
        ref: controlRef,
        width,
        position: "relative",
        cursor: "pointer",
        sx,
        children: /* @__PURE__ */ jsx109(PopoverAnchor3, { children: /* @__PURE__ */ jsxs75(
          Grid,
          {
            alignItems: "center",
            "data-testid": dataTestId,
            position: "relative",
            outline: "none",
            ...styles.toggleButton,
            ...isInvalid && {
              borderColor: RedRed602,
              boxShadow: errorBoxShadow,
              outline: `2px solid ${RedRed602}`
            },
            _hover: {
              border: `1px solid ${GreyGrey505}`
            },
            _focus: {
              outline: `2px solid ${IndigoIndigo5012}`,
              outlineOffset: "-1px"
            },
            _disabled: {
              cursor: "not-allowed",
              opacity: 0.4
            },
            outlineOffset: "-1px",
            transition: "outline 0.2s ease-in-out",
            borderRadius: "6px",
            ...toggleButtonProps,
            onFocus: () => {
              setFocused(true);
            },
            tabIndex: disabled ? void 0 : 0,
            py: "8px",
            px: "12px",
            overflow: "hidden",
            ...variantStyles,
            ...inputStyle,
            templateColumns: "auto 1fr auto",
            width: "100%",
            alignContent: "center",
            children: [
              /* @__PURE__ */ jsx109(GridItem, { children: rightIcon }),
              /* @__PURE__ */ jsx109(
                Box22,
                {
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflowX: "hidden",
                  overflowY: "hidden",
                  paddingRight: "16px",
                  ...!selectedItem ? { color: GreyGrey6015 } : {},
                  children: selectedItem ? itemToString(selectedItem.model) : placeholder
                }
              ),
              /* @__PURE__ */ jsx109(
                Icon7,
                {
                  as: isOpen || focused ? ChevronUpIcon3 : ChevronDownIcon5,
                  color: GreyGrey705,
                  height: "16px",
                  width: "16px"
                }
              )
            ]
          }
        ) })
      }
    ),
    /* @__PURE__ */ jsx109(Portal6, { children: /* @__PURE__ */ jsx109(
      PopoverContent9,
      {
        minW: "200",
        width: controlRef.current?.offsetWidth,
        padding: "0",
        children: /* @__PURE__ */ jsx109(PopoverBody5, { ref: popoverRef, overflow: "hidden", borderRadius: "6px", children: /* @__PURE__ */ jsx109(
          VirtualList,
          {
            parentRef,
            menuProps: {
              ...getMenuProps({}, { suppressRefError: true })
            },
            tokens: supplementedItems,
            getItemProps,
            highlightedIndex,
            itemToContent,
            styles,
            tokenSize: 40,
            currentValueIndex,
            variant: ["new", "default"].includes(variant) ? variant : "default",
            baseDataTestId: dataTestId
          }
        ) })
      }
    ) })
  ] });
};

// src/forms/inputs/SelectInput/SelectInput.tsx
import { jsx as jsx110 } from "react/jsx-runtime";
var SelectInput = ({
  options,
  ...props
}) => {
  return /* @__PURE__ */ jsx110(
    CustomSelect,
    {
      popoverRef: props.popoverRef,
      "data-testid": props["data-testid"],
      inputRef: props.inputRef,
      items: options.map((option) => ({
        value: option.value,
        label: option.label,
        id: option.value,
        disabled: "disabled" in option ? !!option.disabled : false
      })),
      itemToString: (item) => item?.label ?? "",
      placeholder: props.placeholder,
      itemToContent: (model) => ({
        title: model?.label
      }),
      value: props.value,
      onChange: props.onChange,
      isInvalid: props.isInvalid,
      disabled: props.isDisabled,
      width: props.width ?? "100%",
      height: props.height,
      sx: props.sx,
      variant: props.variant,
      inputStyle: props.inputStyle ?? {}
    }
  );
};

// src/forms/inputs/SelectInput/CustomSelectWithActions.tsx
import { useSelect as useSelect2 } from "downshift";
import {
  useMultiStyleConfig as useMultiStyleConfig4,
  Box as Box23,
  Popover as Popover8,
  PopoverAnchor as PopoverAnchor4,
  PopoverContent as PopoverContent10,
  PopoverBody as PopoverBody6,
  Flex as Flex21,
  Portal as Portal7,
  Icon as Icon8,
  Grid as Grid2,
  GridItem as GridItem2
} from "@chakra-ui/react";
import {
  useMemo as useMemo8,
  useRef as useRef15,
  useState as useState19
} from "react";
import { errorBoxShadow as errorBoxShadow2 } from "@sequencehq/core-theme";
import {
  GreyGrey40 as GreyGrey4010,
  GreyGrey60 as GreyGrey6016,
  IndigoIndigo50 as IndigoIndigo5013,
  GreyGrey50 as GreyGrey506,
  RedRed60 as RedRed603,
  Lato13Regular as Lato13Regular18,
  GreyGrey90 as GreyGrey909,
  GreyGrey70 as GreyGrey706
} from "@sequencehq/design-tokens";
import { match as match7 } from "ts-pattern";
import ChevronDownIcon6 from "@heroicons/react/16/solid/ChevronDownIcon";
import ChevronUpIcon4 from "@heroicons/react/16/solid/ChevronUpIcon";
import PlusIcon5 from "@heroicons/react/16/solid/PlusIcon";
import { jsx as jsx111, jsxs as jsxs76 } from "react/jsx-runtime";
var CustomSelectWithActions = ({
  placeholder,
  items,
  itemToString,
  itemToContent,
  tokenIsDisabled,
  disabled,
  rightIcon,
  onChange,
  width,
  height,
  isInvalid,
  addNewAction,
  postChange,
  model,
  value,
  modelLookup,
  popoverRef,
  inputRef,
  variant,
  "data-testid": dataTestId
}) => {
  const supplementedItems = items.map(modelToToken);
  if (addNewAction) {
    if (items.length > 0) {
      supplementedItems.push({ type: "divider", location: "footer" });
    }
    supplementedItems.push({
      key: "add-new-button",
      type: "button",
      leftIcon: PlusIcon5,
      label: "Create new",
      location: "footer",
      onClick: addNewAction
    });
  }
  const actualModel = useMemo8(() => {
    if (model) {
      return model;
    }
    if (modelLookup) {
      modelLookup(value);
    }
    return items.find((item) => item.id === value || item?.value === value) ?? null;
  }, [items, model, modelLookup, value]);
  model === void 0 && modelLookup ? modelLookup(value) : model ?? null;
  const {
    isOpen = true,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect2({
    items: supplementedItems,
    itemToString: (token) => {
      return token?.type === "model" ? itemToString(token.model) : "";
    },
    onSelectedItemChange: ({ selectedItem: selectedValue }) => {
      if (!selectedValue || selectedValue?.type === "model") {
        onChange(selectedValue?.type === "model" ? selectedValue.model.id : "");
        postChange?.();
      }
    },
    selectedItem: actualModel ? modelToToken(actualModel) : null
  });
  const [focused, setFocused] = useState19(false);
  const currentValueIndex = supplementedItems.findIndex(
    (item) => item.model?.id === selectedItem?.model?.id
  );
  const parentRef = useRef15(null);
  const controlRef = useRef15(null);
  const styles = useMultiStyleConfig4("CustomSelect");
  const toggleButtonProps = getToggleButtonProps({
    disabled,
    //@ts-expect-error The 'button' ref type is super weird from the perspective of the outside, so we ignore the error here (and expect div)
    ref: inputRef
  });
  return /* @__PURE__ */ jsx111(Popover8, { isOpen: isOpen || focused, autoFocus: false, variant: "downshift", children: /* @__PURE__ */ jsxs76(Box23, { ref: controlRef, width, position: "relative", cursor: "pointer", children: [
    /* @__PURE__ */ jsx111(PopoverAnchor4, { children: /* @__PURE__ */ jsxs76(
      Grid2,
      {
        alignItems: "center",
        "data-testid": dataTestId,
        position: "relative",
        outline: "none",
        ...styles.toggleButton,
        ...isInvalid && {
          borderColor: RedRed603,
          boxShadow: errorBoxShadow2,
          outline: `2px solid ${RedRed603}`
        },
        _hover: {
          border: `1px solid ${GreyGrey506}`
        },
        _focus: {
          outline: `2px solid ${IndigoIndigo5013}`,
          outlineOffset: "-1px"
        },
        _disabled: {
          cursor: "not-allowed",
          opacity: 0.4
        },
        outlineOffset: "-1px",
        transition: "outline 0.2s ease-in-out",
        border: `1px solid ${GreyGrey4010}`,
        boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px -2px 2px 0px rgba(0, 0, 0, 0.04) inset",
        borderRadius: "6px",
        ...toggleButtonProps,
        onBlur: () => {
          setFocused(false);
        },
        onFocus: () => {
          setFocused(true);
        },
        tabIndex: disabled ? void 0 : 0,
        py: "8px",
        px: "12px",
        ...variant === "new" ? { height: height ?? "32px" } : { height: height ?? "40px" },
        templateColumns: "auto 1fr auto",
        width: "100%",
        alignContent: "center",
        children: [
          /* @__PURE__ */ jsx111(GridItem2, { children: rightIcon }),
          /* @__PURE__ */ jsx111(
            Flex21,
            {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflowX: "hidden",
              overflowY: "hidden",
              paddingRight: "16px",
              alignItems: "center",
              ...variant === "new" ? { ...Lato13Regular18, color: GreyGrey909, height: "32px" } : {},
              ...selectedItem?.type !== "model" ? { color: GreyGrey6016 } : {},
              children: selectedItem?.type === "model" ? itemToString(selectedItem.model) : placeholder
            }
          ),
          /* @__PURE__ */ jsx111(
            Icon8,
            {
              as: isOpen || focused ? ChevronUpIcon4 : ChevronDownIcon6,
              color: GreyGrey706,
              height: "16px",
              width: "16px"
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx111(Portal7, { children: /* @__PURE__ */ jsx111(PopoverContent10, { width: controlRef.current?.offsetWidth, padding: "0", children: /* @__PURE__ */ jsx111(PopoverBody6, { ref: popoverRef, overflow: "hidden", borderRadius: "6px", children: /* @__PURE__ */ jsx111(
      VirtualListWithActions,
      {
        parentRef,
        menuProps: {
          ...getMenuProps({}, { suppressRefError: true })
        },
        tokens: supplementedItems,
        getItemProps,
        highlightedIndex,
        itemToContent,
        styles,
        tokenIsDisabled,
        getSizeByType,
        currentValueIndex,
        variant: "new"
      }
    ) }) }) })
  ] }) });
};
var getSizeByType = (item) => {
  return match7(item.type).with("divider", () => 17).otherwise(() => 40);
};

// src/forms/inputs/SelectInput/SelectInputWithActions.tsx
import { jsx as jsx112 } from "react/jsx-runtime";
var SelectInputWithActions = ({
  options,
  ...props
}) => {
  return /* @__PURE__ */ jsx112(
    CustomSelectWithActions,
    {
      popoverRef: props.popoverRef,
      "data-testid": props["data-testid"],
      inputRef: props.inputRef,
      items: options.map((option) => ({ ...option, id: option.value })),
      itemToString: (item) => item?.label ?? "",
      placeholder: props.placeholder,
      itemToContent: (model) => ({
        title: model?.label
      }),
      value: props.value,
      onChange: props.onChange,
      addNewAction: props.onAddNew,
      isInvalid: props.isInvalid,
      disabled: props.isDisabled,
      width: props.width ?? "100%",
      height: props.height,
      tokenIsDisabled: props.tokenIsDisabled,
      variant: props.variant
    }
  );
};

// src/forms/inputs/SelectInput/CustomSelectSearch.tsx
import { useSelect as useSelect3 } from "downshift";
import {
  useMultiStyleConfig as useMultiStyleConfig5,
  Box as Box24,
  Popover as Popover9,
  PopoverAnchor as PopoverAnchor5,
  PopoverContent as PopoverContent11,
  PopoverBody as PopoverBody7,
  Portal as Portal8,
  Input as Input3,
  InputGroup as InputGroup7,
  InputRightElement
} from "@chakra-ui/react";
import { useEffect as useEffect13, useMemo as useMemo9, useRef as useRef16, useState as useState20 } from "react";
import { ChevronDownIcon as ChevronDownIcon7, ChevronUpIcon as ChevronUpIcon5 } from "@chakra-ui/icons";
import { errorBoxShadow as errorBoxShadow3 } from "@sequencehq/core-theme";
import {
  GreyGrey40 as GreyGrey4011,
  IndigoIndigo50 as IndigoIndigo5014,
  GreyGrey50 as GreyGrey507,
  RedRed60 as RedRed604
} from "@sequencehq/design-tokens";
import { match as match8 } from "ts-pattern";
import { Plus } from "react-feather";
import { jsx as jsx113, jsxs as jsxs77 } from "react/jsx-runtime";
var isToggleButtonProps = (obj) => {
  return "onKeyDown" in obj;
};
var CustomSelectSearch = ({
  placeholder,
  items,
  itemToString,
  itemToContent,
  tokenIsDisabled,
  disabled,
  onChange,
  width,
  height = "40px",
  isInvalid,
  addNewAction,
  postChange,
  model,
  value,
  modelLookup,
  inputRef,
  onBlur,
  variant,
  "data-testid": dataTestId
}) => {
  const [query, setQuery] = useState20("");
  const filteredItems = items.filter(
    (item) => itemToString(item).toLowerCase().includes(query.toLowerCase())
  );
  const supplementedItems = filteredItems.flatMap((item) => {
    if (item.nestedOptions !== void 0) {
      const baseItem = {
        location: "body",
        type: "title",
        title: item.label ?? "",
        id: item.id
      };
      const nestedItems = item.nestedOptions?.map((option) => {
        if ("type" in option && option.type !== "token") {
          return {
            key: `add-new-${option.id}`,
            type: "button",
            leftIcon: Plus,
            dataTestId: option.dataTestId,
            label: option.label,
            location: "body",
            onClick: "onClick" in option && option.onClick ? option.onClick : () => {
            }
          };
        }
        return modelToToken(option);
      }) ?? [];
      return [baseItem, ...nestedItems];
    }
    return modelToToken(item);
  });
  if (addNewAction) {
    if (filteredItems.length) {
      supplementedItems.push({ type: "divider", location: "footer" });
    }
    supplementedItems.push({
      key: "add-new-button",
      type: "button",
      leftIcon: Plus,
      label: "Create new",
      location: "footer",
      onClick: addNewAction
    });
  }
  const actualModel = useMemo9(() => {
    if (model) {
      return model;
    }
    if (modelLookup) {
      modelLookup(value);
    }
    return items.find((item) => item.id === value || item?.value === value) ?? null;
  }, [items, model, modelLookup, value]);
  model === void 0 && modelLookup ? modelLookup(value) : model ?? null;
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect3({
    items: supplementedItems,
    itemToString: (token) => {
      return token?.type === "model" ? itemToString(token.model) : "";
    },
    onSelectedItemChange: ({ selectedItem: selectedValue }) => {
      if (selectedValue && selectedValue?.type === "model") {
        onChange(selectedValue?.type === "model" ? selectedValue.model.id : "");
        postChange?.();
      }
    },
    selectedItem: actualModel ? modelToToken(actualModel) : null,
    stateReducer: (state, { changes, type }) => {
      if (type === useSelect3.stateChangeTypes.ToggleButtonKeyDownCharacter) {
        return {
          ...state,
          ...changes,
          highlightedIndex: -1
        };
      }
      return {
        ...state,
        ...changes
      };
    }
  });
  const currentValueIndex = supplementedItems.findIndex(
    (item) => item.model?.id === selectedItem?.model?.id
  );
  const parentRef = useRef16(null);
  const controlRef = useRef16(null);
  const styles = useMultiStyleConfig5("CustomSelect");
  useEffect13(() => {
    if (!isOpen && controlRef.current && document.activeElement?.contains(controlRef.current)) {
      onBlur?.();
    }
  }, [onBlur, isOpen]);
  const toggleButtonProps = getToggleButtonProps({ disabled, ref: inputRef });
  useEffect13(() => {
    if (!parentRef.current) {
      return;
    }
    parentRef.current.scrollTo({ top: 0 });
  }, [parentRef.current, query]);
  useMemo9(() => {
    const initialValue = actualModel ? itemToString(actualModel) : "";
    setQuery(initialValue);
  }, [actualModel, itemToString]);
  return /* @__PURE__ */ jsx113(Popover9, { isOpen: Boolean(isOpen), autoFocus: false, variant: "downshift", children: /* @__PURE__ */ jsxs77(Box24, { ref: controlRef, width, position: "relative", cursor: "pointer", children: [
    /* @__PURE__ */ jsx113(PopoverAnchor5, { children: /* @__PURE__ */ jsxs77(InputGroup7, { children: [
      /* @__PURE__ */ jsx113(
        Input3,
        {
          "data-testid": dataTestId,
          placeholder,
          alignItems: "center",
          position: "relative",
          outline: "none",
          ...styles.toggleButton,
          ...isInvalid && {
            borderColor: RedRed604,
            boxShadow: errorBoxShadow3,
            outline: `2px solid ${RedRed604}`
          },
          _hover: {
            border: `1px solid ${GreyGrey507}`
          },
          _focus: {
            outline: `2px solid ${IndigoIndigo5014}`,
            outlineOffset: "-1px"
          },
          _disabled: {
            cursor: "not-allowed",
            opacity: 0.4
          },
          outlineOffset: "-1px",
          transition: "outline 0.2s ease-in-out",
          border: `1px solid ${GreyGrey4011}`,
          boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px -2px 2px 0px rgba(0, 0, 0, 0.04) inset",
          borderRadius: "6px",
          ...toggleButtonProps,
          tabIndex: disabled ? void 0 : 0,
          py: 3,
          height,
          value: query,
          onChange: (e) => {
            if (e.target.value === "") {
              onChange("");
            } else {
              setQuery(e.target.value);
            }
          },
          onKeyDown: (e) => {
            if (e.code === "Space") {
              return;
            }
            if (isToggleButtonProps(toggleButtonProps)) {
              toggleButtonProps.onKeyDown(e);
            }
          }
        }
      ),
      /* @__PURE__ */ jsx113(InputRightElement, { children: /* @__PURE__ */ jsx113(SelectIcon, { __css: styles.icon, children: isOpen ? /* @__PURE__ */ jsx113(ChevronUpIcon5, {}) : /* @__PURE__ */ jsx113(ChevronDownIcon7, {}) }) })
    ] }) }),
    /* @__PURE__ */ jsx113(Portal8, { children: /* @__PURE__ */ jsx113(PopoverContent11, { width: controlRef.current?.offsetWidth, padding: "0", children: /* @__PURE__ */ jsx113(PopoverBody7, { children: /* @__PURE__ */ jsx113(
      VirtualListWithActions,
      {
        parentRef,
        menuProps: { ...getMenuProps({}, { suppressRefError: true }) },
        tokens: supplementedItems,
        getItemProps,
        highlightedIndex,
        itemToContent,
        styles,
        tokenIsDisabled,
        getSizeByType: getSizeByType2,
        currentValueIndex,
        variant
      }
    ) }) }) })
  ] }) });
};
var getSizeByType2 = (item) => {
  return match8(item.type).with("divider", () => 17).otherwise(() => 40);
};

// src/forms/inputs/SelectInput/SearchSelectInput.tsx
import { jsx as jsx114 } from "react/jsx-runtime";
var SearchSelectInput = ({
  options,
  ...props
}) => {
  return /* @__PURE__ */ jsx114(
    CustomSelectSearch,
    {
      items: options.map((option) => ({ ...option, id: option.value })),
      itemToString: (item) => item?.label ?? "",
      placeholder: props.placeholder,
      itemToContent: (model) => ({
        title: model?.label
      }),
      "data-testid": props["data-testid"],
      value: props.value,
      onChange: props.onChange,
      addNewAction: props.onAddNew,
      isInvalid: props.isInvalid,
      disabled: props.isDisabled,
      width: props.width,
      height: props.height,
      inputRef: props.inputRef,
      onBlur: props.onBlur,
      variant: props.variant ?? "default"
    }
  );
};

// src/forms/inputs/TextAreaInput.tsx
import { Textarea } from "@chakra-ui/react";
import {
  GreyWhite as GreyWhite12,
  IndigoIndigo50 as IndigoIndigo5015,
  GreyGrey50 as GreyGrey508,
  Lato14Regular as Lato14Regular7,
  GreyGrey10 as GreyGrey105,
  GreyGrey30 as GreyGrey3011,
  RedRed50 as RedRed505,
  GreyGrey40 as GreyGrey4012,
  Lato13Regular as Lato13Regular19,
  ShadowXs as ShadowXs3
} from "@sequencehq/design-tokens";
import { jsx as jsx115 } from "react/jsx-runtime";
var TextAreaInput = (props) => /* @__PURE__ */ jsx115(
  Textarea,
  {
    ...Lato14Regular7,
    height: "40px",
    width: "100%",
    border: "1px solid var(--grey-grey-40, #D1D9E4)",
    boxShadow: "0px 1px 2px 0px rgba(20, 23, 28, 0.04)",
    borderRadius: "6px",
    backgroundColor: GreyWhite12,
    padding: "10px 12px",
    _hover: {
      border: `1px solid ${GreyGrey508}`,
      boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.07)"
    },
    _focus: {
      outline: `2px solid ${IndigoIndigo5015}`,
      outlineOffset: "-1px",
      boxShadow: `0px 1px 3px 0px rgba(20, 23, 28, 0.06)`
    },
    ...props,
    onChange: (e) => props.onChange(e.target.value)
  }
);
var disabledStyles3 = {
  backgroundColor: GreyGrey105,
  color: GreyGrey508,
  border: `1px solid ${GreyGrey3011}`,
  cursor: "not-allowed",
  boxShadow: "none"
};
var invalidStyles3 = {
  border: `1px solid ${RedRed505}`,
  boxShadow: "none"
};
var focusStyles3 = {
  border: `1px solid ${IndigoIndigo5015}`,
  boxShadow: "none"
};
var TextAreaFieldInput = ({
  fullWidth = true,
  isInvalid = false,
  isDisabled = false,
  prefix,
  onBlur: onBlurProps,
  onFocus: onFocusProps,
  autoFocus,
  ...props
}) => {
  const inputWidth = fullWidth ? "100%" : "unset";
  return /* @__PURE__ */ jsx115(
    Textarea,
    {
      px: "8px",
      width: inputWidth,
      isInvalid,
      isDisabled,
      borderRadius: "6px",
      boxShadow: ShadowXs3,
      border: `1px solid ${GreyGrey4012}`,
      backgroundColor: GreyWhite12,
      ...Lato13Regular19,
      ...props,
      _placeholder: { color: GreyGrey508 },
      _focus: focusStyles3,
      _invalid: invalidStyles3,
      _disabled: disabledStyles3
    }
  );
};

// src/forms/withFormField.tsx
import { Box as Box25, Flex as Flex22 } from "@chakra-ui/react";
import {
  GreyGrey70 as GreyGrey707,
  Lato13Regular as Lato13Regular20,
  Lato14Bold as Lato14Bold4,
  Lato14Regular as Lato14Regular8,
  RedRed60 as RedRed605
} from "@sequencehq/design-tokens";
import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";

// src/components/InfoPopover.tsx
import {
  IconButton as IconButton3,
  Popover as Popover10,
  PopoverBody as PopoverBody8,
  PopoverContent as PopoverContent12,
  PopoverHeader as PopoverHeader2,
  PopoverTrigger as PopoverTrigger5,
  Portal as Portal9
} from "@chakra-ui/react";
import { jsx as jsx116, jsxs as jsxs78 } from "react/jsx-runtime";
var InfoPopover = ({
  title,
  body,
  placement,
  padding,
  minimal,
  icon,
  variant = "fieldInfo"
}) => /* @__PURE__ */ jsxs78(Popover10, { placement: placement || "left", variant, trigger: "hover", children: [
  /* @__PURE__ */ jsx116(PopoverTrigger5, { children: /* @__PURE__ */ jsx116(
    IconButton3,
    {
      minWidth: minimal ? "16px" : "24px",
      height: minimal ? "16px" : "24px",
      "aria-label": "Show info",
      icon: icon || /* @__PURE__ */ jsx116(InfoPopoverIcon, {}),
      variant: "clear",
      padding: padding || "0px"
    }
  ) }),
  /* @__PURE__ */ jsx116(Portal9, { children: /* @__PURE__ */ jsxs78(PopoverContent12, { children: [
    title ? /* @__PURE__ */ jsx116(PopoverHeader2, { children: title }) : null,
    /* @__PURE__ */ jsx116(PopoverBody8, { pt: title ? "0" : "20px", children: body })
  ] }) })
] });

// src/forms/withFormField.tsx
import { jsx as jsx117, jsxs as jsxs79 } from "react/jsx-runtime";
var withFormFieldWrapper = (Component, options = {}) => ({
  validationErrors,
  label,
  description,
  styles,
  tooltip,
  fieldTooltip,
  infoPopover,
  showValidationMessage = true,
  ...rest
}) => {
  const component = (
    /* @ts-expect-error TODO: find a type for this, since it otherwise makes sense and works */
    /* @__PURE__ */ jsx117(Component, { isInvalid: Boolean(validationErrors?.length), ...rest })
  );
  return /* @__PURE__ */ jsxs79(
    Box25,
    {
      marginBottom: "16px",
      width: rest.width ?? options.defaultWidth ?? "100%",
      ...styles?.wrapper ?? {},
      children: [
        label && /* @__PURE__ */ jsxs79(
          Flex22,
          {
            "data-testid": "data-testid" in rest ? `${rest["data-testid"]}.label` : `select.label`,
            alignItems: "center",
            color: GreyGrey707,
            ...Lato14Bold4,
            height: "16px",
            marginBottom: "8px",
            ...styles?.label ?? {},
            gap: "4px",
            children: [
              label,
              infoPopover && /* @__PURE__ */ jsx117(InfoPopover, { ...infoPopover }),
              Boolean(tooltip) && /* @__PURE__ */ jsx117(TooltipLegacy, { title: tooltip?.title, content: tooltip?.content, children: /* @__PURE__ */ jsx117(InformationCircleIcon, { height: "20px" }) })
            ]
          }
        ),
        showValidationMessage && fieldTooltip ? /* @__PURE__ */ jsx117(
          Tooltip,
          {
            label: fieldTooltip.label,
            placement: fieldTooltip.placement,
            children: component
          }
        ) : component,
        validationErrors && validationErrors.length > 0 && /* @__PURE__ */ jsx117(
          Box25,
          {
            marginTop: "8px",
            ...Lato13Regular20,
            color: RedRed605,
            ...styles?.error ?? {},
            children: validationErrors[0].message
          },
          validationErrors[0].message
        ),
        description && /* @__PURE__ */ jsx117(Box25, { ...Lato14Regular8, marginTop: "8px", children: description })
      ]
    }
  );
};

// src/forms/inputs/RadioGroupInput.tsx
import {
  Box as Box26,
  Flex as Flex23,
  Radio,
  RadioGroup,
  Stack
} from "@chakra-ui/react";
import {
  GreyGrey70 as GreyGrey708,
  Lato14Bold as Lato14Bold5,
  Lato14Regular as Lato14Regular9
} from "@sequencehq/design-tokens";
import { Fragment as Fragment14, jsx as jsx118, jsxs as jsxs80 } from "react/jsx-runtime";
var RadioGroupInput = (props) => {
  return /* @__PURE__ */ jsx118(
    RadioGroup,
    {
      onChange: props.onChange,
      value: props.value,
      isDisabled: props.isDisabled,
      children: /* @__PURE__ */ jsx118(Stack, { children: props.options.map((option) => /* @__PURE__ */ jsx118(
        Radio,
        {
          isInvalid: props.isInvalid,
          value: option.value,
          isDisabled: option.disabled,
          children: /* @__PURE__ */ jsxs80(Flex23, { ...Lato14Regular9, gap: "4px", alignItems: "center", children: [
            /* @__PURE__ */ jsx118(Box26, { ...Lato14Bold5, children: option.label }),
            option.description && /* @__PURE__ */ jsxs80(Fragment14, { children: [
              /* @__PURE__ */ jsx118(Box26, { color: GreyGrey708, children: "-" }),
              /* @__PURE__ */ jsx118(Box26, { color: GreyGrey708, children: option.description })
            ] })
          ] })
        },
        option.value
      )) })
    }
  );
};

// src/forms/inputs/CheckboxGroupInput.tsx
import { Stack as Stack2 } from "@chakra-ui/react";
import { GreyGrey10 as GreyGrey106, RedRed60 as RedRed606 } from "@sequencehq/design-tokens";

// src/forms/inputs/CheckboxLineInput.tsx
import { Checkbox as Checkbox2, Flex as Flex24, Grid as Grid3, Box as Box27 } from "@chakra-ui/react";
import { GreyGrey80 as GreyGrey807 } from "@sequencehq/design-tokens";
import { jsx as jsx119, jsxs as jsxs81 } from "react/jsx-runtime";
var CheckboxLineInput = ({
  onChange,
  value,
  text,
  description,
  isInvalid,
  isDisabled,
  styles
}) => {
  return /* @__PURE__ */ jsxs81(
    Flex24,
    {
      alignItems: "center",
      onClick: () => !isDisabled && onChange(!value),
      width: "100%",
      children: [
        /* @__PURE__ */ jsx119(
          Flex24,
          {
            alignItems: "center",
            onClick: (e) => {
              e.stopPropagation();
            },
            children: /* @__PURE__ */ jsx119(
              Checkbox2,
              {
                isChecked: value,
                onChange: (e) => onChange(Boolean(e.target.checked)),
                isInvalid,
                isDisabled
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs81(
          Grid3,
          {
            templateColumns: "2fr auto",
            justifyContent: "space-between",
            alignContent: "center",
            columnGap: "24px",
            width: "100%",
            children: [
              /* @__PURE__ */ jsx119(
                Box27,
                {
                  cursor: "pointer",
                  ml: "8px",
                  color: GreyGrey807,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  ...styles?.label || {},
                  children: text
                }
              ),
              /* @__PURE__ */ jsx119(
                Box27,
                {
                  cursor: "pointer",
                  ml: "8px",
                  color: GreyGrey807,
                  textAlign: "right",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  ...styles?.description || {},
                  children: description
                }
              )
            ]
          }
        )
      ]
    }
  );
};

// src/forms/inputs/CheckboxGroupInput.tsx
import uniq from "lodash/fp/uniq";
import { jsx as jsx120 } from "react/jsx-runtime";
var CheckboxGroupInput = (props) => {
  const handleUpdate = (optionValue) => (newSelection) => {
    if (!newSelection) {
      return props.onChange(props.value.filter((v) => v !== optionValue));
    }
    return props.onChange(uniq([...props.value, optionValue]));
  };
  return /* @__PURE__ */ jsx120(
    Stack2,
    {
      padding: "12px 16px",
      borderRadius: "8px",
      backgroundColor: GreyGrey106,
      gap: "16px",
      outline: props.isInvalid ? `1px solid ${RedRed606}` : "none",
      children: props.options.map((opt) => /* @__PURE__ */ jsx120(
        CheckboxLineInput,
        {
          value: props.value.includes(opt.value),
          onChange: handleUpdate(opt.value),
          text: opt.label,
          isDisabled: props.isDisabled || opt.disabled,
          description: opt.description,
          styles: {
            description: {
              width: "220px",
              opacity: opt.disabled ? 0.4 : 1
            },
            label: {
              opacity: opt.disabled ? 0.4 : 1
            }
          }
        },
        opt.value
      ))
    }
  );
};

// src/forms/inputs/MultiTagInput.tsx
import {
  useEffect as useEffect14,
  useLayoutEffect as useLayoutEffect2,
  useRef as useRef17,
  useState as useState21
} from "react";
import {
  Input as ChakraInput,
  InputGroup as InputGroup8,
  usePrevious
} from "@chakra-ui/react";
import { borderColor } from "@sequencehq/core-theme";
import {
  IndigoIndigo50 as IndigoIndigo5016,
  Lato14Regular as Lato14Regular10,
  RedRed60 as RedRed607
} from "@sequencehq/design-tokens";
import { Fragment as Fragment15, jsx as jsx121, jsxs as jsxs82 } from "react/jsx-runtime";
var MultiTagInput = ({
  values,
  onChange,
  isInvalid,
  isDisabled,
  ...props
}) => {
  const inputRef = useRef17(null);
  const previous = usePrevious(values);
  const [containsFocus, setContainsFocus] = useState21(false);
  const [inputValue, setInputValue] = useState21("");
  useEffect14(() => {
    if (!previous || !containsFocus) {
      return;
    }
    if (values.length !== previous.length) {
      inputRef.current?.focus();
    }
  }, [values, previous, containsFocus]);
  return /* @__PURE__ */ jsx121(
    InputGroup8,
    {
      borderRadius: "6px",
      border: "1px solid",
      outline: "2px solid",
      borderColor: isInvalid ? "transparent" : borderColor,
      outlineColor: isInvalid ? RedRed607 : "transparent",
      _focusWithin: {
        outlineColor: IndigoIndigo5016,
        borderColor: "transparent"
      },
      display: "flex",
      alignItems: "center",
      position: "relative",
      tabIndex: isDisabled ? void 0 : 0,
      gap: "8px",
      px: "12px",
      py: 2,
      flexWrap: "wrap",
      onFocus: () => {
        inputRef.current?.focus();
        setContainsFocus(true);
      },
      onBlur: () => setContainsFocus(false),
      ...props,
      children: /* @__PURE__ */ jsxs82(Fragment15, { children: [
        values.map((value, index) => /* @__PURE__ */ jsx121(
          Badge,
          {
            sentiment: "monospaced",
            textTransform: "none",
            size: "md",
            onDelete: () => onChange(values.filter((v) => v !== value)),
            children: value
          },
          `badge-${value}-${index}`
        )),
        /* @__PURE__ */ jsx121(
          TagInput,
          {
            index: values.length,
            value: inputValue,
            inputRef,
            onChange: (newValue) => setInputValue(newValue),
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (inputValue) {
                  onChange([...values, inputValue]);
                  setInputValue("");
                }
              }
            }
          }
        )
      ] })
    }
  );
};
var TagInput = ({
  value,
  inputRef,
  onChange,
  onKeyDown
}) => {
  const [textWidth, setTextWidth] = useState21();
  useLayoutEffect2(() => {
    setTextWidth(measureTextWidth(value));
  }, [value]);
  return /* @__PURE__ */ jsx121(
    ChakraInput,
    {
      ref: inputRef,
      variant: "plain",
      p: 0,
      ...Lato14Regular10,
      height: "20px",
      minHeight: "28px",
      value,
      onChange: (e) => onChange(e.target.value),
      onKeyDown,
      width: textWidth ? `${textWidth + 24}px` : "30px"
    }
  );
};
var measureTextWidth = (text) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return void 0;
  }
  context.font = "16px Lato";
  const { width } = context.measureText(text);
  return width;
};

// src/forms/inputs/SwitchLineInput.tsx
import { Flex as Flex25, Box as Box28 } from "@chakra-ui/react";
import { GreyGrey80 as GreyGrey808, Lato14Regular as Lato14Regular11, RedRed60 as RedRed608 } from "@sequencehq/design-tokens";

// src/forms/inputs/Switch.tsx
import {
  Switch as ChakraSwitch
} from "@chakra-ui/react";
import { jsx as jsx122 } from "react/jsx-runtime";
var Switch = ({
  onChange,
  children,
  checked,
  disabled,
  size = "sm",
  ...props
}) => /* @__PURE__ */ jsx122(
  ChakraSwitch,
  {
    variant: "default",
    size,
    style: { alignItems: "center", display: "flex" },
    disabled,
    cursor: disabled ? "not-allowed" : "pointer",
    isChecked: checked,
    defaultChecked: checked,
    ...props,
    onChange: (event) => onChange(event.target.checked),
    children
  }
);

// src/forms/inputs/SwitchLineInput.tsx
import { jsx as jsx123, jsxs as jsxs83 } from "react/jsx-runtime";
var SwitchLineInput = ({
  onChange,
  value,
  text,
  isInvalid,
  isDisabled,
  styles,
  "data-testid": dataTestId
}) => {
  return /* @__PURE__ */ jsxs83(
    Flex25,
    {
      alignItems: "center",
      onClick: () => !isDisabled && onChange(!value),
      width: "100%",
      children: [
        /* @__PURE__ */ jsx123(
          Flex25,
          {
            alignItems: "center",
            onClick: (e) => {
              e.stopPropagation();
            },
            children: /* @__PURE__ */ jsx123(
              Switch,
              {
                checked: value,
                disabled: isDisabled,
                isInvalid,
                mr: "8px",
                onChange: (checked) => !isDisabled && onChange(checked),
                "data-testid": dataTestId
              }
            )
          }
        ),
        /* @__PURE__ */ jsx123(
          Box28,
          {
            cursor: isDisabled ? "not-allowed" : "pointer",
            color: isInvalid ? RedRed608 : GreyGrey808,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...Lato14Regular11,
            userSelect: "none",
            ...styles?.label || {},
            children: text
          }
        )
      ]
    }
  );
};

// src/forms/fields.tsx
var TextField = withFormFieldWrapper(TextInput);
var TextInputField = withFormFieldWrapper(TextFieldInput);
var TextAreaField = withFormFieldWrapper(TextAreaInput);
var NumberField = withFormFieldWrapper(NumberInput);
var PriceField = withFormFieldWrapper(PriceInput);
var SelectField = withFormFieldWrapper(SelectInput);
var SelectFieldWithActions = withFormFieldWrapper(SelectInputWithActions);
var SearchSelectField = withFormFieldWrapper(SearchSelectInput);
var HorizontalSelectorField = withFormFieldWrapper(HorizontalSelectorInput, {
  defaultWidth: "100%"
});
var PercentageField = withFormFieldWrapper(PercentageInput);
var DayOfMonthField = withFormFieldWrapper(DayOfMonthInput);
var RadioGroupField = withFormFieldWrapper(RadioGroupInput);
var DatePickerField = withFormFieldWrapper(DatePickerInput);
var DateDurationField = withFormFieldWrapper(DateDurationInput);
var CheckboxLineField = withFormFieldWrapper(CheckboxLineInput);
var CheckboxGroupField = withFormFieldWrapper(CheckboxGroupInput);
var MultiTagField = withFormFieldWrapper(MultiTagInput);
var SwitchLineField = withFormFieldWrapper(SwitchLineInput);

// src/forms/inputs/dates/DateRangePicker/DateRangePickerTextInput.tsx
import { jsx as jsx124 } from "react/jsx-runtime";
var DateRangePickerTextInput = ({
  label,
  date,
  isFocused,
  onFocus,
  dateFormat = "d MMM yyyy"
}) => {
  return /* @__PURE__ */ jsx124(
    TextInputField,
    {
      label,
      suffix: /* @__PURE__ */ jsx124(CalendarIcon3, { width: "16px", height: "16px" }),
      value: date ? dateTimeWithFormat(date.toISOString(), dateFormat) : void 0,
      placeholder: "Select end date",
      onFocus: onFocus ? onFocus : void 0,
      borderColor: isFocused ? IndigoIndigo5017 : GreyGrey4013,
      styles: {
        label: {
          ...Lato13Bold10
        },
        wrapper: {
          marginBottom: "0px"
        }
      }
    }
  );
};

// src/forms/inputs/dates/DateRangePicker/DateRangePicker.tsx
import { getMonth } from "@sequencehq/utils/dist/dates";
import { Fragment as Fragment16, jsx as jsx125, jsxs as jsxs84 } from "react/jsx-runtime";
var DateRangePicker = (props) => {
  const [selectedDates, setSelectedDates] = useState22(
    props.defaultDates ?? [/* @__PURE__ */ new Date(), void 0]
  );
  const [dateInView, setDateInView] = useState22(
    props.defaultDates?.[0] ?? /* @__PURE__ */ new Date()
  );
  useEffect15(() => {
    if (props.defaultDates && props.defaultDates.length === 2 && props.defaultDates[0] !== selectedDates[0] && props.defaultDates[1] !== selectedDates[1]) {
      setSelectedDates(props.defaultDates);
    }
  }, [props.defaultDates]);
  const [focusedInput, setFocusedInput] = useState22("start");
  const [offset, setOffset] = useState22(0);
  const onDateChange = (newDates) => {
    props.onChange?.(newDates);
  };
  const handleOnDateSelected = ({ selectable, date }) => {
    if (!selectable) {
      return;
    }
    const timezoneCorrectedDate = dayzedAdapter.fromDayzed(date);
    const displayedMonths = calendars.map((calendar) => calendar.month);
    if (focusedInput === "start") {
      if (selectedDates[1] && timezoneCorrectedDate > selectedDates[1]) {
        setSelectedDates([timezoneCorrectedDate, void 0]);
        onDateChange([timezoneCorrectedDate, void 0]);
      } else {
        setSelectedDates([timezoneCorrectedDate, selectedDates[1]]);
        onDateChange([timezoneCorrectedDate, selectedDates[1]]);
      }
      setFocusedInput("end");
      setDateInView(timezoneCorrectedDate);
      if (getMonth(timezoneCorrectedDate) === displayedMonths[1]) {
        setOffset(-1);
      } else {
        setOffset(0);
      }
    }
    if (focusedInput === "end") {
      setSelectedDates([selectedDates[0], timezoneCorrectedDate]);
      onDateChange([selectedDates[0], timezoneCorrectedDate]);
      setFocusedInput("start");
    }
  };
  const handleOffsetChange = (newOffset) => {
    setOffset(newOffset);
  };
  const { calendars, ...dayzedProps } = useDayzed3({
    selected: selectedDates.filter(Boolean).map(
      dayzedAdapter.toDayzed
    ),
    onDateSelected: handleOnDateSelected,
    minDate: focusedInput === "start" ? void 0 : selectedDates[0] && dayzedAdapter.toDayzed(selectedDates[0]),
    offset,
    onOffsetChanged: handleOffsetChange,
    monthsToDisplay: 2,
    firstDayOfWeek: 1,
    date: dateInView
  });
  const onPopoverClose = () => {
    setDateInView(selectedDates[0] || /* @__PURE__ */ new Date());
    props.onClose?.();
    props.onOpenChange?.(false);
  };
  const onPopoverSubmit = () => {
    if (isValidDate(selectedDates[0]) && isValidDate(selectedDates[1])) {
      props.onSubmit?.([selectedDates[0], selectedDates[1]]);
    }
  };
  return /* @__PURE__ */ jsx125(Flex26, { gap: 2, zIndex: "9999", children: /* @__PURE__ */ jsx125(
    Popover11,
    {
      placement: "bottom",
      onClose: onPopoverClose,
      isOpen: props.isOpen,
      onOpen: () => props.onOpenChange?.(true),
      children: ({ onClose }) => /* @__PURE__ */ jsxs84(Fragment16, { children: [
        /* @__PURE__ */ jsx125(PopoverAnchor6, { children: /* @__PURE__ */ jsx125(PopoverTrigger6, { children: props.children }) }),
        /* @__PURE__ */ jsxs84(
          PopoverContent13,
          {
            zIndex: "9999",
            width: "100%",
            boxShadow: ShadowS4,
            borderRadius: 6,
            "data-testid": props["data-testid"],
            children: [
              /* @__PURE__ */ jsx125(PopoverArrow2, {}),
              /* @__PURE__ */ jsxs84(PopoverBody9, { p: 0, zIndex: "2", children: [
                /* @__PURE__ */ jsxs84(Stack3, { gap: 2, direction: "row", padding: 2, children: [
                  /* @__PURE__ */ jsx125(
                    Box29,
                    {
                      width: "100%",
                      borderRadius: "6px",
                      backgroundColor: GreyGrey107,
                      padding: "8px",
                      children: /* @__PURE__ */ jsx125(
                        DateRangePickerTextInput,
                        {
                          label: "Start date",
                          date: selectedDates[0],
                          isFocused: focusedInput === "start",
                          onFocus: () => setFocusedInput("start")
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx125(
                    Box29,
                    {
                      width: "100%",
                      borderRadius: "6px",
                      backgroundColor: GreyGrey107,
                      padding: "8px",
                      children: /* @__PURE__ */ jsx125(
                        DateRangePickerTextInput,
                        {
                          label: "End date",
                          date: selectedDates[1],
                          isFocused: focusedInput === "end",
                          onFocus: () => setFocusedInput("end")
                        }
                      )
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx125(Stack3, { gap: 4, direction: "row", padding: 2, pt: 0, children: calendars.map((calendar, calendarIdx) => {
                  return /* @__PURE__ */ jsx125(Flex26, { children: /* @__PURE__ */ jsx125(
                    CalendarView,
                    {
                      ...dayzedProps,
                      calendar,
                      type: "range",
                      focusedInput,
                      selectedDates: [selectedDates[0], selectedDates[1]]
                    }
                  ) }, calendarIdx);
                }) }),
                /* @__PURE__ */ jsx125(Divider7, { color: borderColor2 }),
                /* @__PURE__ */ jsxs84(Flex26, { justifyContent: "flex-end", gap: "4px", p: 2, children: [
                  /* @__PURE__ */ jsx125(Button2, { variant: "secondary", onClick: onClose, children: "Cancel" }),
                  /* @__PURE__ */ jsx125(
                    Button2,
                    {
                      onClick: () => {
                        onPopoverSubmit();
                        onClose();
                      },
                      disabled: selectedDates.filter(Boolean).length < 2,
                      children: "Done"
                    }
                  )
                ] })
              ] })
            ]
          }
        )
      ] })
    }
  ) });
};

// src/forms/inputs/dates/DateRangePicker/DateRangePickerInput.tsx
import { useEffect as useEffect16, useLayoutEffect as useLayoutEffect3, useRef as useRef18, useState as useState23 } from "react";
import { CalendarIcon as CalendarIcon4 } from "@heroicons/react/16/solid";
import { Input as Input4, InputGroup as InputGroup9, InputLeftAddon as InputLeftAddon4 } from "@chakra-ui/react";
import {
  formatDateRange,
  isSameDay,
  startOfMonth,
  endOfMonth,
  sub,
  previousCalendarQuarter
} from "@sequencehq/utils/dates";
import {
  GreyWhite as GreyWhite13,
  IndigoIndigo50 as IndigoIndigo5018,
  GreyGrey50 as GreyGrey509,
  GreyGrey60 as GreyGrey6017,
  Lato13Regular as Lato13Regular21,
  GreyGrey40 as GreyGrey4014,
  ShadowXs as ShadowXs4
} from "@sequencehq/design-tokens";
import { jsx as jsx126, jsxs as jsxs85 } from "react/jsx-runtime";
var AutoWidthDateInput = (props) => {
  const [width, setWidth] = useState23("150px");
  const inputRef = useRef18(null);
  useLayoutEffect3(() => {
    if (!props.value) {
      setWidth("150px");
      return;
    }
    const tempSpan = document.createElement("span");
    tempSpan.style.visibility = "hidden";
    tempSpan.style.position = "absolute";
    tempSpan.style.whiteSpace = "pre";
    tempSpan.style.fontFamily = "Lato, sans-serif";
    tempSpan.style.fontSize = "13px";
    tempSpan.style.fontWeight = "400";
    tempSpan.innerText = props.value;
    document.body.appendChild(tempSpan);
    const iconWidth = 24;
    const padding = 20;
    const measuredWidth = tempSpan.getBoundingClientRect().width;
    const calculatedWidth = measuredWidth + iconWidth + padding;
    document.body.removeChild(tempSpan);
    setWidth(`${calculatedWidth}px`);
  }, [props.value]);
  const focusOnInput = () => {
    inputRef?.current?.focus();
  };
  const focusStyles4 = {
    border: `1px solid ${IndigoIndigo5018}`
  };
  return /* @__PURE__ */ jsxs85(
    InputGroup9,
    {
      borderRadius: "6px",
      boxShadow: ShadowXs4,
      border: `1px solid ${GreyGrey4014}`,
      backgroundColor: GreyWhite13,
      onClick: focusOnInput,
      height: "32px",
      width,
      display: "flex",
      alignItems: "center",
      style: props.style,
      _focusWithin: {
        ...focusStyles4
      },
      children: [
        /* @__PURE__ */ jsx126(
          InputLeftAddon4,
          {
            h: "32px",
            pl: "8px",
            pr: "4px",
            pointerEvents: "none",
            border: "none",
            color: GreyGrey6017,
            backgroundColor: "transparent",
            ...Lato13Regular21,
            children: /* @__PURE__ */ jsx126(CalendarIcon4, { width: "16px", height: "16px" })
          }
        ),
        /* @__PURE__ */ jsx126(
          Input4,
          {
            ref: inputRef,
            border: "none",
            height: "32px",
            boxShadow: "none",
            pl: "0",
            backgroundColor: "transparent",
            width: "100%",
            ...Lato13Regular21,
            value: props.value,
            placeholder: props.placeholder,
            "data-testid": props["data-testid"],
            _placeholder: { color: GreyGrey509 },
            _focus: {
              border: "none",
              boxShadow: "none"
            }
          }
        )
      ]
    }
  );
};
var now = /* @__PURE__ */ new Date();
var CUSTOM_OPTION_VALUE = "custom";
var DEFAULT_OPTIONS = [
  {
    label: "This month",
    value: "thisMonth",
    getDateRange: () => {
      return [startOfMonth(now), endOfMonth(now)];
    }
  },
  {
    label: "Last month",
    value: "lastMonth",
    getDateRange: () => {
      const lastMonth = sub(now, { months: 1 });
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
    }
  },
  {
    label: "Last quarter",
    value: "lastQuarter",
    getDateRange: () => {
      return previousCalendarQuarter();
    }
  },
  {
    label: "Last year",
    value: "lastYear",
    getDateRange: () => {
      const lastYear = now.getFullYear() - 1;
      const startDate = new Date(Date.UTC(lastYear, 0, 1));
      const endDate = new Date(Date.UTC(lastYear, 11, 31));
      return [startDate, endDate];
    }
  },
  {
    label: "Custom",
    value: CUSTOM_OPTION_VALUE,
    getDateRange: () => [/* @__PURE__ */ new Date(), /* @__PURE__ */ new Date()],
    dividerAfter: true
  },
  {
    label: "Month-to-date",
    value: "monthToDate",
    getDateRange: () => {
      return [sub(now, { months: 1 }), now];
    }
  },
  {
    label: "Quarter-to-date",
    value: "quarterToDate",
    getDateRange: () => {
      return [sub(now, { months: 3 }), now];
    }
  },
  {
    label: "Year-to-date",
    value: "yearToDate",
    getDateRange: () => {
      return [sub(now, { years: 1 }), now];
    }
  }
];
var getSelectedOptionPresetValue = (value, options) => {
  if (!value) {
    return;
  }
  const [startDate, endDate] = value;
  if (!startDate || !endDate) {
    return;
  }
  const matchingOption = options.find((option) => {
    if (option.value === CUSTOM_OPTION_VALUE)
      return false;
    const [presetStart, presetEnd] = option.getDateRange();
    return isSameDay(presetStart, startDate) && isSameDay(presetEnd, endDate);
  });
  return matchingOption?.value || null;
};
var DateRangePickerInput = ({
  value,
  onChange,
  withOptions = true,
  options = DEFAULT_OPTIONS,
  placeholder = "Select date range",
  style,
  "data-testid": dataTestId
}) => {
  const [selectedOptionValue, setSelectedOptionValue] = useState23(
    getSelectedOptionPresetValue(value, options) || CUSTOM_OPTION_VALUE
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState23(false);
  useEffect16(() => {
    const matchingOptionValue = getSelectedOptionPresetValue(value, options);
    setSelectedOptionValue(matchingOptionValue || CUSTOM_OPTION_VALUE);
  }, [value, options]);
  const handleOptionChange = (optionValue) => {
    const selectedOption = options.find((option) => option.value === optionValue);
    if (!selectedOption)
      return;
    setSelectedOptionValue(optionValue);
    if (optionValue === CUSTOM_OPTION_VALUE) {
      setIsPopoverOpen(true);
      return;
    }
    const dateRange = selectedOption.getDateRange();
    onChange(dateRange);
  };
  const handleDateRangeSubmit = (selectedDates) => {
    onChange(selectedDates);
    setIsPopoverOpen(false);
    const presetValue = getSelectedOptionPresetValue(selectedDates, options);
    setSelectedOptionValue(presetValue || CUSTOM_OPTION_VALUE);
  };
  return /* @__PURE__ */ jsxs85(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        ...style,
        height: "32px"
      },
      "data-testid": dataTestId,
      children: [
        withOptions && /* @__PURE__ */ jsx126(
          InputSelectControl,
          {
            initialValue: selectedOptionValue,
            onChange: handleOptionChange,
            triggerStyle: {
              width: "160px",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            },
            options: options.map((option) => ({
              label: option.label,
              value: option.value,
              dividerAfter: option.dividerAfter
            })),
            "data-testid": dataTestId ? `${dataTestId}-select` : void 0
          }
        ),
        /* @__PURE__ */ jsx126(
          DateRangePicker,
          {
            defaultDates: value,
            onSubmit: handleDateRangeSubmit,
            isOpen: isPopoverOpen,
            onOpenChange: setIsPopoverOpen,
            children: /* @__PURE__ */ jsx126("span", { children: /* @__PURE__ */ jsx126(
              AutoWidthDateInput,
              {
                style: withOptions ? {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: "none"
                } : {},
                value: value && value[0] && value[1] ? formatDateRange({
                  from: value[0],
                  to: value[1],
                  separator: "-"
                }) : void 0,
                placeholder,
                "data-testid": dataTestId ? `${dataTestId}-input` : void 0
              }
            ) })
          },
          value?.[0]?.getTime()
        )
      ]
    }
  );
};
Object.assign(DateRangePickerInput, {
  defaultOptions: DEFAULT_OPTIONS
});

// src/forms/inputs/InputLabel.tsx
import { Box as Box30 } from "@chakra-ui/react";
import { Lato14Bold as Lato14Bold6 } from "@sequencehq/design-tokens";
import { jsx as jsx127 } from "react/jsx-runtime";
var InputLabel = ({ children, ...props }) => /* @__PURE__ */ jsx127(Box30, { ...Lato14Bold6, marginBottom: "8px", ...props, children });

// src/forms/inputs/SimpleTableInput/SimpleTableInput.tsx
import { Box as Box32, Flex as Flex28, Table as Table2, Tbody as Tbody2, Th as Th2, Thead as Thead2, Tr as Tr3 } from "@chakra-ui/react";
import {
  GreyGrey0 as GreyGrey04,
  GreyGrey30 as GreyGrey3013,
  GreyGrey60 as GreyGrey6018,
  Lato13Bold as Lato13Bold11
} from "@sequencehq/design-tokens";

// src/forms/inputs/SimpleTableInput/TableRow.tsx
import {
  Box as Box31,
  Flex as Flex27,
  Popover as Popover12,
  PopoverContent as PopoverContent14,
  PopoverTrigger as PopoverTrigger7,
  Portal as Portal10,
  Td,
  Tr as Tr2,
  useDisclosure as useDisclosure3
} from "@chakra-ui/react";
import {
  GreyGrey30 as GreyGrey3012,
  GreyWhite as GreyWhite14,
  Lato14Regular as Lato14Regular12,
  RedRed40
} from "@sequencehq/design-tokens";
import { createElement, useCallback as useCallback8, useRef as useRef19 } from "react";
import { jsx as jsx128, jsxs as jsxs86 } from "react/jsx-runtime";
var TableRow = (props) => {
  const { onOpen, onClose, isOpen } = useDisclosure3();
  const popoverContentRef = useRef19(null);
  const handleOpen = useCallback8(() => {
    onOpen();
  }, [onOpen]);
  const handleClose = useCallback8(() => {
    onClose();
  }, [onClose]);
  return /* @__PURE__ */ jsxs86(Popover12, { isOpen: !props.isDisabled, placement: "left", children: [
    /* @__PURE__ */ jsx128(PopoverTrigger7, { children: /* @__PURE__ */ jsx128(
      Tr2,
      {
        onMouseEnter: handleOpen,
        onMouseLeave: handleClose,
        _notLast: { borderBottom: `1px solid ${GreyGrey3012}` },
        _last: { borderBottom: "1px solid transparent" },
        children: props.row.columns.map((column) => /* @__PURE__ */ jsx128(
          Td,
          {
            outline: column.invalid ? `1px solid ${RedRed40}` : "none",
            width: column.width ?? `${100 / props.context.columns.filter((c) => !c.width).length}%`,
            border: "none",
            padding: "0",
            _notLast: {
              borderRight: `1px solid ${GreyGrey3012}`
            },
            children: column.cell ? createElement(column.cell, {
              invalid: column.invalid ?? false,
              value: column.value,
              rowData: props.row.data,
              onChange: column.onChange,
              rowIndex: props.rowIndex,
              rows: props.context.rows.map((row) => row.data),
              disabled: props.isDisabled,
              config: props.context.config,
              onBlur: column.onBlur
            }) : /* @__PURE__ */ jsx128(
              Flex27,
              {
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "12px",
                ...column.invalid ? { borderColor: `1px solid ${RedRed40}` } : {},
                ...Lato14Regular12,
                children: column.value
              }
            )
          },
          `${props.row.id}-${String(column.property)}`
        ))
      },
      props.row.id
    ) }),
    /* @__PURE__ */ jsx128(Portal10, { children: /* @__PURE__ */ jsx128(
      PopoverContent14,
      {
        width: "fit-content",
        border: "none",
        right: "-12px",
        ref: popoverContentRef,
        onMouseEnter: handleOpen,
        onMouseLeave: handleClose,
        backgroundColor: "transparent",
        boxShadow: "none",
        children: /* @__PURE__ */ jsxs86(Flex27, { height: "42px", userSelect: "none", children: [
          /* @__PURE__ */ jsx128(
            Box31,
            {
              width: "fit-content",
              height: "42px",
              borderLeftRadius: "8px",
              borderRightRadius: "0",
              border: `1px solid ${GreyGrey3012}`,
              boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
              backgroundColor: GreyWhite14,
              overflow: "visible",
              opacity: isOpen ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
              children: !props.isDisabled && props.rowActions && createElement(props.rowActions.component, {
                rowData: props.row.data,
                rowIndex: props.rowIndex
              })
            }
          ),
          /* @__PURE__ */ jsx128(
            Box31,
            {
              width: "4px",
              height: "42px",
              backgroundColor: GreyWhite14,
              opacity: isOpen ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
              borderTop: `1px solid ${GreyGrey3012}`,
              borderBottom: `1px solid ${GreyGrey3012}`
            }
          )
        ] })
      }
    ) })
  ] });
};

// src/forms/inputs/SimpleTableInput/SimpleTableInput.tsx
import { createElement as createElement2, useMemo as useMemo10 } from "react";
import { jsx as jsx129, jsxs as jsxs87 } from "react/jsx-runtime";
var SimpleTableInput = ({
  rows,
  rowActions,
  columns,
  onChange,
  onBlur,
  invalidRows,
  isDisabled = false,
  config = {},
  "data-testid": dataTestId
}) => {
  const resolvedRows = useMemo10(() => {
    if (!rows) {
      return [];
    }
    return rows.map((row) => {
      const invalidProperties = (invalidRows ?? []).find((invalidRow) => invalidRow.id === row.id)?.invalidProperties ?? [];
      return {
        id: row.id,
        data: row,
        columns: columns.map((column) => ({
          width: column.width ?? `${100 / columns.filter((c) => !c.width).length}%`,
          value: column.property ? row[column.property] : null,
          property: column.property ?? null,
          invalid: invalidProperties.includes(column.property ?? ""),
          onBlur,
          onChange: (newValue) => {
            onChange(
              rows.map((rowToUpdate) => {
                if (rowToUpdate.id === row.id && column.property) {
                  return {
                    ...rowToUpdate,
                    [column.property]: newValue
                  };
                }
                return rowToUpdate;
              })
            );
          },
          cell: column.bodyComponent
        }))
      };
    });
  }, [rows, columns, onChange, invalidRows, onBlur]);
  return /* @__PURE__ */ jsx129(
    Box32,
    {
      position: "relative",
      borderRadius: "6px",
      border: `1px solid ${GreyGrey3013}`,
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
      overflow: "hidden",
      children: /* @__PURE__ */ jsxs87(Table2, { position: "relative", "data-testid": dataTestId, children: [
        /* @__PURE__ */ jsx129(Thead2, { backgroundColor: GreyGrey04, overflow: "hidden", children: /* @__PURE__ */ jsx129(Tr3, { borderBottom: `1px solid ${GreyGrey3013}`, children: columns.map((column) => /* @__PURE__ */ jsx129(
          Th2,
          {
            width: column.width ?? `${100 / columns.length}%`,
            padding: "0",
            border: "none",
            _notLast: {
              borderRight: `1px solid ${GreyGrey3013}`
            },
            children: column.headerComponent ? createElement2(column.headerComponent, {
              label: column.label ?? ""
            }) : /* @__PURE__ */ jsx129(
              Flex28,
              {
                alignItems: "center",
                justifyContent: "flex-start",
                color: GreyGrey6018,
                padding: "16px 12px",
                textTransform: "none",
                ...Lato13Bold11,
                children: column.label
              }
            )
          },
          String(column.property)
        )) }) }),
        /* @__PURE__ */ jsx129(Tbody2, { position: "relative", zIndex: "1", children: resolvedRows.map((row, rowIdx) => /* @__PURE__ */ jsx129(
          TableRow,
          {
            row,
            rowIndex: rowIdx,
            context: {
              rows: resolvedRows,
              columns,
              config
            },
            isDisabled,
            rowActions
          },
          row.id
        )) })
      ] })
    }
  );
};

// src/components/Control/InputSelectControl.tsx
import { ChevronDownIcon as ChevronDownIcon8, ChevronUpIcon as ChevronUpIcon6 } from "@heroicons/react/20/solid";
import { Fragment as Fragment17, jsx as jsx130, jsxs as jsxs88 } from "react/jsx-runtime";
var InputSelectControl = ({
  matchWidth = true,
  initiallyOpen,
  ...restProps
}) => {
  return /* @__PURE__ */ jsx130(
    ControlContextProvider,
    {
      showSearchInPopover: false,
      matchWidth: matchWidth ?? true,
      isOpen: initiallyOpen,
      placement: restProps.flip ? "auto" : "bottom-start",
      children: /* @__PURE__ */ jsx130(InputSelectControlCore, { ...restProps })
    }
  );
};
var InputSelectControlField = withFormFieldWrapper(InputSelectControl);
var InputSelectControlCore = ({
  options,
  initialValue,
  onChange,
  onCreate,
  onToggle,
  createLabel,
  placeholder,
  disabled = false,
  name,
  triggerStyle,
  filterable = true,
  emptyState,
  noResultsState,
  filterOptionGroups,
  overrideSelectedLabel,
  leadingIcon,
  trailingIcon,
  flip = false,
  inputRef: inputRefProp,
  "data-testid": dataTestId,
  id
}) => {
  const [value, setValue] = useState24(initialValue);
  let inputRef = useRef20(null);
  if (inputRefProp) {
    inputRef = inputRefProp;
  }
  const { setSearchTerm } = useControlContext();
  const handleChange = (val) => {
    setValue(val);
    onChange?.(val);
    setSearchTerm("");
  };
  useEffect17(() => {
    setValue(initialValue);
  }, [initialValue]);
  const selectedOption = useSelectedOption(options, value);
  return /* @__PURE__ */ jsx130(
    SelectControl,
    {
      name,
      initialValue,
      options,
      onChange: handleChange,
      placeholder,
      onCreate,
      createLabel,
      disabled,
      externalFocusRef: inputRef,
      filterOptionGroups: filterable ? filterOptionGroups : null,
      onToggle,
      emptyState,
      noResultsState,
      flip,
      trigger: /* @__PURE__ */ jsx130(
        InputTrigger,
        {
          id,
          "data-testid": dataTestId,
          ref: inputRef,
          filterable,
          selectedOption,
          placeholder,
          style: triggerStyle,
          disabled,
          overrideSelectedLabel,
          leadingIcon,
          trailingIcon
        }
      ),
      "data-testid": dataTestId
    }
  );
};
var InputTrigger = forwardRef4(
  ({
    id,
    placeholder,
    style,
    selectedOption,
    filterable,
    disabled,
    overrideSelectedLabel,
    leadingIcon,
    trailingIcon,
    "data-testid": dataTestId
  }, ref) => {
    const { searchTerm, setSearchTerm, isOpen, setIsOpen } = useControlContext();
    const handleClick = () => {
      setIsOpen(!isOpen);
    };
    const showPlaceholder = isOpen || !selectedOption;
    return /* @__PURE__ */ jsxs88(Flex29, { alignItems: "center", width: "100%", position: "relative", height: "32px", children: [
      /* @__PURE__ */ jsx130(
        TextInput,
        {
          "data-testid": dataTestId,
          "data-1p-ignore": true,
          value: isOpen ? searchTerm : "",
          onChange: filterable ? setSearchTerm : void 0,
          onClick: handleClick,
          ref,
          placeholder: showPlaceholder ? placeholder : "",
          style,
          isReadOnly: !filterable,
          height: "32px",
          color: GreyGrey9010,
          isDisabled: disabled,
          ...Lato13Regular22,
          lineHeight: "32px",
          boxShadow: ShadowS5,
          paddingLeft: leadingIcon ? 7 : 3,
          paddingRight: 4,
          _hover: {
            background: GreyGrey05,
            cursor: filterable ? "text" : "pointer"
          },
          _readOnly: {
            boxShadow: ShadowS5
          },
          _disabled: {
            borderColor: GreyGrey3014,
            background: GreyGrey108,
            cursor: "not-allowed",
            color: GreyGrey5010,
            boxShadow: "none"
          },
          _focus: {
            border: `1px solid ${IndigoIndigo5019}`
          },
          _focusVisible: {
            outline: "none"
          },
          id
        }
      ),
      /* @__PURE__ */ jsxs88(
        Flex29,
        {
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          pointerEvents: "none",
          alignItems: "center",
          zIndex: 2,
          opacity: disabled ? 0.5 : 1,
          children: [
            leadingIcon && /* @__PURE__ */ jsx130(
              Box33,
              {
                width: "20px",
                marginLeft: 2,
                alignItems: "end",
                color: GreyGrey6019,
                children: leadingIcon
              }
            ),
            /* @__PURE__ */ jsx130(
              Box33,
              {
                flex: 1,
                paddingLeft: leadingIcon ? 0 : 3,
                textOverflow: "ellipsis",
                overflowX: "hidden",
                whiteSpace: "nowrap",
                ...Lato13Regular22,
                color: GreyGrey6019,
                children: !isOpen && /* @__PURE__ */ jsxs88(Fragment17, { children: [
                  /* @__PURE__ */ jsx130(
                    Text10,
                    {
                      color: GreyGrey9010,
                      display: "inline",
                      "data-testid": `${dataTestId}-label`,
                      ...Lato13Regular22,
                      children: overrideSelectedLabel ?? selectedOption?.label
                    }
                  ),
                  selectedOption?.secondaryLabel && ` ${selectedOption.secondaryLabel}`
                ] })
              }
            ),
            /* @__PURE__ */ jsxs88(Flex29, { marginRight: 2, alignItems: "center", children: [
              trailingIcon,
              /* @__PURE__ */ jsx130(Center6, { width: "20px", color: GreyGrey6019, children: isOpen ? /* @__PURE__ */ jsx130(ChevronUpIcon6, { width: 20 }) : /* @__PURE__ */ jsx130(ChevronDownIcon8, { width: 20 }) })
            ] })
          ]
        }
      )
    ] });
  }
);
var InputSelectControlLegacyStyleBridge = (props) => {
  return /* @__PURE__ */ jsx130(
    InputSelectControl,
    {
      ...props,
      matchWidth: true,
      triggerStyle: {
        height: "40px",
        paddingLeft: "16px",
        ...props.triggerStyle
      }
    }
  );
};
var InputSelectControlLegacyStyleBridgeField = withFormFieldWrapper(InputSelectControlLegacyStyleBridge);

// src/components/Control/MultiSelectControl.tsx
import { jsx as jsx131 } from "react/jsx-runtime";
var MultiSelectControl = (props) => {
  return /* @__PURE__ */ jsx131(ControlContextProvider, { children: /* @__PURE__ */ jsx131(MultiSelectControlCore, { ...props }) });
};

// src/components/CopyButton/CopyButton.tsx
import { Flex as Flex30 } from "@chakra-ui/react";
import { useState as useState25 } from "react";
import { Copy, Check } from "react-feather";
import { jsx as jsx132 } from "react/jsx-runtime";
var CopyButton = ({
  value,
  enableGroupHover = true
}) => {
  const [copied, setCopied] = useState25(false);
  const copyToClipboard = (copiedValue) => {
    navigator.clipboard.writeText(copiedValue).catch(() => {
      console.error("Failed to copy to clipboard");
    });
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  return /* @__PURE__ */ jsx132(
    Flex30,
    {
      display: enableGroupHover ? "none" : void 0,
      height: "24px",
      width: "24px",
      borderRadius: "50%",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "transparent",
      cursor: "pointer",
      _groupHover: enableGroupHover ? { display: "flex" } : void 0,
      _hover: {
        backgroundColor: "gray.30",
        boxShadow: "0px 2px 4px rgba(20, 23, 28, 0.08)"
      },
      _active: {
        backgroundColor: "gray.40"
      },
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        copyToClipboard(value);
      },
      children: copied ? /* @__PURE__ */ jsx132(Check, { height: "16px", width: "16px", strokeWidth: 1.5 }) : /* @__PURE__ */ jsx132(Copy, { height: "16px", width: "16px", strokeWidth: 1.5 })
    }
  );
};

// src/components/EntityLink/EntityLink.tsx
import { Link as ChakraLink } from "@chakra-ui/react";
import { Link as ReactRouterLink } from "react-router-dom";
import { IndigoIndigo50 as IndigoIndigo5020, Lato14Regular as Lato14Regular13 } from "@sequencehq/design-tokens";
import { borderColor as borderColor3 } from "@sequencehq/core-theme";
import { jsx as jsx133 } from "react/jsx-runtime";
var EntityLink = ({ to, children, target }) => /* @__PURE__ */ jsx133(
  ChakraLink,
  {
    as: ReactRouterLink,
    border: `1px solid ${borderColor3}`,
    borderRadius: "8px",
    padding: "3px 6px",
    to,
    target,
    color: IndigoIndigo5020,
    ...Lato14Regular13,
    sx: {
      '[role="row"] &': {
        borderColor: "transparent"
      },
      ":hover": {
        borderColor: borderColor3,
        textDecoration: "none"
      }
    },
    children
  }
);

// src/components/EnvironmentBanner/EnvironmentBanner.tsx
import { Box as Box34, Center as Center7, Flex as Flex31, Text as Text11 } from "@chakra-ui/react";
import { Fragment as Fragment18, jsx as jsx134, jsxs as jsxs89 } from "react/jsx-runtime";
var EnvironmentBanner = ({
  environment,
  children
}) => {
  function getBackgroundColour() {
    switch (environment) {
      case "local":
      case "dev":
        return "green.20";
      case "test":
        return "indigo.20";
      case "stable":
        return "red.20";
      case "sandbox":
        return "yellow.20";
      case "demo":
        return "orange.40";
      default:
        return void 0;
    }
  }
  function getTextColour() {
    switch (environment) {
      case "local":
      case "dev":
        return "green.90";
      case "test":
        return "indigo.90";
      case "stable":
        return "red.90";
      case "sandbox":
        return "yellow.90";
      case "demo":
        return "white";
      default:
        return void 0;
    }
  }
  const toTitleCase = (value) => {
    return value ? value.replace(/\w\S*/g, function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }) : "";
  };
  return environment === "production" ? /* @__PURE__ */ jsx134(Fragment18, {}) : /* @__PURE__ */ jsxs89(Flex31, { flexDirection: "column", children: [
    /* @__PURE__ */ jsx134(Box34, { height: "4px", backgroundColor: getBackgroundColour() }),
    /* @__PURE__ */ jsx134(Center7, { children: /* @__PURE__ */ jsx134(
      Box34,
      {
        backgroundColor: getBackgroundColour(),
        borderBottomRadius: "4px",
        padding: "2px 4px",
        height: "20px",
        children: /* @__PURE__ */ jsx134(
          Text11,
          {
            "data-testid": "environment-name",
            color: getTextColour(),
            fontSize: "12px",
            children: children ?? toTitleCase(environment)
          }
        )
      }
    ) })
  ] });
};

// src/components/InfoBox/InfoBox.tsx
import { Flex as Flex32 } from "@chakra-ui/react";
import { GreyGrey10 as GreyGrey109 } from "@sequencehq/design-tokens";
import { jsx as jsx135 } from "react/jsx-runtime";
var InfoBox = ({
  children,
  ["data-testid"]: dataTestId
}) => /* @__PURE__ */ jsx135(
  Flex32,
  {
    p: 5,
    bg: GreyGrey109,
    borderRadius: "6px",
    gap: "12px",
    flexDirection: "column",
    "data-testid": dataTestId,
    children
  }
);

// src/components/InfoBox/InfoRow.tsx
import { Flex as Flex33, Text as Text12 } from "@chakra-ui/react";
import {
  GreyGrey100 as GreyGrey1003,
  GreyGrey60 as GreyGrey6020,
  Lato14Regular as Lato14Regular14
} from "@sequencehq/design-tokens";
import { jsx as jsx136, jsxs as jsxs90 } from "react/jsx-runtime";
var InfoRow = ({ label, children }) => /* @__PURE__ */ jsxs90(Flex33, { justifyContent: "space-between", width: "100%", alignItems: "center", children: [
  /* @__PURE__ */ jsx136(Text12, { ...Lato14Regular14, color: GreyGrey6020, children: label }),
  typeof children === "string" ? /* @__PURE__ */ jsx136(Text12, { ...Lato14Regular14, color: GreyGrey1003, children }) : children
] });

// src/components/KebabMenu/KebabMenu.tsx
import { IconButton as IconButton5, Menu as Menu2, MenuButton as MenuButton3, Portal as Portal11 } from "@chakra-ui/react";
import { BorderRadius6 as BorderRadius62 } from "@sequencehq/design-tokens";

// src/components/KebabMenu/KebabMenuList.tsx
import { Box as Box35, MenuItem as ChakraMenuItem, MenuList as MenuList2 } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { GreyGrey40 as GreyGrey4015 } from "@sequencehq/design-tokens";
import { jsx as jsx137 } from "react/jsx-runtime";
var KebabMenuItem = ChakraMenuItem;
var KebabMenuList = ({
  menuItemsToRender,
  stopPropogation = false
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx137(
    MenuList2,
    {
      fontSize: "14px",
      overflow: "hidden",
      borderColor: GreyGrey4015,
      padding: "8px",
      boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.08)",
      children: menuItemsToRender.map(({ itemIcon, label, ...menuItem }, index) => {
        switch (menuItem.status) {
          case "LIVE":
            return typeof label === "string" ? /* @__PURE__ */ jsx137(
              ChakraMenuItem,
              {
                icon: itemIcon && itemIcon,
                iconSpacing: "8px",
                onClick: (e) => {
                  if (stopPropogation) {
                    e.stopPropagation();
                  }
                  return menuItem.action?.({ dispatch, navigate });
                },
                sx: { ...menuItem.styles },
                "data-testid": menuItem.testId,
                children: label
              },
              `menu${index}`
            ) : /* @__PURE__ */ jsx137(
              Box35,
              {
                onClick: (e) => {
                  if (stopPropogation) {
                    e.stopPropagation();
                  }
                  return menuItem.action?.({ dispatch, navigate });
                },
                "data-testid": menuItem.testId,
                children: label
              },
              `menu${index}`
            );
          case "DISABLED":
            return typeof label === "string" ? /* @__PURE__ */ jsx137(
              ChakraMenuItem,
              {
                icon: itemIcon ? itemIcon : void 0,
                iconSpacing: "8px",
                sx: { ...menuItem.styles },
                isDisabled: true,
                "data-testid": menuItem.testId,
                children: label
              },
              `menu${index}`
            ) : /* @__PURE__ */ jsx137(Box35, { "data-testid": menuItem.testId, children: label }, `menu${index}`);
        }
      })
    }
  );
};
var KebabMenuList_default = KebabMenuList;

// src/components/KebabMenu/MagicTableKebabMenuButtonV3.tsx
import { IconButton as IconButton4, MenuButton as MenuButton2 } from "@chakra-ui/react";
import { EllipsisHorizontalIcon as EllipsisHorizontalIcon2 } from "@heroicons/react/16/solid";
import { jsx as jsx138 } from "react/jsx-runtime";
var MagicTableKebabMenuButtonV3 = ({
  stopPropogation,
  disabled,
  "data-testid": dataTestId
}) => /* @__PURE__ */ jsx138(
  MenuButton2,
  {
    as: IconButton4,
    variant: "component-library-secondary",
    icon: /* @__PURE__ */ jsx138(EllipsisHorizontalIcon2, { color: "inherit", width: "16px", height: "16px" }),
    onClick: (e) => {
      if (stopPropogation) {
        e.stopPropagation();
      }
    },
    minWidth: "initial",
    width: "24px",
    height: "24px",
    padding: "4px",
    borderRadius: "6px",
    isDisabled: disabled,
    "data-testid": dataTestId
  }
);

// src/components/KebabMenu/KebabMenu.tsx
import { memo } from "react";
import { jsx as jsx139, jsxs as jsxs91 } from "react/jsx-runtime";
var KebabMenu2 = memo(
  ({
    menuItems,
    renderListInPortal = true,
    disabled,
    isLazy,
    flags,
    onOpen,
    onClose,
    stopPropogation = false,
    useMagicTableStyle = false,
    buttonComponent,
    kebabIcon,
    menuButtonStyle,
    "data-testid": dataTestId
  }) => {
    const menuItemsToRender = menuItems.filter(
      (item) => !item.featureFlag || Boolean(flags?.[item.featureFlag])
    );
    if (!menuItemsToRender.length) {
      return null;
    }
    return /* @__PURE__ */ jsxs91(
      Menu2,
      {
        placement: "bottom-end",
        isLazy,
        lazyBehavior: "keepMounted",
        onOpen,
        onClose,
        children: [
          buttonComponent ? buttonComponent : useMagicTableStyle ? /* @__PURE__ */ jsx139(
            MagicTableKebabMenuButtonV3,
            {
              "data-testid": dataTestId,
              stopPropogation,
              disabled
            }
          ) : /* @__PURE__ */ jsx139(
            MenuButton3,
            {
              "data-testid": dataTestId,
              as: IconButton5,
              "aria-label": "Options",
              icon: kebabIcon ?? /* @__PURE__ */ jsx139(KebabIcon, {}),
              variant: "kebab",
              width: menuButtonStyle?.width ?? "36px",
              minWidth: menuButtonStyle?.minWidth ?? "36px",
              maxWidth: "36px",
              height: menuButtonStyle?.width ?? "36px",
              borderRadius: BorderRadius62,
              borderColor: "transparent",
              borderWidth: "2px",
              _hover: {
                backgroundColor: menuButtonStyle?.hoverBackgroundColor ?? "gray.20"
              },
              _active: { backgroundColor: "gray.30" },
              _focus: { borderColor: "turquoise.60" },
              isDisabled: disabled,
              onClick: (e) => {
                if (stopPropogation) {
                  e.stopPropagation();
                }
              }
            }
          ),
          renderListInPortal ? /* @__PURE__ */ jsx139(Portal11, { children: /* @__PURE__ */ jsx139(
            KebabMenuList_default,
            {
              menuItemsToRender,
              stopPropogation
            }
          ) }) : /* @__PURE__ */ jsx139(
            KebabMenuList_default,
            {
              menuItemsToRender,
              stopPropogation
            }
          )
        ]
      }
    );
  }
);

// src/components/PageTitle/PageTitle.tsx
import { Helmet } from "react-helmet";
import { jsx as jsx140 } from "react/jsx-runtime";
var PageTitle = ({
  title,
  defaultTitle: defaultTitle2 = "Sequence"
}) => {
  const separator = "\u2013";
  const finalTitle = title ? `${[...title, defaultTitle2].join(` ${separator} `)}` : defaultTitle2;
  return /* @__PURE__ */ jsx140(Helmet, { children: /* @__PURE__ */ jsx140("title", { children: finalTitle }) });
};

// src/components/Toast/InfoToast.tsx
import { Box as Box36, Flex as Flex34, Text as Text13 } from "@chakra-ui/react";
import { jsx as jsx141, jsxs as jsxs92 } from "react/jsx-runtime";
var InfoToast = ({ title, description }) => {
  return /* @__PURE__ */ jsxs92(Box36, { width: "356px", height: "72px", borderRadius: "4px", backgroundColor: "white", children: [
    /* @__PURE__ */ jsx141(Box36, { backgroundColor: "indigo.60", height: "4px", borderRadius: "4px" }),
    /* @__PURE__ */ jsx141(Box36, { padding: "12px", children: /* @__PURE__ */ jsxs92(Flex34, { flexDirection: "row", children: [
      /* @__PURE__ */ jsx141(InfoIcon2, {}),
      /* @__PURE__ */ jsx141(Box36, { width: "14px" }),
      /* @__PURE__ */ jsxs92(Flex34, { flexDirection: "column", children: [
        /* @__PURE__ */ jsx141(Text13, { fontSize: "16px", fontWeight: "700", children: title }),
        /* @__PURE__ */ jsx141(Box36, { height: "8px" }),
        /* @__PURE__ */ jsx141(Text13, { fontSize: "16px", children: description })
      ] })
    ] }) })
  ] });
};

// src/components/Toast/Toast.tsx
import { Box as Box37, Flex as Flex35, Text as Text14, useToast, Icon as Icon9, Link } from "@chakra-ui/react";
import { X, CheckCircle, AlertCircle, ExternalLink } from "react-feather";
import { isExternalLink } from "@sequencehq/utils";
import { Fragment as Fragment19, jsx as jsx142, jsxs as jsxs93 } from "react/jsx-runtime";
var Toast = ({
  type,
  title,
  description,
  url,
  linkText,
  isClosable
}) => {
  const toast = useToast();
  let topBar;
  let icon;
  const iconSize = "16px";
  switch (type) {
    case "info":
      topBar = "indigo.60";
      icon = /* @__PURE__ */ jsx142(
        Icon9,
        {
          as: AlertCircle,
          color: "indigo.60",
          width: iconSize,
          height: iconSize
        }
      );
      break;
    case "success":
      topBar = "green.70";
      icon = /* @__PURE__ */ jsx142(
        Icon9,
        {
          as: CheckCircle,
          color: "green.70",
          width: iconSize,
          height: iconSize
        }
      );
      break;
    case "error":
      topBar = "red.60";
      icon = /* @__PURE__ */ jsx142(
        Icon9,
        {
          as: AlertCircle,
          color: "red.60",
          width: iconSize,
          height: iconSize
        }
      );
      break;
    case "warning":
      topBar = "yellow.60";
      icon = /* @__PURE__ */ jsx142(
        Icon9,
        {
          as: AlertCircle,
          color: "yellow.60",
          width: iconSize,
          height: iconSize
        }
      );
      break;
    case "neutral":
      topBar = "gray.60";
      icon = /* @__PURE__ */ jsx142(
        Icon9,
        {
          as: AlertCircle,
          color: "gray.60",
          width: iconSize,
          height: iconSize
        }
      );
      break;
  }
  return /* @__PURE__ */ jsx142(
    Box37,
    {
      borderRadius: "4px",
      maxWidth: "420px",
      backgroundColor: "white",
      boxShadow: "0px 12px 16px -4px rgba(23, 59, 85, 0.1), 0px 4px 6px -2px rgba(20, 62, 86, 0.05)",
      borderTopRadius: "4px",
      borderTop: "4px solid",
      borderTopColor: topBar,
      children: /* @__PURE__ */ jsx142(Box37, { px: "16px", py: "12px", children: /* @__PURE__ */ jsxs93(
        Flex35,
        {
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          children: [
            /* @__PURE__ */ jsxs93(
              Flex35,
              {
                minWidth: "14px",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "flex-start",
                children: [
                  /* @__PURE__ */ jsx142(Flex35, { width: "14px", height: "24px", alignItems: "center", mr: "8px", children: icon }),
                  /* @__PURE__ */ jsxs93(Flex35, { flexDirection: "column", children: [
                    /* @__PURE__ */ jsx142(Text14, { textStyle: "toastTitle", children: title }),
                    description && /* @__PURE__ */ jsxs93(Fragment19, { children: [
                      /* @__PURE__ */ jsx142(Box37, { height: "8px" }),
                      /* @__PURE__ */ jsx142(Text14, { textStyle: "toastDescription", children: description })
                    ] }),
                    url && /* @__PURE__ */ jsxs93(Fragment19, { children: [
                      /* @__PURE__ */ jsx142(Box37, { height: "8px" }),
                      /* @__PURE__ */ jsx142(Link, { href: url, children: /* @__PURE__ */ jsxs93(Flex35, { flexDirection: "row", alignItems: "center", children: [
                        /* @__PURE__ */ jsx142(Text14, { textStyle: "toastLink", children: linkText }),
                        isExternalLink(url) && /* @__PURE__ */ jsx142(
                          Icon9,
                          {
                            as: ExternalLink,
                            color: "indigo.60",
                            width: "16px",
                            height: "16px",
                            ml: "8px"
                          }
                        )
                      ] }) })
                    ] })
                  ] })
                ]
              }
            ),
            isClosable && /* @__PURE__ */ jsx142(
              Flex35,
              {
                onClick: () => toast.closeAll(),
                height: "24px",
                minWidth: "24px",
                borderRadius: "24px",
                justifyContent: "center",
                alignItems: "center",
                _hover: { cursor: "pointer", bg: "gray.30" },
                _active: { bg: "gray.40" },
                _focus: { outline: "none" },
                _disabled: { bg: "gray.20" },
                children: /* @__PURE__ */ jsx142(X, { color: "gray", size: "16px" })
              }
            )
          ]
        }
      ) })
    }
  );
};

// src/components/Toast/useErrorDisplay.tsx
import { useToast as useToast2 } from "@chakra-ui/react";
import { useCallback as useCallback9, useId } from "react";
import { jsx as jsx143 } from "react/jsx-runtime";
var useErrorDisplay = () => {
  const defaultId = useId();
  const toast = useToast2();
  const displayError = useCallback9(
    (description, error, id = defaultId) => {
      const message = `${description}: ${"message" in error ? error.message : `${error.status} ${JSON.stringify(
        error.data
      )}`}`;
      toast({
        id,
        position: "bottom",
        isClosable: true,
        render: () => /* @__PURE__ */ jsx143(Toast, { type: "error", title: message, isClosable: true })
      });
    },
    [toast, defaultId]
  );
  return {
    displayError
  };
};

// src/components/VirtualList/ListButton.tsx
import { Button as Button7, useMultiStyleConfig as useMultiStyleConfig6 } from "@chakra-ui/react";
import { standardHoverBackground as standardHoverBackground4 } from "@sequencehq/core-theme";
import { jsx as jsx144 } from "react/jsx-runtime";
var ListButton2 = ({
  label,
  leftIcon,
  onClick,
  px
}) => {
  const styles = useMultiStyleConfig6("CustomSelect");
  return /* @__PURE__ */ jsx144(
    Button7,
    {
      __css: styles.item,
      leftIcon,
      onClick,
      color: "grey.100",
      fontWeight: "700",
      display: "flex",
      px: px ? px : 4,
      py: 2,
      alignItems: "center",
      _hover: {
        bg: standardHoverBackground4
      },
      children: label
    }
  );
};

// src/components/VirtualList/ListContent.tsx
import { Text as Text15, HStack as HStack3 } from "@chakra-ui/react";
import { GreyGrey60 as GreyGrey6021, IndigoIndigo50 as IndigoIndigo5021 } from "@sequencehq/design-tokens";
import { jsx as jsx145, jsxs as jsxs94 } from "react/jsx-runtime";
var ListContent2 = (props) => {
  const { title, subTitle, icon, tag, px, selectedValue } = props;
  return /* @__PURE__ */ jsxs94(HStack3, { spacing: 2, px: px ? px : 4, py: 3, width: "100%", children: [
    icon,
    /* @__PURE__ */ jsx145(
      Text15,
      {
        textOverflow: "ellipsis",
        overflow: "hidden",
        color: selectedValue ? IndigoIndigo5021 : void 0,
        children: title
      }
    ),
    /* @__PURE__ */ jsx145(Text15, { color: GreyGrey6021, children: subTitle }),
    tag
  ] });
};

// src/components/VirtualList/ListDivider.tsx
import { internalBorder as internalBorder2 } from "@sequencehq/core-theme";
import { Flex as Flex36 } from "@chakra-ui/react";
import { jsx as jsx146 } from "react/jsx-runtime";
var ListDivider2 = () => /* @__PURE__ */ jsx146(Flex36, { borderBottom: internalBorder2, my: 2, width: "100%" });

// src/components/VirtualList/ListTitle.tsx
import { useMultiStyleConfig as useMultiStyleConfig7, Text as Text16 } from "@chakra-ui/react";
import { GreyGrey60 as GreyGrey6022 } from "@sequencehq/design-tokens";
import { jsx as jsx147 } from "react/jsx-runtime";
var ListTitle2 = ({ title, px }) => {
  const styles = useMultiStyleConfig7("CustomSelect");
  return /* @__PURE__ */ jsx147(
    Text16,
    {
      ...styles.groupTitle,
      px: px ? px : 4,
      pt: 3,
      pb: 2,
      backgroundColor: "white",
      color: GreyGrey6022,
      children: title
    }
  );
};

// src/components/VirtualList/VirtualList.tsx
import { Box as Box38, Icon as Icon10, List as List3, ListItem as ListItem3 } from "@chakra-ui/react";
import { standardHoverBackground as standardHoverBackground5 } from "@sequencehq/core-theme";
import { useVirtualizer as useVirtualizer3 } from "@tanstack/react-virtual";

// src/components/VirtualList/tokenToOption.tsx
import { match as match9 } from "ts-pattern";
import { jsx as jsx148 } from "react/jsx-runtime";
var tokenToOption2 = ({
  px,
  token,
  itemToContent,
  selectedValue
}) => {
  return match9(token).with({ type: "model" }, ({ model }) => /* @__PURE__ */ jsx148(
    ListContent2,
    {
      ...itemToContent(model),
      px,
      selectedValue
    }
  )).with({ type: "button" }, ({ label, leftIcon, onClick }) => /* @__PURE__ */ jsx148(ListButton2, { label, leftIcon, onClick, px })).with({ type: "divider" }, () => /* @__PURE__ */ jsx148(ListDivider2, {})).with({ type: "title" }, ({ title }) => /* @__PURE__ */ jsx148(ListTitle2, { title, px })).exhaustive();
};

// src/components/VirtualList/VirtualList.tsx
import { match as match10 } from "ts-pattern";
import { IndigoIndigo50 as IndigoIndigo5022 } from "@sequencehq/design-tokens";
import CheckIcon4 from "@heroicons/react/24/outline/CheckIcon";
import { jsx as jsx149, jsxs as jsxs95 } from "react/jsx-runtime";
var VirtualList2 = ({
  parentRef,
  menuProps,
  tokens,
  getItemProps,
  highlightedIndex,
  itemToContent,
  styles,
  tokenIsDisabled,
  px,
  getSizeByType: getSizeByType3,
  tokenToOption: tokenToOption3 = tokenToOption2,
  currentValueIndex
}) => {
  const { header, body, footer } = groupTokensByLocation2(tokens);
  const rowVirtualizer = useVirtualizer3({
    count: body.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => getSizeByType3(body[index]),
    overscan: 5,
    getItemKey: getItemKey3(body)
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
  return /* @__PURE__ */ jsx149(Box38, { ...menuProps, margin: "8px", children: /* @__PURE__ */ jsxs95(Box38, { children: [
    /* @__PURE__ */ jsx149(List3, { children: header.map((token, index) => /* @__PURE__ */ jsx149(
      TokenItem3,
      {
        token,
        tokenIsDisabled,
        highlightedIndex,
        getItemProps,
        itemToContent,
        styles,
        px,
        getSizeByType: getSizeByType3,
        tokenToOption: tokenToOption3
      },
      getItemKey3(header)(index)
    )) }),
    /* @__PURE__ */ jsx149(
      Box38,
      {
        ref: parentRef,
        overflowY: "auto",
        maxHeight: "200px",
        position: "relative",
        children: /* @__PURE__ */ jsxs95(List3, { height: `${rowVirtualizer.getTotalSize()}px`, children: [
          paddingTop > 0 && /* @__PURE__ */ jsx149("li", { style: { height: `${paddingTop}px` } }),
          virtualRows.map(({ key, index, size }) => /* @__PURE__ */ jsx149(
            TokenItem3,
            {
              token: body[index],
              tokenIsDisabled,
              highlightedIndex,
              getItemProps,
              itemToContent,
              styles,
              size,
              px,
              getSizeByType: getSizeByType3,
              tokenToOption: tokenToOption3,
              currentValueIndex
            },
            key
          )),
          paddingBottom > 0 && /* @__PURE__ */ jsx149(
            "li",
            {
              style: { height: `${paddingBottom}px`, marginBottom: "8px" }
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx149(List3, { children: footer.map((token, index) => /* @__PURE__ */ jsx149(
      TokenItem3,
      {
        token,
        tokenIsDisabled,
        highlightedIndex,
        getItemProps,
        itemToContent,
        styles,
        px,
        getSizeByType: getSizeByType3,
        tokenToOption: tokenToOption3
      },
      getItemKey3(footer)(index)
    )) })
  ] }) });
};
var TokenItem3 = ({
  token,
  tokenIsDisabled,
  highlightedIndex,
  getItemProps,
  itemToContent,
  styles,
  size,
  px,
  getSizeByType: getSizeByType3,
  tokenToOption: tokenToOption3,
  currentValueIndex
}) => {
  const disabled = (token.type === "divider" || token.type === "title" || tokenIsDisabled?.(token)) ?? false;
  const highLighted = highlightedIndex === token.tokenIndex;
  const selectedValue = currentValueIndex === token.tokenIndex;
  return /* @__PURE__ */ jsxs95(
    ListItem3,
    {
      display: selectedValue ? "flex" : void 0,
      justifyContent: selectedValue ? "space-between" : void 0,
      height: `${size ?? getSizeByType3(token)}px`,
      opacity: disabled && token.type !== "divider" ? 0.4 : 1,
      ...getItemProps({
        item: token,
        index: token.tokenIndex,
        disabled
      }),
      ...styles.item,
      backgroundColor: highLighted ? standardHoverBackground5 : "white",
      borderRadius: token.type !== "divider" ? "6px" : "0",
      color: selectedValue ? IndigoIndigo5022 : void 0,
      _notLast: {
        marginBottom: "4px"
      },
      children: [
        tokenToOption3({ token, itemToContent, px, selectedValue }),
        selectedValue ? /* @__PURE__ */ jsx149(Box38, { paddingRight: "16px", children: /* @__PURE__ */ jsx149(
          Icon10,
          {
            as: CheckIcon4,
            width: "16px",
            height: "16px",
            color: IndigoIndigo5022
          }
        ) }) : null
      ]
    }
  );
};
var groupTokensByLocation2 = (tokens) => {
  return tokens.reduce(
    (acc, item, tokenIndex) => {
      return match10(item.location).with("header", () => {
        acc.header.push({ ...item, tokenIndex });
        return acc;
      }).with("body", () => {
        acc.body.push({ ...item, tokenIndex });
        return acc;
      }).with("footer", () => {
        acc.footer.push({ ...item, tokenIndex });
        return acc;
      }).exhaustive();
    },
    {
      header: [],
      body: [],
      footer: []
    }
  );
};
var getItemKey3 = (tokens) => (index) => {
  const key = match10(tokens[index]).with({ type: "model" }, (matched) => matched.model.id).with({ type: "divider" }, () => "divider").with({ type: "button" }, (matched) => matched.key).with({ type: "title" }, (matched) => matched.title).exhaustive();
  return key;
};

// src/components/Card/Card.tsx
import { Box as Box39 } from "@chakra-ui/react";
import {
  GreyGrey30 as GreyGrey3015,
  GreyWhite as GreyWhite15,
  GreyGrey40 as GreyGrey4016,
  ShadowXs as ShadowXs5
} from "@sequencehq/design-tokens";
import { jsx as jsx150 } from "react/jsx-runtime";
var Card = ({
  children,
  variant = "default",
  ...props
}) => {
  return /* @__PURE__ */ jsx150(
    Box39,
    {
      borderRadius: "8px",
      border: `1px solid ${variant === "v2" ? GreyGrey4016 : GreyGrey3015}`,
      overflow: "hidden",
      boxShadow: variant === "v2" ? ShadowXs5 : "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
      backgroundColor: GreyWhite15,
      height: "fit-content",
      padding: "16px",
      ...props,
      children
    }
  );
};

// src/components/RadioButtonGroup.tsx
import { Box as Box40, Button as Button8, ButtonGroup } from "@chakra-ui/react";
import { GreyGrey10 as GreyGrey1010, GreyGrey40 as GreyGrey4017 } from "@sequencehq/design-tokens";
import { useEffect as useEffect18, useMemo as useMemo11, useState as useState26 } from "react";
import { jsx as jsx151, jsxs as jsxs96 } from "react/jsx-runtime";
var RadioButtonGroup = ({
  options,
  initialValue,
  value,
  onChange,
  fullWidth = false,
  "data-testid": dataTestId
}) => {
  const memoizedOptions = useMemo11(() => options, [options]);
  const [selectedOption, setSelectedOption] = useState26(
    options.find((option) => option.value === initialValue) ?? options[0]
  );
  useEffect18(() => {
    const option = memoizedOptions.find((opt) => opt.value === initialValue);
    if (option) {
      setSelectedOption(option);
    }
  }, [initialValue]);
  useEffect18(() => {
    const option = memoizedOptions.find((opt) => opt.value === value);
    if (option && selectedOption.value !== option.value) {
      setSelectedOption(option);
    }
  }, [value]);
  const select = (option) => {
    setSelectedOption(option);
    onChange(option.value);
  };
  return /* @__PURE__ */ jsxs96(
    ButtonGroup,
    {
      variant: "component-library-button-group",
      spacing: "0px",
      borderRadius: "6px",
      position: "relative",
      background: GreyGrey1010,
      height: 8,
      width: fullWidth ? "100%" : void 0,
      "data-testid": dataTestId,
      children: [
        memoizedOptions.map((option) => /* @__PURE__ */ jsx151(
          Button8,
          {
            onClick: () => select(option),
            isActive: selectedOption.value === option.value,
            "aria-selected": selectedOption.value === option.value,
            isDisabled: option.disabled,
            border: "1px solid transparent",
            "data-testid": dataTestId ? `${dataTestId}.${option.value}` : option.value,
            _focus: {},
            flex: 1,
            children: option.label
          },
          option.label
        )),
        /* @__PURE__ */ jsx151(
          Box40,
          {
            width: "100%",
            height: "100%",
            position: "absolute",
            top: "0",
            left: "0",
            border: `${GreyGrey4017} 1px solid`,
            borderRadius: "6px",
            pointerEvents: "none"
          }
        )
      ]
    }
  );
};

// src/components/Modal/Dialog.tsx
import { Box as Box42, Center as Center8, Flex as Flex38, Spinner as Spinner2, Text as Text18 } from "@chakra-ui/react";

// src/components/Modal/Modal.tsx
import {
  Modal as ChakraModal,
  ModalContent as ChakraModalContent,
  ModalOverlay,
  Text as Text17,
  Box as Box41,
  Flex as Flex37
} from "@chakra-ui/react";
import { XMarkIcon as XMarkIcon5 } from "@heroicons/react/20/solid";
import {
  GreyGrey30 as GreyGrey3016,
  GreyGrey60 as GreyGrey6023,
  GreyGrey80 as GreyGrey809,
  Lato13Regular as Lato13Regular23,
  Lato14Bold as Lato14Bold7
} from "@sequencehq/design-tokens";

// src/components/Modal/modal.context.tsx
import {
  createContext as createContext2,
  useContext as useContext2,
  useEffect as useEffect19,
  useState as useState27
} from "react";
import { jsx as jsx152 } from "react/jsx-runtime";
var defaults2 = {
  isOpen: false,
  setIsOpen: () => {
  }
};
var ModalContext = createContext2(defaults2);
var useModalContext = () => {
  return useContext2(ModalContext);
};
function withUnmountOnModalClose(Component) {
  return (props) => {
    const modalContext = useModalContext();
    if (!modalContext.isOpen) {
      return null;
    }
    return /* @__PURE__ */ jsx152(Component, { ...props });
  };
}
var ModalContextProvider = ({
  children,
  ...contextProps
}) => {
  const [isOpen, setIsOpen] = useState27(!!contextProps.isOpen);
  useEffect19(() => {
    setIsOpen(!!contextProps.isOpen);
  }, [contextProps.isOpen]);
  return /* @__PURE__ */ jsx152(
    ModalContext.Provider,
    {
      value: {
        ...defaults2,
        ...contextProps,
        isOpen,
        setIsOpen
      },
      children
    }
  );
};

// src/components/Modal/Modal.tsx
import { Fragment as Fragment20 } from "react";
import { jsx as jsx153, jsxs as jsxs97 } from "react/jsx-runtime";
var SharedModalComponent = ({
  children,
  width,
  height,
  onClose,
  onKeyDown,
  variant = "default",
  hideOverlay = false,
  unmountOnClose = false,
  "data-testid": dataTestId
}) => {
  const { isOpen, setIsOpen } = useModalContext();
  const Overlay = hideOverlay ? Fragment20 : ModalOverlay;
  return /* @__PURE__ */ jsx153(
    ChakraModal,
    {
      isCentered: variant !== "drawer",
      isOpen,
      onClose: () => {
        setIsOpen(false);
        onClose?.();
      },
      onEsc: () => {
        setIsOpen(false);
        onClose?.();
      },
      motionPreset: variant === "drawer" ? "slideInRight" : void 0,
      children: /* @__PURE__ */ jsx153(
        Overlay,
        {
          background: "rgba(0,0,0,0.2)",
          "data-testid": `${dataTestId}.overlay`,
          children: (isOpen || !unmountOnClose) && /* @__PURE__ */ jsx153(
            ModalContent,
            {
              width,
              height,
              variant,
              onKeyDown,
              "data-testid": dataTestId,
              children
            }
          )
        }
      )
    }
  );
};
var ModalContent = ({
  children,
  onKeyDown,
  variant,
  width,
  height,
  ["data-testid"]: dataTestId
}) => {
  const sharedProps = {
    minW: width ?? (variant === "dialog" ? "414px" : "480px"),
    borderRadius: 8,
    overflowY: "auto",
    onKeyDown
  };
  if (variant === "drawer") {
    return /* @__PURE__ */ jsx153(
      ChakraModalContent,
      {
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        margin: 2,
        "data-testid": dataTestId,
        ...sharedProps,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx153(
    ChakraModalContent,
    {
      height,
      maxHeight: "90vh",
      "data-testid": dataTestId,
      ...sharedProps,
      maxWidth: variant === "dialog" ? "414px" : void 0,
      children
    }
  );
};
var ModalComponent = (props) => {
  return /* @__PURE__ */ jsx153(SharedModalComponent, { ...props });
};
var Header = ({
  children,
  showCloseButton = false,
  withBorder = false,
  onClose,
  minHeight
}) => {
  return /* @__PURE__ */ jsxs97(
    Flex37,
    {
      px: 4,
      py: 3,
      justifyContent: "center",
      borderBottom: !withBorder ? void 0 : `1px solid ${GreyGrey3016}`,
      minHeight,
      children: [
        /* @__PURE__ */ jsx153(Text17, { paddingX: 1, ...Lato14Bold7, flex: 1, alignContent: "center", children }),
        showCloseButton && /* @__PURE__ */ jsx153(CloseTrigger, { onClick: onClose, children: /* @__PURE__ */ jsx153(
          Flex37,
          {
            justifyContent: "center",
            height: "24px",
            width: "24px",
            color: GreyGrey6023,
            _hover: { color: GreyGrey809 },
            children: /* @__PURE__ */ jsx153(XMarkIcon5, { width: 20 })
          }
        ) })
      ]
    }
  );
};
var Footer = ({
  children,
  withBorder = false
}) => {
  return /* @__PURE__ */ jsx153(
    Flex37,
    {
      paddingX: 3,
      paddingY: 2,
      borderTop: !withBorder ? void 0 : `1px solid ${GreyGrey3016}`,
      gap: 2,
      width: "100%",
      children
    }
  );
};
var Content = ({
  children,
  padding = 5,
  minHeight = "220px",
  maxHeight
}) => {
  return /* @__PURE__ */ jsx153(
    Box41,
    {
      padding,
      color: GreyGrey809,
      ...Lato13Regular23,
      minHeight,
      maxHeight,
      overflow: maxHeight ? "auto" : void 0,
      flex: 1,
      children
    }
  );
};
var CloseTrigger = ({ children, onClick }) => {
  const { setIsOpen } = useModalContext();
  if (typeof children === "function") {
    return children(() => setIsOpen(false));
  }
  return /* @__PURE__ */ jsx153("button", { type: "button", onClick: onClick ? onClick : () => setIsOpen(false), children });
};
var Trigger = ({ children }) => {
  const { setIsOpen } = useModalContext();
  if (typeof children === "function") {
    return children(() => setIsOpen(true));
  }
  return /* @__PURE__ */ jsx153("div", { onClick: () => setIsOpen(true), children });
};
var Modal = Object.assign(ModalComponent, {
  Header,
  Footer,
  Content,
  CloseTrigger,
  Trigger
});

// src/components/Modal/Dialog.tsx
import {
  GreyGrey20 as GreyGrey207,
  GreyGrey60 as GreyGrey6024,
  GreyGrey80 as GreyGrey8010,
  GreyWhite as GreyWhite16,
  Lato13Regular as Lato13Regular24,
  Lato14Bold as Lato14Bold8
} from "@sequencehq/design-tokens";
import { InformationCircleIcon as InformationCircleIcon2 } from "@heroicons/react/16/solid";
import { XMarkIcon as XMarkIcon6 } from "@heroicons/react/20/solid";
import { Fragment as Fragment21, jsx as jsx154, jsxs as jsxs98 } from "react/jsx-runtime";
var DialogComponent = (props) => {
  return /* @__PURE__ */ jsx154(SharedModalComponent, { variant: "dialog", ...props });
};
var DialogHeader = ({ children, showCloseButton = false }) => {
  return /* @__PURE__ */ jsxs98(
    Box42,
    {
      padding: 4,
      paddingBottom: 2,
      flex: 1,
      alignContent: "center",
      position: "relative",
      children: [
        /* @__PURE__ */ jsx154(
          Text18,
          {
            ...Lato14Bold8,
            flex: 1,
            alignContent: "center",
            marginRight: showCloseButton ? "16px" : void 0,
            children
          }
        ),
        showCloseButton && /* @__PURE__ */ jsx154(Center8, { position: "absolute", top: "8px", right: "8px", children: /* @__PURE__ */ jsx154(CloseTrigger, { children: /* @__PURE__ */ jsx154(
          Flex38,
          {
            justifyContent: "center",
            height: "24px",
            width: "24px",
            color: GreyGrey6024,
            _hover: { color: GreyGrey8010 },
            children: /* @__PURE__ */ jsx154(XMarkIcon6, { width: 20 })
          }
        ) }) })
      ]
    }
  );
};
var DialogContent = ({ children }) => {
  return /* @__PURE__ */ jsx154(Box42, { paddingX: 4, ...Lato13Regular24, children });
};
var DialogFooter = ({
  cancelButtonText = "Cancel",
  onCancel,
  confirmButtonText = "Continue",
  submittingButtonText,
  showCancelButton = true,
  onConfirm,
  confirmButtonDisabled = false,
  destructive = false,
  isSticky = false,
  closeOnCancel = true,
  closeOnSubmit = true,
  baseDataTestId,
  isSubmitting = false,
  submitButtonProps
}) => {
  return /* @__PURE__ */ jsx154(
    Flex38,
    {
      padding: 4,
      gap: 2,
      width: "100%",
      ...isSticky ? { background: GreyWhite16, position: "sticky", bottom: "0" } : {},
      children: /* @__PURE__ */ jsx154(Modal.CloseTrigger, { children: (closeModal) => /* @__PURE__ */ jsxs98(Fragment21, { children: [
        showCancelButton && cancelButtonText !== null && /* @__PURE__ */ jsx154(
          Button2,
          {
            "data-testid": baseDataTestId ? `${baseDataTestId}.cancel` : "dialog.footer.cancel",
            variant: "secondary",
            onClick: () => {
              onCancel?.();
              if (closeOnCancel) {
                closeModal();
              }
            },
            style: { flex: 1 },
            disabled: isSubmitting,
            children: cancelButtonText
          }
        ),
        confirmButtonText !== null && /* @__PURE__ */ jsx154(
          Button2,
          {
            "data-testid": baseDataTestId ? `${baseDataTestId}.confirm` : "dialog.footer.confirm",
            variant: destructive ? "destructive" : "primary",
            onClick: async () => {
              const res = await onConfirm?.();
              if (typeof res === "boolean" && res === false) {
                return;
              }
              if (closeOnSubmit) {
                closeModal();
              }
            },
            style: { flex: 1 },
            disabled: confirmButtonDisabled || isSubmitting,
            leadingIcon: isSubmitting ? /* @__PURE__ */ jsx154(Spinner2, { size: "sm" }) : void 0,
            trailingIcon: submitButtonProps?.trailingIcon,
            children: isSubmitting ? submittingButtonText ?? confirmButtonText : confirmButtonText
          }
        )
      ] }) })
    }
  );
};
var DialogNote = ({
  children,
  showIcon = true
}) => {
  return /* @__PURE__ */ jsxs98(
    Flex38,
    {
      alignItems: "center",
      background: GreyGrey207,
      padding: 4,
      gap: 2,
      borderRadius: 8,
      children: [
        showIcon && /* @__PURE__ */ jsx154(InformationCircleIcon2, { width: 16, height: 16 }),
        /* @__PURE__ */ jsx154(Box42, { children })
      ]
    }
  );
};
var Dialog = Object.assign(DialogComponent, {
  Header: DialogHeader,
  Content: DialogContent,
  Footer: DialogFooter,
  Trigger: Modal.Trigger,
  CloseTrigger: Modal.CloseTrigger,
  Note: DialogNote
});

// src/components/Modal/SimpleDialog.tsx
import { useEffect as useEffect20 } from "react";
import { Box as Box43, usePrevious as usePrevious2 } from "@chakra-ui/react";
import { jsx as jsx155, jsxs as jsxs99 } from "react/jsx-runtime";
var SimpleDialogUI = ({
  title,
  onCancel,
  onClose,
  onConfirm,
  children,
  cancelButtonText,
  confirmButtonText,
  submittingButtonText,
  destructive = false,
  hasStickyFooter = false,
  showCloseButton = false,
  showCancelButton = true,
  closeOnCancel = true,
  closeOnSubmit = true,
  confirmDisabled = false,
  isSubmitting = false,
  "data-testid": dataTestId,
  submitButtonProps
}) => {
  const showFooter = cancelButtonText !== null || confirmButtonText !== null;
  const { setIsOpen } = useModalContext();
  const prevIsSubmitting = usePrevious2(isSubmitting);
  useEffect20(() => {
    if (prevIsSubmitting && !isSubmitting) {
      onClose?.();
      setIsOpen(false);
    }
  }, [isSubmitting]);
  return /* @__PURE__ */ jsxs99(
    Dialog,
    {
      "data-testid": dataTestId,
      onClose: () => {
        onCancel?.();
        onClose?.();
      },
      children: [
        /* @__PURE__ */ jsx155(Dialog.Header, { showCloseButton, children: title }),
        /* @__PURE__ */ jsx155(Dialog.Content, { children }),
        !showFooter ? /* @__PURE__ */ jsx155(Box43, { height: 2 }) : /* @__PURE__ */ jsx155(
          Dialog.Footer,
          {
            isSticky: hasStickyFooter,
            cancelButtonText,
            showCancelButton,
            onCancel,
            confirmButtonText,
            submittingButtonText,
            onConfirm,
            destructive,
            confirmButtonDisabled: confirmDisabled,
            baseDataTestId: dataTestId,
            isSubmitting,
            closeOnCancel,
            closeOnSubmit,
            submitButtonProps
          }
        )
      ]
    }
  );
};
var SimpleDialog = ({ trigger, ...props }) => {
  return /* @__PURE__ */ jsxs99(ModalContextProvider, { children: [
    /* @__PURE__ */ jsx155(Dialog.Trigger, { children: trigger }),
    /* @__PURE__ */ jsx155(SimpleDialogUI, { ...props })
  ] });
};

// src/components/Modal/SimpleModal.tsx
import { Box as Box44, Grid as Grid4, GridItem as GridItem3, usePrevious as usePrevious3 } from "@chakra-ui/react";
import { useCallback as useCallback10, useEffect as useEffect21, useState as useState28 } from "react";
import { match as match11 } from "ts-pattern";
import { jsx as jsx156, jsxs as jsxs100 } from "react/jsx-runtime";
var getSubmitButtonState = ({
  externalSubmitDisabled,
  disableOnSubmit,
  submitting
}) => {
  if (externalSubmitDisabled) {
    return true;
  }
  if (disableOnSubmit) {
    return submitting;
  }
  return false;
};
var SimpleModalUI = ({
  title,
  onSubmit,
  onCancel,
  onClose,
  children,
  cancelButtonText,
  submitButtonText,
  disableOnSubmit = false,
  submitDisabled: externalSubmitDisabled = false,
  closeOnSubmit = true,
  variant,
  contentProps = {},
  closeOnCancel = true,
  unmountOnClose = false,
  hideOverlay = false,
  "data-testid": dataTestId
}) => {
  const { setIsOpen, isOpen } = useModalContext();
  const previousIsOpen = usePrevious3(isOpen);
  const [openCount, setOpenCount] = useState28(isOpen ? 1 : 0);
  const [submitting, setSubmitting] = useState28(false);
  useEffect21(() => {
    if (isOpen) {
      setOpenCount((prev) => prev + 1);
    }
  }, [isOpen]);
  const handleSubmit = useCallback10(() => {
    setSubmitting(true);
    onSubmit();
    if (closeOnSubmit) {
      setIsOpen(false);
    }
  }, [onSubmit, closeOnSubmit, setIsOpen]);
  const handleClose = useCallback10(() => {
    if (!submitting) {
      onCancel?.();
    }
    setSubmitting(false);
  }, [onCancel, submitting]);
  useEffect21(() => {
    if (isOpen === previousIsOpen) {
      return;
    }
    if (!isOpen && openCount > 0) {
      handleClose();
    }
  }, [isOpen, handleClose, openCount, previousIsOpen]);
  const submitDisabled = getSubmitButtonState({
    externalSubmitDisabled,
    disableOnSubmit,
    submitting
  });
  const handleKeyDown = useCallback10(
    (e) => {
      match11(e.key).with("Enter", () => {
        e.stopPropagation();
        if (submitDisabled) {
          return;
        }
        handleSubmit();
      }).with("Escape", () => {
        e.stopPropagation();
        setIsOpen(false);
      });
    },
    [handleSubmit, setIsOpen, submitDisabled]
  );
  const renderContent = unmountOnClose ? isOpen : true;
  return /* @__PURE__ */ jsx156(
    Modal,
    {
      variant,
      onKeyDown: handleKeyDown,
      hideOverlay,
      onClose,
      "data-testid": dataTestId,
      children: /* @__PURE__ */ jsxs100(
        Grid4,
        {
          gridTemplateAreas: `
        "header"
        "content"
        "footer"
        `,
          gridTemplateRows: "auto 1fr auto",
          overflow: "hidden",
          height: "100%",
          width: "100%",
          children: [
            /* @__PURE__ */ jsx156(GridItem3, { gridArea: "header", children: /* @__PURE__ */ jsx156(Modal.Header, { showCloseButton: true, withBorder: true, children: title }) }),
            /* @__PURE__ */ jsx156(GridItem3, { gridArea: "content", overflow: "auto", children: /* @__PURE__ */ jsx156(Modal.Content, { ...contentProps, children: renderContent ? children : null }) }),
            /* @__PURE__ */ jsx156(GridItem3, { gridArea: "footer", children: /* @__PURE__ */ jsxs100(Modal.Footer, { withBorder: true, children: [
              /* @__PURE__ */ jsx156(Box44, { flex: 1 }),
              /* @__PURE__ */ jsx156(
                Modal.CloseTrigger,
                {
                  onClick: !closeOnCancel ? () => {
                    onCancel?.();
                    onClose?.();
                  } : onClose,
                  children: /* @__PURE__ */ jsx156(
                    Button2,
                    {
                      "data-testid": dataTestId ? `${dataTestId}.cancel` : "modal.footer.cancel",
                      variant: "secondary",
                      children: cancelButtonText
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx156(
                Button2,
                {
                  "data-testid": dataTestId ? `${dataTestId}.submit` : "modal.footer.submit",
                  variant: "primary",
                  onClick: handleSubmit,
                  disabled: submitDisabled,
                  children: submitButtonText
                }
              )
            ] }) })
          ]
        }
      )
    }
  );
};
var SimpleModal = ({ trigger, ...props }) => {
  return /* @__PURE__ */ jsxs100(ModalContextProvider, { children: [
    /* @__PURE__ */ jsx156(Modal.Trigger, { children: trigger }),
    /* @__PURE__ */ jsx156(SimpleModalUI, { ...props })
  ] });
};

// src/components/Modal/SimpleAsyncModal.tsx
import {
  Box as Box45,
  Flex as Flex39,
  Grid as Grid5,
  GridItem as GridItem4,
  Spinner as Spinner3,
  usePrevious as usePrevious4
} from "@chakra-ui/react";
import { useCallback as useCallback11, useEffect as useEffect22, useState as useState29 } from "react";
import { match as match12 } from "ts-pattern";
import { GreyGrey30 as GreyGrey3017, GreyGrey70 as GreyGrey709 } from "@sequencehq/design-tokens";
import { jsx as jsx157, jsxs as jsxs101 } from "react/jsx-runtime";
var SimpleAsyncModalUI = ({
  title,
  onSubmit,
  onCancel,
  children,
  cancelButtonText,
  submitButtonText,
  submittingButtonText,
  submitDisabled = false,
  hideSubmitButton = false,
  closeOnSubmit = true,
  variant,
  contentProps = {},
  closeOnCancel = true,
  unmountOnClose = false,
  "data-testid": dataTestId,
  drawerLeadingContent,
  width
}) => {
  const { setIsOpen, isOpen } = useModalContext();
  const [openCount, setOpenCount] = useState29(isOpen ? 1 : 0);
  const previousIsOpen = usePrevious4(isOpen);
  const [submitting, setSubmitting] = useState29(false);
  useEffect22(() => {
    if (isOpen) {
      setOpenCount((prev) => prev + 1);
    }
  }, [isOpen]);
  const handleSubmit = useCallback11(async () => {
    setSubmitting(true);
    const success = await onSubmit();
    setSubmitting(false);
    if (closeOnSubmit && success) {
      setIsOpen(false);
    }
  }, [onSubmit, closeOnSubmit, setIsOpen]);
  const handleClose = useCallback11(() => {
    if (!submitting) {
      onCancel?.();
    }
    setSubmitting(false);
  }, [onCancel, submitting]);
  useEffect22(() => {
    if (isOpen === previousIsOpen) {
      return;
    }
    if (!isOpen && openCount > 0) {
      handleClose();
    }
  }, [isOpen, handleClose, openCount, previousIsOpen]);
  const handleKeyDown = useCallback11(
    (e) => {
      match12(e.key).with("Enter", () => {
        e.stopPropagation();
        if (submitDisabled) {
          return;
        }
        void handleSubmit();
      }).with("Escape", () => {
        e.stopPropagation();
        setIsOpen(false);
      });
    },
    [handleSubmit, setIsOpen, submitDisabled]
  );
  const renderContent = unmountOnClose ? isOpen : true;
  return /* @__PURE__ */ jsx157(
    Modal,
    {
      variant,
      onKeyDown: handleKeyDown,
      "data-testid": dataTestId,
      width: width ?? (variant === "drawer" && drawerLeadingContent ? "984px" : void 0),
      children: /* @__PURE__ */ jsxs101(
        Grid5,
        {
          gridTemplateAreas: drawerLeadingContent ? `"drawerLeadingContent mainContent"` : `"mainContent"`,
          gridTemplateColumns: drawerLeadingContent ? "1fr 1fr" : "1fr",
          overflow: "hidden",
          height: "100%",
          width: "100%",
          children: [
            drawerLeadingContent && /* @__PURE__ */ jsx157(
              GridItem4,
              {
                gridArea: "drawerLeadingContent",
                borderRight: `${GreyGrey3017} 1px solid`,
                minHeight: "0",
                children: drawerLeadingContent
              }
            ),
            /* @__PURE__ */ jsx157(GridItem4, { gridArea: "mainContent", minHeight: "0", children: /* @__PURE__ */ jsxs101(
              Grid5,
              {
                gridTemplateAreas: `
        "header"
        "content"
        "footer"
        `,
                gridTemplateRows: "auto 1fr auto",
                overflow: "hidden",
                height: "100%",
                width: "100%",
                children: [
                  /* @__PURE__ */ jsx157(GridItem4, { gridArea: "header", children: /* @__PURE__ */ jsx157(Modal.Header, { showCloseButton: true, withBorder: true, children: title }) }),
                  /* @__PURE__ */ jsx157(GridItem4, { gridArea: "content", overflow: "auto", children: /* @__PURE__ */ jsx157(Modal.Content, { ...contentProps, children: renderContent ? children : null }) }),
                  /* @__PURE__ */ jsx157(GridItem4, { gridArea: "footer", children: /* @__PURE__ */ jsxs101(Modal.Footer, { withBorder: true, children: [
                    /* @__PURE__ */ jsx157(Box45, { flex: 1 }),
                    /* @__PURE__ */ jsx157(
                      Modal.CloseTrigger,
                      {
                        onClick: !closeOnCancel ? onCancel : void 0,
                        children: /* @__PURE__ */ jsx157(
                          Button2,
                          {
                            "data-testid": dataTestId ? `${dataTestId}.cancel` : "modal.footer.cancel",
                            variant: "secondary",
                            children: cancelButtonText
                          }
                        )
                      }
                    ),
                    !hideSubmitButton && /* @__PURE__ */ jsx157(
                      Button2,
                      {
                        "data-testid": dataTestId ? `${dataTestId}.submit` : "modal.footer.submit",
                        variant: "primary",
                        onClick: () => void handleSubmit(),
                        disabled: submitDisabled || submitting,
                        children: /* @__PURE__ */ jsxs101(Flex39, { gap: "8px", alignItems: "center", children: [
                          submitting && /* @__PURE__ */ jsx157(
                            Spinner3,
                            {
                              height: "16px",
                              width: "16px",
                              color: GreyGrey709
                            }
                          ),
                          submitting ? submittingButtonText : submitButtonText
                        ] })
                      }
                    )
                  ] }) })
                ]
              }
            ) })
          ]
        }
      )
    }
  );
};
var SimpleAsyncModal = ({ trigger, ...props }) => {
  return /* @__PURE__ */ jsxs101(ModalContextProvider, { children: [
    /* @__PURE__ */ jsx157(Modal.Trigger, { children: trigger }),
    /* @__PURE__ */ jsx157(SimpleAsyncModalUI, { ...props })
  ] });
};

// src/components/Banner/Banner.tsx
import styled3 from "@emotion/styled";
import { match as match13 } from "ts-pattern";
import {
  CheckCircleIcon as CheckCircleIcon3,
  ExclamationTriangleIcon as ExclamationTriangleIcon2,
  InformationCircleIcon as InformationCircleIcon3,
  XCircleIcon
} from "@heroicons/react/16/solid";
import {
  GreenGreen10 as GreenGreen102,
  GreyGrey10 as GreyGrey1011,
  GreyGrey20 as GreyGrey208,
  GreyGrey70 as GreyGrey7010,
  GreyGrey80 as GreyGrey8011
} from "@sequencehq/design-tokens";
import { Spinner as Spinner4 } from "@chakra-ui/react";
import { jsx as jsx158, jsxs as jsxs102 } from "react/jsx-runtime";
var BannerWrapper = styled3.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 8px;
  font-size: 13px;
  line-height: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  width: 100%;
  align-items: center;

  ${(props) => match13(props.variant).with(
  "neutral",
  () => `
          background-color: ${GreyGrey208};
          color: ${GreyGrey8011};
        `
).with(
  "info",
  () => `
          background-color: #F2F2FF;
          color: #5F5CFF;
        `
).with(
  "success",
  () => `
          background-color: ${GreenGreen102};
          color: #44724C;
        `
).with(
  "warning",
  () => `
          background-color: #FFF6DF;
          color: #7D5D07;
        `
).with(
  "error",
  () => `
          background-color: #FFF2F2;
          color: #CC0F26;
        `
).with(
  "loading",
  () => `
        background-color: ${GreyGrey1011};
        color: ${GreyGrey7010};
      `
).exhaustive()}
`;
var BannerWrapperText = styled3.div`
  strong {
    font-weight: 600;
  }
`;
var BannerWrapperIcon = styled3.div`
  flex-shrink: 0;
  height: fit-content;
`;
var Banner = ({
  variant,
  withIcon = true,
  alignIcon = "start",
  "data-testid": dataTestId,
  children,
  IconComponent
}) => {
  const iconProps = {
    width: "16px",
    height: "16px",
    color: "inherit"
  };
  const icon = IconComponent ? /* @__PURE__ */ jsx158(IconComponent, { ...iconProps }) : match13(variant).with("neutral", "info", () => /* @__PURE__ */ jsx158(InformationCircleIcon3, { ...iconProps })).with("success", () => /* @__PURE__ */ jsx158(CheckCircleIcon3, { ...iconProps })).with("warning", () => /* @__PURE__ */ jsx158(ExclamationTriangleIcon2, { ...iconProps })).with("error", () => /* @__PURE__ */ jsx158(XCircleIcon, { ...iconProps })).with("loading", () => /* @__PURE__ */ jsx158(Spinner4, { ...iconProps })).exhaustive();
  return /* @__PURE__ */ jsxs102(BannerWrapper, { variant, "data-testid": dataTestId, children: [
    withIcon ? /* @__PURE__ */ jsx158(
      BannerWrapperIcon,
      {
        style: {
          alignSelf: alignIcon
        },
        children: icon
      }
    ) : null,
    /* @__PURE__ */ jsx158(BannerWrapperText, { children })
  ] });
};

// src/components/Checkbox.tsx
import { Box as Box46, Flex as Flex40 } from "@chakra-ui/react";
import { Lato13Regular as Lato13Regular25 } from "@sequencehq/design-tokens";
import { useEffect as useEffect23, useRef as useRef21 } from "react";
import { jsx as jsx159, jsxs as jsxs103 } from "react/jsx-runtime";
var Checkbox3 = ({
  label,
  disabled,
  isChecked = false,
  onChange,
  isIndeterminate = false,
  id,
  "data-testid": dataTestId
}) => {
  const checkboxRef = useRef21(null);
  useEffect23(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  return /* @__PURE__ */ jsxs103(Flex40, { gap: "8px", alignItems: "start", ...Lato13Regular25, children: [
    /* @__PURE__ */ jsx159(
      "input",
      {
        "data-testid": dataTestId,
        type: "checkbox",
        checked: isChecked,
        disabled,
        id,
        onChange: (e) => {
          if (disabled) {
            return;
          }
          onChange?.(e);
        },
        ref: checkboxRef,
        style: {
          cursor: !disabled ? "pointer" : "not-allowed",
          marginTop: "2px"
        }
      }
    ),
    /* @__PURE__ */ jsx159(
      Box46,
      {
        as: "label",
        htmlFor: id,
        cursor: !disabled ? "pointer" : "not-allowed",
        children: label
      }
    )
  ] });
};

// src/components/RadioButton.tsx
import { Box as Box47, Flex as Flex41 } from "@chakra-ui/react";
import { Lato13Regular as Lato13Regular26 } from "@sequencehq/design-tokens";
import { jsx as jsx160, jsxs as jsxs104 } from "react/jsx-runtime";
var RadioButton = ({
  name,
  label,
  disabled,
  isChecked = false,
  onChange,
  id,
  "data-testid": dataTestId
}) => {
  return /* @__PURE__ */ jsxs104(Flex41, { gap: "8px", alignItems: "start", ...Lato13Regular26, children: [
    /* @__PURE__ */ jsx160(
      "input",
      {
        type: "radio",
        name,
        checked: isChecked,
        disabled,
        id,
        onChange: () => {
          if (disabled) {
            return;
          }
          onChange?.(!isChecked);
        },
        style: {
          cursor: !disabled ? "pointer" : "not-allowed",
          marginTop: "2px"
        },
        "data-testid": dataTestId
      }
    ),
    /* @__PURE__ */ jsx160(
      Box47,
      {
        as: "label",
        htmlFor: id,
        cursor: !disabled ? "pointer" : "not-allowed",
        onClick: () => {
          if (disabled) {
            return;
          }
          onChange?.(!isChecked);
        },
        children: label
      }
    )
  ] });
};

// src/components/Inspector/Inspector.tsx
import { Box as Box48, Grid as Grid6, GridItem as GridItem5 } from "@chakra-ui/react";

// src/components/Inspector/inspector.domain.ts
import {
  createContext as createContext3,
  useCallback as useCallback12,
  useContext as useContext3,
  useEffect as useEffect24,
  useMemo as useMemo12,
  useState as useState30
} from "react";
var loadExpansionState = () => {
  try {
    return localStorage.getItem("inspectorExpanded") === "true";
  } catch {
    return null;
  }
};
var saveExpansionState = (newState) => {
  try {
    localStorage.setItem("inspectorExpanded", newState.toString());
    return;
  } catch {
    return;
  }
};
var shouldExpandInspector = (args) => {
  if (args.showContentOnMount || args.userPersistedExpansion) {
    return true;
  }
  if (args.userPersistedExpansion === false) {
    return false;
  }
  return args.windowWidth >= 1500;
};
var useInspectorDomain = (props) => {
  const userPersistedExpansion = useMemo12(() => loadExpansionState(), []);
  const [activeItemId, setActiveItemId] = useState30(null);
  const [items, setItems] = useState30([]);
  const [initialLoadDone, setInitialLoadDone] = useState30(false);
  const [expanded, setExpanded] = useState30(
    shouldExpandInspector({
      userPersistedExpansion,
      windowWidth: window.innerWidth,
      showContentOnMount: props.showContentOnMount ?? false
    })
  );
  const toggleActiveItem = useCallback12(
    async (newActiveItemId) => {
      if (activeItemId === newActiveItemId) {
        setExpanded(false);
        saveExpansionState(false);
        await new Promise((resolve) => setTimeout(resolve, 250));
        setActiveItemId(null);
        return;
      }
      setExpanded(true);
      saveExpansionState(true);
      setActiveItemId(newActiveItemId);
    },
    [activeItemId]
  );
  const enhancedItems = useMemo12(() => {
    return items.map((item) => ({
      ...item,
      active: item.id === activeItemId,
      onSelect: () => {
        void toggleActiveItem(item.id);
      }
    }));
  }, [items, activeItemId, toggleActiveItem]);
  const registerItem = useCallback12((newItem) => {
    setItems((prevItems) => [...prevItems, newItem]);
    return {
      remove: () => {
        setItems((prevItems) => prevItems.filter((item) => item.id !== newItem.id));
      }
    };
  }, []);
  const activeContent = useMemo12(() => {
    return enhancedItems.find((tab) => tab.active)?.content;
  }, [enhancedItems]);
  useEffect24(() => {
    if (!activeContent && expanded && items.length) {
      toggleActiveItem(items[0].id);
    }
  }, [activeContent, expanded, toggleActiveItem, items]);
  useEffect24(() => {
    if (initialLoadDone || !items.length) {
      return;
    }
    if (shouldExpandInspector({
      userPersistedExpansion,
      windowWidth: window.innerWidth,
      showContentOnMount: props.showContentOnMount ?? false
    })) {
      const defaultItem = items.find((item) => item.id === props.defaultItemId);
      setActiveItemId(defaultItem?.id ?? items[0].id);
    } else {
      setActiveItemId(null);
    }
    setInitialLoadDone(true);
  }, [
    initialLoadDone,
    items,
    props.showContentOnMount,
    userPersistedExpansion,
    props.defaultItemId
  ]);
  useEffect24(() => {
    if (!items.length || !props.defaultItemId)
      return;
    const defaultItem = items.find((item) => item.id === props.defaultItemId);
    if (!defaultItem)
      return;
    setActiveItemId(defaultItem.id);
  }, [props.defaultItemId, items]);
  useEffect24(() => {
    if (!initialLoadDone) {
      const loadTimeout = setTimeout(() => setInitialLoadDone(true), 3e3);
      return () => {
        clearTimeout(loadTimeout);
      };
    }
  }, [initialLoadDone]);
  return {
    showInspector: items.length > 0,
    activeContent,
    loaded: initialLoadDone,
    activeItemId,
    expanded,
    registerItem,
    updateActiveItem: (itemId) => void toggleActiveItem(itemId)
  };
};
var InspectorContext = createContext3(
  null
);
var useInspectorContext = () => {
  const ctx = useContext3(InspectorContext);
  if (!ctx) {
    throw new Error(
      "Inspector item needs to be mounted as a child of an Inspector component"
    );
  }
  return ctx;
};

// src/components/Inspector/Inspector.tsx
import {
  useLayoutEffect as useLayoutEffect4,
  useMemo as useMemo13,
  useRef as useRef22,
  useState as useState31
} from "react";
import { GreyGrey30 as GreyGrey3018, GreyWhite as GreyWhite17 } from "@sequencehq/design-tokens";

// src/components/Skeleton.tsx
import {
  Skeleton as ChakraSkeleton
} from "@chakra-ui/react";
import { GreyGrey0 as GreyGrey06, GreyGrey20 as GreyGrey209 } from "@sequencehq/design-tokens";
import { jsx as jsx161 } from "react/jsx-runtime";
var Skeleton = (props) => {
  return /* @__PURE__ */ jsx161(
    ChakraSkeleton,
    {
      height: props.height ?? "100%",
      width: props.width ?? "100%",
      minHeight: props.minHeight,
      minWidth: props.minWidth,
      borderRadius: props.borderRadius ?? "8px",
      backgroundColor: GreyGrey209,
      endColor: GreyGrey209,
      startColor: GreyGrey06,
      opacity: "unset"
    }
  );
};

// src/components/Inspector/Inspector.tsx
import { jsx as jsx162, jsxs as jsxs105 } from "react/jsx-runtime";
var Inspector = ({
  showContentOnMount = false,
  displayPlaceholderDuringInitialization = true,
  children,
  defaultItemId
}) => {
  const domain = useInspectorDomain({
    showContentOnMount,
    defaultItemId
  });
  const animationContainerRef = useRef22(null);
  const [enableAnimation, setEnableAnimation] = useState31(false);
  const [animationContainerWidth, setAnimationContainerWidth] = useState31(0);
  useLayoutEffect4(() => {
    const childElement = animationContainerRef.current?.firstChild;
    if (childElement) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setAnimationContainerWidth(entry.target.clientWidth);
        }
      });
      observer.observe(childElement);
      return () => {
        observer.disconnect();
      };
    }
  }, [domain.activeContent]);
  useLayoutEffect4(() => {
    if (domain.loaded && (animationContainerWidth > 0 || !domain.activeContent)) {
      setTimeout(() => setEnableAnimation(true), 100);
    }
  }, [domain.loaded, animationContainerWidth, domain.activeContent]);
  const contentToShow = useMemo13(() => {
    if (domain.loaded) {
      return domain.activeContent;
    }
    if (displayPlaceholderDuringInitialization) {
      return /* @__PURE__ */ jsx162(Box48, { padding: "16px", height: "100%", width: "320px", children: /* @__PURE__ */ jsx162(Skeleton, { height: "100%", width: "100%" }) });
    }
    return null;
  }, [
    domain.loaded,
    domain.activeContent,
    displayPlaceholderDuringInitialization
  ]);
  const widths = useMemo13(() => {
    if (domain.loaded && animationContainerWidth) {
      return {
        width: domain.expanded ? animationContainerWidth : "0",
        maxWidth: domain.expanded ? animationContainerWidth : "0"
      };
    }
    if (displayPlaceholderDuringInitialization && domain.expanded) {
      return {
        width: "320px",
        maxWidth: "320px"
      };
    }
    return {
      width: "0",
      maxWidth: "0"
    };
  }, [
    animationContainerWidth,
    domain.expanded,
    domain.loaded,
    displayPlaceholderDuringInitialization
  ]);
  const displayInspector = domain.showInspector || !domain.loaded && displayPlaceholderDuringInitialization;
  return /* @__PURE__ */ jsx162(InspectorContext.Provider, { value: domain, children: /* @__PURE__ */ jsxs105(
    Grid6,
    {
      display: displayInspector ? "grid" : "none",
      gridTemplateColumns: `auto auto`,
      gridTemplateAreas: `"content tabList"`,
      height: "100%",
      overflow: "hidden",
      backgroundColor: GreyWhite17,
      "data-testid": "inspector",
      children: [
        /* @__PURE__ */ jsx162(
          GridItem5,
          {
            borderLeft: domain.expanded ? `1px solid ${GreyGrey3018}` : "0px solid transparent",
            ...widths,
            transition: enableAnimation ? "width 0.25s ease-out, border-left 0.25s ease-out, max-width 0.25s ease-out" : "none",
            gridArea: "content",
            height: "100%",
            overflow: "hidden",
            ref: animationContainerRef,
            children: contentToShow
          }
        ),
        /* @__PURE__ */ jsx162(
          GridItem5,
          {
            gridArea: "tabList",
            height: "100%",
            overflow: "auto",
            borderLeft: `1px solid ${GreyGrey3018}`,
            width: "min-content",
            children
          }
        )
      ]
    }
  ) });
};

// src/components/Inspector/InspectorContent.tsx
import { Box as Box49, Flex as Flex42 } from "@chakra-ui/react";
import { Lato14Bold as Lato14Bold9 } from "@sequencehq/design-tokens";
import { jsx as jsx163, jsxs as jsxs106 } from "react/jsx-runtime";
var InspectorContent = (props) => {
  return /* @__PURE__ */ jsxs106(
    Flex42,
    {
      width: props.width ?? "320px",
      height: "100%",
      overflow: "auto",
      padding: props.padding ?? "16px",
      gap: "16px",
      "data-testid": props["data-testid"],
      flexDirection: "column",
      children: [
        Boolean(props.title) && /* @__PURE__ */ jsxs106(Flex42, { ...Lato14Bold9, gap: "8px", children: [
          props.icon ? props.icon : null,
          props.title
        ] }),
        /* @__PURE__ */ jsx163(Box49, { flex: "1", children: props.children })
      ]
    }
  );
};

// src/components/Inspector/InspectorIconItem.tsx
import { Flex as Flex43 } from "@chakra-ui/react";
import { Fragment as Fragment22 } from "react";
import { GreyGrey20 as GreyGrey2010 } from "@sequencehq/design-tokens";

// src/components/Inspector/useInspectorItem.ts
import { useCallback as useCallback13, useEffect as useEffect25, useId as useId2 } from "react";
var useInspectorItem = (props) => {
  const randomId = useId2();
  const itemId = props.id ?? randomId;
  const { registerItem, activeItemId, updateActiveItem } = useInspectorContext();
  useEffect25(() => {
    const { remove } = registerItem({
      id: itemId,
      content: props.content
    });
    return () => {
      remove();
    };
  }, [props.content, registerItem, itemId]);
  const onClick = useCallback13(() => {
    updateActiveItem(itemId);
  }, [updateActiveItem, itemId]);
  return {
    active: activeItemId === itemId,
    onClick
  };
};

// src/components/Inspector/InspectorItem.tsx
var InspectorItem = (props) => {
  const { active, onClick } = useInspectorItem({
    content: props.content,
    id: props.id
  });
  return props.children({ active, onClick });
};

// src/components/Inspector/InspectorIconItem.tsx
import { jsx as jsx164 } from "react/jsx-runtime";
var InspectorIconItem = (props) => {
  const Wrapper = props.tooltip ? Tooltip : Fragment22;
  const activeIcon = props.activeIcon || props.icon;
  return /* @__PURE__ */ jsx164(InspectorItem, { id: props.id, content: props.content, children: ({ active, onClick }) => /* @__PURE__ */ jsx164(
    Wrapper,
    {
      ...props.tooltip ? {
        label: props.tooltip,
        placement: "left"
      } : {},
      children: /* @__PURE__ */ jsx164(
        Flex43,
        {
          "data-testid": props["data-testid"],
          textDecoration: "none",
          height: "32px",
          width: "32px",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: active ? GreyGrey2010 : "transparent",
          _hover: {
            backgroundColor: GreyGrey2010
          },
          onClick,
          children: active ? activeIcon : props.icon
        }
      )
    }
  ) });
};

// src/components/Inspector/InspectorItemGroup.tsx
import { Grid as Grid7 } from "@chakra-ui/react";
import { jsx as jsx165 } from "react/jsx-runtime";
var InspectorItemGroup = (props) => {
  return /* @__PURE__ */ jsx165(
    Grid7,
    {
      display: "grid",
      alignContent: "start",
      rowGap: "2px",
      gridAutoFlow: "row",
      padding: "8px",
      children: props.children
    }
  );
};

// src/components/Inspector/InspectorEmptyState.tsx
import { Box as Box50, Flex as Flex44, Grid as Grid8, Text as Text19 } from "@chakra-ui/react";
import {
  GreyGrey20 as GreyGrey2011,
  GreyGrey50 as GreyGrey5011,
  GreyGrey60 as GreyGrey6025,
  GreyGrey70 as GreyGrey7011,
  GreyGrey80 as GreyGrey8012,
  Lato12Regular,
  Lato13Bold as Lato13Bold12,
  Lato13Regular as Lato13Regular27
} from "@sequencehq/design-tokens";
import { jsx as jsx166, jsxs as jsxs107 } from "react/jsx-runtime";
var InspectorEmptyState = ({
  IconComponent,
  title,
  body,
  button,
  helperText
}) => {
  return /* @__PURE__ */ jsx166(
    Grid8,
    {
      justifyContent: "center",
      alignContent: "center",
      minHeight: "100%",
      textAlign: "center",
      padding: 10,
      children: /* @__PURE__ */ jsxs107(Flex44, { direction: "column", alignItems: "center", gap: 4, children: [
        IconComponent && /* @__PURE__ */ jsx166(
          EmptyStateIcon,
          {
            icon: /* @__PURE__ */ jsx166(IconComponent, { width: "24px", height: "24px", color: GreyGrey7011 })
          }
        ),
        /* @__PURE__ */ jsxs107(Flex44, { flexDir: "column", gap: 1, children: [
          /* @__PURE__ */ jsx166(Text19, { ...Lato13Bold12, color: GreyGrey8012, children: title }),
          /* @__PURE__ */ jsx166(Text19, { ...Lato13Regular27, color: GreyGrey6025, children: body })
        ] }),
        /* @__PURE__ */ jsxs107(Flex44, { flexDir: "column", gap: 1, children: [
          button && /* @__PURE__ */ jsx166(Box50, { children: button }),
          helperText && /* @__PURE__ */ jsx166(Text19, { ...Lato12Regular, color: GreyGrey5011, children: helperText })
        ] })
      ] })
    }
  );
};
var EmptyStateIcon = ({ icon }) => {
  return /* @__PURE__ */ jsx166(
    Grid8,
    {
      width: "36px",
      height: "36px",
      backgroundColor: GreyGrey2011,
      borderRadius: "6px",
      justifyContent: "center",
      alignContent: "center",
      children: icon
    }
  );
};

// src/components/Inspector/index.ts
var Inspector2 = Object.assign(Inspector, {
  Content: InspectorContent,
  Item: InspectorItem,
  IconItem: InspectorIconItem,
  ItemGroup: InspectorItemGroup
});

// src/components/TextOverflow.tsx
import { useEffect as useEffect26, useRef as useRef23, useState as useState32 } from "react";
import { jsx as jsx167, jsxs as jsxs108 } from "react/jsx-runtime";
var TextOverflow = ({ children, splitPercentage = 70 }) => {
  const ref = useRef23(null);
  const [isOverflowing, setIsOverflowing] = useState32(false);
  useEffect26(() => {
    if (ref.current) {
      setIsOverflowing(ref.current.clientWidth < ref.current.scrollWidth);
    }
  }, [setIsOverflowing]);
  return /* @__PURE__ */ jsxs108(
    "div",
    {
      style: {
        overflow: "hidden",
        whiteSpace: "nowrap",
        display: "flex"
      },
      children: [
        /* @__PURE__ */ jsx167(
          "div",
          {
            ref,
            style: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: splitPercentage
            },
            children
          }
        ),
        isOverflowing && /* @__PURE__ */ jsx167(
          "div",
          {
            style: {
              flex: 100 - splitPercentage,
              direction: "rtl",
              overflow: "hidden"
            },
            children
          }
        )
      ]
    }
  );
};

// src/components/FilePickerButton.tsx
import { useRef as useRef24 } from "react";
import { Fragment as Fragment23, jsx as jsx168, jsxs as jsxs109 } from "react/jsx-runtime";
var FilePickerButton = ({
  accept,
  multiple = false,
  onFilesPicked,
  ...props
}) => {
  const fileInputRef = useRef24(null);
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const handleFilePicked = () => {
    const files = fileInputRef.current?.files;
    if (!files) {
      return;
    }
    onFilesPicked?.(Array.from(files));
    fileInputRef.current.value = "";
  };
  return /* @__PURE__ */ jsxs109(Fragment23, { children: [
    /* @__PURE__ */ jsx168(
      "input",
      {
        type: "file",
        style: { display: "none" },
        ref: fileInputRef,
        accept,
        multiple,
        onChange: handleFilePicked
      }
    ),
    /* @__PURE__ */ jsx168(Button2, { ...props, onClick: triggerFileInput })
  ] });
};

// src/components/MetricCard/MetricCard.tsx
import {
  GreyGrey30 as GreyGrey3019,
  ShadowXs as ShadowXs6,
  Lato13Bold as Lato13Bold13,
  GreyGrey60 as GreyGrey6026,
  Lato18Bold,
  GreyGrey90 as GreyGrey9011,
  Lato13Regular as Lato13Regular28
} from "@sequencehq/design-tokens";
import { Box as Box51, Flex as Flex45, Text as Text20 } from "@chakra-ui/react";
import { jsx as jsx169, jsxs as jsxs110 } from "react/jsx-runtime";
var MetricCard = ({
  children,
  header,
  subheader,
  amount,
  inputElement,
  isUpdating,
  width = "100%",
  height = "100%"
}) => {
  return /* @__PURE__ */ jsxs110(
    Flex45,
    {
      borderRadius: "6px",
      padding: "16px",
      border: `1px solid ${GreyGrey3019}`,
      boxShadow: ShadowXs6,
      width,
      height,
      flexDirection: "column",
      gap: "16px",
      children: [
        /* @__PURE__ */ jsxs110(Flex45, { alignItems: "flex-start", justifyContent: "space-between", children: [
          /* @__PURE__ */ jsxs110(Flex45, { flexDirection: "column", gap: "8px", children: [
            /* @__PURE__ */ jsx169(Text20, { ...Lato13Bold13, fontWeight: 600, color: GreyGrey6026, children: header }),
            /* @__PURE__ */ jsx169(Text20, { ...Lato18Bold, color: GreyGrey9011, children: amount }),
            /* @__PURE__ */ jsx169(Text20, { ...Lato13Regular28, color: GreyGrey6026, children: subheader })
          ] }),
          inputElement && /* @__PURE__ */ jsx169(Box51, { flexBasis: "33.3%", children: inputElement })
        ] }),
        /* @__PURE__ */ jsx169(
          Box51,
          {
            width: "100%",
            height: "100%",
            opacity: isUpdating ? 0.5 : 1,
            transition: "opacity 250ms",
            children
          }
        )
      ]
    }
  );
};

// src/components/Layouts/StandardLayout.tsx
import { Box as Box52, Flex as Flex46, Grid as Grid9, GridItem as GridItem6 } from "@chakra-ui/react";
import {
  GreyGrey0 as GreyGrey07,
  GreyGrey30 as GreyGrey3020,
  GreyWhite as GreyWhite18,
  Lato14Bold as Lato14Bold10
} from "@sequencehq/design-tokens";
import { jsx as jsx170, jsxs as jsxs111 } from "react/jsx-runtime";
var StandardLayoutBase = (props) => {
  return /* @__PURE__ */ jsxs111(
    Grid9,
    {
      gridTemplateAreas: `"header header" "content inspector"`,
      gridTemplateColumns: "1fr auto",
      gridTemplateRows: "auto 1fr",
      minWidth: "600px",
      height: "100vh",
      maxHeight: "100%",
      width: "100%",
      backgroundColor: GreyGrey07,
      overflow: "hidden",
      children: [
        /* @__PURE__ */ jsx170(GridItem6, { gridArea: "header", children: props.header }),
        /* @__PURE__ */ jsx170(GridItem6, { gridArea: "content", height: "100%", overflow: "auto", children: props.content }),
        props.inspector && /* @__PURE__ */ jsx170(GridItem6, { gridArea: "inspector", children: props.inspector })
      ]
    }
  );
};
var StandardLayoutHeader = (props) => {
  return /* @__PURE__ */ jsxs111(
    Grid9,
    {
      gridArea: "header",
      gridTemplateColumns: "1fr auto",
      height: "48px",
      alignItems: "center",
      padding: `0 16px`,
      borderBottom: `1px solid ${GreyGrey3020}`,
      backgroundColor: GreyWhite18,
      children: [
        typeof props.title === "string" ? /* @__PURE__ */ jsxs111(StandardLayout.Title, { children: [
          /* @__PURE__ */ jsx170(PageTitle, { title: [props.title] }),
          props.title
        ] }) : /* @__PURE__ */ jsx170(GridItem6, { children: props.title }),
        /* @__PURE__ */ jsx170(GridItem6, { children: props.actions })
      ]
    }
  );
};
var StandardLayoutTitle = (props) => {
  return /* @__PURE__ */ jsx170(Box52, { ...Lato14Bold10, children: props.children });
};
var StandardLayoutActions = (props) => {
  return /* @__PURE__ */ jsx170(Flex46, { gap: "8px", children: props.children });
};
var StandardLayoutContent = (props) => {
  return /* @__PURE__ */ jsx170(Box52, { padding: "16px", height: "100%", backgroundColor: GreyGrey07, children: props.children });
};
var StandardLayout = Object.assign(StandardLayoutBase, {
  Header: StandardLayoutHeader,
  Title: StandardLayoutTitle,
  Actions: StandardLayoutActions,
  Content: StandardLayoutContent
});

// src/components/PreviewCard/PreviewCard.tsx
import { Box as Box53, Flex as Flex47, Text as Text21 } from "@chakra-ui/react";
import { GreyWhite as GreyWhite19, Lato13Bold as Lato13Bold14, Lato13Regular as Lato13Regular29 } from "@sequencehq/design-tokens";
import { jsx as jsx171, jsxs as jsxs112 } from "react/jsx-runtime";
var PreviewCard = ({
  logoSrc,
  title,
  subtitle,
  content,
  button,
  kebabMenu
}) => {
  return /* @__PURE__ */ jsxs112(
    Box53,
    {
      width: "330px",
      p: 2,
      backgroundColor: GreyWhite19,
      ...Lato13Regular29,
      onClick: stopPropagation,
      children: [
        /* @__PURE__ */ jsxs112(Flex47, { px: 2, gap: 4, my: 2, children: [
          logoSrc && /* @__PURE__ */ jsx171(Box53, { maxWidth: 8, children: /* @__PURE__ */ jsx171("img", { src: logoSrc }) }),
          /* @__PURE__ */ jsxs112(Box53, { flex: 1, children: [
            /* @__PURE__ */ jsx171(Text21, { mb: 1, ...Lato13Bold14, children: title }),
            /* @__PURE__ */ jsx171(Text21, { ...Lato13Regular29, children: subtitle })
          ] })
        ] }),
        content,
        /* @__PURE__ */ jsxs112(Flex47, { mt: 2, gap: 2, children: [
          /* @__PURE__ */ jsx171(
            Button2,
            {
              variant: "secondary",
              onClick: button.onClick,
              style: { flex: 1 },
              children: button.label
            }
          ),
          kebabMenu
        ] })
      ]
    }
  );
};
var stopPropagation = (e) => e.stopPropagation();

// src/components/LineItems/LineItemGroup.tsx
import { Table as Table3, Tbody as Tbody3, Td as Td2, Text as Text22, Th as Th3, Thead as Thead3, Tr as Tr4 } from "@chakra-ui/react";
import { PlusIcon as PlusIcon6 } from "@heroicons/react/16/solid";
import { currencyToSymbol } from "@sequencehq/core-models";
import { Lato13Bold as Lato13Bold15, RedRed50 as RedRed506 } from "@sequencehq/design-tokens";
import { toPercentage } from "@sequencehq/formatters";
import { percentageFromDecimal, toMoney } from "@sequencehq/utils";
import {
  createContext as createContext4,
  useCallback as useCallback14,
  useContext as useContext4,
  useEffect as useEffect27,
  useState as useState33
} from "react";
import { Fragment as Fragment24, jsx as jsx172, jsxs as jsxs113 } from "react/jsx-runtime";
var DESCRIPTION_COL_SPAN = 3;
var SELECT_COL_WIDTH = "32px";
var MENU_COL_WIDTH = "48px";
var LineItemGroupContext = createContext4({
  currency: "GBP",
  selectable: false,
  editable: false,
  registerEditableLineItem: () => {
  }
});
var LineItemGroupComponent = ({ children, currency, selected = false, onChangeSelected, variant }) => {
  const [editableLineItemCount, setEditableLineItemCount] = useState33(0);
  const handleChangeSelected = () => {
    onChangeSelected?.(!selected);
  };
  const showCheckbox = onChangeSelected;
  const registerEditableLineItem = useCallback14((mounted) => {
    if (mounted) {
      setEditableLineItemCount((count) => count + 1);
    } else {
      setEditableLineItemCount((count) => count - 1);
    }
  }, []);
  const editable = editableLineItemCount > 0;
  return /* @__PURE__ */ jsx172(
    LineItemGroupContext.Provider,
    {
      value: {
        currency,
        selectable: !!onChangeSelected,
        registerEditableLineItem,
        editable
      },
      children: /* @__PURE__ */ jsxs113(TableCard, { children: [
        /* @__PURE__ */ jsx172(Thead3, { children: /* @__PURE__ */ jsxs113(Tr4, { children: [
          showCheckbox && /* @__PURE__ */ jsx172(Th3, { width: SELECT_COL_WIDTH, children: /* @__PURE__ */ jsx172(
            Checkbox3,
            {
              onChange: handleChangeSelected,
              isChecked: [true, "indeterminate"].includes(selected),
              isIndeterminate: selected === "indeterminate",
              "data-testid": "lineItemGroupCheckbox"
            }
          ) }),
          /* @__PURE__ */ jsx172(
            Th3,
            {
              colSpan: DESCRIPTION_COL_SPAN,
              style: { paddingLeft: showCheckbox ? "2px" : "12px" },
              children: /* @__PURE__ */ jsx172(
                "span",
                {
                  style: { cursor: "pointer" },
                  onClick: showCheckbox ? handleChangeSelected : void 0,
                  children: "Description"
                }
              )
            }
          ),
          /* @__PURE__ */ jsx172(Th3, { textAlign: "right", width: "90px", children: "Qty" }),
          /* @__PURE__ */ jsx172(Th3, { textAlign: "right", width: "124px", children: "Rate" }),
          /* @__PURE__ */ jsx172(Th3, { textAlign: "right", width: "64px", children: "Tax" }),
          /* @__PURE__ */ jsx172(Th3, { textAlign: "right", width: "128px", children: variant === "default" ? "Amount" : "Credit amount" }),
          editable && /* @__PURE__ */ jsx172(Th3, { width: MENU_COL_WIDTH })
        ] }) }),
        /* @__PURE__ */ jsx172(Tbody3, { children })
      ] })
    }
  );
};
var LineItem = ({ title, quantity, taxRate, rate, rateDisplay, netTotal }) => {
  const { currency } = useContext4(LineItemGroupContext);
  return /* @__PURE__ */ jsxs113(Tr4, { children: [
    /* @__PURE__ */ jsx172(Td2, { colSpan: DESCRIPTION_COL_SPAN, children: title }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", children: quantity }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", children: /* @__PURE__ */ jsx172(
      LineItemRate,
      {
        rate,
        rateDisplay,
        currency
      }
    ) }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", children: toPercentage(percentageFromDecimal(taxRate)) }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", children: toMoney(
      {
        currency,
        value: netTotal
      },
      "",
      10
    ) })
  ] });
};
var EditableLineItem = ({
  title,
  quantity,
  rate,
  rateDisplay,
  taxRate,
  netTotal,
  selected,
  onChangeSelected,
  onChangeQuantity,
  onChangeTotal,
  onDelete,
  inputsDisabled = false,
  testIdPrefix = ""
}) => {
  const { currency, selectable, registerEditableLineItem } = useContext4(LineItemGroupContext);
  useEffect27(() => {
    registerEditableLineItem(true);
    return () => {
      registerEditableLineItem(false);
    };
  }, [registerEditableLineItem]);
  const handleChangeSelected = () => {
    onChangeSelected?.(!selected);
  };
  const handleChangeValue = (e) => {
    e.stopPropagation();
    onChangeTotal(e.target.value);
  };
  const handleChangeQuantity = (e) => {
    e.stopPropagation();
    onChangeQuantity(e.target.value);
  };
  return /* @__PURE__ */ jsxs113(Tr4, { children: [
    selectable && /* @__PURE__ */ jsx172(Td2, { width: SELECT_COL_WIDTH, children: /* @__PURE__ */ jsx172(
      Checkbox3,
      {
        onChange: handleChangeSelected,
        isChecked: !!selected,
        "data-testid": `${testIdPrefix}checkbox`
      }
    ) }),
    /* @__PURE__ */ jsx172(
      Td2,
      {
        colSpan: DESCRIPTION_COL_SPAN,
        style: { paddingLeft: selectable ? "2px" : "12px" },
        children: /* @__PURE__ */ jsx172(
          "span",
          {
            style: { cursor: "pointer" },
            onClick: selectable ? handleChangeSelected : void 0,
            children: title
          }
        )
      }
    ),
    /* @__PURE__ */ jsx172(Td2, { children: /* @__PURE__ */ jsx172(
      TextFieldInput,
      {
        type: "number",
        value: quantity,
        onChange: handleChangeQuantity,
        isDisabled: inputsDisabled,
        onClick: stopPropagation2,
        height: "28px",
        style: { textAlign: "right" },
        "data-testid": `${testIdPrefix}quantity`
      }
    ) }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", children: /* @__PURE__ */ jsx172(
      LineItemRate,
      {
        "data-testid": `${testIdPrefix}rate`,
        rate,
        rateDisplay,
        currency
      }
    ) }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", "data-testid": `${testIdPrefix}tax`, children: toPercentage(percentageFromDecimal(taxRate)) }),
    /* @__PURE__ */ jsx172(Td2, { children: /* @__PURE__ */ jsx172(
      TextFieldInput,
      {
        type: "number",
        value: netTotal,
        onChange: handleChangeValue,
        prefix: currencyToSymbol[currency],
        style: { textAlign: "right" },
        isDisabled: inputsDisabled,
        onClick: stopPropagation2,
        height: "28px",
        "data-testid": `${testIdPrefix}value`
      }
    ) }),
    /* @__PURE__ */ jsx172(Td2, { children: /* @__PURE__ */ jsx172(
      SequenceKebabMenu,
      {
        variant: "ghost",
        alignment: "bottom-right",
        "data-testid": `${testIdPrefix}kebab`,
        items: [
          /* @__PURE__ */ jsx172(
            MenuItem,
            {
              uuid: "delete",
              label: "Delete line item",
              onClick: onDelete,
              style: { color: RedRed506 },
              "data-testid": `${testIdPrefix}delete`
            },
            "delete"
          )
        ]
      }
    ) })
  ] });
};
var TableCard = ({ children }) => {
  return /* @__PURE__ */ jsx172(Card, { p: 0, variant: "v2", children: /* @__PURE__ */ jsx172(Table3, { variant: "v2-without-hover", children }) });
};
var LineItemRate = ({ rateDisplay = "ABSOLUTE", rate, currency }) => {
  return /* @__PURE__ */ jsx172(Fragment24, { children: rateDisplay === "PERCENTAGE" ? toPercentage(percentageFromDecimal(rate)) : toMoney(
    {
      currency,
      value: rate
    },
    "",
    10
  ) });
};
var Subtotal = ({ value }) => {
  const { currency, editable } = useContext4(LineItemGroupContext);
  return /* @__PURE__ */ jsxs113(Tr4, { width: "100%", children: [
    /* @__PURE__ */ jsx172(Td2, { colSpan: 2, children: "Subtotal" }),
    /* @__PURE__ */ jsx172(Td2, { textAlign: "right", colSpan: editable ? 7 : 5, children: /* @__PURE__ */ jsx172(Text22, { ...Lato13Bold15, align: "right", children: toMoney(
      {
        currency,
        value
      },
      "",
      10
    ) }) })
  ] });
};
var AddLineItem = ({ onClick }) => {
  return /* @__PURE__ */ jsx172(Tr4, { children: /* @__PURE__ */ jsx172(Td2, { colSpan: 7, children: /* @__PURE__ */ jsx172(
    Button2,
    {
      onClick,
      variant: "ghost",
      leadingIcon: /* @__PURE__ */ jsx172(PlusIcon6, { width: 16 }),
      children: "Add line item"
    }
  ) }) });
};
var LineItemGroup = Object.assign(LineItemGroupComponent, {
  LineItem,
  EditableLineItem,
  Subtotal,
  AddLineItem
});
var stopPropagation2 = (e) => {
  e.stopPropagation();
};

// src/components/LineItems/Totals.tsx
import {
  Table as Table4,
  Tbody as Tbody4,
  Td as Td3,
  Text as Text23,
  Tr as Tr5,
  Divider as ChakraDivider2
} from "@chakra-ui/react";
import {
  GreyGrey30 as GreyGrey3021,
  GreyGrey60 as GreyGrey6027,
  GreyGrey70 as GreyGrey7012,
  GreyGrey90 as GreyGrey9012,
  Lato13Bold as Lato13Bold16,
  Lato13Regular as Lato13Regular30
} from "@sequencehq/design-tokens";
import { toMoney as toMoney2 } from "@sequencehq/utils";
import { createContext as createContext5, useContext as useContext5 } from "react";
import { jsx as jsx173, jsxs as jsxs114 } from "react/jsx-runtime";
var TotalsContext = createContext5({
  currency: "GBP"
});
var TotalsComponent = ({
  currency,
  children
}) => {
  return /* @__PURE__ */ jsx173(
    TotalsContext.Provider,
    {
      value: {
        currency
      },
      children: /* @__PURE__ */ jsx173(Table4, { variant: "totals", children: /* @__PURE__ */ jsx173(Tbody4, { children }) })
    }
  );
};
var Row = ({ title, amount, emphasised }) => {
  const { currency } = useContext5(TotalsContext);
  const amountToDisplay = toMoney2(
    {
      currency,
      value: amount.toString()
    },
    "",
    2
  );
  return /* @__PURE__ */ jsxs114(Tr5, { fontWeight: emphasised ? "bold" : "normal", children: [
    /* @__PURE__ */ jsx173(Td3, { children: /* @__PURE__ */ jsx173(Text23, { ...emphasised ? Lato13Bold16 : Lato13Regular30, color: GreyGrey7012, children: title }) }),
    /* @__PURE__ */ jsx173(Td3, { textAlign: "right", overflow: "hidden", children: /* @__PURE__ */ jsx173(
      Text23,
      {
        "data-testid": `Totals.${title}`,
        ...emphasised ? Lato13Bold16 : Lato13Regular30,
        color: emphasised ? GreyGrey9012 : GreyGrey7012,
        textAlign: "right",
        whiteSpace: "nowrap",
        title: amountToDisplay,
        children: amountToDisplay
      }
    ) })
  ] });
};
var Tax = ({ amount, percentage }) => {
  return /* @__PURE__ */ jsx173(Row, { title: `Tax (${percentage}%)`, amount });
};
var Total = ({ title = "Total", amount }) => {
  return /* @__PURE__ */ jsx173(Row, { title, amount, emphasised: true });
};
var Divider8 = ({ strong }) => {
  return /* @__PURE__ */ jsx173(Tr5, { children: /* @__PURE__ */ jsx173(Td3, { colSpan: 2, children: /* @__PURE__ */ jsx173(ChakraDivider2, { color: strong ? GreyGrey6027 : GreyGrey3021 }) }) });
};
var Totals = Object.assign(TotalsComponent, {
  Row,
  Tax,
  Total,
  Divider: Divider8
});

// src/components/ScrollFade.tsx
import { Box as Box54 } from "@chakra-ui/react";
import {
  useCallback as useCallback15,
  useLayoutEffect as useLayoutEffect5,
  useRef as useRef25,
  useState as useState34
} from "react";
import { jsx as jsx174, jsxs as jsxs115 } from "react/jsx-runtime";
var FADE_HEIGHT = 40;
var ScrollFade = ({
  children,
  maxHeight,
  height,
  ["data-testid"]: dataTestId
}) => {
  const [bottomOffset, setBottomOffset] = useState34(null);
  const ref = useRef25(null);
  const storeBottomOffset = useCallback15((element) => {
    if (element.scrollHeight <= element.clientHeight) {
      setBottomOffset(0);
      return;
    }
    const calculatedBottomOffset = element.scrollHeight - element.scrollTop - element.clientHeight;
    setBottomOffset(
      calculatedBottomOffset < FADE_HEIGHT ? calculatedBottomOffset : null
    );
  }, []);
  useLayoutEffect5(() => {
    if (!ref.current) {
      return;
    }
    storeBottomOffset(ref.current);
  }, [storeBottomOffset]);
  const handleScroll = (e) => {
    storeBottomOffset(e.currentTarget);
  };
  return /* @__PURE__ */ jsxs115(
    Box54,
    {
      maxHeight,
      height,
      position: "relative",
      overflowY: "hidden",
      "data-testid": dataTestId,
      children: [
        /* @__PURE__ */ jsx174(
          Box54,
          {
            overflowY: "auto",
            maxHeight,
            height: "100%",
            onScroll: handleScroll,
            ref,
            children
          }
        ),
        /* @__PURE__ */ jsx174(
          Box54,
          {
            position: "absolute",
            left: "0",
            width: "100%",
            height: `${FADE_HEIGHT}px`,
            bgGradient: "linear(to-b, rgba(255, 255, 255, 0.00), #FFF)",
            pointerEvents: "none",
            bottom: bottomOffset !== null ? `-${FADE_HEIGHT - bottomOffset}px` : 0
          }
        )
      ]
    }
  );
};

// src/components/InfiniteScroll.tsx
import React2 from "react";
import { Fragment as Fragment25, jsx as jsx175 } from "react/jsx-runtime";
var InfiniteScroll = ({
  isLoading,
  hasMore,
  next,
  threshold = 1,
  root = null,
  rootMargin = "0px",
  reverse,
  children
}) => {
  const observer = React2.useRef();
  const observerRef = React2.useCallback(
    (element) => {
      let safeThreshold = threshold;
      if (threshold < 0 || threshold > 1) {
        console.warn(
          "threshold should be between 0 and 1. will use default value: 1"
        );
        safeThreshold = 1;
      }
      if (isLoading) {
        return;
      }
      if (observer.current) {
        observer.current.disconnect();
      }
      if (!element) {
        return;
      }
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            next();
          }
        },
        { threshold: safeThreshold, root, rootMargin }
      );
      observer.current.observe(element);
    },
    [hasMore, isLoading, next, threshold, root, rootMargin]
  );
  const flattenedChildren = React2.useMemo(
    () => React2.Children.toArray(children),
    [children]
  );
  return /* @__PURE__ */ jsx175(Fragment25, { children: flattenedChildren.map((child, index) => {
    if (!React2.isValidElement(child)) {
      process.env.NODE_ENV === "development" && console.warn("You should use a valid element with InfiniteScroll");
    }
    const isObserveTarget = reverse ? index === 0 : index === flattenedChildren.length - 1;
    const ref = isObserveTarget ? observerRef : null;
    return React2.cloneElement(child, { ref });
  }) });
};

// src/components/OverviewCard/OverviewCard.tsx
import { Fade, Flex as Flex48, Grid as Grid10, Link as Link2, Text as Text24 } from "@chakra-ui/react";
import { ChevronRightIcon as ChevronRightIcon4 } from "@heroicons/react/16/solid";
import {
  GreyGrey10 as GreyGrey1012,
  GreyGrey20 as GreyGrey2012,
  GreyGrey30 as GreyGrey3022,
  GreyGrey40 as GreyGrey4018,
  GreyGrey50 as GreyGrey5012,
  GreyGrey60 as GreyGrey6028,
  GreyGrey80 as GreyGrey8013,
  GreyGrey90 as GreyGrey9013,
  Lato13Bold as Lato13Bold17,
  Lato13Regular as Lato13Regular31,
  Lato24Bold,
  OrangeOrange10 as OrangeOrange102,
  OrangeOrange20,
  OrangeOrange50,
  RedRed10 as RedRed102,
  RedRed20,
  RedRed60 as RedRed609,
  ShadowL as ShadowL2,
  ShadowM as ShadowM2
} from "@sequencehq/design-tokens";
import { Fragment as Fragment26, useRef as useRef26 } from "react";
import { Link as RouterLink } from "react-router-dom";

// src/utils/text.ts
var replaceSlashesWithDash = (str) => str.replace(/\//g, "-");

// src/components/OverviewCard/OverviewCard.tsx
import { jsx as jsx176, jsxs as jsxs116 } from "react/jsx-runtime";
var cardTheme = {
  grey: {
    gradient: GreyGrey2012,
    gradientOpacity: 0.5,
    secondaryGradientOpacity: 0.7,
    text: GreyGrey9013,
    border: GreyGrey5012
  },
  orange: {
    gradient: OrangeOrange102,
    gradientOpacity: 0.3,
    secondaryGradientOpacity: 0.2,
    text: OrangeOrange50,
    border: OrangeOrange20
  },
  red: {
    gradient: RedRed102,
    gradientOpacity: 0.3,
    secondaryGradientOpacity: 0.2,
    text: RedRed609,
    border: RedRed20
  }
};
var OverviewCard = ({
  quantity,
  title,
  description,
  href,
  variant = "grey",
  icon,
  isLoading = false
}) => {
  const titleCardId = `overview-card-${replaceSlashesWithDash(href)}`;
  const isEmpty = !isLoading && quantity === 0;
  const linkRef = useRef26(null);
  const onMouseMove = (e) => {
    if (!linkRef.current)
      return;
    const rect = e.currentTarget.getBoundingClientRect(), x = e.clientX - rect.left, y = e.clientY - rect.top;
    linkRef.current.style.setProperty("--x", `${x}px`);
    linkRef.current.style.setProperty("--y", `${y}px`);
  };
  return /* @__PURE__ */ jsx176(
    Link2,
    {
      as: RouterLink,
      to: href,
      _hover: {
        textDecoration: "none"
      },
      ref: linkRef,
      onMouseMove,
      "aria-labelledby": titleCardId,
      className: "group",
      css: {
        position: "relative",
        background: "white",
        "[data-icon]": {
          display: "inline-block",
          verticalAlign: "middle",
          transform: "translateX(-4px)",
          transition: "transform ease-out 100ms, opacity ease-out 80ms, blur ease-out 80ms",
          filter: "blur(2px)",
          opacity: 0
        },
        "&:hover [data-icon]": {
          transform: "translateX(1px)",
          filter: "blur(0)",
          opacity: 1
        },
        "&:hover:after": {
          opacity: 1
        },
        "&:hover:before": {
          opacity: cardTheme[variant].secondaryGradientOpacity
        }
      },
      _before: {
        position: "absolute",
        inset: 0,
        content: '" "',
        userSelect: "none",
        pointerEvents: "none",
        transition: "opacity 300ms",
        borderRadius: "8px",
        opacity: 0,
        background: `radial-gradient(circle 150px at var(--x, -200px) var(--y, -200px), ${cardTheme[variant].gradient},transparent 85%)`
      },
      _after: {
        position: "absolute",
        inset: 0,
        content: '" "',
        userSelect: "none",
        pointerEvents: "none",
        transition: "opacity 300ms",
        borderRadius: "8px",
        opacity: 0,
        border: `1px solid ${isEmpty ? GreyGrey5012 : cardTheme[variant].border}`,
        maskImage: "radial-gradient(circle 180px at var(--x, -180px) var(--y, -180px), white 40%, transparent)"
      },
      children: /* @__PURE__ */ jsxs116(
        Flex48,
        {
          flexDirection: "column",
          width: "full",
          height: "full",
          p: "16px",
          borderRadius: "8px",
          borderStyle: "solid",
          borderWidth: "1px",
          borderColor: GreyGrey4018,
          boxShadow: ShadowM2,
          position: "relative",
          overflow: "clip",
          transition: "border-color 100ms, box-shadow 150ms",
          background: isEmpty ? GreyGrey1012 : "transparent",
          _before: {
            content: '" "',
            position: "absolute",
            width: "160px",
            height: "160px",
            left: "-20%",
            top: "-20%",
            background: isEmpty ? "transparent" : `radial-gradient(${cardTheme[variant].gradient},transparent 85%)`,
            opacity: isLoading ? 0 : cardTheme[variant].gradientOpacity,
            transition: "opacity 100ms"
          },
          _groupHover: {
            boxShadow: ShadowL2
          },
          children: [
            /* @__PURE__ */ jsx176(Fade, { in: isEmpty, children: /* @__PURE__ */ jsx176(
              Grid10,
              {
                position: "absolute",
                inset: "-6px 0",
                templateColumns: "repeat(8,1fr)",
                templateRows: "repeat(auto-fill,20px)",
                columnGap: "0px",
                rowGap: "8px",
                "aria-hidden": "true",
                pointerEvents: "none",
                css: {
                  maskComposite: "add",
                  maskImage: `linear-gradient(30deg,transparent 60%, rgba(0,0,0,0.9)), radial-gradient(circle 120px at var(--x, -200px) var(--y, -200px), rgba(0,0,0,0.4) 40%,  rgba(0,0,0,0))`,
                  "& > svg": {
                    width: "20px",
                    height: "20px",
                    color: GreyGrey3022
                  }
                },
                children: Array.from({ length: 40 }).map((_, i) => /* @__PURE__ */ jsx176(Fragment26, { children: icon }, i))
              }
            ) }),
            /* @__PURE__ */ jsx176(
              Text24,
              {
                position: "relative",
                color: isEmpty ? GreyGrey5012 : cardTheme[variant].text,
                pb: "12px",
                minHeight: "40px",
                opacity: isLoading ? 0 : 1,
                transition: "opacity 250ms ease-out",
                ...Lato24Bold,
                children: quantity
              }
            ),
            /* @__PURE__ */ jsxs116(
              Text24,
              {
                position: "relative",
                id: titleCardId,
                ...Lato13Bold17,
                color: GreyGrey8013,
                pb: "4px",
                children: [
                  title,
                  /* @__PURE__ */ jsx176(
                    ChevronRightIcon4,
                    {
                      "data-icon": true,
                      width: "14px",
                      height: "14px",
                      "aria-hidden": "true"
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx176(
              Text24,
              {
                position: "relative",
                opacity: isLoading ? 0 : 1,
                transition: "opacity 250ms ease-out",
                ...Lato13Regular31,
                color: GreyGrey6028,
                children: description
              }
            )
          ]
        }
      )
    }
  );
};

// src/components/ActivityCard/ActivityCard.tsx
import { ChevronRightIcon as ChevronRightIcon5 } from "@chakra-ui/icons";
import { Flex as Flex49, Link as Link3, Text as Text25 } from "@chakra-ui/react";
import {
  BorderRadius4,
  GreyGrey10 as GreyGrey1013,
  GreyGrey40 as GreyGrey4019,
  GreyGrey50 as GreyGrey5013,
  GreyGrey60 as GreyGrey6029,
  GreyGrey70 as GreyGrey7013,
  GreyWhite as GreyWhite20,
  Lato13Bold as Lato13Bold18,
  Lato13Regular as Lato13Regular32,
  ShadowM as ShadowM3
} from "@sequencehq/design-tokens";
import { dateTimeWithFormat as dateTimeWithFormat2, relativeTimeToNow } from "@sequencehq/formatters";
import { Link as RouterLink2 } from "react-router-dom";
import { jsx as jsx177, jsxs as jsxs117 } from "react/jsx-runtime";
var ActivityCard = ({
  title,
  children,
  dateLabel,
  dateIcon,
  date,
  href
}) => {
  const titleCardId = `activity-card-${replaceSlashesWithDash(href)}`;
  return /* @__PURE__ */ jsxs117(
    Link3,
    {
      as: RouterLink2,
      to: href,
      width: "full",
      borderRadius: "8px",
      "aria-labelledby": titleCardId,
      border: `1px solid ${GreyGrey4019}`,
      _hover: {
        textDecoration: "none",
        borderColor: GreyGrey5013
      },
      boxShadow: ShadowM3,
      backgroundColor: GreyWhite20,
      transition: "border-color 100ms",
      p: "8px",
      css: {
        "[data-icon]": {
          transform: "translateX(-4px)",
          width: "14px",
          height: "14px",
          transition: "transform ease-out 100ms, opacity ease-out 80ms, blur ease-out 80ms",
          filter: "blur(2px)",
          opacity: 0
        },
        "&:hover [data-icon]": {
          transform: "translateX(1px)",
          filter: "blur(0)",
          opacity: 1
        }
      },
      children: [
        /* @__PURE__ */ jsxs117(
          Flex49,
          {
            paddingTop: "4px",
            paddingX: "4px",
            paddingBottom: "8px",
            justifyItems: "center",
            justifyContent: "space-between",
            children: [
              /* @__PURE__ */ jsxs117(Flex49, { position: "relative", alignItems: "center", id: titleCardId, children: [
                /* @__PURE__ */ jsx177(Text25, { ...Lato13Bold18, color: GreyGrey6029, children: title }),
                /* @__PURE__ */ jsx177(ChevronRightIcon5, { "data-icon": true, "aria-hidden": "true", color: GreyGrey6029 })
              ] }),
              children
            ]
          }
        ),
        /* @__PURE__ */ jsxs117(
          Flex49,
          {
            p: "4px",
            backgroundColor: GreyGrey1013,
            borderRadius: BorderRadius4,
            justifyItems: "center",
            justifyContent: "space-between",
            children: [
              /* @__PURE__ */ jsxs117(
                Flex49,
                {
                  ...Lato13Regular32,
                  alignItems: "center",
                  color: GreyGrey7013,
                  css: {
                    "&>svg": {
                      width: "16px",
                      height: "16px",
                      color: GreyGrey6029,
                      marginRight: "4px"
                    }
                  },
                  children: [
                    dateIcon,
                    dateLabel,
                    relativeTimeToNow({
                      dateTarget: date,
                      dateNow: (/* @__PURE__ */ new Date()).toString()
                    })
                  ]
                }
              ),
              /* @__PURE__ */ jsx177(Text25, { ...Lato13Regular32, color: GreyGrey7013, children: dateTimeWithFormat2(date, "d MMM yyyy") })
            ]
          }
        )
      ]
    }
  );
};

// src/components/ErrorMessage/ErrorMessage.tsx
import { Flex as Flex50, Text as Text26, Center as Center9 } from "@chakra-ui/react";
import ExclamationTriangleIcon3 from "@heroicons/react/24/solid/ExclamationTriangleIcon";
import {
  GreyGrey80 as GreyGrey8014,
  GreyGrey90 as GreyGrey9014,
  Lato14ExtendedRegular,
  Lato16Bold,
  YellowYellow10 as YellowYellow102,
  YellowYellow60
} from "@sequencehq/design-tokens";
import { jsx as jsx178, jsxs as jsxs118 } from "react/jsx-runtime";
var defaultTitle = "Oops! Something went wrong";
var defaultMessage = "Try reloading the page if you believe this is an error.";
var ErrorMessage = ({
  title = defaultTitle,
  message = defaultMessage,
  button,
  ...props
}) => {
  return /* @__PURE__ */ jsxs118(
    Flex50,
    {
      flexDirection: "column",
      alignItems: "center",
      gap: "16px",
      "data-testid": props["data-testid"],
      children: [
        /* @__PURE__ */ jsx178(
          Center9,
          {
            width: "48px",
            height: "48px",
            borderRadius: "48px",
            backgroundColor: YellowYellow102,
            children: /* @__PURE__ */ jsx178(
              ExclamationTriangleIcon3,
              {
                width: "24px",
                height: "24px",
                color: YellowYellow60
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs118(Flex50, { flexDirection: "column", alignItems: "center", gap: "4px", children: [
          /* @__PURE__ */ jsx178(Text26, { ...Lato16Bold, color: GreyGrey9014, children: title }),
          /* @__PURE__ */ jsx178(Text26, { ...Lato14ExtendedRegular, color: GreyGrey8014, children: message })
        ] }),
        button
      ]
    }
  );
};

// src/components/CustomerLogo.tsx
import { Box as Box55 } from "@chakra-ui/react";
import { IdentificationIcon } from "@heroicons/react/16/solid";
import { jsx as jsx179 } from "react/jsx-runtime";
var CustomerLogo = ({
  src,
  size = 16
}) => {
  return src ? /* @__PURE__ */ jsx179(
    Box55,
    {
      width: `${size}px`,
      height: `${size}px`,
      backgroundImage: `url(${src}), none`,
      backgroundPosition: "center center",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat"
    }
  ) : /* @__PURE__ */ jsx179(IdentificationIcon, { width: size, height: size });
};

// src/components/InlineToast/InlineToast.tsx
import { CheckCircleIcon as CheckCircleIcon4 } from "@chakra-ui/icons";
import { Flex as Flex51, SlideFade, Spinner as Spinner5, Text as Text27 } from "@chakra-ui/react";
import { XCircleIcon as XCircleIcon2 } from "@heroicons/react/16/solid";
import {
  GreenGreen80 as GreenGreen802,
  GreyWhite as GreyWhite21,
  IndigoIndigo50 as IndigoIndigo5023,
  Lato13Regular as Lato13Regular33,
  RedRed60 as RedRed6010
} from "@sequencehq/design-tokens";
import { SECONDS } from "@sequencehq/utils/src/dates";
import { useEffect as useEffect28, useRef as useRef27, useState as useState35, useCallback as useCallback16 } from "react";
import { match as match14 } from "ts-pattern";

// src/components/InlineToast/LoadingGlow.tsx
import { Box as Box56, keyframes } from "@chakra-ui/react";
import { jsx as jsx180 } from "react/jsx-runtime";
var transitionSnazzyLoading = keyframes`
  0% { left: -500px; }
  100% { left: calc(100% + 500px); }
`;
var LoadingGlow = () => /* @__PURE__ */ jsx180(
  Box56,
  {
    position: "absolute",
    width: "180px",
    height: "276px",
    borderRadius: "100%",
    left: "-500px",
    top: "50%",
    transform: "translateY(-50%) rotate(25deg)",
    animation: `${transitionSnazzyLoading} 2s ease-in-out infinite`,
    background: "rgba(255, 255, 255, 0.4)",
    filter: "blur(106px)"
  }
);

// src/components/InlineToast/InlineToast.tsx
import { jsx as jsx181, jsxs as jsxs119 } from "react/jsx-runtime";
var TRANSITION_MS = 250;
var DISMISS_TIMEOUT_MS = 3 * SECONDS;
var InlineToast = ({ state, onClick, dismissOnClick }) => {
  const [isVisible, setIsVisible] = useState35(false);
  const [displayedState, setDisplayedState] = useState35();
  const refDismissTimer = useRef27(null);
  const dismiss = useCallback16(() => {
    if (refDismissTimer.current) {
      clearTimeout(refDismissTimer.current);
    }
    setIsVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedState(void 0);
    }, TRANSITION_MS);
    return () => {
      clearTimeout(timeout);
    };
  }, []);
  const setDismissTimer = () => {
    refDismissTimer.current = setTimeout(() => {
      dismiss();
    }, DISMISS_TIMEOUT_MS);
  };
  useEffect28(() => {
    if (refDismissTimer.current) {
      clearTimeout(refDismissTimer.current);
    }
    if (typeof state === "undefined") {
      return;
    }
    setIsVisible(false);
    setTimeout(
      () => {
        setDisplayedState(state);
        setIsVisible(true);
        if (state.persistent) {
          return;
        }
        setDismissTimer();
      },
      typeof displayedState === "undefined" ? 0 : TRANSITION_MS
    );
  }, [state]);
  const icon = displayedState ? match14(displayedState.variant).with("LOADING", () => /* @__PURE__ */ jsx181(Spinner5, { boxSize: "16px", color: GreyWhite21 })).with("SUCCESS", () => /* @__PURE__ */ jsx181(CheckCircleIcon4, { width: "16px", height: "16px", color: GreyWhite21 })).with("FAILURE", () => /* @__PURE__ */ jsx181(XCircleIcon2, { width: "16px", height: "16px", color: GreyWhite21 })).exhaustive() : void 0;
  const background = displayedState ? match14(displayedState.variant).with("LOADING", () => IndigoIndigo5023).with("SUCCESS", () => GreenGreen802).with("FAILURE", () => RedRed6010).exhaustive() : void 0;
  if (!displayedState) {
    return null;
  }
  const handleClick = () => {
    onClick?.();
    if (dismissOnClick) {
      dismiss();
    }
  };
  const clickable = !!onClick || dismissOnClick;
  return /* @__PURE__ */ jsx181(
    SlideFade,
    {
      offsetY: "56px",
      in: isVisible,
      style: {
        position: "absolute",
        bottom: 0,
        left: 0,
        zIndex: 1,
        display: "flex",
        justifyContent: "center",
        width: "100%"
      },
      children: /* @__PURE__ */ jsxs119(
        Flex51,
        {
          borderRadius: "8px",
          background,
          width: "fit-content",
          marginBottom: "16px",
          boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.08), 0px 0px 5px 0px rgba(0, 0, 0, 0.03)",
          position: "relative",
          overflow: "hidden",
          onClick: handleClick,
          cursor: clickable ? "pointer" : "default",
          children: [
            /* @__PURE__ */ jsxs119(Flex51, { padding: "8px", gap: "8px", position: "relative", zIndex: "1", children: [
              icon,
              /* @__PURE__ */ jsx181(Text27, { ...Lato13Regular33, color: GreyWhite21, children: displayedState?.text })
            ] }),
            displayedState?.variant === "LOADING" && /* @__PURE__ */ jsx181(LoadingGlow, {})
          ]
        }
      )
    }
  );
};

// src/components/SettingsGroup/SettingsGroup.tsx
import { Center as Center10, Flex as Flex52, Spinner as Spinner6, Text as Text28 } from "@chakra-ui/react";
import {
  BorderRadius6 as BorderRadius63,
  BorderRadius8,
  GreyGrey20 as GreyGrey2013,
  GreyGrey40 as GreyGrey4020,
  GreyGrey60 as GreyGrey6030,
  GreyGrey70 as GreyGrey7014,
  GreyGrey80 as GreyGrey8015,
  GreyWhite as GreyWhite22,
  Lato12Regular as Lato12Regular2,
  Lato13Bold as Lato13Bold19,
  Lato14Bold as Lato14Bold11,
  ShadowXs as ShadowXs7
} from "@sequencehq/design-tokens";
import { useId as useId3 } from "@chakra-ui/react";
import { jsx as jsx182, jsxs as jsxs120 } from "react/jsx-runtime";
var SettingsGroup = ({
  title,
  id,
  footer,
  isLoading,
  rows = 1,
  height,
  children
}) => {
  const sectionId = useId3(id, "settings");
  return /* @__PURE__ */ jsxs120(
    Flex52,
    {
      as: "section",
      width: "full",
      maxWidth: "600px",
      "aria-labelledby": title ? sectionId : void 0,
      flexDirection: "column",
      children: [
        title && /* @__PURE__ */ jsx182(
          Text28,
          {
            as: "h3",
            marginBottom: "16px",
            id: sectionId,
            ...Lato14Bold11,
            color: GreyGrey8015,
            children: title
          }
        ),
        /* @__PURE__ */ jsx182(
          Flex52,
          {
            width: "100%",
            flexDirection: "column",
            borderRadius: BorderRadius8,
            justifyContent: "center",
            border: `1px solid ${GreyGrey4020}`,
            boxShadow: ShadowXs7,
            px: "16px",
            backgroundColor: GreyWhite22,
            children: isLoading ? /* @__PURE__ */ jsx182(Center10, { height: `${height ?? rows * 64}px`, children: /* @__PURE__ */ jsx182(Spinner6, {}) }) : children
          }
        ),
        typeof footer === "string" ? /* @__PURE__ */ jsx182(Text28, { as: "span", marginTop: "8px", ...Lato12Regular2, color: GreyGrey6030, children: footer }) : footer
      ]
    }
  );
};
var SettingsFormControl = ({
  icon,
  label,
  helper,
  htmlFor,
  children
}) => {
  return /* @__PURE__ */ jsxs120(
    Flex52,
    {
      height: "64px",
      backgroundColor: GreyWhite22,
      alignItems: "center",
      justifyContent: "space-between",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: "gray.30",
      _last: {
        borderBottomWidth: "0px"
      },
      children: [
        /* @__PURE__ */ jsxs120(Flex52, { alignItems: "center", gap: "16px", children: [
          icon,
          /* @__PURE__ */ jsxs120(Flex52, { flexDirection: "column", height: "fit-content", gap: "2px", children: [
            /* @__PURE__ */ jsx182(Text28, { as: "label", htmlFor, color: GreyGrey7014, ...Lato13Bold19, children: label }),
            helper && /* @__PURE__ */ jsx182(Text28, { as: "span", color: GreyGrey6030, ...Lato12Regular2, children: helper })
          ] })
        ] }),
        children
      ]
    }
  );
};
var SettingsFormControlIcon = (props) => /* @__PURE__ */ jsx182(
  Flex52,
  {
    borderRadius: BorderRadius63,
    backgroundColor: GreyGrey2013,
    width: "32px",
    height: "32px",
    alignItems: "center",
    justifyContent: "center",
    css: {
      "&>svg": {
        width: "16px",
        height: "16px",
        color: GreyGrey7014
      }
    },
    children: props.children
  }
);

// src/components/EmptyState/EmptyState.tsx
import { Box as Box57, Flex as Flex53, Grid as Grid11, Text as Text29 } from "@chakra-ui/react";
import {
  BorderRadius6 as BorderRadius64,
  GreyGrey20 as GreyGrey2014,
  GreyGrey50 as GreyGrey5014,
  GreyGrey60 as GreyGrey6031,
  GreyGrey70 as GreyGrey7015,
  GreyGrey80 as GreyGrey8016,
  Lato12Regular as Lato12Regular3,
  Lato13Bold as Lato13Bold20,
  Lato13Regular as Lato13Regular34
} from "@sequencehq/design-tokens";
import { jsx as jsx183, jsxs as jsxs121 } from "react/jsx-runtime";
var iconSizes = {
  sm: {
    backgroundSize: "32px",
    size: "20px"
  },
  md: {
    backgroundSize: "36px",
    size: "24px"
  }
};
var EmptyState = ({
  icon: IconComponent,
  iconSize = "md",
  title,
  body,
  button,
  helperText,
  ...props
}) => {
  return /* @__PURE__ */ jsx183(
    Grid11,
    {
      justifyContent: "center",
      alignContent: "center",
      minHeight: "100%",
      textAlign: "center",
      padding: 10,
      ...props,
      children: /* @__PURE__ */ jsxs121(Flex53, { direction: "column", alignItems: "center", maxWidth: 280, gap: 4, children: [
        IconComponent && /* @__PURE__ */ jsx183(
          Grid11,
          {
            width: iconSizes[iconSize].backgroundSize,
            height: iconSizes[iconSize].backgroundSize,
            backgroundColor: GreyGrey2014,
            borderRadius: BorderRadius64,
            justifyContent: "center",
            alignContent: "center",
            children: /* @__PURE__ */ jsx183(
              IconComponent,
              {
                width: iconSizes[iconSize].size,
                height: iconSizes[iconSize].size,
                color: GreyGrey7015
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs121(Flex53, { flexDir: "column", gap: 1, children: [
          /* @__PURE__ */ jsx183(Text29, { ...Lato13Bold20, color: GreyGrey8016, children: title }),
          body && /* @__PURE__ */ jsx183(Text29, { ...Lato13Regular34, color: GreyGrey6031, children: body })
        ] }),
        (button || helperText) && /* @__PURE__ */ jsxs121(Flex53, { flexDir: "column", gap: 1, children: [
          button && /* @__PURE__ */ jsx183(Box57, { children: button }),
          helperText && /* @__PURE__ */ jsx183(Text29, { ...Lato12Regular3, color: GreyGrey5014, children: helperText })
        ] })
      ] })
    }
  );
};

// src/features/ContractExtraction/CustomerCard.tsx
import { Box as Box58, Flex as Flex54, Text as Text30 } from "@chakra-ui/react";
import {
  GreyGrey50 as GreyGrey5015,
  GreyGrey80 as GreyGrey8017,
  Lato13Bold as Lato13Bold21,
  Lato13Regular as Lato13Regular35
} from "@sequencehq/design-tokens";
import { match as match15 } from "ts-pattern";
import { ScaleIcon } from "@heroicons/react/16/solid";
import { Link as Link4 } from "react-router-dom";
import { formatAddress } from "@sequencehq/utils";
import { jsx as jsx184, jsxs as jsxs122 } from "react/jsx-runtime";
var CustomerCard = ({
  legalName,
  logoSrc,
  taxStatus,
  customerId,
  onUpdate,
  onIgnore,
  onCreate,
  ...props
}) => {
  const address = "address" in props ? props.address : void 0;
  const newAddress = "newAddress" in props ? props.newAddress : void 0;
  const billingEmail = "billingEmail" in props ? props.billingEmail : void 0;
  const newBillingEmail = "newBillingEmail" in props ? props.newBillingEmail : void 0;
  const addressArray = address ? formatAddress(address) : [];
  const newAddressArray = newAddress ? formatAddress(newAddress) : [];
  const contactDetails = [...[billingEmail].filter(Boolean), ...addressArray];
  const newContactDetails = [
    ...[newBillingEmail ?? billingEmail].filter(Boolean),
    ...newAddress ? newAddressArray : addressArray
  ];
  const diffs = contactDetails.map(
    (addressPart, i) => newContactDetails[i] !== addressPart
  );
  const [emailChanged, ...addressDiffs] = diffs;
  const addressChanged = addressDiffs.some(Boolean);
  const state = getState(emailChanged, addressChanged, !!address);
  return /* @__PURE__ */ jsxs122(Box58, { "data-testid": "customer.view", children: [
    /* @__PURE__ */ jsxs122(Flex54, { gap: 4, children: [
      /* @__PURE__ */ jsx184(Box58, { flexShrink: 0, children: customerId ? /* @__PURE__ */ jsx184(Link4, { to: `/customers/${customerId}`, children: /* @__PURE__ */ jsx184(CustomerLogo, { src: logoSrc, size: 32 }) }) : /* @__PURE__ */ jsx184(CustomerLogo, { src: logoSrc, size: 32 }) }),
      /* @__PURE__ */ jsxs122(Box58, { children: [
        customerId ? /* @__PURE__ */ jsx184(Link4, { to: `/customers/${customerId}`, color: GreyGrey8017, children: /* @__PURE__ */ jsx184(Text30, { ...Lato13Bold21, "data-testid": "customer.legalName", children: legalName }) }) : /* @__PURE__ */ jsx184(Text30, { ...Lato13Bold21, color: GreyGrey8017, children: legalName }),
        /* @__PURE__ */ jsxs122(Flex54, { gap: 4, children: [
          /* @__PURE__ */ jsx184(Box58, { children: (contactDetails.length ? contactDetails : newContactDetails).map(
            (row, i) => /* @__PURE__ */ jsx184(
              Text30,
              {
                ...Lato13Regular35,
                lineHeight: "18px",
                textDecoration: diffs[i] ? "line-through" : "none",
                color: diffs[i] ? GreyGrey5015 : GreyGrey8017,
                "data-testid": `customer.address[${i}]`,
                children: row
              },
              i
            )
          ) }),
          /* @__PURE__ */ jsx184(Box58, { textAlign: "right", children: newContactDetails.map((row, i) => /* @__PURE__ */ jsx184(
            Text30,
            {
              ...Lato13Regular35,
              lineHeight: "18px",
              visibility: diffs[i] ? "visible" : "hidden",
              "data-testid": `customer.address.new[${i}]`,
              children: row
            },
            i
          )) })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx184(Box58, { marginTop: 4, children: match15(state).with("NEW", () => /* @__PURE__ */ jsx184(
      Button2,
      {
        variant: "primary",
        fullWidth: true,
        onClick: onCreate,
        "data-testid": "customer.create",
        children: "Create customer"
      }
    )).with("EXISTING", () => /* @__PURE__ */ jsx184("div", { "data-testid": "customer.taxStatus", children: /* @__PURE__ */ jsx184(Banner, { variant: "neutral", IconComponent: ScaleIcon, children: TAX_STATUS_LABELS[taxStatus] }) })).with("CHANGED_EMAIL", () => /* @__PURE__ */ jsxs122(Flex54, { direction: "column", gap: "8px", children: [
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "primary",
          fullWidth: true,
          onClick: onUpdate,
          "data-testid": "customer.address.update",
          children: "Create billing contact"
        }
      ),
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "secondary",
          fullWidth: true,
          onClick: onIgnore,
          "data-testid": "customer.address.ignore",
          children: "Ignore changes"
        }
      )
    ] })).with("CHANGED_ADDRESS", () => /* @__PURE__ */ jsxs122(Flex54, { direction: "column", gap: "8px", children: [
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "primary",
          fullWidth: true,
          onClick: onUpdate,
          "data-testid": "customer.address.update",
          children: "Update customer"
        }
      ),
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "secondary",
          fullWidth: true,
          onClick: onIgnore,
          "data-testid": "customer.address.ignore",
          children: "Ignore changes"
        }
      )
    ] })).with("CHANGED_EMAIL_AND_ADDRESS", () => /* @__PURE__ */ jsxs122(Flex54, { direction: "column", gap: "8px", children: [
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "primary",
          fullWidth: true,
          onClick: onUpdate,
          "data-testid": "customer.address.update",
          children: "Update customer"
        }
      ),
      /* @__PURE__ */ jsx184(
        Button2,
        {
          variant: "secondary",
          fullWidth: true,
          onClick: onIgnore,
          "data-testid": "customer.address.ignore",
          children: "Ignore changes"
        }
      )
    ] })).exhaustive() })
  ] });
};
function getState(emailChanged, addressChanged, customerExists) {
  if (!customerExists) {
    return "NEW";
  }
  if (emailChanged && addressChanged) {
    return "CHANGED_EMAIL_AND_ADDRESS";
  }
  if (addressChanged) {
    return "CHANGED_ADDRESS";
  }
  if (emailChanged) {
    return "CHANGED_EMAIL";
  }
  return "EXISTING";
}
var TAX_STATUS_LABELS = {
  TAX_EXEMPT: "Tax exempt",
  REVERSE_CHARGED: "Reverse charged",
  TAXED: "Customer will be charged tax based on the included product's tax category"
};

// src/features/Attachments/AttachmentUIV2.tsx
import {
  GreyGrey10 as GreyGrey1014,
  GreyGrey30 as GreyGrey3023,
  GreyGrey60 as GreyGrey6032,
  GreyGrey70 as GreyGrey7016,
  GreyGrey80 as GreyGrey8018,
  GreyWhite as GreyWhite23,
  Lato13Regular as Lato13Regular36
} from "@sequencehq/design-tokens";
import { Box as Box59, Flex as Flex55, Grid as Grid12, Text as Text31 } from "@chakra-ui/react";
import { DocumentCheckIcon, PhotoIcon } from "@heroicons/react/16/solid";
import { Fragment as Fragment27, jsx as jsx185, jsxs as jsxs123 } from "react/jsx-runtime";
var AttachmentUIV2 = ({
  name,
  type,
  metadata,
  preview,
  menuItems,
  additionalContent,
  onClick
}) => {
  return /* @__PURE__ */ jsxs123(
    Flex55,
    {
      direction: "column",
      gap: 2,
      padding: 2,
      borderRadius: 8,
      role: "group",
      border: `1px solid ${GreyGrey3023}`,
      transition: "background 0.2s ease-in-out",
      _hover: { background: GreyGrey1014 },
      _focusWithin: { background: GreyGrey1014 },
      onClick,
      cursor: onClick ? "pointer" : "default",
      background: GreyWhite23,
      minW: "296px",
      children: [
        /* @__PURE__ */ jsxs123(Flex55, { gap: 2, alignItems: "center", height: "16px", children: [
          /* @__PURE__ */ jsx185(AttachmentIcon, { type }),
          /* @__PURE__ */ jsx185(
            Text31,
            {
              ...Lato13Regular36,
              fontWeight: "semibold",
              color: GreyGrey8018,
              flex: 1,
              overflow: "hidden",
              children: /* @__PURE__ */ jsx185(TextOverflow, { splitPercentage: 80, children: name })
            }
          ),
          menuItems && /* @__PURE__ */ jsx185(SequenceKebabMenu, { items: menuItems, variant: "ghost" })
        ] }),
        /* @__PURE__ */ jsxs123(Flex55, { background: GreyGrey1014, padding: 1, borderRadius: 4, gap: 2, children: [
          /* @__PURE__ */ jsx185(
            Box59,
            {
              pointerEvents: "none",
              overflow: "hidden",
              borderRadius: 4,
              border: `0.5px solid ${GreyGrey3023}`,
              width: "52px",
              height: "73px",
              children: preview
            }
          ),
          /* @__PURE__ */ jsx185(
            Grid12,
            {
              templateColumns: "auto 1fr",
              gap: 2,
              flex: 1,
              ...Lato13Regular36,
              padding: 1,
              children: metadata.map(([key, value]) => /* @__PURE__ */ jsxs123(Fragment27, { children: [
                /* @__PURE__ */ jsx185(Text31, { color: GreyGrey6032, children: key }, key),
                /* @__PURE__ */ jsx185(
                  Text31,
                  {
                    justifySelf: "end",
                    color: GreyGrey7016,
                    children: value
                  },
                  `${key}-${value}`
                )
              ] }))
            }
          )
        ] }),
        additionalContent
      ]
    }
  );
};
var AttachmentIcon = ({ type }) => {
  return type === "document" ? /* @__PURE__ */ jsx185(DocumentCheckIcon, { width: 16, color: GreyGrey7016 }) : /* @__PURE__ */ jsx185(PhotoIcon, { width: 16, color: GreyGrey7016 });
};

// src/charts/BarChart.tsx
import { Fragment as Fragment28, useEffect as useEffect29, useMemo as useMemo14, useState as useState36 } from "react";
import { Bar, BarRounded, Line } from "@visx/shape";
import { Group } from "@visx/group";
import { LinearGradient } from "@visx/gradient";
import { GridRows, GridColumns } from "@visx/grid";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { useTooltip } from "@visx/tooltip";
import { motion, AnimatePresence } from "framer-motion";
import { Box as Box60 } from "@chakra-ui/react";
import { v4 as uuidv43 } from "uuid";
import { GreyGrey60 as GreyGrey6033, Lato12Regular as Lato12Regular4 } from "@sequencehq/design-tokens";
import { jsx as jsx186, jsxs as jsxs124 } from "react/jsx-runtime";
var BarChart = ({
  width,
  height,
  data,
  parseDataX,
  parseDataY,
  tooltip,
  axisBottomTickFormat,
  alignFirstXAxisLabelToLeft = true,
  style = {},
  showColumnGrid = true,
  useXAxisLines = true
}) => {
  const marginLeft = 30;
  const marginTop = 12;
  const marginBottom = 26;
  const xMax = width - marginLeft;
  const yMax = height - marginTop - marginBottom;
  const xScale = useMemo14(
    () => scaleBand({
      range: [0, xMax],
      domain: data.map(parseDataX),
      padding: 0.4,
      align: 0
    }),
    [xMax, data, parseDataX]
  );
  const yScale = useMemo14(() => {
    const domainUpper = Math.max(...data.map(parseDataY));
    return scaleLinear({
      range: [yMax, 0],
      domain: [0, domainUpper === 0 ? 1 : domainUpper],
      nice: true
    });
  }, [yMax, data, parseDataY]);
  const yScaleTicks = yScale.ticks();
  const calcGridRowTickValues = useMemo14(() => {
    const max = Math.max(...yScaleTicks);
    const third = max / 3;
    return [0, third, 2 * third, max];
  }, [yScaleTicks]);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip
  } = useTooltip();
  const handleMouseOver = (barX, barY, d) => {
    showTooltip({
      tooltipLeft: barX,
      tooltipTop: barY,
      tooltipData: d
    });
  };
  const [id, setId] = useState36();
  useEffect29(() => {
    setId(uuidv43());
  }, []);
  return /* @__PURE__ */ jsxs124(Box60, { position: "relative", children: [
    /* @__PURE__ */ jsxs124(
      "svg",
      {
        width,
        height,
        onMouseLeave: hideTooltip,
        overflow: "visible",
        children: [
          /* @__PURE__ */ jsx186(
            LinearGradient,
            {
              id: `fill-${id}`,
              from: style.barGradient?.from ?? "rgba(77, 217, 219, 0.69)",
              to: style.barGradient?.to ?? "rgba(77, 217, 219, 1.0)"
            }
          ),
          /* @__PURE__ */ jsx186(
            GridRows,
            {
              scale: yScale,
              width: xMax,
              tickValues: calcGridRowTickValues,
              top: marginTop,
              left: marginLeft,
              stroke: "#E4E8EF",
              strokeDasharray: style.yAxis?.strokeDash ?? void 0
            }
          ),
          showColumnGrid && /* @__PURE__ */ jsx186(
            GridColumns,
            {
              scale: xScale,
              height: yMax,
              top: marginTop,
              left: marginLeft,
              numTicks: data.length,
              stroke: "#E4E8EF",
              strokeDasharray: "2"
            }
          ),
          /* @__PURE__ */ jsx186(AnimatePresence, { initial: false, children: tooltipData && /* @__PURE__ */ jsx186(
            motion.g,
            {
              initial: {
                opacity: 0,
                x: (tooltipLeft || 0) + xScale.bandwidth() / 2
              },
              animate: {
                opacity: 1,
                x: (tooltipLeft || 0) + xScale.bandwidth() / 2
              },
              exit: { opacity: 0 },
              transition: { ease: "linear", duration: 0.1 },
              children: /* @__PURE__ */ jsx186(
                Line,
                {
                  from: { x: 0, y: marginTop },
                  to: { x: 0, y: marginTop + yMax },
                  stroke: "rgba(228, 232, 239, 1)",
                  strokeWidth: 2,
                  pointerEvents: "none"
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx186(Group, { children: data.map((d) => {
            const parsedX = parseDataX(d);
            const barWidth = xScale.bandwidth();
            const barHeight = yMax - (yScale(parseDataY(d)) ?? 0);
            const barX = (xScale(parsedX) || 0) + marginLeft;
            const barY = yMax - barHeight + marginTop;
            return /* @__PURE__ */ jsxs124(Fragment28, { children: [
              /* @__PURE__ */ jsx186(
                Bar,
                {
                  width: barWidth,
                  height: xMax - marginBottom,
                  fill: "transparent",
                  x: barX,
                  y: marginTop,
                  onMouseOver: () => handleMouseOver(barX, barY, d)
                }
              ),
              /* @__PURE__ */ jsx186(
                BarRounded,
                {
                  x: barX || 0,
                  y: barY,
                  width: barWidth,
                  height: barHeight,
                  fill: `url(#fill-${id})`,
                  radius: 2,
                  top: true,
                  pointerEvents: "none"
                }
              )
            ] }, `bar-${parsedX}`);
          }) }),
          /* @__PURE__ */ jsx186(AnimatePresence, { initial: false, children: tooltipData && /* @__PURE__ */ jsx186(
            motion.circle,
            {
              initial: { opacity: 0 },
              animate: {
                opacity: 1,
                cx: (tooltipLeft || 0) + xScale.bandwidth() / 2,
                cy: tooltipTop
              },
              exit: { opacity: 0 },
              transition: { ease: "linear", duration: 0.1 },
              r: 4,
              fill: "#00A0A2",
              stroke: "white",
              strokeWidth: 2,
              pointerEvents: "none"
            }
          ) }),
          /* @__PURE__ */ jsx186(
            AxisLeft,
            {
              scale: yScale,
              left: marginLeft + 5,
              top: marginTop,
              hideAxisLine: true,
              hideTicks: true,
              tickFormat: (v, index, values) => index === 0 || index === values.length - 1 ? v.toString() : "",
              tickLabelProps: {
                fill: GreyGrey6033,
                ...Lato12Regular4
              }
            }
          ),
          /* @__PURE__ */ jsx186(
            AxisBottom,
            {
              left: marginLeft,
              scale: xScale,
              top: yMax + marginTop,
              tickFormat: axisBottomTickFormat,
              numTicks: 6,
              hideTicks: true,
              stroke: "#E4E8EF",
              tickComponent: ({ x, y, formattedValue }) => {
                const isFirst = x === xScale.bandwidth() / 2;
                return /* @__PURE__ */ jsxs124("g", { children: [
                  useXAxisLines && /* @__PURE__ */ jsx186(
                    "line",
                    {
                      x1: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                      x2: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                      y1: "0",
                      y2: "8",
                      fill: "transparent",
                      shapeRendering: "crispEdges",
                      stroke: "#D1D9E4",
                      strokeWidth: "1",
                      strokeLinecap: "square"
                    }
                  ),
                  /* @__PURE__ */ jsx186(
                    "text",
                    {
                      x: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                      y: y + 4,
                      textAnchor: isFirst && alignFirstXAxisLabelToLeft ? "left" : "middle",
                      fill: style.xAxis?.labelColor ?? "#485972",
                      ...Lato12Regular4,
                      children: formattedValue
                    }
                  )
                ] });
              }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx186(AnimatePresence, { initial: false, children: tooltipOpen && tooltipData && /* @__PURE__ */ jsx186(
      motion.div,
      {
        initial: {
          left: (tooltipLeft || 0) + xScale.bandwidth() / 2,
          opacity: 0
        },
        animate: {
          left: (tooltipLeft || 0) + xScale.bandwidth() / 2,
          opacity: 1
        },
        exit: { opacity: 0 },
        transition: { ease: "linear", duration: 0.1 },
        style: {
          transform: "translate(-50%, 0px)",
          border: "rgba(228, 232, 239, 1) 1px solid",
          background: "white",
          padding: "4px 8px",
          borderRadius: "44px",
          boxShadow: "0px 2px 4px 0px rgba(20, 23, 28, 0.08)",
          top: 0,
          position: "absolute",
          pointerEvents: "none"
        },
        children: tooltip(tooltipData)
      }
    ) })
  ] });
};

// src/charts/EmptyChart.tsx
import { useEffect as useEffect30, useMemo as useMemo15, useState as useState37 } from "react";
import { Line as Line2 } from "@visx/shape";
import { Group as Group2 } from "@visx/group";
import { LinearGradient as LinearGradient2 } from "@visx/gradient";
import { GridRows as GridRows2 } from "@visx/grid";
import { scaleBand as scaleBand2, scaleLinear as scaleLinear2 } from "@visx/scale";
import { AxisBottom as AxisBottom2, AxisLeft as AxisLeft2 } from "@visx/axis";
import { Box as Box61 } from "@chakra-ui/react";
import { v4 as uuidv44 } from "uuid";
import { jsx as jsx187, jsxs as jsxs125 } from "react/jsx-runtime";
var EmptyChart = ({
  width,
  height,
  data,
  parseDataX,
  axisBottomTickFormat,
  alignFirstXAxisLabelToLeft = true,
  style = {}
}) => {
  const marginLeft = 30;
  const marginTop = 12;
  const marginBottom = 26;
  const xMax = width - marginLeft;
  const yMax = height - marginTop - marginBottom;
  const xScale = useMemo15(
    () => scaleBand2({
      range: [0, xMax],
      domain: data.map(parseDataX),
      padding: 0.4,
      align: 0
    }),
    [xMax, data, parseDataX]
  );
  const yScale = useMemo15(() => {
    return scaleLinear2({
      range: [yMax, 0],
      domain: [0, 1],
      nice: true
    });
  }, [yMax]);
  const yScaleTicks = yScale.ticks();
  const calcGridRowTickValues = useMemo15(() => {
    const max = Math.max(...yScaleTicks);
    const third = max / 2;
    return [0, third, max];
  }, [yScaleTicks]);
  const [id, setId] = useState37();
  useEffect30(() => {
    setId(uuidv44());
  }, []);
  return /* @__PURE__ */ jsx187(Box61, { position: "relative", children: /* @__PURE__ */ jsxs125(
    "svg",
    {
      width,
      height,
      overflow: "visible",
      children: [
        /* @__PURE__ */ jsx187(
          LinearGradient2,
          {
            id: `fill-${id}`,
            from: style.barGradient?.from ?? "rgba(77, 217, 219, 0.69)",
            to: style.barGradient?.to ?? "rgba(77, 217, 219, 1.0)"
          }
        ),
        /* @__PURE__ */ jsx187(
          GridRows2,
          {
            scale: yScale,
            width: xMax,
            tickValues: calcGridRowTickValues,
            top: marginTop,
            left: marginLeft,
            stroke: "#E4E8EF",
            strokeDasharray: style.yAxis?.strokeDash ?? void 0
          }
        ),
        /* @__PURE__ */ jsx187(Group2, { top: marginTop, left: marginLeft, children: /* @__PURE__ */ jsx187(
          Line2,
          {
            from: { x: 0, y: yMax / 2 },
            to: { x: xMax, y: yMax / 2 },
            stroke: "rgba(95, 92, 255, 1)",
            strokeWidth: 1,
            pointerEvents: "none"
          }
        ) }),
        /* @__PURE__ */ jsx187(
          AxisLeft2,
          {
            scale: yScale,
            left: marginLeft + 5,
            top: marginTop,
            hideAxisLine: true,
            hideTicks: true,
            tickFormat: (_, index, values) => (
              // Cheeky bit of obfuscation to hide the fact the scale is technically 0 -> 1
              index === 0 || index === values.length - 1 ? "0" : ""
            ),
            tickLabelProps: {
              fill: "#485972",
              fontSize: 12
            }
          }
        ),
        /* @__PURE__ */ jsx187(
          AxisBottom2,
          {
            left: marginLeft,
            scale: xScale,
            top: yMax + marginTop,
            tickFormat: axisBottomTickFormat,
            numTicks: 6,
            hideTicks: true,
            stroke: "#E4E8EF",
            tickComponent: ({ x, y, formattedValue }) => {
              const isFirst = x === xScale.bandwidth() / 2;
              return /* @__PURE__ */ jsxs125("g", { children: [
                /* @__PURE__ */ jsx187(
                  "line",
                  {
                    x1: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                    x2: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                    y1: "0",
                    y2: "8",
                    fill: "transparent",
                    shapeRendering: "crispEdges",
                    stroke: "#D1D9E4",
                    strokeWidth: "1",
                    strokeLinecap: "square"
                  }
                ),
                /* @__PURE__ */ jsx187(
                  "text",
                  {
                    x: isFirst && alignFirstXAxisLabelToLeft ? 0 : x,
                    y: y + 4,
                    fontSize: 12,
                    textAnchor: isFirst && alignFirstXAxisLabelToLeft ? "left" : "middle",
                    fill: style.xAxis?.labelColor ?? "#485972",
                    children: formattedValue
                  }
                )
              ] });
            }
          }
        )
      ]
    }
  ) });
};

// src/charts/UsageChart/UsageChart.tsx
import { Box as Box62, Flex as Flex56, Text as Text32 } from "@chakra-ui/react";
import {
  GreyGrey30 as GreyGrey3024,
  GreyGrey60 as GreyGrey6034,
  GreyGrey90 as GreyGrey9015,
  IndigoIndigo50 as IndigoIndigo5024,
  Lato12Bold,
  Lato12Regular as Lato12Regular5,
  Lato13Bold as Lato13Bold22,
  Lato13Regular as Lato13Regular37,
  Lato18Bold as Lato18Bold2,
  ShadowXs as ShadowXs8
} from "@sequencehq/design-tokens";
import { localDateWithFormat } from "@sequencehq/formatters";
import { formatDateRange as formatDateRange2 } from "@sequencehq/utils/dist/dates";
import { ParentSize } from "@visx/responsive";
import { useEffect as useEffect31, useState as useState38 } from "react";
import { Fragment as Fragment29, jsx as jsx188, jsxs as jsxs126 } from "react/jsx-runtime";
var usagePeriodOptions = [
  {
    id: "CURRENT",
    description: "Current billing period"
  },
  {
    id: "LAST",
    description: "Last billing period"
  },
  {
    id: "LAST_THREE",
    description: "Last 3 billing periods"
  },
  {
    id: "ALL",
    description: "All time"
  }
];
var UsageChart = ({
  usagePeriodType,
  setUsagePeriodType,
  usageDataResponse,
  metricName,
  isUpdating = false
}) => {
  const [dateFormat, setDateFormat] = useState38("d MMM");
  const [alignFirstXAxisLabelToLeft, setAlignFirstXAxisLabelToLeft] = useState38(true);
  useEffect31(() => {
    const isUsagePeriodTypeLastThreeOrAll = usagePeriodType === "LAST_THREE" || usagePeriodType === "ALL";
    const newDateFormat = isUsagePeriodTypeLastThreeOrAll ? "MMM yyyy" : "d MMM";
    setDateFormat(newDateFormat);
    setAlignFirstXAxisLabelToLeft(!isUsagePeriodTypeLastThreeOrAll);
  }, [usageDataResponse]);
  const { data: usageData } = usageDataResponse;
  if (!usageData) {
    return /* @__PURE__ */ jsx188(Fragment29, {});
  }
  return /* @__PURE__ */ jsxs126(
    Box62,
    {
      borderRadius: "6px",
      padding: "16px",
      border: `1px solid ${GreyGrey3024}`,
      boxShadow: ShadowXs8,
      children: [
        /* @__PURE__ */ jsxs126(
          Flex56,
          {
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            children: [
              /* @__PURE__ */ jsxs126(Flex56, { flexDirection: "column", gap: "8px", children: [
                /* @__PURE__ */ jsx188(Text32, { ...Lato13Bold22, fontWeight: 600, color: GreyGrey6034, children: metricName }),
                /* @__PURE__ */ jsx188(Text32, { ...Lato18Bold2, color: GreyGrey9015, children: usageData.total }),
                /* @__PURE__ */ jsx188(Text32, { ...Lato13Regular37, color: GreyGrey6034, children: formatDateRange2({
                  from: new Date(usageData.usageStart),
                  to: usagePeriodType === "LAST" ? new Date(usageData.usageEnd) : /* @__PURE__ */ new Date(),
                  separator: "-"
                }) })
              ] }),
              /* @__PURE__ */ jsx188(
                InputSelectControl,
                {
                  options: usagePeriodOptions.map((value) => ({
                    value: value.id,
                    label: value.description
                  })),
                  placeholder: "Choose customer",
                  initialValue: usagePeriodType,
                  onChange: (periodType) => setUsagePeriodType(periodType),
                  matchWidth: true,
                  disabled: isUpdating
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx188(Box62, { height: "28px" }),
        /* @__PURE__ */ jsx188(
          Box62,
          {
            height: "200px",
            width: "100%",
            opacity: isUpdating ? 0.5 : 1,
            transition: "opacity 250ms",
            children: /* @__PURE__ */ jsx188(ParentSize, { children: (parent) => usageData.total === "0" ? /* @__PURE__ */ jsx188(
              EmptyChart,
              {
                width: parent.width,
                height: parent.height,
                data: usageData.usageData,
                parseDataX: (d) => d.periodStart,
                parseDataY: (d) => Number(d.amount),
                axisBottomTickFormat: (v) => localDateWithFormat(v.toString(), dateFormat),
                alignFirstXAxisLabelToLeft,
                style: {
                  barGradient: {
                    from: "rgba(150, 148, 235, 0.69)",
                    to: "#716FFF"
                  },
                  yAxis: {
                    strokeDash: "2"
                  }
                }
              }
            ) : /* @__PURE__ */ jsx188(
              BarChart,
              {
                width: parent.width,
                height: parent.height,
                data: usageData.usageData,
                parseDataX: (d) => d.periodStart,
                parseDataY: (d) => Number(d.amount),
                axisBottomTickFormat: (v) => localDateWithFormat(v.toString(), dateFormat),
                tooltip: (d) => {
                  return /* @__PURE__ */ jsxs126(Text32, { as: "p", whiteSpace: "nowrap", ...Lato12Regular5, children: [
                    /* @__PURE__ */ jsx188(Text32, { as: "span", ...Lato12Bold, children: d.amount }),
                    " \u2013 ",
                    localDateWithFormat(d.periodStart, dateFormat)
                  ] });
                },
                alignFirstXAxisLabelToLeft,
                style: {
                  barGradient: {
                    from: IndigoIndigo5024,
                    to: IndigoIndigo5024
                  },
                  xAxis: {
                    labelColor: GreyGrey6034
                  }
                }
              }
            ) })
          }
        )
      ]
    }
  );
};

// src/charts/XYChart/theme.ts
import {
  GreyGrey40 as GreyGrey4021,
  GreyGrey60 as GreyGrey6035,
  IndigoIndigo50 as IndigoIndigo5025
} from "@sequencehq/design-tokens";
import { buildChartTheme } from "@visx/xychart";
var primaryColor = IndigoIndigo5025;
var theme = buildChartTheme({
  backgroundColor: "transparent",
  colors: [primaryColor],
  gridColor: GreyGrey4021,
  gridColorDark: GreyGrey4021,
  svgLabelSmall: {
    fill: GreyGrey6035
  },
  tickLength: 0,
  xTickLineStyles: {
    stroke: "transparent"
  },
  yTickLineStyles: {
    stroke: "transparent"
  },
  gridStyles: {
    strokeDasharray: "3, 3"
  }
});

// src/charts/XYChart/XYChart.tsx
import {
  AnimatedAxis,
  Axis,
  XYChart as VisxXYChart,
  AnimatedGrid,
  Grid as Grid13
} from "@visx/xychart";
import { useCallback as useCallback17, useMemo as useMemo16 } from "react";
import { GreyGrey60 as GreyGrey6036, Lato12Regular as Lato12Regular6 } from "@sequencehq/design-tokens";
import { ParentSize as ParentSize2 } from "@visx/responsive";

// src/charts/XYChart/XYChartTooltip.tsx
import { Tooltip as Tooltip3 } from "@visx/xychart";
import { jsx as jsx189 } from "react/jsx-runtime";
var XYChartTooltip = ({
  renderTooltip
}) => {
  return /* @__PURE__ */ jsx189(
    Tooltip3,
    {
      showVerticalCrosshair: true,
      snapTooltipToDatumY: true,
      verticalCrosshairStyle: {
        stroke: primaryColor,
        strokeWidth: 1
      },
      style: {
        position: "absolute",
        zIndex: 2e3
      },
      renderTooltip: ({ tooltipData }) => {
        if (!tooltipData || !tooltipData.nearestDatum) {
          return null;
        }
        return renderTooltip(tooltipData.nearestDatum.datum);
      }
    }
  );
};

// src/charts/XYChart/series/BarSeries.tsx
import { AnimatedBarSeries } from "@visx/xychart";
import { jsx as jsx190 } from "react/jsx-runtime";
var BarSeries = ({
  data,
  xAccessor,
  yAccessor
}) => {
  return /* @__PURE__ */ jsx190(
    AnimatedBarSeries,
    {
      dataKey: "value",
      data,
      radius: 2,
      radiusTop: true,
      xAccessor,
      yAccessor
    }
  );
};

// src/charts/XYChart/series/LineSeries.tsx
import { curveMonotoneY } from "@visx/curve";
import { AnimatedLineSeries } from "@visx/xychart";
import { jsx as jsx191 } from "react/jsx-runtime";
var LineSeries = ({
  data,
  xAccessor,
  yAccessor
}) => {
  return /* @__PURE__ */ jsx191(
    AnimatedLineSeries,
    {
      dataKey: "value",
      data,
      xAccessor,
      yAccessor,
      curve: curveMonotoneY
    }
  );
};

// src/charts/XYChart/XYChart.tsx
import { AxisBottom as AxisBottom3 } from "@visx/axis";
import { scaleBand as scaleBand3 } from "@visx/scale";
import { format as format6 } from "@sequencehq/utils/dates";
import { jsx as jsx192, jsxs as jsxs127 } from "react/jsx-runtime";
var ANIMATION_TRAJECTORY = "max";
var NUM_TICKS = 2;
var XYChart = ({
  variant,
  dateFrom,
  dateTo,
  data,
  unit,
  unitTickValues,
  renderTooltip,
  animate = true,
  dateFormat = "MMM yyyy"
}) => {
  const accessors = useMemo16(
    () => ({
      date: (d) => d.date,
      value: (d) => d.value
    }),
    []
  );
  const tickFormat = useCallback17(
    (value) => {
      return `${unit}${Intl.NumberFormat("en-GB", {
        notation: "compact",
        maximumFractionDigits: 1
      }).format(value)}`;
    },
    [unit]
  );
  const renderDataSeries = () => {
    switch (variant) {
      case "line":
        return /* @__PURE__ */ jsx192(
          LineSeries,
          {
            data,
            xAccessor: accessors.date,
            yAccessor: accessors.value
          }
        );
      case "bar":
        return /* @__PURE__ */ jsx192(
          BarSeries,
          {
            data,
            xAccessor: accessors.date,
            yAccessor: accessors.value
          }
        );
      default:
        return null;
    }
  };
  const dateTickValues = [dateFrom, dateTo];
  const shouldUseCustomTicks = data.length > 0;
  const yAxisMargin = useMemo16(() => {
    const maxDigits = Math.max(
      ...unitTickValues.map((value) => tickFormat(value).length)
    );
    return maxDigits * 4.7;
  }, [tickFormat, unitTickValues]);
  return /* @__PURE__ */ jsx192(ParentSize2, { children: ({ width, height }) => {
    const margins = {
      top: 10,
      right: 29 + yAxisMargin,
      bottom: 40,
      left: 0
      //29
    };
    const xScale = scaleBand3({
      range: [0, width - margins.right],
      domain: data.map((d) => accessors.date(d)),
      padding: 0.3,
      align: 0
    });
    return /* @__PURE__ */ jsxs127(
      VisxXYChart,
      {
        theme,
        xScale: {
          type: "band",
          padding: 0.3
        },
        yScale: {
          type: "linear",
          zero: true,
          /**
           * This determines the scale of the y-axis, meaning we're able to - if we choose
           * - have a y-axis larger than the maximum size of a given value.
           */
          domain: shouldUseCustomTicks ? [0, unitTickValues[unitTickValues.length - 1]] : void 0
        },
        height,
        width,
        margin: {
          top: 10,
          right: 29 + yAxisMargin,
          bottom: 40,
          left: 0
          //29
        },
        children: [
          animate ? /* @__PURE__ */ jsx192(
            AnimatedGrid,
            {
              rows: true,
              columns: false,
              tickValues: shouldUseCustomTicks ? unitTickValues : [dateFrom, dateTo],
              animationTrajectory: ANIMATION_TRAJECTORY
            }
          ) : /* @__PURE__ */ jsx192(
            Grid13,
            {
              rows: true,
              columns: false,
              tickValues: shouldUseCustomTicks ? unitTickValues : [dateFrom, dateTo]
            }
          ),
          animate ? /* @__PURE__ */ jsx192(
            AnimatedAxis,
            {
              hideAxisLine: true,
              orientation: "right",
              numTicks: NUM_TICKS,
              animationTrajectory: ANIMATION_TRAJECTORY,
              tickLabelProps: {
                ...Lato12Regular6
              },
              tickFormat,
              tickLength: 3,
              tickValues: shouldUseCustomTicks ? unitTickValues : [0, 0]
            }
          ) : /* @__PURE__ */ jsx192(
            Axis,
            {
              hideAxisLine: true,
              orientation: "right",
              numTicks: NUM_TICKS,
              tickLabelProps: {
                ...Lato12Regular6
              },
              tickFormat,
              tickLength: 3,
              tickValues: shouldUseCustomTicks ? unitTickValues : [0, 0]
            }
          ),
          data.length >= 1 || !animate ? /* @__PURE__ */ jsx192(
            AxisBottom3,
            {
              scale: xScale,
              orientation: "bottom",
              numTicks: NUM_TICKS,
              tickLabelProps: {
                ...Lato12Regular6
              },
              strokeWidth: 1,
              tickFormat: (date) => format6(new Date(date), dateFormat),
              tickValues: dateTickValues,
              top: height - margins.bottom,
              hideTicks: true,
              stroke: "#E4E8EF",
              tickComponent: ({ x, formattedValue }) => {
                const isFirst = x === xScale.bandwidth() / 2;
                return /* @__PURE__ */ jsx192("g", { children: /* @__PURE__ */ jsx192(
                  "text",
                  {
                    x: isFirst ? 0 : width - margins.right - 25,
                    y: 30,
                    fontSize: 12,
                    textAnchor: isFirst ? "left" : "middle",
                    fill: GreyGrey6036,
                    children: formattedValue
                  }
                ) });
              }
            }
          ) : /* @__PURE__ */ jsx192(
            AnimatedAxis,
            {
              orientation: "bottom",
              numTicks: NUM_TICKS,
              tickLabelProps: {
                ...Lato12Regular6
              },
              strokeWidth: 1,
              tickFormat: (date) => format6(new Date(date), dateFormat),
              tickValues: dateTickValues,
              tickLength: 30
            }
          ),
          renderDataSeries(),
          /* @__PURE__ */ jsx192(XYChartTooltip, { renderTooltip })
        ]
      }
    );
  } });
};
export {
  ActivityCard,
  AmazonRedshiftIcon,
  ArrowLeftIcon,
  AttachmentUIV2,
  AvalaraIcon,
  Badge,
  Banner,
  BarChart,
  BulkActionActionsList,
  BulkActionBar,
  BulkActions,
  BulkActionsPositioner,
  BulkActionsSummary,
  Button2 as Button,
  CalendarIcon,
  Card,
  CheckCircleIcon2 as CheckCircleIcon,
  Checkbox3 as Checkbox,
  CheckboxGroupField,
  CheckboxGroupInput,
  CheckboxIcon,
  CheckboxLineField,
  CheckboxLineInput,
  ChevronDownIcon2 as ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon2 as ChevronRightIcon,
  ChevronUpIcon,
  ClearButton,
  CloseTrigger,
  Control,
  ControlContext,
  ControlContextProvider,
  ControlEmptyState,
  ConvertInvoicesToDraftDialog,
  CopyButton,
  CreateMenuItem2 as CreateMenuItem,
  CreditNoteIcon,
  CrossIcon,
  CustomerCard,
  CustomerIcon,
  CustomerLogo,
  DateControl,
  DateDurationField,
  DateDurationInput,
  DatePickerField,
  DatePickerInput,
  DateRangePicker,
  DateRangePickerInput,
  DayOfMonthField,
  DayOfMonthInput,
  DaysDurationPicker,
  DeleteInvoicesDialog,
  Dialog,
  Divider3 as Divider,
  EmptyChart,
  EmptyState,
  EntityLink,
  EnvironmentBanner,
  ErrorMessage,
  FilePickerButton,
  FinalizeInvoicesDialog,
  GcpLogsIcon,
  GoogleBigQueryIcon,
  GoogleIcon,
  GoogleMailIcon,
  GoogleSheetsIcon,
  GraphIcon,
  HorizontalSelectorField,
  HorizontalSelectorInput,
  HubspotIcon,
  InfiniteScroll,
  InfoBox,
  InfoIcon,
  InfoIcon2,
  InfoPopover,
  InfoPopoverIcon,
  InfoRow,
  InfoToast,
  InlineToast,
  InputLabel,
  InputSelectControl,
  InputSelectControlField,
  InputSelectControlLegacyStyleBridge,
  InputSelectControlLegacyStyleBridgeField,
  Inspector2 as Inspector,
  InspectorEmptyState,
  InvoiceBulkActionBar,
  InvoiceIcon,
  KebabIcon,
  KebabMenu2 as KebabMenu,
  KebabMenuItem,
  LightningIcon,
  LineItemGroup,
  ListButton2 as ListButton,
  ListContent2 as ListContent,
  ListDivider2 as ListDivider,
  ListTitle2 as ListTitle,
  MerchantIcon,
  MergeInvoicesDialog,
  MetricCard,
  MicrosoftIcon,
  Modal,
  ModalContext,
  ModalContextProvider,
  MultiSelectControl,
  MultiSelectControlCore,
  MultiTagField,
  MultiTagInput,
  NetsuiteIcon,
  NumberField,
  NumberInput,
  OutlookIcon,
  OverviewCard,
  PageTitle,
  PaidIcon,
  PartiallyPaidIcon,
  PercentageField,
  PercentageInput,
  Pill,
  PillRow,
  PillSelectControl,
  PlanIcon,
  PlanMinimumIcon,
  PlusIcon,
  Popover2 as Popover,
  PopoverWrapper,
  PreviewCard,
  PriceField,
  PriceInput,
  QuickbooksIcon,
  RadioButton,
  RadioButtonGroup,
  RadioGroupField,
  RadioGroupInput,
  RecalculateInvoicesDialog,
  RedirectIcon,
  SalesforceIcon,
  ScheduleIcon,
  ScrollFade,
  SearchSelectField,
  SearchSelectInput,
  SelectControl,
  SelectField,
  SelectFieldWithActions,
  SelectIcon,
  SelectInput,
  SendInvoicesDialog,
  SendPaymentReminderForInvoicesDialog,
  SentryIcon,
  SequenceKebabMenu,
  SequenceMenu,
  MenuDivider as SequenceMenuDivider,
  MenuItem as SequenceMenuItem,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  SharedModalComponent,
  SimpleAsyncModal,
  SimpleAsyncModalUI,
  SimpleDialog,
  SimpleDialogUI,
  SimpleModal,
  SimpleModalUI,
  SimpleSearchMenuInput,
  SimpleTableInput,
  Skeleton,
  SlackIcon,
  SnowflakeIcon,
  StandardLayout,
  StripeIcon,
  SubscriptionIcon,
  Switch,
  SwitchLineField,
  SwitchLineInput,
  TaxRateIcon,
  TextAreaField,
  TextAreaFieldInput,
  TextAreaInput,
  TextField,
  TextFieldInput,
  TextInput,
  TextInputField,
  TextOverflow,
  Toast,
  Tooltip,
  TooltipLegacy,
  Totals,
  TrashIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  UncollectibleIcon,
  UnpaidIcon,
  UsageChart,
  VirtualList2 as VirtualList,
  VoidInvoicesDialog,
  Wordmark,
  XYChart,
  XeroIcon,
  badgeSizes,
  dayzedAdapter,
  defaultDurationOptions,
  filterOptionGroupsOnGroupNameOnly,
  sentiments,
  toOptionGroupArray,
  useControlContext,
  useErrorDisplay,
  useInspectorItem,
  useModalContext,
  withUnmountOnModalClose
};
//# sourceMappingURL=index.js.map